import type {
  StaffingCoverage,
  AiMessageActionHandler,
  BprGraphAreaPriority,
  ChatMode,
  ContractRecommendation,
  DealSow,
  EstimateGroundingRef,
  DealCategoryOption,
  EstimateVersionSummary,
  JiraSyncSummary,
  JiraTicketPlan,
  RagReference,
  ResourcePlanPricingBasis,
  ResourcePlanRoleLevel,
  RolePricingSelection,
  SalesDealAvailableField,
  SalesDealAvailableStructure,
  SavedDealEstimate,
  ScopeMeta,
  SowVersionSummary,
  StaffingPlanEntry,
} from '@protoss/shared';
import {
  ADDITIONAL_INFO_GROUP,
  ADHOC_QUESTION_CONTROLS,
  coerceCount,
  coercePositiveNumber,
  computeContractRecommendation,
  isStageAvailable,
  moveAdhocFieldToArea,
  normalizeCurrencyValue,
  parseCurrencyValue,
  REFERENCE_ESTIMATE_EXTENSIONS,
  SCOPE_BRIEF,
  stripSowDataTableNodes,
  TRANSCRIPT_MAX_FILES,
  SCOPE_IMPORT_TARGET_LABELS,
  SCOPE_IMPORT_TARGETS,
  isScopeFileUpload,
  SCOPE_FILE_EXTENSIONS,
  solutionCandidatesEnabledForCategory,
  DEAL_DOCUMENT_ACCEPT_ATTR,
  DEAL_DOCUMENT_DRIVE_MIME_TYPES,
  DEAL_DOCUMENT_FORMATS_LABEL,
  isMaxioProductKey,
  isQuestionnaireGreenfieldProductKey,
  isSowSupportedProductKey,
  NO_SCOPE_CAPTURED_MESSAGE,
} from '@protoss/shared';
import {
  AutocompleteField,
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  collapseAllHeadings,
  Cross1Icon,
  CurrencyInput,
  Dialog,
  DropdownMenu,
  DynamicSearchField,
  expandAllHeadings,
  FileTextIcon,
  IconSize,
  ListBulletIcon,
  MultiSelect,
  NumberInput,
  Pencil1Icon,
  Pill,
  PillDropdown,
  PlusIcon,
  PillRadio,
  PillSelect,
  RequiredLabel,
  SegmentedControl,
  Select,
  SelectItem,
  SkeletonBlock,
  SliderField,
  TagInput,
  TagSelectField,
  TextArea,
  TextField,
  Tooltip,
  type Editor,
  type MultiSelectAnnotationConfig,
  type PillVariant,
  type TabsItem,
} from '@protoss/ui';
import * as React from 'react';

import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  BPR_ARCHITECTURE_SECTION_KEY,
  BprDealArchitecture,
} from '../features/bpr/components/BprDealArchitecture';
import { BprSectionDiagram } from '../features/bpr/components/BprSectionDiagram';
import { bprApi } from '../features/bpr/services/bprApi';
import {
  ensureSectionDiagrams,
  generateDealDiagrams,
  prebuildDealGraph,
  sectionsMissingFlow,
  refreshDealFutureState,
  useDealDiagrams,
} from '../features/bpr/services/dealDiagramsStore';
import { AppNav } from '../components/AppNav';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { PageErrorState } from '../components/shared/PageErrorState';
import {
  AiProvenanceBadge,
  StackOptionField,
  Wizard,
  type WizardStep,
  type WizardStepQuestion,
} from '../components/shared/sales';
import { VendorCard, VendorCardSkeleton, VendorLogo } from '../components/shared/VendorCard';
import {
  AppShell,
  ContextDrawer,
  MainContentShell,
  MainContentSidePanelContext,
  type RightRailItem,
} from '../components/shell';
import { useAuth } from '../contexts/AuthContext';
import { useModuleChatSession } from '../contexts/ChatSessionContext';
import { useDirtyStateContext } from '../contexts/DirtyStateContext';
import { useLayout, useLayoutConfig } from '../contexts/LayoutContext';
import { usePublicAppConfig } from '../contexts/PublicAppConfigContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { AiChatActionProvider } from '../features/ai-chat/AiChatActionContext';
import { AiChatReferenceProvider } from '../features/ai-chat/AiChatReferenceContext';
import { AiChatPane } from '../features/ai-chat/components/AiChatPane';
import { ChatConnectionIndicator } from '../features/ai-chat/components/ChatConnectionIndicator';
import { CHAT_MODE_OPTIONS } from '../features/ai-chat/components/ChatModeToggle';
import {
  ComposerPicker,
  type ComposerPickerOption,
} from '../features/ai-chat/components/ComposerPicker';
import {
  SessionControls,
  SessionTitleEditable,
} from '../features/ai-chat/components/SessionControls';
import { resolveProjectMenuIcon } from '../features/project-menu/icons/iconRegistry';
import { CurrentTechnologyStackLandscape } from '../features/sales/components/CurrentTechnologyStackLandscape';
import { resolveTechStackSystemsFromAnswers } from '../features/sales/components/techStackLandscapeUtils';
import { TechStackEditor } from '../features/sales/components/TechStackEditor';
import {
  DealDocumentsPanel,
  DealDocumentViewPanel,
} from '../features/sales/components/DealDocumentsPanel';
import { AdhocScopeControls } from '../features/sales/components/AdhocScopeControls';
import { DealViewSurface } from '../features/sales/components/DealViewSurface';
import { DealAdditionalNotesBox } from '../features/sales/components/DealAdditionalNotesBox';
import { DealDocumentPasteBox } from '../features/sales/components/DealDocumentPasteBox';
import {
  driveFileKey,
  formatFileIssue,
  isFileLevelUploadError,
  localFileKey,
  NOTES_NOT_SAVED_MESSAGE,
  uploadFailureReason,
  type FileNote,
} from '../features/sales/lib/dealDocumentUpload';
import { DealEstimatePane } from '../features/sales/components/DealEstimatePane';
import { ReferenceEstimateDialog } from '../features/sales/components/ReferenceEstimateDialog';
import { DealResourcePlanPane } from '../features/sales/components/DealResourcePlanPane';
import { DealSowPane } from '../features/sales/components/DealSowPane';
import { clearSowRenderContextCache } from '../features/sales/components/DealSowDocView';
import { DealScopePane } from '../features/sales/components/DealScopePane';
import { parseScopeDocKey } from '../features/sales/utils/scope-doc';
import { harvestScopeDoc } from '../features/sales/utils/scope-doc-harvest';
import { DealPrioritizationPane } from '../features/sales/components/DealPrioritizationPane';
import { RegenerateMenuButton } from '../features/sales/components/RegenerateMenuButton';
import { StaffingRatesDialog } from '../features/sales/components/StaffingRatesDialog';
import { isDealPageBlockedForNoScope } from '../features/sales/utils/no-scope-gate';
import { SalesLandingChat } from '../features/sales/components/SalesLandingChat';
import {
  STATIC_SOW_WIZARD_CATEGORY_KEYS,
  type DealWorkspaceOutletContext,
  type GenerateDisabledReason,
  type ViewingDocState,
} from '../features/sales/dealWorkspaceContext';
import { useDealDocuments } from '../features/sales/hooks/useDealDocuments';
import { useMeetingContext } from '../features/sales/hooks/useMeetingContext';
import { MeetingContextPanel } from '../features/sales/components/MeetingContextPanel';
import { useDealGeneration } from '../features/sales/hooks/useDealGeneration';
import { useQuestionnaireAutocomplete } from '../features/sales/hooks/useQuestionnaireAutocomplete';
import { useSalesRightRail } from '../features/sales/hooks/useSalesRightRail';
import {
  salesApi,
  type ScopeFileParseResponse,
  SowReadinessError,
  type ResourcePlanAvailableRole,
  type SalesDealQuestionnaireSummary,
  type SalesDealSummary,
  type SalesDealTemplateFieldSummary,
  type SalesDealTemplateGroupSummary,
  type WorkspaceProductSummary,
} from '../features/sales/services/salesApi';
import {
  buildQuestionnaireFieldLookup,
  evaluateConditionalRule,
  getFieldNestingLevelByRules,
  isFieldEnabledByRules,
  isFieldVisibleByRules,
  isGroupHiddenByRules,
  normalizeAnswerChoice,
  normalizeMultiSelectValue,
  normalizeQuestionnaireDirtyValue,
  normalizeTextValue,
  type QuestionnaireAnswerMap,
  type QuestionnaireFieldLookup,
} from '../features/sales/utils/questionnaire-field-visibility';
import { useScrollToFocusedAdhoc } from '../features/sales/utils/useScrollToFocusedAdhoc';
import { useDirtyState } from '../hooks/useDirtyState';
import { useProtectedImageUrl } from '../hooks/useProtectedImageUrl';
import { useGoogleDriveIntegration } from '../hooks/useGoogleDriveIntegration';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';

import { AiBrandIcon } from '../components/shared/AiBrandIcon';
import { apiFetch, ApiError } from '../lib/apiClient';
import { openFilePicker, type PickedFile } from '../lib/googlePicker';
import { createDirtyStateSnapshot } from '../lib/dirtyState';
import { StageBadge } from '../lib/featureStage';
import './deal-page.css';
import './deal-view-page.css';
// BPR section-embedded diagrams (canvas + section styles) render inside the
// questionnaire surfaces for bpr-category deals.
import './bpr-page.css';

// ─── Deal preview column types (mirroring SalesDealsPage) ────────────────────
type DealPreviewAttributeDef = {
  attributeKey: string;
  label: string;
  options: { valueKey: string; label: string }[];
};
type DealPreviewTemplateDef = { fieldKey: string; label: string };
const DEAL_PREVIEW_COLUMN_LABELS: Record<string, string> = {
  title: 'Deal',
  company: 'Company',
  product: 'Product',
  createdBy: 'Created by',
  createdAt: 'Created',
  status: 'Status',
};
const DEFAULT_DEAL_PREVIEW_COLUMN_KEYS = [
  'title',
  'company',
  'product',
  'createdBy',
  'createdAt',
  'status',
];
const DEAL_PREVIEW_UI_PREF_SURFACE = 'so.deals_table';

/**
 * Sentinel for "this scope field takes no column" in the scope-upload mapping dropdowns. Radix
 * Select has no empty-string value, so an explicit token stands in for absent.
 */
const SCOPE_COLUMN_NONE = '__none__';

// ─── Module-level constants ───────────────────────────────────────────────────

const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 480;
const DEFAULT_SIDEBAR_WIDTH = 300;

// Transcript-upload dialog mode picker. Same Ask/Agent/Auto vocabulary as the
// chat composer, with descriptions phrased for what each mode means HERE:
// agent (the DEFAULT, COS-93) saves the results directly - fills the questionnaire and
// captures the scope questions, never overwriting an answer a person gave; ask/auto run in
// review mode (diff cards, nothing saved until the user clicks Fill / Capture). New users
// missed those clicks and got estimates built on an empty scope, so saving is the default and
// Ask is the opt-in preview. Auto stays gated 'alpha' (local/dev only); filtered at the call
// sites via isStageAvailable + workspace.featureMinStage.
const TRANSCRIPT_ANALYZE_MODE_OPTIONS: ReadonlyArray<ComposerPickerOption<ChatMode>> = [
  {
    value: 'agent',
    label: 'Agent',
    description: 'Fill the questionnaire and add the scope questions automatically',
  },
  {
    value: 'ask',
    label: 'Ask',
    description: 'Preview first - nothing is saved until you click Fill and Capture',
  },
  {
    value: 'auto',
    label: 'Auto',
    description: 'AI decides - document analysis defaults to a preview',
    stage: 'alpha',
  },
];

/** Append newly-picked transcript files to the current selection, de-duping by name+size
 *  and capping at `max`. Shared by the edit-page modal and the create-from-script panel. */
function addTranscriptFiles(current: File[], picked: FileList | File[], max: number): File[] {
  const next = [...current];
  for (const f of Array.from(picked)) {
    if (next.length >= max) break;
    if (!next.some((e) => e.name === f.name && e.size === f.size)) next.push(f);
  }
  return next;
}

/** One result line (unreadable file / very long file) under a picked file in an upload list. */
function FileNoteLine({ note }: { note: FileNote | undefined }) {
  if (!note) return null;
  return (
    <span
      className={`deal-page__transcript-upload-file-note${
        note.kind === 'error' ? ' deal-page__transcript-upload-file-note--error' : ''
      }`}
      role={note.kind === 'error' ? 'alert' : undefined}
    >
      {note.text}
    </span>
  );
}

// Drive-selectable mime types for deal documents: the shared device accept list plus Google Docs
// and Slides, which the server exports (to .docx / PDF) on download (see downloadFileForIngest).
const TRANSCRIPT_DRIVE_MIME_TYPES = DEAL_DOCUMENT_DRIVE_MIME_TYPES;

/**
 * DriveTranscriptImport - the "Import from Google Drive" affordance shown under a transcript
 * dropzone. Reused by both the new-deal create panel and the existing-deal upload dialog so the
 * Drive path stays identical to local upload. Renders while the integration lookup is in flight
 * (so it appears together with the panel) and collapses if no Drive is connected. The server
 * downloads the picked files (deal from-drive routes), so this only forwards file references.
 */
function DriveTranscriptImport({
  integrationUuid,
  disabled,
  remainingCapacity,
  onPicked,
  onError,
  onPickerOpenChange,
}: {
  integrationUuid: string | null;
  disabled: boolean;
  remainingCapacity: number;
  onPicked: (files: PickedFile[]) => void;
  onError: (message: string) => void;
  /**
   * Notified while the Google Picker is on screen. The picker iframe + backdrop mount on
   * document.body, so a containing modal (Radix Dialog) treats them as an outside interaction and
   * would dismiss itself; the parent uses this to suppress that dismissal until the picker closes.
   */
  onPickerOpenChange?: (open: boolean) => void;
}) {
  const [opening, setOpening] = React.useState(false);
  // Render only once the integration lookup has confirmed a connected Drive - never render while
  // the check is in flight and then hide it.
  if (!integrationUuid) return null;

  const handleClick = async () => {
    if (!integrationUuid || remainingCapacity <= 0) return;
    setOpening(true);
    onPickerOpenChange?.(true);
    try {
      const cfg = await apiFetch<{
        accessToken: string;
        apiKey: string | null;
        appId: string | null;
      }>(`/api/integration/${integrationUuid}/picker-config`);
      if (!cfg.apiKey) {
        onError('Google Drive picker is not configured.');
        return;
      }
      const picked = await openFilePicker({
        accessToken: cfg.accessToken,
        apiKey: cfg.apiKey,
        appId: cfg.appId,
        multi: true,
        mimeTypes: TRANSCRIPT_DRIVE_MIME_TYPES,
      });
      if (picked.length > 0) onPicked(picked.slice(0, remainingCapacity));
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Could not import from Google Drive.');
    } finally {
      setOpening(false);
      onPickerOpenChange?.(false);
    }
  };

  return (
    <div className="customer-doc-add-panel__drive-source">
      <span className="customer-doc-add-panel__drive-or">or</span>
      <button
        type="button"
        className="customer-doc-add-panel__drive-btn"
        disabled={disabled || opening || remainingCapacity <= 0}
        onClick={() => void handleClick()}
      >
        <img
          src="/integrations/google-drive/logo.png"
          alt=""
          className="customer-doc-add-panel__drive-btn-icon"
        />
        {opening ? 'Opening…' : 'Import from Google Drive'}
      </button>
      <span className="customer-doc-add-panel__drive-hint">from the workspace Drive</span>
    </div>
  );
}

// Extracted to components/shared so the app shell can use it; re-exported for
// existing importers (DealMain).
export { AiBrandIcon } from '../components/shared/AiBrandIcon';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mergeSavedAnswers(
  questionnaire: SalesDealQuestionnaireSummary | null,
  currentAnswers: QuestionnaireAnswerMap,
  savedAnswers: QuestionnaireAnswerMap,
  saveSnapshot: QuestionnaireAnswerMap
): QuestionnaireAnswerMap {
  const merged: QuestionnaireAnswerMap = { ...currentAnswers };
  const fieldKeys = new Set([
    ...Object.keys(currentAnswers),
    ...Object.keys(savedAnswers),
    ...Object.keys(saveSnapshot),
  ]);

  for (const fieldKey of fieldKeys) {
    const currentValue = currentAnswers[fieldKey] ?? null;
    const snapshotValue = saveSnapshot[fieldKey] ?? null;
    const currentSnapshot = createDirtyStateSnapshot(currentValue);
    const saveStartSnapshot = createDirtyStateSnapshot(snapshotValue);

    if (currentSnapshot === saveStartSnapshot) {
      merged[fieldKey] = savedAnswers[fieldKey] ?? null;
    }
  }

  return sanitizeQuestionnaireAnswers(questionnaire, merged);
}

function getChangedAnswerKeys(
  currentAnswers: QuestionnaireAnswerMap,
  persistedAnswers: QuestionnaireAnswerMap
): Set<string> {
  const fieldKeys = new Set([...Object.keys(currentAnswers), ...Object.keys(persistedAnswers)]);

  return new Set(
    [...fieldKeys].filter(
      (fieldKey) =>
        createDirtyStateSnapshot(currentAnswers[fieldKey] ?? null) !==
        createDirtyStateSnapshot(persistedAnswers[fieldKey] ?? null)
    )
  );
}

// ─── Create deal wizard steps ────────────────────────────────────────────────

function getDealStatusPresentation(status: SalesDealSummary['status'] | null | undefined): {
  label: string;
  variant: PillVariant;
} {
  if (typeof status !== 'string' || !status.trim()) {
    return { label: 'Unknown', variant: 'neutral' };
  }

  switch (status) {
    case 'draft':
      return { label: 'Draft', variant: 'muted' };
    case 'ready_for_generation':
      return { label: 'Ready for Generation', variant: 'warning' };
    case 'generated':
      return { label: 'Generated', variant: 'success' };
    default:
      return { label: status.replace(/_/g, ' '), variant: 'neutral' };
  }
}

type DealAttributeOptionsByKey = Record<string, { valueKey: string; label: string }[]>;

function formatDealRowDate(value: string | null | undefined): string | null {
  if (!value) return null;
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Presentational row for an existing deal: title, created/updated dates, the
 * category/type/status attribute chips, and the status pill. Shared by the
 * create wizard's "Existing deals" list and the new-vs-continue dialog so the
 * two stay visually identical.
 */
function ExistingDealRow({
  deal,
  attributeOptionsByKey,
  showOwner,
  selected = false,
  locked = false,
  onToggle,
}: {
  deal: SalesDealSummary;
  attributeOptionsByKey: DealAttributeOptionsByKey;
  showOwner: boolean;
  selected?: boolean;
  locked?: boolean;
  onToggle?: () => void;
}) {
  const statusPres = getDealStatusPresentation(deal.status);
  const createdDate = formatDealRowDate(deal.createdAt);
  const updatedDate = formatDealRowDate(deal.updatedAt);
  const interactive = !locked && typeof onToggle === 'function';
  const chipLabel = (key: string, value: string) =>
    attributeOptionsByKey[key]?.find((o) => o.valueKey === value)?.label ?? value;

  return (
    <div
      {...(interactive && {
        role: 'button',
        tabIndex: 0,
        onClick: onToggle,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle?.();
          }
        },
      })}
      className={[
        'sales-create-shell__deal-row',
        selected ? 'sales-create-shell__deal-row--active' : '',
        locked ? 'sales-create-shell__deal-row--locked' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="sales-create-shell__deal-info">
        <span className="sales-create-shell__deal-title">{deal.title}</span>
        {showOwner && deal.createdByDisplayName ? (
          <span className="sales-create-shell__deal-meta">{deal.createdByDisplayName}</span>
        ) : null}
        {createdDate ? (
          <span className="sales-create-shell__deal-meta">Created: {createdDate}</span>
        ) : null}
        {updatedDate ? (
          <span className="sales-create-shell__deal-meta">Updated: {updatedDate}</span>
        ) : null}
        {deal.dealCategoryKey || deal.dealTypeKey || deal.dealStatusKey ? (
          <div className="sales-create-shell__deal-pills">
            {deal.dealCategoryKey ? (
              <span className="sales-create-shell__category-chip sales-create-shell__category-chip--static">
                {chipLabel('deal_category', deal.dealCategoryKey)}
              </span>
            ) : null}
            {deal.dealTypeKey ? (
              <span className="sales-create-shell__category-chip sales-create-shell__category-chip--static">
                {chipLabel('deal_type', deal.dealTypeKey)}
              </span>
            ) : null}
            {deal.dealStatusKey ? (
              <span className="sales-create-shell__category-chip sales-create-shell__category-chip--static">
                {chipLabel('deal_status', deal.dealStatusKey)}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <Pill variant={statusPres.variant}>{statusPres.label}</Pill>
    </div>
  );
}

export interface ExistingDealChoicePrompt {
  deals: SalesDealSummary[];
  intent: 'manual' | 'transcript';
}

/**
 * Asks the user, at deal-creation time, whether to continue an existing deal
 * (matched on company + product + category, any status) or create a new draft
 * that coexists alongside it. Selecting a row enables "Continue this deal";
 * "Create a new draft" is always available.
 */
function ExistingDealChoiceDialog({
  prompt,
  attributeOptionsByKey,
  showOwner,
  busy = false,
  onContinue,
  onCreateNew,
  onCancel,
}: {
  prompt: ExistingDealChoicePrompt | null;
  attributeOptionsByKey: DealAttributeOptionsByKey;
  showOwner: boolean;
  busy?: boolean;
  onContinue: (deal: SalesDealSummary) => void;
  onCreateNew: () => void;
  onCancel: () => void;
}) {
  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(null);

  // Reset the selection whenever a fresh prompt opens.
  React.useEffect(() => {
    setSelectedUuid(null);
  }, [prompt]);

  const open = prompt !== null;
  const deals = prompt?.deals ?? [];
  const selectedDeal = deals.find((d) => d.uuid === selectedUuid) ?? null;
  const transcript = prompt?.intent === 'transcript';

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (!next) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="existing-deal-choice__overlay" />
        <Dialog.Content className="existing-deal-choice__content">
          <div className="existing-deal-choice__header">
            <Dialog.Title className="existing-deal-choice__title">
              {deals.length === 1
                ? 'A matching deal already exists'
                : 'Matching deals already exist'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="existing-deal-choice__close"
                aria-label="Close"
                disabled={busy}
              >
                <Cross1Icon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="existing-deal-choice__prompt">
            Continue working on an existing deal, or{' '}
            {transcript ? 'create a new deal from the document' : 'create a new draft'} that lives
            alongside it.
          </Dialog.Description>
          <div className="existing-deal-choice__list">
            {deals.map((d) => (
              <ExistingDealRow
                key={d.uuid}
                deal={d}
                attributeOptionsByKey={attributeOptionsByKey}
                showOwner={showOwner}
                selected={selectedUuid === d.uuid}
                onToggle={() => setSelectedUuid((prev) => (prev === d.uuid ? null : d.uuid))}
              />
            ))}
          </div>
          <div className="existing-deal-choice__actions">
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy || !selectedDeal}
              onClick={() => selectedDeal && onContinue(selectedDeal)}
            >
              {transcript ? 'Add to this deal' : 'Continue this deal'}
            </Button>
            <Button type="button" variant="primary" size="sm" disabled={busy} onClick={onCreateNew}>
              {busy ? 'Working…' : transcript ? 'Create new deal' : 'Create a new draft'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function getAnswerKey(field: SalesDealTemplateFieldSummary): string {
  return field.templateFieldId !== null ? String(field.templateFieldId) : field.fieldKey;
}

function getAnswerValue(
  answers: QuestionnaireAnswerMap,
  field: SalesDealTemplateFieldSummary
): unknown {
  return answers[getAnswerKey(field)] ?? null;
}

function setAnswerValueByFieldKey(
  answers: QuestionnaireAnswerMap,
  fieldKey: string,
  value: unknown,
  fieldLookup?: QuestionnaireFieldLookup
): void {
  answers[fieldKey] = value;

  const field = fieldLookup?.get(fieldKey);
  if (field) {
    answers[getAnswerKey(field)] = value;
  }
}

function isYesNoPillRadioField(field: SalesDealTemplateFieldSummary): boolean {
  if (field.uiControl !== 'pill_radio') {
    return false;
  }

  const optionValues = field.options.map((option) => option.value.toLowerCase());
  return optionValues.length === 2 && optionValues.includes('yes') && optionValues.includes('no');
}

function applyFieldDefaults(field: SalesDealTemplateFieldSummary, value: unknown): unknown {
  return value ?? getDefaultFieldValue(field);
}

function getDefaultFieldValue(field: SalesDealTemplateFieldSummary): unknown {
  // Backend profileDefault: the resolved default is already baked into `field.value`.
  // Returning it here keeps the nav-guard baseline (defaults applied) matching the
  // current value so a pristine deal stays quiet, while the Save-button baseline
  // (defaults NOT applied) still sees the unsaved default and activates Save.
  if (field.valueIsDefault) {
    return field.value ?? null;
  }

  if (isYesNoPillRadioField(field)) {
    return 'no';
  }

  if (field.uiControl === 'slider') {
    const dv = field.displayHints?.sliderConfig?.defaultValue;
    return typeof dv === 'number' ? dv : null;
  }

  if (Array.isArray(field.options) && field.options.length > 0) {
    const defaultOption = field.options.find((o) => o.default);
    if (defaultOption) {
      const isMulti =
        field.uiControl === 'pill_select' ||
        field.uiControl === 'pill_dropdown' ||
        field.uiControl === 'multi_select' ||
        field.uiControl === 'tag_input';
      return isMulti ? [defaultOption.value] : defaultOption.value;
    }
  }

  return null;
}

function sanitizeQuestionnaireAnswers(
  questionnaire: SalesDealQuestionnaireSummary | null,
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): QuestionnaireAnswerMap {
  const rules = questionnaire?.conditionalRules ?? [];
  if (rules.length === 0) return answers;

  const lookup = fieldLookup ?? buildQuestionnaireFieldLookup(questionnaire);
  const next = { ...answers };

  // Process target fields in displayOrder so parent nulls cascade to children.
  const sortedTargetKeys = [
    ...new Set(
      [...rules].sort((a, b) => a.displayOrder - b.displayOrder).map((r) => r.targetFieldKey)
    ),
  ];

  for (const fieldKey of sortedTargetKeys) {
    if (!isFieldVisibleByRules(fieldKey, rules, next, lookup)) {
      setAnswerValueByFieldKey(next, fieldKey, null, lookup);
    } else if (!isFieldEnabledByRules(fieldKey, rules, next, lookup)) {
      setAnswerValueByFieldKey(next, fieldKey, null, lookup);
    }
  }

  return next;
}

function applyTransitionRules(
  questionnaire: SalesDealQuestionnaireSummary | null,
  changedAnswerKey: string,
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): QuestionnaireAnswerMap {
  const rules = questionnaire?.conditionalRules ?? [];
  const lookup = fieldLookup ?? buildQuestionnaireFieldLookup(questionnaire);

  // onChange passes the answer key (numeric templateFieldId) but rules use the human-readable
  // fieldKey. Resolve back to fieldKey so the filter below matches.
  let changedFieldKey = changedAnswerKey;
  for (const [fk, field] of lookup) {
    if (getAnswerKey(field) === changedAnswerKey) {
      changedFieldKey = fk;
      break;
    }
  }

  const transitionRules = rules.filter(
    (r) => r.sourceFieldKey === changedFieldKey && (r.effect === 'clear' || r.effect === 'fill')
  );
  if (transitionRules.length === 0) return answers;
  const next = { ...answers };
  for (const rule of transitionRules) {
    if (!evaluateConditionalRule(rule, next, lookup)) continue;
    if (rule.effect === 'clear') {
      setAnswerValueByFieldKey(next, rule.targetFieldKey, null, lookup);
    } else if (rule.effect === 'fill') {
      const currentVal = next[rule.targetFieldKey];
      const isEmpty = currentVal === null || currentVal === undefined || currentVal === '';
      if (isEmpty) {
        setAnswerValueByFieldKey(next, rule.targetFieldKey, rule.effectPayload ?? null, lookup);
      }
    }
  }
  return next;
}

/**
 * Numeric answer coercion for calculation rules. Number inputs store locale
 * STRINGS ("12", "1,234" - see formatNumberInput), and persisted answers only
 * round-trip back to numbers when JSON.parse succeeds (comma values never do),
 * so every numeric formula input must coerce, not typeof-check. Null when the
 * value is not a usable finite number.
 */
function coerceCalcNumber(val: unknown): number | null {
  const num =
    typeof val === 'number'
      ? val
      : typeof val === 'string' && val.trim()
        ? Number.parseFloat(val.replace(/,/g, ''))
        : NaN;
  return Number.isFinite(num) ? num : null;
}

/**
 * Evaluate all calculation rules against the current answers and write computed values.
 * Rules run in displayOrder, writing into the same map, so chained rules (a count feeding
 * a sum) resolve within one pass. Numeric inputs go through coerceCalcNumber.
 */
function applyCalculationRules(
  questionnaire: SalesDealQuestionnaireSummary | null,
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): QuestionnaireAnswerMap {
  const rules = questionnaire?.calculationRules ?? [];
  if (rules.length === 0) return answers;

  const lookup = fieldLookup ?? buildQuestionnaireFieldLookup(questionnaire);
  const next = { ...answers };

  for (const rule of rules) {
    const sourceField = lookup.get(rule.sourceFieldKey);
    const targetField = lookup.get(rule.targetFieldKey);
    if (!sourceField || !targetField) continue;

    const sourceValue = next[getAnswerKey(sourceField)];
    const sourceStr = typeof sourceValue === 'string' ? sourceValue.trim() : '';

    if (rule.formula === 'date_add') {
      if (!sourceStr) {
        next[getAnswerKey(targetField)] = null;
      } else {
        let days = typeof rule.params?.days === 'number' ? rule.params.days : 0;
        if (typeof rule.params?.daysField === 'string') {
          const daysFieldDef = lookup.get(rule.params.daysField);
          if (daysFieldDef) {
            const daysNum = coerceCalcNumber(next[getAnswerKey(daysFieldDef)]);
            if (daysNum !== null && daysNum > 0) days = daysNum;
          }
        }
        const sourceDate = new Date(sourceStr);
        if (!Number.isNaN(sourceDate.getTime())) {
          const resultDate = new Date(sourceDate);
          resultDate.setDate(resultDate.getDate() + days);
          next[getAnswerKey(targetField)] = resultDate.toISOString().slice(0, 10);
        } else {
          next[getAnswerKey(targetField)] = null;
        }
      }
    } else if (rule.formula === 'month_diff') {
      const startFieldKey =
        typeof rule.params?.startField === 'string' ? rule.params.startField : rule.sourceFieldKey;
      const endFieldKey =
        typeof rule.params?.endField === 'string' ? rule.params.endField : rule.targetFieldKey;
      const startField = lookup.get(startFieldKey);
      const endField = lookup.get(endFieldKey);
      if (!startField || !endField) continue;
      const startStr =
        typeof next[getAnswerKey(startField)] === 'string'
          ? (next[getAnswerKey(startField)] as string).trim()
          : '';
      const endStr =
        typeof next[getAnswerKey(endField)] === 'string'
          ? (next[getAnswerKey(endField)] as string).trim()
          : '';
      if (!startStr || !endStr) {
        next[getAnswerKey(targetField)] = null;
      } else {
        const start = new Date(startStr);
        const end = new Date(endStr);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          const months =
            (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
          next[getAnswerKey(targetField)] = months >= 0 ? months : null;
        } else {
          next[getAnswerKey(targetField)] = null;
        }
      }
    } else if (rule.formula === 'count_sum') {
      const arrayFieldKey =
        typeof rule.params?.arrayField === 'string' ? rule.params.arrayField : rule.sourceFieldKey;
      const numberFieldKey =
        typeof rule.params?.numberField === 'string' ? rule.params.numberField : null;
      const arrayField = lookup.get(arrayFieldKey);
      const numberField = numberFieldKey ? lookup.get(numberFieldKey) : null;
      const arrayValue = arrayField ? next[getAnswerKey(arrayField)] : null;
      const arrayCount = Array.isArray(arrayValue) ? arrayValue.length : 0;
      const numberValue = numberField ? next[getAnswerKey(numberField)] : null;
      const numberCount = coerceCalcNumber(numberValue) ?? 0;
      next[getAnswerKey(targetField)] = arrayCount + numberCount;
    } else if (rule.formula === 'field_sum') {
      const fields = Array.isArray(rule.params?.fields) ? (rule.params.fields as string[]) : [];
      let total = 0;
      let hasAny = false;
      for (const fieldKey of fields) {
        const f = lookup.get(fieldKey);
        if (!f) continue;
        const val = coerceCalcNumber(next[getAnswerKey(f)]);
        if (val !== null) {
          total += val;
          hasAny = true;
        }
      }
      next[getAnswerKey(targetField)] = hasAny ? total : null;
    } else if (rule.formula === 'weighted_sum') {
      const weights =
        typeof rule.params?.weights === 'object' && rule.params.weights !== null
          ? (rule.params.weights as Record<string, number>)
          : {};
      let total = 0;
      let hasAny = false;
      for (const [fieldKey, weight] of Object.entries(weights)) {
        const f = lookup.get(fieldKey);
        if (!f) continue;
        const val = coerceCalcNumber(next[getAnswerKey(f)]);
        if (val !== null) {
          total += val * weight;
          hasAny = true;
        }
      }
      next[getAnswerKey(targetField)] = hasAny ? Math.round(total * 100) / 100 : null;
    } else if (rule.formula === 'field_product') {
      // Product of the fields listed in params.fields (e.g. managed_services
      // Contract Amount = rate/hour x hours/month x term months). Number inputs
      // save locale strings ("25,000"), so coerce commas away; the target stays
      // null until EVERY factor is a usable number, so a half-filled form never
      // shows a misleading partial product.
      const fields = Array.isArray(rule.params?.fields) ? (rule.params.fields as string[]) : [];
      let product = 1;
      let complete = fields.length > 0;
      for (const fieldKey of fields) {
        const f = lookup.get(fieldKey);
        const num = coerceCalcNumber(f ? next[getAnswerKey(f)] : null);
        if (num === null) {
          complete = false;
          break;
        }
        product *= num;
      }
      next[getAnswerKey(targetField)] = complete ? Math.round(product * 100) / 100 : null;
    }
  }

  return next;
}

/**
 * Compute the CURRENT contract recommendation from the live form values over the
 * server-provided `questionnaire.contractRecommendation` context, using the
 * SHARED computeContractRecommendation from @protoss/shared - client and server
 * always agree on the math. Null when the questionnaire carries no context
 * (every other category). Used by both the live recalculation below and the
 * "recommends" hint under the contract-term fields.
 */
function computeLiveContractRecommendation(
  questionnaire: SalesDealQuestionnaireSummary | null,
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): ContractRecommendation | null {
  const ctx = questionnaire?.contractRecommendation;
  if (!ctx) return null;
  const lookup = fieldLookup ?? buildQuestionnaireFieldLookup(questionnaire);
  const src = ctx.sourceFieldKeys;
  const read = (fk: string): unknown => {
    const field = lookup.get(fk);
    return field ? answers[getAnswerKey(field)] : answers[fk];
  };
  return computeContractRecommendation(
    {
      projectHours: coercePositiveNumber(read(src.hours)) ?? ctx.derived.projectHours,
      projectCost: coercePositiveNumber(read(src.cost)) ?? ctx.derived.projectCost,
      // The expected yearly totals: the live form values, falling back to the
      // discovered counts (the fields' own default) - matching the server, so a
      // cleared field reverts the recommendation to the count-based baseline.
      expectedTotalTickets: coerceCount(read(src.expectedTotalTickets)) ?? ctx.derived.ticketCount,
      expectedTotalInitiatives:
        coerceCount(read(src.expectedTotalInitiatives)) ?? ctx.derived.initiativeCount,
      avgTicketHours: ctx.derived.avgTicketHours,
      avgInitiativeHours: ctx.derived.avgInitiativeHours,
    },
    ctx.policy
  );
}

/**
 * The current live contract recommendation, provided by SalesCreateDealSurface so
 * the number-field renderer can show a dynamic "{product} recommends: N hours."
 * hint under the recommendation-target fields - the hint tracks the live inputs
 * even after the user overrides the field's value. Null outside a
 * contract-recommendation questionnaire.
 */
const ContractRecommendationHintContext = React.createContext<ContractRecommendation | null>(null);

/**
 * Live contract-recommendation recalculation (managed-services Contract tab).
 * Driven entirely by the server-provided `questionnaire.contractRecommendation`
 * context (resolved policy + estimate-derived inputs + source field keys) and the
 * SHARED compute - see computeLiveContractRecommendation. Runs on every answer
 * change to one of the recommendation's SOURCE fields (the expected yearly
 * totals, project hours/cost) and rewrites the recommendation TARGET fields
 * (identified by their profileDefault configKey `recommended_*` - config-driven,
 * no hardcoded field keys) from the live form values, so the recommended terms
 * recalculate as the client types, without a round trip. A target the user typed
 * into this session (touched) is never overwritten, and the field currently
 * being typed is never a write target. No context (every other category) = no-op.
 */
function applyContractRecommendationRules(
  questionnaire: SalesDealQuestionnaireSummary | null,
  changedAnswerKey: string,
  answers: QuestionnaireAnswerMap,
  isTargetLocked: (answerKey: string) => boolean,
  fieldLookup?: QuestionnaireFieldLookup
): QuestionnaireAnswerMap {
  const ctx = questionnaire?.contractRecommendation;
  if (!ctx) return answers;
  const lookup = fieldLookup ?? buildQuestionnaireFieldLookup(questionnaire);
  const src = ctx.sourceFieldKeys;
  const sourceFieldKeys = [src.hours, src.cost, src.expectedTotalTickets, src.expectedTotalInitiatives];
  const changedIsSource = sourceFieldKeys.some((fk) => {
    if (fk === changedAnswerKey) return true;
    const field = lookup.get(fk);
    return field ? getAnswerKey(field) === changedAnswerKey : false;
  });
  if (!changedIsSource) return answers;

  const rec = computeLiveContractRecommendation(questionnaire, answers, lookup);
  if (!rec) return answers;

  const targetValueByConfigKey: Record<string, number | undefined> = {
    recommended_hours_per_month: rec.recommended_hours_per_month,
    recommended_term_months: rec.recommended_term_months,
    recommended_rate_per_hour: rec.recommended_rate_per_hour,
  };
  let next = answers;
  for (const section of questionnaire?.sections ?? []) {
    for (const group of section.groups) {
      for (const field of group.fields) {
        const profileDefault = field.displayHints?.profileDefault;
        if (profileDefault?.profile !== 'estimate') continue;
        if (!(profileDefault.configKey in targetValueByConfigKey)) continue;
        const answerKey = getAnswerKey(field);
        // Never write back into the field currently being typed (the term is both
        // a source and a target - mid-keystroke rewrites would fight the input).
        if (answerKey === changedAnswerKey || field.fieldKey === changedAnswerKey) continue;
        if (isTargetLocked(answerKey) || isTargetLocked(field.fieldKey)) continue;
        const value = targetValueByConfigKey[profileDefault.configKey] ?? null;
        if (next[answerKey] === value && next[field.fieldKey] === value) continue;
        if (next === answers) next = { ...answers };
        next[answerKey] = value;
        next[field.fieldKey] = value;
      }
    }
  }
  return next;
}

function getVisibleQuestionFields(
  question: SalesDealTemplateGroupSummary,
  answers: QuestionnaireAnswerMap,
  rules: SalesDealQuestionnaireSummary['conditionalRules'],
  fieldLookup?: QuestionnaireFieldLookup
): SalesDealTemplateFieldSummary[] {
  return question.fields.filter((field) =>
    isFieldVisibleByRules(field.fieldKey, rules, answers, fieldLookup)
  );
}

function isStackOptionUiControl(uiControl: string): boolean {
  return uiControl === 'stack_option';
}

function isFieldValidationRequired(field: SalesDealTemplateFieldSummary): boolean {
  return field.isRequired;
}

/**
 * Email format error for a field carrying `displayHints.validator: 'email'`, or null when
 * valid/not applicable. `displayHints.emailDomain` additionally restricts the address to
 * one domain (e.g. the managed-services support email must be @rapidcloudpartners.com).
 * Applies to optional fields too once a value has been entered.
 */
function getEmailFormatError(field: SalesDealTemplateFieldSummary, value: unknown): string | null {
  if (field.displayHints?.validator !== 'email') return null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const trimmed = value.trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return 'Enter a valid email address';
  }
  const domain = field.displayHints.emailDomain;
  if (domain && !trimmed.toLowerCase().endsWith(`@${domain.toLowerCase()}`)) {
    return `Email must end with @${domain}`;
  }
  return null;
}

function getFieldValidationState(
  field: SalesDealTemplateFieldSummary,
  answers: QuestionnaireAnswerMap
): { isInvalid: boolean; errorMessage?: string; rowErrors?: Set<string> } {
  const value = getAnswerValue(answers, field);

  if (!isFieldValidationRequired(field)) {
    // Format validation applies even to optional fields when a value has been entered.
    const optionalEmailError = getEmailFormatError(field, value);
    if (optionalEmailError) {
      return { isInvalid: true, errorMessage: optionalEmailError };
    }
    return { isInvalid: false };
  }

  if (value === null || value === undefined || value === '') {
    return { isInvalid: true };
  }

  if (Array.isArray(value) && value.length === 0) {
    return { isInvalid: true };
  }

  if (typeof value === 'object' && value !== null && 'amount' in value && 'currency' in value) {
    // A currency answer is valid only when it normalizes to a usable amount.
    return { isInvalid: normalizeCurrencyValue(value) === null };
  }

  // Format validation for required fields that have a value but fail format rules.
  const emailError = getEmailFormatError(field, value);
  if (emailError) {
    return { isInvalid: true, errorMessage: emailError };
  }

  return { isInvalid: false };
}

function buildQuestionSavePayload(
  question: SalesDealTemplateGroupSummary,
  answers: QuestionnaireAnswerMap,
  fieldLookup: ReturnType<typeof buildQuestionnaireFieldLookup>,
  rules: SalesDealQuestionnaireSummary['conditionalRules']
): Array<{ templateFieldId?: number | null; fieldKey: string; value: unknown }> {
  const payload: Array<{ templateFieldId?: number | null; fieldKey: string; value: unknown }> = [];
  for (const field of question.fields) {
    if (!isFieldVisibleByRules(field.fieldKey, rules, answers, fieldLookup)) {
      payload.push({
        templateFieldId: field.templateFieldId,
        fieldKey: field.fieldKey,
        value: null,
      });
      continue;
    }
    payload.push({
      templateFieldId: field.templateFieldId,
      fieldKey: field.fieldKey,
      value: answers[getAnswerKey(field)] ?? null,
    });
  }
  return payload;
}

function buildQuestionnaireSavePayload(
  questionnaire: SalesDealQuestionnaireSummary | null,
  answers: QuestionnaireAnswerMap
): Array<{ templateFieldId?: number | null; fieldKey: string; value: unknown }> {
  const sanitizedAnswers = sanitizeQuestionnaireAnswers(questionnaire, answers);
  const fieldLookup = buildQuestionnaireFieldLookup(questionnaire);
  const dedupePayload = (
    entries: Array<{ templateFieldId?: number | null; fieldKey: string; value: unknown }>
  ) =>
    Array.from(
      new Map(
        entries.map((entry) => [String(entry.templateFieldId ?? entry.fieldKey), entry])
      ).values()
    );

  if (!questionnaire) {
    return dedupePayload(
      Object.entries(sanitizedAnswers).map(([fieldKey, value]) => ({ fieldKey, value }))
    );
  }

  const payload: Array<{ templateFieldId?: number | null; fieldKey: string; value: unknown }> = [];

  for (const section of questionnaire.sections) {
    for (const question of section.groups) {
      for (const field of question.fields) {
        if (
          !isFieldVisibleByRules(
            field.fieldKey,
            questionnaire.conditionalRules,
            sanitizedAnswers,
            fieldLookup
          )
        ) {
          payload.push({
            templateFieldId: field.templateFieldId,
            fieldKey: field.fieldKey,
            value: null,
          });
          continue;
        }

        payload.push({
          templateFieldId: field.templateFieldId,
          fieldKey: field.fieldKey,
          value: sanitizedAnswers[getAnswerKey(field)] ?? null,
        });
      }
    }
  }

  for (const field of questionnaire.headerFields ?? []) {
    payload.push({
      templateFieldId: field.templateFieldId,
      fieldKey: field.fieldKey,
      value: sanitizedAnswers[getAnswerKey(field)] ?? null,
    });
  }

  return dedupePayload(payload);
}

function buildInitialAnswers(
  questionnaire: SalesDealQuestionnaireSummary | null
): QuestionnaireAnswerMap {
  if (!questionnaire) return {};
  const next: QuestionnaireAnswerMap = {};
  for (const section of questionnaire.sections) {
    for (const question of section.groups) {
      for (const field of question.fields) {
        next[getAnswerKey(field)] = applyFieldDefaults(field, field.value);
      }
    }
  }
  for (const field of questionnaire.headerFields ?? []) {
    next[getAnswerKey(field)] = applyFieldDefaults(field, field.value);
  }
  const withCalc = applyCalculationRules(questionnaire, next);
  return sanitizeQuestionnaireAnswers(questionnaire, withCalc);
}

// Like buildInitialAnswers but does NOT apply field defaults - persisted answers
// reflect only what has been explicitly saved to the server. A value flagged
// `valueIsDefault` (a backend profileDefault) is treated as unsaved (null) here so
// the dirty baseline counts it as a change and the Save button activates.
function buildPersistedAnswers(
  questionnaire: SalesDealQuestionnaireSummary | null
): QuestionnaireAnswerMap {
  if (!questionnaire) return {};
  const next: QuestionnaireAnswerMap = {};
  for (const section of questionnaire.sections) {
    for (const question of section.groups) {
      for (const field of question.fields) {
        next[getAnswerKey(field)] = field.valueIsDefault ? null : (field.value ?? null);
      }
    }
  }
  for (const field of questionnaire.headerFields ?? []) {
    next[getAnswerKey(field)] = field.valueIsDefault ? null : (field.value ?? null);
  }
  const withCalc = applyCalculationRules(questionnaire, next);
  return sanitizeQuestionnaireAnswers(questionnaire, withCalc);
}

/**
 * Canonical signature of an ad-hoc scope meta, for dirty comparison: empty/absent fields are
 * ignored, object keys + array values are sorted, so two semantically-equal metas (e.g. the original
 * vs an edit reverted back to it) compare equal regardless of key/array order.
 */
function scopeMetaSignature(meta: ScopeMeta | undefined | null): string {
  if (!meta) return '';
  const obj = meta as Record<string, unknown>;
  const norm: Record<string, unknown> = {};
  for (const k of Object.keys(obj).sort()) {
    const v = obj[k];
    if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    norm[k] = Array.isArray(v) ? [...(v as unknown[])].map(String).sort() : v;
  }
  return JSON.stringify(norm);
}

function buildQuestionnaireDirtySnapshot(
  questionnaire: SalesDealQuestionnaireSummary | null,
  answers: QuestionnaireAnswerMap,
  applyDefaults = false
): Array<{ fieldKey: string; value: unknown }> {
  if (!questionnaire) return [];

  const sanitizedAnswers = sanitizeQuestionnaireAnswers(questionnaire, answers);
  const fieldLookup = buildQuestionnaireFieldLookup(questionnaire);
  const snapshot: Array<{ fieldKey: string; value: unknown }> = [];

  for (const section of questionnaire.sections) {
    for (const question of section.groups) {
      for (const field of question.fields) {
        const rawValue = sanitizedAnswers[getAnswerKey(field)];
        const resolvedValue = applyDefaults ? (rawValue ?? getDefaultFieldValue(field)) : rawValue;
        const value = isFieldVisibleByRules(
          field.fieldKey,
          questionnaire.conditionalRules,
          sanitizedAnswers,
          fieldLookup
        )
          ? normalizeQuestionnaireDirtyValue(field, resolvedValue)
          : null;
        snapshot.push({ fieldKey: getAnswerKey(field), value });
      }
    }
  }

  return snapshot;
}

function buildQuestionnaireDirtyValueMap(
  questionnaire: SalesDealQuestionnaireSummary | null,
  answers: QuestionnaireAnswerMap,
  applyDefaults = false
): Map<string, string> {
  const snapshot = buildQuestionnaireDirtySnapshot(questionnaire, answers, applyDefaults);
  return new Map(
    snapshot.map((entry) => [entry.fieldKey, createDirtyStateSnapshot(entry.value ?? null)])
  );
}

// Field keys whose baseline value differs from the current value.
function diffDirtyValueMaps(
  baseline: Map<string, string>,
  current: Map<string, string>
): Set<string> {
  const allKeys = new Set([...baseline.keys(), ...current.keys()]);
  return new Set(
    [...allKeys].filter((fieldKey) => baseline.get(fieldKey) !== current.get(fieldKey))
  );
}

function getQuestionnaireCompletionState(
  questionnaire: SalesDealQuestionnaireSummary | null,
  answers: QuestionnaireAnswerMap,
  scope: 'estimate' | 'contract' = 'estimate'
): {
  requiredCount: number;
  missingRequiredCount: number;
  /** Labels of the required fields still missing, in questionnaire order. */
  missingRequiredLabels: string[];
  isComplete: boolean;
} {
  if (!questionnaire) {
    return {
      requiredCount: 0,
      missingRequiredCount: 0,
      missingRequiredLabels: [],
      isComplete: false,
    };
  }

  const fieldLookup = buildQuestionnaireFieldLookup(questionnaire);
  let requiredCount = 0;
  let missingRequiredCount = 0;
  const missingRequiredLabels: string[] = [];

  for (const section of questionnaire.sections) {
    // Two disjoint gates share this walk. 'estimate' = ready to generate the
    // estimate: every required field EXCEPT the contract terms (the contract's own
    // prefills derive from the estimate, so requiring them first would deadlock).
    // 'contract' = ready to generate the static SOW: ONLY the contract terms
    // (mirrors the server-side SOW readiness report, as a client-side button gate).
    const isContractSection = section.sectionKey === CONTRACT_SECTION_KEY;
    if (scope === 'estimate' ? isContractSection : !isContractSection) continue;
    for (const question of section.groups) {
      for (const field of question.fields) {
        if (
          !isFieldVisibleByRules(
            field.fieldKey,
            questionnaire.conditionalRules,
            answers,
            fieldLookup
          ) ||
          !isFieldEnabledByRules(
            field.fieldKey,
            questionnaire.conditionalRules,
            answers,
            fieldLookup
          )
        ) {
          continue;
        }
        if (!isFieldValidationRequired(field)) {
          continue;
        }

        requiredCount += 1;
        // If the field was never explicitly saved and has a synthetic default
        // (pill_radio 'no'), treat it as missing.
        if (
          (field.value == null && isYesNoPillRadioField(field)) ||
          getFieldValidationState(field, answers).isInvalid
        ) {
          missingRequiredCount += 1;
          missingRequiredLabels.push(field.label);
        }
      }
    }
  }

  return {
    requiredCount,
    missingRequiredCount,
    missingRequiredLabels,
    isComplete: missingRequiredCount === 0,
  };
}

function toWizardSteps(
  questionnaire: SalesDealQuestionnaireSummary | null,
  answers: QuestionnaireAnswerMap,
  savingGroupKeys: Set<string>,
  activeSectionIndex: number,
  activeQuestionIndex: number,
  changedFieldKeys?: Set<string>
): WizardStep[] {
  if (!questionnaire || questionnaire.sections.length === 0) {
    return [];
  }

  const fieldLookup = buildQuestionnaireFieldLookup(questionnaire);

  return questionnaire.sections.map((section, sectionIdx) => {
    const questionCount = section.groups.length;

    const questions: WizardStepQuestion[] = section.groups.map((q, qIdx) => {
      const isCurrent = sectionIdx === activeSectionIndex && qIdx === activeQuestionIndex;
      const visibleFields = getVisibleQuestionFields(
        q,
        answers,
        questionnaire.conditionalRules,
        fieldLookup
      ).filter((f) =>
        isFieldEnabledByRules(f.fieldKey, questionnaire.conditionalRules, answers, fieldLookup)
      );

      // Calculated read-only fields are excluded from the sidebar counts: the user
      // cannot answer them directly - they fill themselves via calculationRules once
      // their source fields are set (e.g. the managed_services Contract Amount). The
      // SOW readiness gate still checks them server-side, which stays aligned because
      // the calculation writes the target in the same save as its inputs.
      const answerableFields = visibleFields.filter((f) => !f.displayHints?.readOnly);
      const requiredFields = answerableFields.filter((f) => isFieldValidationRequired(f));
      const optionalFields = answerableFields.filter((f) => !isFieldValidationRequired(f));

      const isFieldAnswered = (field: SalesDealTemplateFieldSummary) => {
        // Sidebar completion reflects only explicitly saved values - defaults alone
        // are not enough to count a field as answered.
        const v = getAnswerValue(answers, field);
        if (v === null || v === undefined || v === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        if (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0)
          return false;
        if (typeof v === 'object' && v !== null && 'amount' in v && 'currency' in v) {
          return normalizeCurrencyValue(v) !== null;
        }
        return true;
      };

      // For each required/optional field, count unanswered.
      function countUnanswered(fields: typeof requiredFields): number {
        return fields.reduce((count, f) => {
          return count + (isFieldAnswered(f) ? 0 : 1);
        }, 0);
      }

      function countTotal(fields: typeof requiredFields): number {
        return fields.length;
      }

      const totalRequiredCount = countTotal(requiredFields);
      const totalOptionalCount = countTotal(optionalFields);
      const requiredCount = countUnanswered(requiredFields);
      const optionalCount = countUnanswered(optionalFields);
      const requiredComplete = totalRequiredCount > 0 && requiredCount === 0;
      const optionalComplete = totalOptionalCount > 0 && optionalCount === 0;

      const state: WizardStepQuestion['state'] = isCurrent
        ? 'current'
        : requiredComplete
          ? 'completed'
          : 'upcoming';

      const hasDirtyFields = changedFieldKeys
        ? q.fields.some((f) => changedFieldKeys.has(getAnswerKey(f)))
        : false;

      // An AI-captured scope item marked "mentioned" (context) or "ongoing" (deferred) is NOT
      // included in the estimate (0h); strike it through in the sidebar so the user sees at a
      // glance which questions are out of scope.
      const adhocPrimary = isAdhocSection(section.sectionKey)
        ? (q.fields.find((f) => f.isAdhocPrimary) ?? q.fields[0])
        : undefined;
      const outOfScope =
        adhocPrimary?.scopeMeta?.scopeStatus === 'mentioned' ||
        adhocPrimary?.scopeMeta?.scopeStatus === 'ongoing';

      return {
        label: q.label,
        state,
        saving: savingGroupKeys.has(q.groupKey),
        requiredCount,
        requiredComplete,
        optionalCount,
        optionalComplete,
        hasDirtyFields,
        outOfScope,
      };
    });

    return {
      id: section.sectionKey,
      title: section.label,
      description:
        questionCount > 0
          ? `${questionCount} question${questionCount === 1 ? '' : 's'}`
          : undefined,
      questions,
    };
  });
}

/** Ensures a non-empty URL has an https:// scheme. Applied on blur and on selection. */
function normalizeUrlValue(v: string): string {
  const trimmed = v.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

/**
 * Auto-format a raw input into MM/DD as the user types.
 * Strips non-digits, limits to 4 digits, and inserts `/` after the month part.
 */
function formatMonthDayInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function formatNumberInput(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('en-US');
}

function getSliderValueContent(
  value: number,
  valueBreakpoints: Array<{ upTo?: number; label: string; description: string }>
): { valueLabel: string; valueDescription: string } | null {
  if (valueBreakpoints.length === 0) return null;
  for (const bp of valueBreakpoints) {
    if (bp.upTo === undefined || value <= bp.upTo) {
      return { valueLabel: bp.label, valueDescription: bp.description };
    }
  }
  return null;
}

// ─── Row-grid field (uiControl: 'table') ─────────────────────────────────────

interface RowGridFieldProps {
  field: SalesDealTemplateFieldSummary;
  value: unknown;
  answerKey: string;
  disabled?: boolean;
  onChange: (key: string, value: unknown) => void;
  error?: string;
  showRequiredText?: boolean;
  autocompleteValues?: Record<string, string[]>;
}

function RowGridField({
  field,
  value,
  answerKey,
  disabled,
  onChange,
  error,
  showRequiredText,
  autocompleteValues,
}: RowGridFieldProps) {
  const columns = field.displayHints?.columns ?? [];
  const maxRows = field.displayHints?.maxRows ?? 5;

  const rows: Array<Record<string, string>> = Array.isArray(value)
    ? (value as Array<Record<string, string>>)
    : [];

  const normalizedRows = Array.from({ length: maxRows }, (_, i) => {
    const existing = rows[i] ?? {};
    return Object.fromEntries(columns.map((c) => [c.key, existing[c.key] ?? '']));
  });

  const [cellErrors, setCellErrors] = React.useState<Record<string, string | undefined>>({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleCellChange = (rowIdx: number, colKey: string, cellValue: string) => {
    const next = normalizedRows.map((r, i) =>
      i === rowIdx ? { ...r, [colKey]: cellValue } : { ...r }
    );
    onChange(answerKey, next);
    const col = columns.find((c) => c.key === colKey);
    if (col?.validator === 'email') {
      setCellErrors((prev) => ({
        ...prev,
        [`${rowIdx}:${colKey}`]:
          cellValue && !emailRegex.test(cellValue) ? 'Enter a valid email' : undefined,
      }));
    }
  };

  return (
    <div className="sales-create-deal__field-block">
      {field.label && (
        <p className="sales-create-deal__field-label">
          <RequiredLabel
            label={field.label}
            required={!disabled && field.isRequired}
            showRequiredText={showRequiredText || Boolean(error)}
          />
        </p>
      )}
      {field.description && (
        <span className="sales-create-deal__field-help">{field.description}</span>
      )}
      <div className="questionnaire-row-grid">
        {normalizedRows.map((row, rowIdx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={rowIdx} className="questionnaire-row-grid__row">
            {columns.map((col) =>
              col.autocompleteSource ? (
                <AutocompleteField
                  key={col.key}
                  name={`${answerKey}.${rowIdx}.${col.key}`}
                  ariaLabel={col.label}
                  placeholder={col.placeholder ?? col.label}
                  value={row[col.key] ?? ''}
                  options={autocompleteValues?.[col.autocompleteSource] ?? []}
                  disabled={disabled}
                  size="comfortable"
                  onChange={(v) => handleCellChange(rowIdx, col.key, v)}
                />
              ) : (
                <TextField
                  key={col.key}
                  name={`${answerKey}.${rowIdx}.${col.key}`}
                  aria-label={col.label}
                  type={col.validator === 'email' ? 'email' : 'text'}
                  placeholder={col.placeholder ?? col.label}
                  value={row[col.key] ?? ''}
                  error={cellErrors[`${rowIdx}:${col.key}`]}
                  suppressErrorMessage={false}
                  disabled={disabled}
                  size="comfortable"
                  onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)}
                />
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * FieldBlock - labelled wrapper for questionnaire controls that don't render their own label.
 *
 * Generates a stable id (via React.useId) and exposes it to the control through a render-prop so the
 * visible label can be programmatically associated via htmlFor. Keeps the existing
 * `sales-create-deal__field-*` markup/styling unchanged.
 */
function FieldBlock({
  label,
  description,
  required,
  showRequiredText,
  children,
}: {
  label?: string;
  description?: string | null;
  required?: boolean;
  showRequiredText?: boolean;
  children: (fieldId: string) => React.ReactNode;
}) {
  const fieldId = React.useId();
  return (
    <div className="sales-create-deal__field-block">
      {(label || description) && (
        <div className="sales-create-deal__field-header">
          {label ? (
            <label htmlFor={fieldId} className="sales-create-deal__field-label">
              <RequiredLabel
                label={label}
                required={required}
                showRequiredText={showRequiredText}
              />
            </label>
          ) : null}
          {description ? (
            <span className="sales-create-deal__field-help">{description}</span>
          ) : null}
        </div>
      )}
      {children(fieldId)}
    </div>
  );
}

/**
 * The recommended-value hint units, keyed by the recommendation configKey a field's
 * profileDefault points at. Only the fields the product owner asked to annotate.
 */
const RECOMMENDATION_HINT_UNITS: Record<string, string> = {
  recommended_hours_per_month: 'hours',
  recommended_term_months: 'months',
};

/**
 * Who the "recommends" hint attributes the recommendation to: STATIC platform
 * branding, per the product owner - never the workspace's own brand
 * (`appConfig.productName` resolves to the tenant's white-label name, e.g.
 * "Rapid Cloud Partners", which read wrong here). Casing per owner's request.
 */
const RECOMMENDER_NAME = 'Cosow';

/**
 * Number field with the dynamic "coSow recommends: N hours; you can override it."
 * hint appended to the helper text for contract-recommendation target fields. The
 * hint reads the LIVE recommendation from ContractRecommendationHintContext
 * (provided by SalesCreateDealSurface and the Contract tab), so it updates as the
 * expected totals change - and keeps showing the recommendation even after the
 * user overrides the field's value. Attributed to the platform (RECOMMENDER_NAME),
 * never the workspace's own brand.
 */
function QuestionnaireNumberField({
  field,
  label,
  value,
  error,
  showRequiredText,
  answerKey,
  onChange,
}: {
  field: SalesDealTemplateFieldSummary;
  label: string;
  value: unknown;
  error?: string;
  showRequiredText: boolean;
  answerKey: string;
  onChange: (fieldKey: string, value: unknown) => void;
}) {
  const liveRecommendation = React.useContext(ContractRecommendationHintContext);
  const configKey = field.displayHints?.profileDefault?.configKey;
  const hintUnit = configKey ? RECOMMENDATION_HINT_UNITS[configKey] : undefined;
  const recommendedValue =
    hintUnit && liveRecommendation
      ? (liveRecommendation as Record<string, number | undefined>)[configKey!]
      : undefined;
  const recommendationHint =
    hintUnit && typeof recommendedValue === 'number'
      ? `${RECOMMENDER_NAME} recommends: ${recommendedValue.toLocaleString('en-US')} ${hintUnit}; you can override it.`
      : null;
  const helperText =
    [field.description, recommendationHint].filter(Boolean).join(' ') || undefined;
  return (
    <NumberInput
      label={label}
      secondaryText={
        field.displayHints?.profileDefault
          ? (field.displayHints.profileDefault.label ??
            (field.displayHints.profileDefault.profile === 'estimate'
              ? 'From estimate'
              : 'Config Default'))
          : undefined
      }
      required={field.isRequired}
      size="comfortable"
      value={normalizeTextValue(value)}
      error={error}
      showRequiredText={showRequiredText}
      suppressErrorMessage
      placeholder={field.placeholder ?? undefined}
      helperText={helperText}
      onChange={(event) => onChange(answerKey, formatNumberInput(event.target.value))}
    />
  );
}

function renderQuestionnaireField(
  field: SalesDealTemplateFieldSummary,
  answers: QuestionnaireAnswerMap,
  onChange: (fieldKey: string, value: unknown) => void,
  error?: string,
  disabled = false,
  autocompleteValues?: Record<string, string[]>,
  onPlaceSelect?: (fieldKey: string, label: string) => void,
  placesLoading = false,
  onEntitySelect?: (fieldKey: string, value: string) => void,
  // Ad-hoc fields render their own label header (label + AI pill + remove icon), so the
  // control itself must not render a built-in label. Every control omits its label when
  // `label` is falsy, so suppression is just passing an empty string through (keeps the
  // `string` type for controls whose `label` prop is required).
  hideLabel = false
) {
  const answerKey = getAnswerKey(field);
  const value = answers[answerKey] ?? null;
  const label = hideLabel ? '' : field.label;
  const validationState = getFieldValidationState(field, answers);
  const showRequiredText = !disabled && field.isRequired && validationState.isInvalid;

  // Calculated (read-only) fields - render as a labelled static display, never interactive.
  if (field.displayHints?.readOnly) {
    // Calculated numbers (e.g. field_product results) display with locale
    // separators, matching how the sibling number inputs format what users type.
    const displayValue =
      typeof value === 'number' ? value.toLocaleString('en-US') : normalizeTextValue(value);
    return (
      <div className="questionnaire-field-readonly">
        <div className="questionnaire-field-readonly__header">
          <p className="questionnaire-field-readonly__label">
            <RequiredLabel label={label} showOptionalText={false} secondaryText={['Readonly']} />
          </p>
          {field.description && (
            <p className="questionnaire-field-readonly__helper">{field.description}</p>
          )}
        </div>
        <p className="questionnaire-field-readonly__value">
          {displayValue || <span className="questionnaire-field-readonly__empty">-</span>}
        </p>
      </div>
    );
  }

  if (field.uiControl === 'textarea') {
    return (
      <TextArea
        label={label}
        required={field.isRequired}
        value={normalizeTextValue(value)}
        placeholder={field.placeholder ?? undefined}
        helperText={field.description ?? undefined}
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        rows={4}
        onChange={(event) => onChange(answerKey, event.target.value)}
      />
    );
  }

  if (field.uiControl === 'number') {
    return (
      <QuestionnaireNumberField
        field={field}
        label={label}
        value={value}
        error={error}
        showRequiredText={showRequiredText}
        answerKey={answerKey}
        onChange={onChange}
      />
    );
  }

  if (field.uiControl === 'date') {
    return (
      <TextField
        label={label}
        required={field.isRequired}
        type="date"
        size="comfortable"
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        value={normalizeTextValue(value)}
        helperText={field.description ?? undefined}
        onChange={(event) => onChange(answerKey, event.target.value)}
      />
    );
  }

  if (field.uiControl === 'select') {
    const currentValue = typeof value === 'string' ? value : '';
    return (
      <FieldBlock
        label={label}
        description={field.description}
        required={!disabled && field.isRequired}
        showRequiredText={showRequiredText || Boolean(error)}
      >
        {(fieldId) => (
          <Select
            id={fieldId}
            name={answerKey}
            value={currentValue || undefined}
            onValueChange={(nextValue) => onChange(answerKey, nextValue)}
            size="comfortable"
            disabled={disabled}
            placeholder={field.placeholder ?? 'Select an option'}
          >
            {field.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
        )}
      </FieldBlock>
    );
  }

  if (field.uiControl === 'multi_select') {
    const multiOptions = field.autocompleteSource
      ? (autocompleteValues?.[field.autocompleteSource] ?? []).map((s) => ({ value: s, label: s }))
      : field.options.map((option) => ({ value: option.value, label: option.label }));

    if (field.autocompleteSource) {
      return (
        <TagSelectField
          label={label}
          required={field.isRequired}
          showRequiredText={showRequiredText}
          error={error}
          options={multiOptions}
          selectedValues={normalizeMultiSelectValue(value)}
          onSelectionChange={(nextValue) => onChange(answerKey, nextValue)}
          helperText={field.description ?? undefined}
          placeholder={field.placeholder ?? 'Search…'}
        />
      );
    }

    const annotationConfig = field.displayHints?.annotation as
      | MultiSelectAnnotationConfig
      | undefined;

    if (annotationConfig) {
      type AnnotatedItem = { value: string; [key: string]: string };
      const storedItems: AnnotatedItem[] = Array.isArray(value)
        ? (value as unknown[]).filter(
            (v): v is AnnotatedItem => typeof v === 'object' && v !== null && 'value' in v
          )
        : [];
      const annotSelectedValues = storedItems.map((v) => v.value);
      const annotationValues: Record<string, string> = {};
      for (const v of storedItems) {
        annotationValues[v.value] = v[annotationConfig.key] ?? annotationConfig.default;
      }

      const handleAnnotatedSelectionChange = (nextValues: string[]) => {
        const next = nextValues.map((v) => ({
          value: v,
          [annotationConfig.key]: annotationValues[v] ?? annotationConfig.default,
        }));
        onChange(answerKey, next.length > 0 ? next : null);
      };

      const handleAnnotationChange = (optionValue: string, newAnnotationValue: string) => {
        const next = storedItems.map((v) =>
          v.value === optionValue ? { ...v, [annotationConfig.key]: newAnnotationValue } : v
        );
        onChange(answerKey, next.length > 0 ? next : null);
      };

      return (
        <MultiSelect
          label={label}
          required={field.isRequired}
          error={error}
          showRequiredText={showRequiredText}
          suppressErrorMessage
          options={multiOptions}
          selectedValues={annotSelectedValues}
          onSelectionChange={handleAnnotatedSelectionChange}
          annotation={annotationConfig}
          annotationValues={annotationValues}
          onAnnotationChange={handleAnnotationChange}
          size="comfortable"
          placeholder={field.placeholder ?? 'Select one or more options'}
          helperText={field.description ?? undefined}
        />
      );
    }

    return (
      <MultiSelect
        label={label}
        required={field.isRequired}
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        options={multiOptions}
        selectedValues={normalizeMultiSelectValue(value)}
        onSelectionChange={(nextValue) => onChange(answerKey, nextValue)}
        size="comfortable"
        placeholder={field.placeholder ?? 'Select one or more options'}
        helperText={field.description ?? undefined}
      />
    );
  }

  if (field.uiControl === 'pill_radio') {
    const selectedValue = normalizeTextValue(value) || (isYesNoPillRadioField(field) ? 'no' : null);
    return (
      <PillRadio
        label={label}
        required={!disabled && field.isRequired}
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        options={field.options.map((option) => ({ value: option.value, label: option.label }))}
        selectedValue={selectedValue}
        onSelectionChange={(nextValue) => onChange(answerKey, nextValue)}
        helperText={field.description ?? undefined}
        disabled={disabled}
      />
    );
  }

  if (field.uiControl === 'pill_select') {
    return (
      <PillSelect
        label={label}
        required={!disabled && field.isRequired}
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        options={field.options.map((option) => ({ value: option.value, label: option.label }))}
        selectedValues={normalizeMultiSelectValue(value)}
        onSelectionChange={(nextValue) => onChange(answerKey, nextValue)}
        helperText={field.description ?? undefined}
        disabled={disabled}
      />
    );
  }

  if (field.uiControl === 'pill_dropdown') {
    return (
      <PillDropdown
        label={label}
        required={!disabled && field.isRequired}
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        options={field.options.map((option) => ({ value: option.value, label: option.label }))}
        selectedValues={normalizeMultiSelectValue(value)}
        onSelectionChange={(nextValue) => onChange(answerKey, nextValue)}
        helperText={field.description ?? undefined}
        disabled={disabled}
      />
    );
  }

  if (isStackOptionUiControl(field.uiControl)) {
    return (
      <StackOptionField
        label={label}
        required={field.isRequired}
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        options={field.options.map((option) => ({
          value: option.value,
          label: option.label,
          description: option.description,
          badgeLabel: option.badgeLabel,
          badgeTone: option.badgeTone,
        }))}
        selectedValue={normalizeAnswerChoice(value)}
        onSelectionChange={(nextValue) => onChange(answerKey, nextValue)}
        helperText={field.description ?? undefined}
      />
    );
  }

  if (field.uiControl === 'currency') {
    const defaultCurrency = field.displayHints?.defaultCurrency ?? 'USD';
    const currencyValue = parseCurrencyValue(value, defaultCurrency);
    return (
      <CurrencyInput
        label={label}
        required={field.isRequired}
        size="comfortable"
        value={currencyValue}
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        placeholder={field.placeholder ?? undefined}
        helperText={field.description ?? undefined}
        onChange={(v) => onChange(answerKey, v)}
      />
    );
  }

  if (field.uiControl === 'slider') {
    const parsedSliderValue =
      typeof value === 'number'
        ? value
        : typeof value === 'string' && value.trim()
          ? Number.parseInt(value, 10)
          : null;
    const numValue = Number.isNaN(parsedSliderValue) ? null : parsedSliderValue;
    const sliderConfig = field.displayHints?.sliderConfig ?? null;
    const valueContent =
      sliderConfig?.showValueLabel &&
      typeof numValue === 'number' &&
      sliderConfig.valueBreakpoints?.length
        ? getSliderValueContent(numValue, sliderConfig.valueBreakpoints)
        : null;
    return (
      <SliderField
        label={label}
        required={field.isRequired}
        value={numValue}
        min={sliderConfig?.min ?? 1}
        max={sliderConfig?.max ?? 10}
        step={sliderConfig?.step ?? 1}
        values={sliderConfig?.values}
        defaultValue={sliderConfig?.defaultValue ?? 5}
        helperText={field.description ?? undefined}
        error={error}
        showRequiredText={showRequiredText}
        suppressErrorMessage
        minLabel={sliderConfig?.minLabel}
        centerLabel={sliderConfig?.centerLabel}
        maxLabel={sliderConfig?.maxLabel}
        valueLabel={valueContent?.valueLabel}
        valueDescription={valueContent?.valueDescription}
        supportingText={sliderConfig?.supportingText}
        onChange={(v) => onChange(answerKey, v)}
      />
    );
  }

  if (field.uiControl === 'user_select') {
    // options are resolved workspace members: { label: displayName, value: uuid }
    const optionLabels = field.options.map((o) => o.label);
    const currentValue = normalizeTextValue(value);
    // Display the label for the stored uuid; fall back to the raw value
    const displayValue = field.options.find((o) => o.value === currentValue)?.label ?? currentValue;
    return (
      <FieldBlock
        label={field.label}
        description={field.description}
        required={!disabled && field.isRequired}
        showRequiredText={showRequiredText || Boolean(error)}
      >
        {(fieldId) => (
          <AutocompleteField
            id={fieldId}
            name={answerKey}
            value={displayValue}
            options={optionLabels}
            placeholder={field.placeholder ?? 'Search team members'}
            disabled={disabled}
            size="comfortable"
            onChange={(nextLabel) => {
              const matched = field.options.find((o) => o.label === nextLabel);
              onChange(answerKey, matched ? matched.value : nextLabel);
            }}
          />
        )}
      </FieldBlock>
    );
  }

  if (field.uiControl === 'table') {
    return (
      <RowGridField
        field={field}
        value={value}
        answerKey={answerKey}
        disabled={disabled}
        onChange={onChange}
        error={error}
        showRequiredText={showRequiredText}
        autocompleteValues={autocompleteValues}
      />
    );
  }

  if (field.uiControl === 'tag_input') {
    return (
      <FieldBlock
        label={label}
        description={field.description}
        required={!disabled && field.isRequired}
        showRequiredText={showRequiredText || Boolean(error)}
      >
        {(fieldId) => (
          <TagInput
            id={fieldId}
            name={answerKey}
            tags={normalizeMultiSelectValue(value)}
            onChange={(nextTags) => onChange(answerKey, nextTags)}
            placeholder={field.placeholder ?? undefined}
            disabled={disabled}
            size="comfortable"
          />
        )}
      </FieldBlock>
    );
  }

  if (field.uiControl === 'autocomplete') {
    const source = field.autocompleteSource;
    const suggestions: string[] =
      source === 'field:options'
        ? field.options.map((o) => o.label)
        : source
          ? (autocompleteValues?.[source] ?? [])
          : [];
    const isPlaceSearch = source === 'geocoding:placeAutocomplete';
    const isUrlField = (field.displayHints as Record<string, unknown> | null)?.validator === 'url';
    // For URL fields: strip the https:// scheme from display (shown as prefix adornment)
    // and re-add it on every change so the stored value is always a full URL.
    const rawValue = normalizeTextValue(value);
    const displayValue = isUrlField ? rawValue.replace(/^https?:\/\//i, '') : rawValue;
    const handleUrlChange = (next: string) => {
      onChange(answerKey, next ? normalizeUrlValue(next) : next);
    };
    return (
      <FieldBlock
        label={label}
        description={field.description}
        required={!disabled && field.isRequired}
        showRequiredText={showRequiredText || Boolean(error)}
      >
        {(fieldId) =>
          isPlaceSearch ? (
            <DynamicSearchField
              id={fieldId}
              name={answerKey}
              value={rawValue}
              suggestions={suggestions}
              placeholder={field.placeholder ?? undefined}
              disabled={disabled}
              size="comfortable"
              loading={placesLoading}
              onChange={(nextValue) => onChange(answerKey, nextValue)}
              onSelect={(selected) => onPlaceSelect?.(answerKey, selected)}
            />
          ) : (
            <AutocompleteField
              id={fieldId}
              name={answerKey}
              value={isUrlField ? displayValue : rawValue}
              options={
                isUrlField ? suggestions.map((s) => s.replace(/^https?:\/\//i, '')) : suggestions
              }
              placeholder={field.placeholder ?? undefined}
              disabled={disabled}
              size="comfortable"
              prefix={isUrlField ? 'https://' : undefined}
              normalize={isUrlField ? normalizeUrlValue : undefined}
              error={error}
              onChange={
                isUrlField ? handleUrlChange : (nextValue) => onChange(answerKey, nextValue)
              }
              onSelect={(selected) => onEntitySelect?.(answerKey, selected)}
            />
          )
        }
      </FieldBlock>
    );
  }

  if (field.uiControl === 'month_day') {
    return (
      <TextField
        label={label}
        required={!disabled && field.isRequired}
        size="comfortable"
        error={error}
        showRequiredText={showRequiredText}
        value={normalizeTextValue(value)}
        placeholder={field.placeholder ?? 'MM/DD'}
        helperText={field.description ?? undefined}
        disabled={disabled}
        onChange={(event) => onChange(answerKey, formatMonthDayInput(event.target.value))}
      />
    );
  }

  return (
    <TextField
      label={label}
      required={!disabled && field.isRequired}
      size="comfortable"
      type={field.displayHints?.validator === 'email' ? 'email' : undefined}
      error={error}
      showRequiredText={showRequiredText}
      value={normalizeTextValue(value)}
      placeholder={field.placeholder ?? undefined}
      helperText={field.description ?? undefined}
      disabled={disabled}
      onChange={(event) => onChange(answerKey, event.target.value)}
    />
  );
}

// ─── Question group ────────────────────────────────────────────────────────────

/**
 * Skeleton rows shown while AI-captured questions are being applied + reloaded (one row per incoming
 * question, capped). Rendered both inside the ad-hoc QuestionGroup and at the section level when the
 * ad-hoc section is still empty mid-capture, so "Questions captured" gives immediate loading feedback.
 */
function AdhocCaptureSkeleton({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <>
      {Array.from({ length: Math.min(count, 8) }).map((_, i) => (
        <div
          key={`adhoc-skeleton-${i}`}
          className="sales-create-deal__field sales-create-deal__adhoc-skeleton"
          aria-hidden="true"
        >
          <SkeletonBlock height="14px" width="38%" />
          <SkeletonBlock height="34px" width="100%" />
        </div>
      ))}
    </>
  );
}

interface QuestionGroupProps {
  question: SalesDealTemplateGroupSummary;
  answers: QuestionnaireAnswerMap;
  touchedFields: Set<string>;
  fieldLookup?: QuestionnaireFieldLookup;
  conditionalRules: SalesDealQuestionnaireSummary['conditionalRules'];
  onAnswerChange: (fieldKey: string, value: unknown) => void;
  dealUuid?: string | null;
  onReloadQuestionnaire?: () => Promise<void>;
  autocompleteValues?: Record<string, string[]>;
  onPlaceSelect?: (fieldKey: string, label: string) => void;
  onEntitySelect?: (fieldKey: string, value: string) => void;
  placesLoading?: boolean;
  /**
   * True for the synthetic ad-hoc questions group: each field gets an "AI" pill (when
   * AI-captured) plus a remove control, and the group ends with an add-question form so
   * the user can fully customize this one-time question set.
   */
  isAdhocSection?: boolean;
  /**
   * Ad-hoc section only: number of AI-captured questions currently being applied + reloaded. While
   * > 0 the group renders that many skeleton rows (capped) so "Questions captured" gives immediate
   * writing/loading feedback before the real fields arrive.
   */
  loadingCount?: number;
  /** Ad-hoc section only: focus this primary scope item across the split view (on click/focus). */
  onFocusAdhocField?: (fieldKey: string) => void;
  /** Non-ad-hoc only: muted hint shown when the group has zero visible fields (e.g. a fresh
   *  "Additional Information" group whose questions are added via the header "Add question" button). */
  emptyHint?: string;
  /**
   * Ad-hoc section only: when true the PRIMARY scope item's remove control is hosted in the
   * card header (the normal single-question view, next to Save), so it is suppressed here.
   * Supplementary fields always keep their inline remove. Compact/split view leaves this
   * false, so the primary keeps its inline × (there is no per-question header there).
   */
  adhocRemoveInHeader?: boolean;
  /**
   * Whether the user may add/remove on the workspace template. Drives the "All future deals"
   * option of the per-field delete shown on user-added Additional Information questions.
   */
  canEditTemplate?: boolean;
  /** False while the questionnaire's permissions have not loaded - forwarded to the per-field
   *  delete so the "needs admin role" hint is suppressed until the gate is actually known. */
  permissionsKnown?: boolean;
}

function QuestionGroup({
  question,
  answers,
  touchedFields,
  fieldLookup,
  conditionalRules,
  onAnswerChange,
  dealUuid,
  onReloadQuestionnaire,
  autocompleteValues,
  onPlaceSelect,
  onEntitySelect,
  placesLoading,
  isAdhocSection,
  loadingCount = 0,
  onFocusAdhocField,
  emptyHint,
  adhocRemoveInHeader = false,
  canEditTemplate = false,
  permissionsKnown = true,
}: QuestionGroupProps) {
  const { workspace } = useWorkspace();
  const aiProvenanceEnabled = workspace?.aiProvenanceEnabled ?? false;
  const visibleFields = getVisibleQuestionFields(question, answers, conditionalRules, fieldLookup);
  // User-added "Additional Information" questions are plain template fields (never built-in),
  // so each gets an inline × delete (scope: this deal vs the workspace template).
  const isAdditionalInfoGroup = question.groupKey === ADDITIONAL_INFO_GROUP.key;
  // The Current Technology Stack group renders a dedicated structured editor (multi-tech per
  // category + per-tech integration scope) instead of the generic per-field rows. The legacy
  // tech_stack_<cat>_tool/_plan fields stay in the template (AI-capture inputs) but are not
  // rendered here; they are still preserved on save (their values remain in `answers`).
  const isTechStackGroup = question.groupLayoutHint === 'tech-stack';
  const techStackSystemsField =
    question.fields.find((f) => f.fieldKey === 'tech_stack_systems') ?? null;

  return (
    <div className="sales-create-deal__question-group">
      {question.subtitle ? (
        <p className="sales-create-deal__body-description">{question.subtitle}</p>
      ) : null}
      {isTechStackGroup ? (
        <div className="sales-create-deal__field">
          <TechStackEditor
            field={techStackSystemsField}
            answers={answers}
            fieldLookup={fieldLookup}
            onChange={onAnswerChange}
          />
        </div>
      ) : (
        visibleFields.map((field) => {
          const validationState = getFieldValidationState(field, answers);
          const error =
            touchedFields.has(getAnswerKey(field)) && validationState.isInvalid
              ? (validationState.errorMessage ?? 'This field is required')
              : undefined;
          const nestingLevel = getFieldNestingLevelByRules(field.fieldKey, conditionalRules);
          const isEnabled = isFieldEnabledByRules(
            field.fieldKey,
            conditionalRules,
            answers,
            fieldLookup
          );
          const isHalf = field.displayHints?.span === 'half';
          const maxWidth = field.displayHints?.maxWidth ?? undefined;
          // Ad-hoc primary scope item: clicking/focusing it syncs focus to the Prioritization pane.
          const focusAdhoc =
            isAdhocSection && field.isAdhocPrimary && onFocusAdhocField
              ? () => onFocusAdhocField(field.fieldKey)
              : undefined;
          // Additional Information field: show an inline × (positioned on the label row) to remove it.
          // Skipped for deprecated (already-removed) fields the deal still answered.
          const showTemplateRemove =
            isAdditionalInfoGroup && !!dealUuid && !!onReloadQuestionnaire && !field.deprecated;

          return (
            <div
              className={`sales-create-deal__field${isHalf ? ' sales-create-deal__field--half' : ''}${nestingLevel > 0 ? ` sales-create-deal__field--follow-up sales-create-deal__field--follow-up-depth-${nestingLevel}` : ''}${showTemplateRemove ? ' sales-create-deal__field--removable' : ''}`}
              style={maxWidth ? { maxWidth } : undefined}
              key={getAnswerKey(field)}
              data-adhoc-fieldkey={
                isAdhocSection && field.isAdhocPrimary ? field.fieldKey : undefined
              }
              onClick={focusAdhoc}
              onFocus={focusAdhoc}
            >
              {/* Ad-hoc header. PRIMARY (the scope item): "What was discussed" label + AI pill +
                remove; its control's built-in label is suppressed (hideLabel) and the scope brief
                renders below. SUPPLEMENTARY (a plain field added inside the group): no
                "What was discussed", no brief - it keeps its own label + control, with a remove. */}
              {isAdhocSection ? (
                <div className="sales-create-deal__adhoc-field-header">
                  {field.isAdhocPrimary ? (
                    <span className="sales-create-deal__field-label">What was discussed</span>
                  ) : null}
                  {field.isAiCaptured ? (
                    <Pill variant="accent" className="sales-create-deal__adhoc-ai-pill">
                      AI
                    </Pill>
                  ) : null}
                  {dealUuid &&
                  onReloadQuestionnaire &&
                  !(field.isAdhocPrimary && adhocRemoveInHeader) ? (
                    <RemoveAdhocFieldControl
                      dealUuid={dealUuid}
                      fieldKey={field.fieldKey}
                      label={field.label}
                      onReloadQuestionnaire={onReloadQuestionnaire}
                    />
                  ) : null}
                </div>
              ) : null}
              {/* Additional Information question: inline × to remove it (this deal, or the workspace
                template), pinned to the field's top-right so it sits on the label's row. */}
              {showTemplateRemove && dealUuid && onReloadQuestionnaire ? (
                <span className="sales-create-deal__field-remove">
                  <RemoveTemplateFieldControl
                    dealUuid={dealUuid}
                    fieldKey={field.fieldKey}
                    label={field.label}
                    canEditTemplate={canEditTemplate}
                    permissionsKnown={permissionsKnown}
                    onReloadQuestionnaire={onReloadQuestionnaire}
                  />
                </span>
              ) : null}
              {renderQuestionnaireField(
                field,
                answers,
                onAnswerChange,
                error,
                !isEnabled,
                autocompleteValues,
                onPlaceSelect,
                placesLoading,
                onEntitySelect,
                isAdhocSection && !!field.isAdhocPrimary
              )}
              {/* Scope interpretation (requested/context, method, drivers) - PRIMARY field only. */}
              {isAdhocSection && dealUuid && field.isAdhocPrimary ? (
                <AdhocScopeControls
                  dealUuid={dealUuid}
                  fieldKey={field.fieldKey}
                  scopeMeta={field.scopeMeta}
                  disabled={!isEnabled}
                />
              ) : null}
              {field.valueState === 'ai_suggested' &&
              dealUuid &&
              onReloadQuestionnaire &&
              aiProvenanceEnabled ? (
                <AiProvenanceBadge
                  onAccept={async () => {
                    await salesApi.confirmQuestionnaireField(dealUuid, field.fieldKey);
                    await onReloadQuestionnaire();
                  }}
                  onReject={async () => {
                    await salesApi.rejectQuestionnaireField(dealUuid, field.fieldKey);
                    await onReloadQuestionnaire();
                  }}
                />
              ) : null}
              {/* Ad-hoc (AI-captured) questions - templateFieldId === null - may be promoted
                into a permanent template field. Promote is deferred for now (the code is
                kept for later); gated behind ENABLE_ADHOC_PROMOTE so captured questions
                stay deal-scoped. */}
              {ENABLE_ADHOC_PROMOTE &&
              field.templateFieldId === null &&
              dealUuid &&
              onReloadQuestionnaire ? (
                <PromoteAdhocFieldControl
                  dealUuid={dealUuid}
                  fieldKey={field.fieldKey}
                  onReloadQuestionnaire={onReloadQuestionnaire}
                />
              ) : null}
            </div>
          );
        })
      )}
      {/* The systems landscape diagram now lives on its own "Systems Landscape" tab (paired with the
          editor), so the tech-stack question renders the editor only. See SystemsLandscapePane. */}
      {/* Ad-hoc capture in flight ("Questions captured" was clicked): a skeleton row per incoming
          question so the user sees them being written/loaded before the real fields arrive. */}
      {isAdhocSection ? <AdhocCaptureSkeleton count={loadingCount} /> : null}
      {/* Ad-hoc section: the add-question affordance lives in the section header (next to
          Save). When the section is empty (and nothing is loading), show a hint pointing there. */}
      {isAdhocSection &&
      dealUuid &&
      onReloadQuestionnaire &&
      visibleFields.length === 0 &&
      loadingCount === 0 ? (
        <div className="sales-create-deal__field sales-create-deal__adhoc-add">
          <p className="sales-create-deal__field-help">
            No one-time questions yet. Use &ldquo;Add question&rdquo; above to add one that applies
            only to this deal.
          </p>
        </div>
      ) : null}
      {/* Non-ad-hoc empty group (e.g. a fresh "Additional Information" group): point at the
          header "Add question" button. */}
      {!isAdhocSection && emptyHint && visibleFields.length === 0 ? (
        <div className="sales-create-deal__field">
          <p className="sales-create-deal__field-help">{emptyHint}</p>
        </div>
      ) : null}
      {/* One-question-at-a-time view: a group whose fields the answers hide (e.g. Usage Metering
          with no usage revenue) says so instead of opening as a blank panel. */}
      {!isAdhocSection && !emptyHint && !isTechStackGroup && question.fields.length > 0 &&
      visibleFields.length === 0 ? (
        <div className="sales-create-deal__field">
          <p className="sales-create-deal__field-help">
            No questions apply here, based on the answers so far.
          </p>
        </div>
      ) : null}
    </div>
  );
}
/**
 * Feature flag: promote-to-section is deferred for now. The control, its dialog, and
 * the backend `salesApi.promoteAdhocField` path are all kept intact for later - flip
 * this to `true` to re-enable. While off, AI-captured questions stay deal-scoped.
 */
const ENABLE_ADHOC_PROMOTE = false;

/**
 * "Promote to field" affordance for an ad-hoc (AI-captured) question: a button that
 * opens a dialog to choose the target section + group, then promotes the question into
 * a permanent template field (keeping its answer) and reloads the questionnaire.
 */
function PromoteAdhocFieldControl({
  dealUuid,
  fieldKey,
  onReloadQuestionnaire,
}: {
  dealUuid: string;
  fieldKey: string;
  onReloadQuestionnaire: () => Promise<void>;
}) {
  const [open, setOpen] = React.useState(false);
  const [sections, setSections] = React.useState<
    Array<{ sectionKey: string; label: string; groups: Array<{ groupKey: string; label: string }> }>
  >([]);
  const [sectionKey, setSectionKey] = React.useState<string | undefined>(undefined);
  const [groupKey, setGroupKey] = React.useState<string | undefined>(undefined);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    void salesApi
      .getTemplatePlacement(dealUuid)
      .then((res) => {
        if (!cancelled) setSections(res.sections);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load placement options.');
      });
    return () => {
      cancelled = true;
    };
  }, [open, dealUuid]);

  const groups = sections.find((s) => s.sectionKey === sectionKey)?.groups ?? [];

  const handlePromote = async () => {
    if (!sectionKey || !groupKey) return;
    setBusy(true);
    setError(null);
    try {
      await salesApi.promoteAdhocField(dealUuid, fieldKey, { sectionKey, groupKey });
      setOpen(false);
      await onReloadQuestionnaire();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to promote this question.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSectionKey(undefined);
          setGroupKey(undefined);
          setError(null);
        }
      }}
    >
      <Dialog.Trigger asChild>
        <Button type="button" variant="ghost" size="sm">
          Promote to field
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pi-dialog-overlay" />
        <Dialog.Content className="pi-dialog-content">
          <div className="pi-modal__header">
            <Dialog.Title className="pi-modal__title">Promote to template field</Dialog.Title>
            <Dialog.Close asChild>
              <button className="pi-modal__close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="pi-modal__body-text">
            Choose where this question should live in the template. It becomes a permanent field and
            keeps its current answer.
          </Dialog.Description>
          <FieldBlock label="Section">
            {(fieldId) => (
              <Select
                id={fieldId}
                value={sectionKey}
                onValueChange={(v) => {
                  setSectionKey(v);
                  setGroupKey(undefined);
                }}
                size="comfortable"
                placeholder="Select a section"
              >
                {sections.map((s) => (
                  <SelectItem key={s.sectionKey} value={s.sectionKey}>
                    {s.label}
                  </SelectItem>
                ))}
              </Select>
            )}
          </FieldBlock>
          <FieldBlock label="Group">
            {(fieldId) => (
              <Select
                id={fieldId}
                value={groupKey}
                onValueChange={setGroupKey}
                size="comfortable"
                placeholder="Select a group"
                disabled={!sectionKey}
              >
                {groups.map((g) => (
                  <SelectItem key={g.groupKey} value={g.groupKey}>
                    {g.label}
                  </SelectItem>
                ))}
              </Select>
            )}
          </FieldBlock>
          {error ? <p className="pi-modal__error">{error}</p> : null}
          <div className="pi-modal__actions">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handlePromote()}
              disabled={!sectionKey || !groupKey || busy}
            >
              {busy ? 'Promoting…' : 'Promote'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Synthetic ad-hoc questions section key. Mirrors `ADHOC_SECTION_KEY` in
 * apps/api/src/services/sales/deal-adhoc-fields.ts - the server emits this section
 * (always, even empty) and the deal page renders its add/remove affordances.
 */
const ADHOC_SECTION_KEY = 'ai_captured_questions';
/** Prefix for a coarse Business-Process group section (a peer of the ad-hoc catch-all). Mirrors
 *  ADHOC_GROUP_SECTION_PREFIX in apps/api/src/services/sales/deal-adhoc-fields.ts. */
const ADHOC_GROUP_SECTION_PREFIX = `${ADHOC_SECTION_KEY}__group__`;
/** True for the ad-hoc catch-all OR any Business-Process group section - all get the ad-hoc treatment
 *  (scope-brief cards, AI pills, the per-section add affordance, force-open while capturing). */
function isAdhocSection(sectionKey: string | null | undefined): boolean {
  return (
    !!sectionKey &&
    (sectionKey === ADHOC_SECTION_KEY || sectionKey.startsWith(ADHOC_GROUP_SECTION_PREFIX))
  );
}

/**
 * Remove control for a single ad-hoc question (AI-captured or manual). Soft-deletes the
 * question and its answer server-side, then reloads the questionnaire. Confirms first so
 * a captured question isn't lost by an accidental click.
 */
function RemoveAdhocFieldControl({
  dealUuid,
  fieldKey,
  label,
  onReloadQuestionnaire,
  appearance = 'icon',
}: {
  dealUuid: string;
  fieldKey: string;
  label: string;
  onReloadQuestionnaire: () => Promise<void>;
  /**
   * 'icon' = the inline × shown in the field header (supplementary fields, and the primary
   * in compact/split view). 'button' = a destructive "Remove" button shown in the card
   * header next to Save (the normal single-question view's primary scope item).
   */
  appearance?: 'icon' | 'button';
}) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const handleRemove = async () => {
    setBusy(true);
    try {
      await salesApi.deleteAdhocField(dealUuid, fieldKey);
      setOpen(false);
      await onReloadQuestionnaire();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {appearance === 'button' ? (
        <Button type="button" variant="destructive" size="sm" onClick={() => setOpen(true)}>
          Remove
        </Button>
      ) : (
        <button
          type="button"
          className="sales-create-deal__adhoc-remove"
          aria-label={`Remove question: ${label}`}
          onClick={() => setOpen(true)}
        >
          <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
        </button>
      )}
      <ConfirmDeleteDialog
        open={open}
        onOpenChange={setOpen}
        title="Remove question?"
        description={
          <p>
            Remove <strong>{label}</strong> from this deal? Its answer is removed too. This only
            affects this deal.
          </p>
        }
        confirmKeyword={label}
        confirmLabel="Remove question"
        pendingLabel="Removing…"
        isPending={busy}
        onConfirm={handleRemove}
      />
    </>
  );
}

/**
 * Inline × delete for a user-added "Additional Information" question (a template field, not an
 * ad-hoc scope item). Mirrors AddTemplateQuestionControl's scope choice: "Just this deal" (default)
 * removes it from this deal only; "All future deals" also drops it from the workspace template
 * (gated by canEditTemplate; the server re-checks). The field's answer is retained on deals that
 * already answered it (they keep view/edit access as a deprecated field).
 */
function RemoveTemplateFieldControl({
  dealUuid,
  fieldKey,
  label,
  canEditTemplate,
  permissionsKnown = true,
  onReloadQuestionnaire,
}: {
  dealUuid: string;
  fieldKey: string;
  label: string;
  canEditTemplate: boolean;
  /** False while the questionnaire's permissions have not loaded yet - suppresses the "needs the
   *  sales manager or admin role" hint so an unknown state never masquerades as a denial. */
  permissionsKnown?: boolean;
  onReloadQuestionnaire: () => Promise<void>;
}) {
  const [open, setOpen] = React.useState(false);
  const [scope, setScope] = React.useState<'deal_only' | 'reusable'>('deal_only');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reset = () => {
    setScope('deal_only');
    setError(null);
  };

  const handleRemove = async () => {
    setBusy(true);
    setError(null);
    try {
      await salesApi.deleteTemplateField(dealUuid, fieldKey, scope);
      // Hold the dialog open with its busy spinner through the reload so the question is gone
      // from the list the instant the dialog closes - no silent gap.
      await onReloadQuestionnaire();
      setOpen(false);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove this question.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="sales-create-deal__adhoc-remove"
          aria-label={`Remove question: ${label}`}
        >
          <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pi-dialog-overlay" />
        <Dialog.Content className="pi-dialog-content">
          <div className="pi-modal__header">
            <Dialog.Title className="pi-modal__title">Remove question?</Dialog.Title>
            <Dialog.Close asChild>
              <button className="pi-modal__close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="pi-modal__body-text">
            {scope === 'reusable' ? (
              <>
                Remove <strong>{label}</strong> from this deal and the workspace template, so future
                deals stop getting it.
              </>
            ) : (
              <>
                Remove <strong>{label}</strong> from this deal only. Other deals are not affected.
              </>
            )}
          </Dialog.Description>
          <FieldBlock label="Remove from">
            {() => (
              <SegmentedControl<'deal_only' | 'reusable'>
                className="sales-create-deal__scope-toggle"
                size="compact"
                ariaLabel="Removal scope"
                value={scope}
                onChange={setScope}
                options={[
                  { value: 'deal_only', label: 'Just this deal' },
                  { value: 'reusable', label: 'All future deals', disabled: !canEditTemplate },
                ]}
              />
            )}
          </FieldBlock>
          {permissionsKnown && !canEditTemplate ? (
            <p className="sales-create-deal__field-help">
              Removing from future deals (the workspace template) needs the sales manager or admin
              role.
            </p>
          ) : null}
          {error ? <p className="pi-modal__error">{error}</p> : null}
          <div className="pi-modal__actions pi-modal__actions--flush">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={busy}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => void handleRemove()}
              disabled={busy}
            >
              {busy ? (
                <>
                  <span className="wizard__btn-spinner" aria-hidden="true" /> Removing…
                </>
              ) : (
                'Remove question'
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Add-question control for the ad-hoc section: a dialog that collects a label, an answer
 * type (the shared focused control set), and - for choice types - the options. Persists
 * via the existing capture endpoint (sourceRunId stays null, so no "AI" pill), then
 * reloads the questionnaire. Matches the wizard's field styling (FieldBlock + Select +
 * TextField).
 */
/** Rename a coarse Business-Process group (re-tags every question in it). On a group section header. */
function RenameAdhocGroupControl({
  dealUuid,
  groupName,
  onReloadQuestionnaire,
}: {
  dealUuid: string;
  groupName: string;
  onReloadQuestionnaire: () => Promise<void>;
}) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState(groupName);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (open) setName(groupName);
  }, [open, groupName]);
  const canSubmit = name.trim().length > 0 && name.trim() !== groupName && !busy;
  const handleRename = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await salesApi.renameAdhocGroup(dealUuid, groupName, name.trim());
      setOpen(false);
      await onReloadQuestionnaire();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to rename this group.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="sales-create-deal__adhoc-add-icon"
          aria-label={`Rename group ${groupName}`}
        >
          <Pencil1Icon />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pi-dialog-overlay" />
        <Dialog.Content className="pi-dialog-content">
          <div className="pi-modal__header">
            <Dialog.Title className="pi-modal__title">Rename group</Dialog.Title>
            <Dialog.Close asChild>
              <button className="pi-modal__close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="pi-modal__body-text">
            Renames this group across every question in it.
          </Dialog.Description>
          <FieldBlock label="Group name" required>
            {(fieldId) => (
              <TextField
                id={fieldId}
                value={name}
                size="comfortable"
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </FieldBlock>
          {error ? <p className="pi-modal__error">{error}</p> : null}
          <div className="pi-modal__actions pi-modal__actions--flush">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={busy}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleRename()}
              disabled={!canSubmit}
            >
              {busy ? 'Renaming…' : 'Rename'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Friendly labels for the ad-hoc answer-type picker (ADHOC_QUESTION_CONTROLS). */
const ADHOC_CONTROL_LABELS: Record<string, string> = {
  text: 'Short text',
  textarea: 'Long text',
  number: 'Number',
  currency: 'Currency',
  date: 'Date',
  boolean: 'Yes / No',
  select: 'Single select',
  multi_select: 'Multi-select',
};

function AddAdhocQuestionControl({
  dealUuid,
  onReloadQuestionnaire,
  iconOnly,
  groupKey,
  businessProcess,
  onQuestionAdded,
}: {
  dealUuid: string;
  onReloadQuestionnaire: () => Promise<void>;
  /** Render a compact squared "+" icon instead of the "Add question" button. */
  iconOnly?: boolean;
  /** When set, the new field is a plain supplementary question inside this group (primary's
   *  fieldKey); when omitted, a new scope-item group is created in the ad-hoc section. */
  groupKey?: string;
  /** When set (on a coarse group section's "+"), the new scope item is tagged with this
   *  businessProcess so it lands in that group. */
  businessProcess?: string;
  /** Called (after the reload completes) with the new question's fieldKey, so the wizard can open
   *  its section in the left nav and show it in the right pane. */
  onQuestionAdded?: (fieldKey: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Every ad-hoc question is a "captured" scope item: just a label -> a scope item that renders the
  // full scope brief (scope status, activities, complexity drivers, ...) inline. There is no per-question
  // answer type - the answer is free-text and the brief carries the structure.
  const canSubmit = label.trim().length > 0 && !busy;

  const reset = () => {
    setLabel('');
    setError(null);
  };

  const handleAdd = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const { created } = await salesApi.captureAdhocFields(dealUuid, [
        {
          label: label.trim(),
          // Ad-hoc questions are captured scope items: always free-text (the scope brief carries the
          // structure), never a per-question answer type.
          uiControl: 'textarea',
          ...(groupKey ? { groupKey } : {}),
          // A new scope item created from a group section's "+" is tagged with that group, so it lands
          // in that group as a captured-question scope item.
          ...(businessProcess && !groupKey ? { scopeMeta: { businessProcess } } : {}),
        },
      ]);
      setOpen(false);
      reset();
      await onReloadQuestionnaire();
      // Open the newly-added question: jump the left nav + right pane to it once the reloaded
      // questionnaire (which now contains it) has flowed back in. For a supplementary field
      // (groupKey set) the new key lands inside the existing group, so this re-selects that group.
      const newFieldKey = created[0]?.fieldKey;
      if (newFieldKey) onQuestionAdded?.(newFieldKey);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add this question.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Dialog.Trigger asChild>
        {iconOnly ? (
          <button
            type="button"
            className="sales-create-deal__adhoc-add-icon"
            aria-label={groupKey ? 'Add a question to this group' : 'Add a question'}
          >
            <PlusIcon />
          </button>
        ) : (
          <Button type="button" variant="outline" size="sm">
            Add question
          </Button>
        )}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pi-dialog-overlay" />
        <Dialog.Content className="pi-dialog-content">
          <div className="pi-modal__header">
            <Dialog.Title className="pi-modal__title">
              {groupKey
                ? 'Add a question to this group'
                : businessProcess
                  ? `Add a scope item to ${businessProcess}`
                  : 'Add a question'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="pi-modal__close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="pi-modal__body-text">
            {groupKey
              ? 'A plain question added inside this scope item (label + answer). It is not separately costed.'
              : businessProcess
                ? 'A scope item in this group - it gets the full scope brief (scope status, activities, complexity drivers, ...) you can fill in on its card.'
                : 'A one-time question for this deal only, added to the ad-hoc questions. Fill in its scope brief on its card.'}
          </Dialog.Description>
          <FieldBlock label={businessProcess && !groupKey ? 'Deliverable' : 'Question'} required>
            {(fieldId) => (
              <TextField
                id={fieldId}
                value={label}
                placeholder="e.g. Which data center region?"
                size="comfortable"
                onChange={(e) => setLabel(e.target.value)}
              />
            )}
          </FieldBlock>
          {error ? <p className="pi-modal__error">{error}</p> : null}
          <div className="pi-modal__actions pi-modal__actions--flush">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={busy}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleAdd()}
              disabled={!canSubmit}
            >
              {busy ? 'Adding…' : 'Add question'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Add a plain questionnaire field to a standard template group (e.g. Company Overview) directly
 * from the section header. Same 2-control dialog as the generic ad-hoc add (question + answer type,
 * with an Options box for select/multi-select), but it persists via the template-authoring path
 * (salesApi.addTemplateField), so the field renders at the bottom of the group's main pane - not in
 * the Ad-hoc section - and is never a scope/estimate item. The field is aiEnabled (the AI tries to
 * fill it during extraction; missing it is fine), and the server defaults its extraction prompt.
 */
function AddTemplateQuestionControl({
  dealUuid,
  sectionKey,
  groupKey,
  groupLabel,
  canEditTemplate,
  permissionsKnown = true,
  onReloadQuestionnaire,
}: {
  dealUuid: string;
  sectionKey: string;
  groupKey: string;
  /** Created-or-revived on the backend before the field insert; also the dialog's display label. */
  groupLabel: string;
  /** Allow the "All future deals" (reusable template) scope. When false only the deal-only add is offered. */
  canEditTemplate: boolean;
  /** False while permissions have not loaded yet - suppresses the "needs the sales manager or admin
   *  role" hint so an unknown state never masquerades as a denial. */
  permissionsKnown?: boolean;
  onReloadQuestionnaire: () => Promise<void>;
}) {
  const [open, setOpen] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [uiControl, setUiControl] = React.useState<string>('text');
  const [optionsText, setOptionsText] = React.useState('');
  // 'deal_only' = one-time, just this deal (default, safe). 'reusable' = add to the workspace
  // template so future deals get it (gated by canEditTemplate).
  const [scope, setScope] = React.useState<'deal_only' | 'reusable'>('deal_only');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const needsOptions = uiControl === 'select' || uiControl === 'multi_select';
  const parsedOptions = optionsText
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((l) => ({
      label: l,
      value:
        l
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '') || l,
    }));
  const canSubmit = label.trim().length > 0 && !busy && (!needsOptions || parsedOptions.length > 0);

  const reset = () => {
    setLabel('');
    setUiControl('text');
    setOptionsText('');
    setScope('deal_only');
    setError(null);
  };

  const handleAdd = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await salesApi.addTemplateField(dealUuid, {
        sectionKey,
        groupKey,
        groupLabel,
        scope,
        draft: {
          label: label.trim(),
          uiControl,
          ...(needsOptions ? { options: parsedOptions } : {}),
          // Let the AI try to fill it during extraction; it is never a scope/estimate item.
          aiEnabled: true,
        },
      });
      // Hold the dialog open with its busy spinner through the reload so the new question is
      // visible the instant the dialog closes - no silent gap where nothing seems to happen.
      await onReloadQuestionnaire();
      setOpen(false);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add this question.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Dialog.Trigger asChild>
        <Button type="button" variant="outline" size="sm">
          Add question
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pi-dialog-overlay" />
        <Dialog.Content className="pi-dialog-content">
          <div className="pi-modal__header">
            <Dialog.Title className="pi-modal__title">Add a question to {groupLabel}</Dialog.Title>
            <Dialog.Close asChild>
              <button className="pi-modal__close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="pi-modal__body-text">
            {scope === 'reusable'
              ? `Added to your workspace template's ${groupLabel}, so future deals get it too. The AI will try to fill it during extraction; it is not estimated.`
              : `Added to this deal's ${groupLabel} only. The AI will try to fill it during extraction; it is not estimated.`}
          </Dialog.Description>
          <FieldBlock label="Question" required>
            {(fieldId) => (
              <TextField
                id={fieldId}
                value={label}
                placeholder="e.g. Which data center region?"
                size="comfortable"
                onChange={(e) => setLabel(e.target.value)}
              />
            )}
          </FieldBlock>
          <FieldBlock label="Add to">
            {() => (
              <SegmentedControl<'deal_only' | 'reusable'>
                className="sales-create-deal__scope-toggle"
                size="compact"
                ariaLabel="Question scope"
                value={scope}
                onChange={setScope}
                options={[
                  { value: 'deal_only', label: 'Just this deal' },
                  { value: 'reusable', label: 'All future deals', disabled: !canEditTemplate },
                ]}
              />
            )}
          </FieldBlock>
          {permissionsKnown && !canEditTemplate ? (
            <p className="sales-create-deal__field-help">
              Adding to future deals (the workspace template) needs the sales manager or admin role.
            </p>
          ) : null}
          <FieldBlock label="Answer type">
            {(fieldId) => (
              <Select
                id={fieldId}
                value={uiControl}
                onValueChange={setUiControl}
                size="comfortable"
              >
                {ADHOC_QUESTION_CONTROLS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {ADHOC_CONTROL_LABELS[c] ?? c}
                  </SelectItem>
                ))}
              </Select>
            )}
          </FieldBlock>
          {needsOptions ? (
            <FieldBlock label="Options" required>
              {(fieldId) => (
                <TextArea
                  id={fieldId}
                  value={optionsText}
                  placeholder={'One option per line\ne.g. North America\nEMEA\nAPAC'}
                  rows={4}
                  onChange={(e) => setOptionsText(e.target.value)}
                />
              )}
            </FieldBlock>
          ) : null}
          {error ? <p className="pi-modal__error">{error}</p> : null}
          <div className="pi-modal__actions pi-modal__actions--flush">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={busy}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleAdd()}
              disabled={!canSubmit}
            >
              {busy ? (
                <>
                  <span className="wizard__btn-spinner" aria-hidden="true" /> Adding…
                </>
              ) : (
                'Add question'
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Add-sections control for ADDITIVE deals: opens a picker of the category's not-yet-included
 * questionnaire groups (from GET /available-structure) and adds the selected ones via
 * POST /groups. Manual additive deals start empty (only ad-hoc), so this is how the user
 * brings in the sections they need.
 */
function AddSectionsControl({
  dealUuid,
  onReloadQuestionnaire,
}: {
  dealUuid: string;
  onReloadQuestionnaire: () => Promise<void>;
}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [structure, setStructure] = React.useState<SalesDealAvailableStructure | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());

  const loadStructure = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await salesApi.getDealAvailableStructure(dealUuid);
      setStructure(result.structure);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sections.');
    } finally {
      setLoading(false);
    }
  }, [dealUuid]);

  const toggle = (groupUuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(groupUuid)) next.delete(groupUuid);
      else next.add(groupUuid);
      return next;
    });

  const addableSections = (structure?.sections ?? [])
    .map((section) => ({ ...section, groups: section.groups.filter((g) => !g.included) }))
    .filter((section) => section.groups.length > 0);
  const nothingToAdd = !loading && structure != null && addableSections.length === 0;

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      await salesApi.setDealGroupInclusion(dealUuid, { include: [...selected] });
      setOpen(false);
      setSelected(new Set());
      await onReloadQuestionnaire();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add the sections.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setSelected(new Set());
          void loadStructure();
        }
      }}
    >
      <Dialog.Trigger asChild>
        <button type="button" className="wizard__sidebar-add-btn" aria-label="Add sections">
          <span aria-hidden="true">+</span> Add sections
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pi-dialog-overlay" />
        <Dialog.Content className="pi-dialog-content">
          <div className="pi-modal__header">
            <Dialog.Title className="pi-modal__title">Add sections</Dialog.Title>
            <Dialog.Close asChild>
              <button className="pi-modal__close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="pi-modal__body-text">
            Choose the questionnaire groups to add to this deal. You can remove them later.
          </Dialog.Description>
          {loading ? <p className="pi-modal__body-text">Loading…</p> : null}
          {nothingToAdd ? (
            <p className="pi-modal__body-text">All available sections have already been added.</p>
          ) : null}
          {!loading && addableSections.length > 0 ? (
            <div className="sales-create-deal__add-sections">
              {addableSections.map((section) => (
                <div key={section.sectionKey} className="sales-create-deal__add-sections-group">
                  <p className="sales-create-deal__field-label">{section.label}</p>
                  {section.groups.map((g) => (
                    <label key={g.groupUuid} className="sales-create-deal__add-sections-row">
                      <input
                        type="checkbox"
                        checked={selected.has(g.groupUuid)}
                        onChange={() => toggle(g.groupUuid)}
                      />
                      <span>
                        {g.label}
                        {g.fieldCount ? ` (${g.fieldCount})` : ''}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
          {error ? <p className="pi-modal__error">{error}</p> : null}
          <div className="pi-modal__actions">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={busy}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleAdd()}
              disabled={busy || selected.size === 0}
            >
              {busy ? 'Adding…' : selected.size > 0 ? `Add ${selected.size}` : 'Add'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Remove the current (additive) group from the deal, then reset to the first step. */
/**
 * "Remove" control for the current group on an additive deal. Prompts the user to choose the
 * scope: remove the WHOLE GROUP (destructive, type-to-confirm) or remove INDIVIDUAL QUESTIONS
 * (a checklist of the group's fields - unchecking removes, re-checking restores). Removed
 * fields are hidden and excluded from the estimate/SOW/resource plan, but their answers are
 * retained, so re-checking brings them back.
 */
function RemoveSectionControl({
  dealUuid,
  groupUuid,
  groupLabel,
  onAfterGroupRemoved,
  onAfterFieldsChanged,
}: {
  dealUuid: string;
  groupUuid: string;
  groupLabel: string;
  onAfterGroupRemoved: () => Promise<void>;
  onAfterFieldsChanged: () => Promise<void>;
}) {
  const [view, setView] = React.useState<'closed' | 'choice' | 'fields'>('closed');
  const [confirmGroupOpen, setConfirmGroupOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [fields, setFields] = React.useState<SalesDealAvailableField[]>([]);
  // Field uuids currently kept (checked). Seeded from the not-excluded fields on load.
  const [present, setPresent] = React.useState<Set<string>>(() => new Set());

  const loadFields = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { structure } = await salesApi.getDealAvailableStructure(dealUuid);
      const group = structure.sections
        .flatMap((s) => s.groups)
        .find((g) => g.groupUuid === groupUuid);
      const groupFields = group?.fields ?? [];
      setFields(groupFields);
      setPresent(new Set(groupFields.filter((f) => !f.excluded).map((f) => f.fieldUuid)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the questions.');
    } finally {
      setLoading(false);
    }
  }, [dealUuid, groupUuid]);

  const toggle = (fieldUuid: string) =>
    setPresent((prev) => {
      const next = new Set(prev);
      if (next.has(fieldUuid)) next.delete(fieldUuid);
      else next.add(fieldUuid);
      return next;
    });

  const removeGroup = async () => {
    setBusy(true);
    try {
      await salesApi.setDealGroupInclusion(dealUuid, { exclude: [groupUuid] });
      setConfirmGroupOpen(false);
      await onAfterGroupRemoved();
    } finally {
      setBusy(false);
    }
  };

  const applyFields = async () => {
    const excludeFields = fields
      .filter((f) => !f.excluded && !present.has(f.fieldUuid))
      .map((f) => f.fieldUuid);
    const includeFields = fields
      .filter((f) => f.excluded && present.has(f.fieldUuid))
      .map((f) => f.fieldUuid);
    if (excludeFields.length === 0 && includeFields.length === 0) {
      setView('closed');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await salesApi.setDealGroupInclusion(dealUuid, { excludeFields, includeFields });
      setView('closed');
      await onAfterFieldsChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update the questions.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button type="button" variant="destructive" size="sm" onClick={() => setView('choice')}>
        Remove
      </Button>

      <Dialog.Root
        open={view !== 'closed'}
        onOpenChange={(next) => {
          if (busy) return;
          setView(next ? 'choice' : 'closed');
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="pi-dialog-overlay" />
          <Dialog.Content className="pi-dialog-content">
            <div className="pi-modal__header">
              <Dialog.Title className="pi-modal__title">
                {view === 'fields'
                  ? `Remove questions from ${groupLabel}`
                  : 'Remove from this deal'}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="pi-modal__close" aria-label="Close" disabled={busy}>
                  ×
                </button>
              </Dialog.Close>
            </div>

            {view === 'choice' ? (
              <>
                <Dialog.Description className="pi-modal__body-text">
                  Remove the whole <strong>{groupLabel}</strong> group, or pick individual questions
                  to remove. Answers are kept either way and return if you add things back.
                </Dialog.Description>
                <div className="sales-create-deal__remove-choices">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setView('closed');
                      setConfirmGroupOpen(true);
                    }}
                  >
                    Remove the whole group
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setView('fields');
                      void loadFields();
                    }}
                  >
                    Remove individual questions
                  </Button>
                </div>
              </>
            ) : null}

            {view === 'fields' ? (
              <>
                <Dialog.Description className="pi-modal__body-text">
                  Uncheck the questions to remove from this deal. Re-check to restore them.
                </Dialog.Description>
                {loading ? <p className="pi-modal__body-text">Loading…</p> : null}
                {!loading && fields.length > 0 ? (
                  <div className="sales-create-deal__add-sections">
                    {fields.map((f) => (
                      <label key={f.fieldUuid} className="sales-create-deal__add-sections-row">
                        <input
                          type="checkbox"
                          checked={present.has(f.fieldUuid)}
                          onChange={() => toggle(f.fieldUuid)}
                        />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
                {!loading && fields.length === 0 ? (
                  <p className="pi-modal__body-text">This group has no questions to manage.</p>
                ) : null}
              </>
            ) : null}

            {error ? <p className="pi-modal__error">{error}</p> : null}

            <div className="pi-modal__actions">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" size="sm" disabled={busy}>
                  Cancel
                </Button>
              </Dialog.Close>
              {view === 'fields' ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void applyFields()}
                  disabled={busy || loading}
                >
                  {busy ? 'Applying…' : 'Apply'}
                </Button>
              ) : null}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ConfirmDeleteDialog
        open={confirmGroupOpen}
        onOpenChange={setConfirmGroupOpen}
        title="Remove this group?"
        description={
          <p>
            This removes the entire <strong>{groupLabel}</strong> group from this deal. Answers you
            already entered for it are kept and return if you add it back.
          </p>
        }
        confirmKeyword={groupLabel}
        confirmLabel="Remove group"
        pendingLabel="Removing…"
        isPending={busy}
        onConfirm={removeGroup}
      />
    </>
  );
}

// ─── Create deal wizard surface ───────────────────────────────────────────────

interface SalesCreateDealSurfaceProps {
  deal: SalesDealSummary | null;
  loadingDeal: boolean;
  dealError: string | null;
  onRetryLoadDeal: () => void;
  questionnaire: SalesDealQuestionnaireSummary | null;
  loadingQuestionnaire: boolean;
  questionnaireError: string | null;
  onRetryLoadQuestionnaire: () => void;
  answers: QuestionnaireAnswerMap;
  persistedAnswers: QuestionnaireAnswerMap;
  touchedFields: Set<string>;
  setTouchedFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** Fields the user changed away from their default - drives the sidebar `*` dirty markers. */
  changedFieldKeys: Set<string>;
  /** Fields with a value to persist (includes untouched defaults) - drives the Save button. */
  savableFieldKeys: Set<string>;
  saveError: string | null;
  onAnswerChange: (fieldKey: string, value: unknown) => void;
  onPersistAnswers: (question?: SalesDealTemplateGroupSummary) => Promise<boolean>;
  /** True while an AI enrichment run is in flight for this deal (shows spinner). */
  isEnrichmentRunning: boolean;
  /** Number of AI-captured questions being applied/reloaded; > 0 shows ad-hoc-section skeletons. */
  capturingAdhocCount?: number;
  /** True when there are unsaved ad-hoc scope edits - activates the (per-section) Save button. */
  hasPendingAdhocScope?: boolean;
  currentUserUuid?: string | null;
  salesModuleRole?: string | null;
  dealUuid?: string | null;
  onReloadQuestionnaire?: () => Promise<void>;
  /** Optimistically persist + live-sync an ad-hoc scope field (shared with the Prioritization pane).
   *  Used by the sidebar drag-to-move so a functional-area change updates instantly without a reload. */
  commitAdhocScopeFields?: (
    fieldKey: string,
    patch: Partial<
      Pick<
        ScopeMeta,
        | 'scopeStatus'
        | 'customerPriority'
        | 'recommendedPriority'
        | 'businessProcess'
        | 'workType'
        | 'inScope'
        | 'outOfScope'
        | 'complexityDrivers'
        | 'assumptions'
      >
    >
  ) => void;
  sidebarHeader?: React.ReactNode;
  compactMode?: boolean;
  selectedSectionKeys?: string[];
  onChangeSectionFilter?: (keys: string[]) => void;
  /** Split-view cross-pane focus: the ad-hoc fieldKey to scroll to + highlight (null = none). */
  focusedAdhocFieldKey?: string | null;
  /** When set (Prioritization open in the split), focusing an AI-captured question focuses it. */
  onFocusAdhocField?: (fieldKey: string) => void;
  /**
   * Wizard footer for static-SOW categories (STATIC_SOW_WIZARD_CATEGORY_KEYS, e.g.
   * managed_services): Previous/Next step through the sections. SOW generation is
   * deliberately NOT offered here (it lives on the SOW tab, gated on the contract
   * terms). False/absent (every other category) = no footer, pixel-identical to today.
   */
  sectionStepperFooter?: boolean;
}

export function SalesCreateDealSurface({
  deal,
  loadingDeal,
  dealError,
  onRetryLoadDeal,
  questionnaire,
  loadingQuestionnaire,
  questionnaireError,
  onRetryLoadQuestionnaire,
  answers,
  persistedAnswers,
  touchedFields,
  setTouchedFields,
  changedFieldKeys,
  savableFieldKeys,
  saveError,
  onAnswerChange,
  onPersistAnswers,
  isEnrichmentRunning,
  capturingAdhocCount = 0,
  hasPendingAdhocScope = false,
  currentUserUuid,
  salesModuleRole,
  dealUuid,
  onReloadQuestionnaire,
  commitAdhocScopeFields,
  sidebarHeader,
  compactMode,
  selectedSectionKeys: providedSelectedSectionKeys,
  focusedAdhocFieldKey = null,
  onFocusAdhocField,
  sectionStepperFooter,
}: SalesCreateDealSurfaceProps) {
  // Cross-pane focus: scroll + flash the matching ad-hoc field (compact/split mode) on focus change.
  const adhocRootRef = React.useRef<HTMLDivElement>(null);
  useScrollToFocusedAdhoc(focusedAdhocFieldKey, adhocRootRef);
  const [activeStep, setActiveStep] = React.useState(0);
  const [activeQuestionIndex, setActiveQuestionIndex] = React.useState(0);
  // After a question is added via a "+", the fieldKey to jump to once the reloaded questionnaire
  // (which now contains it) flows back in; cleared the moment we select it. See the effect below.
  const [pendingSelectFieldKey, setPendingSelectFieldKey] = React.useState<string | null>(null);
  const [savingGroupKeys, setSavingGroupKeys] = React.useState<Set<string>>(() => new Set());
  const {
    autocompleteValues,
    onAnswerChange: handleAnswerChangeWithGeo,
    onPlaceSelect,
    onCustomerSelect,
    placesLoading,
  } = useQuestionnaireAutocomplete(questionnaire, onAnswerChange);

  // Live contract recommendation for the "recommends" hints under the contract-term
  // fields (managed-services). Recomputed on every answer change; null for
  // questionnaires without a contractRecommendation context.
  const liveContractRecommendation = React.useMemo(
    () => computeLiveContractRecommendation(questionnaire, answers),
    [questionnaire, answers]
  );

  const [sidebarWidth, setSidebarWidth] = React.useState(DEFAULT_SIDEBAR_WIDTH);

  const handleSidebarResize = React.useCallback((delta: number) => {
    setSidebarWidth((prev) =>
      Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, prev + delta))
    );
  }, []);

  // Always-current refs so async callbacks can detect mid-save navigation
  const activeStepRef = React.useRef(activeStep);
  const activeQuestionIndexRef = React.useRef(activeQuestionIndex);
  activeStepRef.current = activeStep;
  activeQuestionIndexRef.current = activeQuestionIndex;

  // Display-only questionnaire: always surface an "Additional Information" group in the company
  // section's left nav (below Primary Contact) - even with zero fields - as the home for the in-deal
  // "Add question" button. Injected on the FRONTEND only (the backend builder is shared with SOW/AI
  // flows, where a synthetic group would leak). Idempotent: once the user adds the first question the
  // real group is fetched and we skip the synthetic one. Feeds the nav + current-selection ONLY; the
  // save/persist path stays on raw `questionnaire` so this empty group never reaches a write.
  //
  // BPR deals additionally pin a display-only "System Architecture" SECTION right after the company
  // section, before the dynamic business-process sections - the BPR workflow's first parsed-out
  // section (current + future state tabs). Same contract as the synthetic group: nav/selection only,
  // never persisted (isBprArchitectureSection guards every write-adjacent branch).
  const showArchitectureSection = dealHasDiagramLayer(deal);
  const displayQuestionnaire = React.useMemo<SalesDealQuestionnaireSummary | null>(() => {
    if (!questionnaire) return questionnaire;
    // Anchor on the section holding the mandatory primary_contact group (NetSuite: 'company-overview',
    // Rillet: 'project-overview'); fall back to the first section.
    let anchorIdx = questionnaire.sections.findIndex((s) =>
      s.groups.some((g) => g.groupKey === 'primary_contact')
    );
    if (anchorIdx < 0) anchorIdx = 0;
    const anchor = questionnaire.sections[anchorIdx];
    if (!anchor) return questionnaire;
    let sections = questionnaire.sections;
    if (!anchor.groups.some((g) => g.groupKey === ADDITIONAL_INFO_GROUP.key)) {
      const syntheticGroup: SalesDealTemplateGroupSummary = {
        groupKey: ADDITIONAL_INFO_GROUP.key,
        label: ADDITIONAL_INFO_GROUP.label,
        subtitle: null,
        description: 'Deal-specific questions that do not fit the sections above.',
        groupLayoutHint: null,
        displayOrder: Number.MAX_SAFE_INTEGER, // sort-last within the section (after Primary Contact)
        fields: [], // groupUuid intentionally omitted - synthetic until the first field materializes it
      };
      sections = sections.map((s, i) =>
        i === anchorIdx ? { ...s, groups: [...s.groups, syntheticGroup] } : s
      );
    }
    if (showArchitectureSection && dealUuid) {
      const archSection: (typeof sections)[number] = {
        sectionKey: BPR_ARCHITECTURE_SECTION_KEY,
        label: 'System Architecture',
        displayOrder: anchor.displayOrder,
        groups: [
          {
            // One synthetic question so the section is clickable in the step
            // nav (navigation runs through sub-item clicks); it renders the
            // architecture pane, never a question form.
            groupKey: BPR_ARCHITECTURE_SECTION_KEY,
            label: 'Current & future state',
            subtitle: null,
            description: null,
            groupLayoutHint: null,
            displayOrder: 0,
            fields: [],
          },
        ],
      };
      sections = [
        ...sections.slice(0, anchorIdx + 1),
        archSection,
        ...sections.slice(anchorIdx + 1),
      ];
    }
    if (sections === questionnaire.sections) return questionnaire;
    return { ...questionnaire, sections };
  }, [questionnaire, showArchitectureSection, dealUuid]);

  const steps = React.useMemo(
    () =>
      toWizardSteps(
        displayQuestionnaire,
        persistedAnswers,
        savingGroupKeys,
        activeStep,
        activeQuestionIndex,
        changedFieldKeys
      ),
    [
      displayQuestionnaire,
      persistedAnswers,
      savingGroupKeys,
      activeStep,
      activeQuestionIndex,
      changedFieldKeys,
    ]
  );
  // Pin a squared "+" on the "Ad-hoc questions" sidebar header that adds a NEW group (scope item)
  // inside that section (not a parallel section).
  const wizardSteps = React.useMemo(() => {
    if (!dealUuid || !onReloadQuestionnaire) return steps;
    // Skeleton sub-items appended to the ad-hoc section while a capture is in flight, so the sidebar
    // "Ad-hoc questions" pane shows placeholder rows (and stays expanded - see the active-open effect
    // in StepAccordion) until the real captured questions load in.
    const adhocSkeletons: WizardStepQuestion[] =
      capturingAdhocCount > 0
        ? Array.from({ length: Math.min(capturingAdhocCount, 8) }, () => ({
            label: '',
            state: 'upcoming' as const,
            skeleton: true,
            requiredCount: 0,
            requiredComplete: false,
            optionalCount: 0,
            optionalComplete: false,
          }))
        : [];
    return steps.map((s) => {
      // The pinned System Architecture section is one synthetic sub-item, not
      // a question - drop the "1 question" descriptor.
      if (s.id === BPR_ARCHITECTURE_SECTION_KEY) {
        const { description: _description, ...rest } = s;
        return rest;
      }
      if (!isAdhocSection(s.id)) return s;
      const questions =
        s.id === ADHOC_SECTION_KEY
          ? [...(s.questions ?? []), ...adhocSkeletons]
          : (s.questions ?? []);
      return {
        ...s,
        // Ad-hoc sections (group sections + the catch-all) accept dragged questions and their
        // questions are draggable - drop re-tags businessProcess (or ungroups onto the catch-all).
        droppable: true,
        titleAction: (
          <span className="sales-create-deal__adhoc-header-actions">
            {s.id !== ADHOC_SECTION_KEY ? (
              <RenameAdhocGroupControl
                dealUuid={dealUuid}
                groupName={s.title}
                onReloadQuestionnaire={onReloadQuestionnaire}
              />
            ) : null}
            <AddAdhocQuestionControl
              dealUuid={dealUuid}
              onReloadQuestionnaire={onReloadQuestionnaire}
              iconOnly
              // A group section's "+" adds a question INTO that group (its label = the
              // businessProcess); the catch-all's "+" adds an ungrouped question.
              businessProcess={s.id === ADHOC_SECTION_KEY ? undefined : s.title}
              onQuestionAdded={setPendingSelectFieldKey}
            />
          </span>
        ),
        questions,
        // Muted nav hint under an empty ad-hoc section (mainly the catch-all once everything is grouped).
        ...(questions.length === 0
          ? {
              emptyHint:
                'No ad-hoc questions yet - use + to add one, or capture them from a transcript.',
            }
          : {}),
      };
    });
  }, [steps, dealUuid, onReloadQuestionnaire, capturingAdhocCount]);

  // When a capture starts ("Questions captured" clicked), jump to the ad-hoc section so the user
  // immediately sees the skeleton and then the questions landing there - instead of staying on
  // whatever section they were on (where the skeleton is invisible). prevRef so it fires once per
  // capture, not on every render while it stays > 0.
  const prevCapturingRef = React.useRef(0);
  React.useEffect(() => {
    if (capturingAdhocCount > 0 && prevCapturingRef.current === 0) {
      // Index over displayQuestionnaire - the nav/current-selection basis
      // (raw indexes drift once the synthetic System Architecture section is
      // pinned in for BPR deals).
      const idx =
        displayQuestionnaire?.sections.findIndex((s) => isAdhocSection(s.sectionKey)) ?? -1;
      if (idx >= 0) {
        setActiveStep(idx);
        setActiveQuestionIndex(0);
      }
    }
    prevCapturingRef.current = capturingAdhocCount;
  }, [capturingAdhocCount, displayQuestionnaire?.sections]);

  // A question was just added via a "+": once the reloaded questionnaire contains its fieldKey,
  // open that question - select its section in the left nav (StepAccordion follows activeStep) and
  // show its group in the right pane (currentQuestion derives from activeStep/activeQuestionIndex).
  // Indices are over displayQuestionnaire.sections (same basis the nav + right pane use).
  React.useEffect(() => {
    if (!pendingSelectFieldKey || !displayQuestionnaire) return;
    const sections = displayQuestionnaire.sections;
    for (let si = 0; si < sections.length; si++) {
      const section = sections[si];
      if (!section) continue;
      const gi = section.groups.findIndex((g) =>
        g.fields.some((f) => f.fieldKey === pendingSelectFieldKey)
      );
      if (gi >= 0) {
        setActiveStep(si);
        setActiveQuestionIndex(gi);
        setPendingSelectFieldKey(null);
        return;
      }
    }
  }, [pendingSelectFieldKey, displayQuestionnaire]);

  const currentSection = displayQuestionnaire?.sections[activeStep] ?? null;
  const isLastStep = activeStep === steps.length - 1;

  const stepQuestions = steps[activeStep]?.questions ?? [];
  const isLastQuestion = activeQuestionIndex >= stepQuestions.length - 1;
  const currentQuestion = currentSection?.groups[activeQuestionIndex] ?? null;
  const currentGroupKey = currentQuestion?.groupKey ?? null;
  // Ad-hoc only: the PRIMARY scope item of the current question. Its remove control is hosted
  // in the card header (next to Save) in this normal single-question view.
  const adhocPrimaryField = currentQuestion?.fields.find((f) => f.isAdhocPrimary) ?? null;
  const questionnaireFieldLookup = React.useMemo(
    () => buildQuestionnaireFieldLookup(questionnaire),
    [questionnaire]
  );
  const currentQuestionVisibleFields = React.useMemo(
    () =>
      currentQuestion
        ? getVisibleQuestionFields(
            currentQuestion,
            answers,
            questionnaire?.conditionalRules ?? [],
            questionnaireFieldLookup
          )
        : [],
    [currentQuestion, answers, questionnaire, questionnaireFieldLookup]
  );
  const currentQuestionHasFieldIn = (fieldKeys: Set<string>) =>
    currentQuestion?.fields.some(
      (field) =>
        isFieldVisibleByRules(
          field.fieldKey,
          questionnaire?.conditionalRules ?? [],
          answers,
          questionnaireFieldLookup
        ) && fieldKeys.has(getAnswerKey(field))
    ) ?? false;
  // Save button: enabled when the group has any value worth persisting, including
  // untouched defaults (savableFieldKeys).
  const currentQuestionHasSavableFields = currentQuestionHasFieldIn(savableFieldKeys);
  // Auto-persist on navigation: only when the user actually changed a field away from its
  // default (changedFieldKeys, same rule as the nav guard) - untouched defaults do not
  // gate navigation.
  const currentQuestionHasDirtyFields = currentQuestionHasFieldIn(changedFieldKeys);
  const isCurrentQuestionInvalid = React.useMemo(() => {
    if (!currentQuestion) return false;
    for (const field of currentQuestionVisibleFields) {
      if (
        !isFieldEnabledByRules(
          field.fieldKey,
          questionnaire?.conditionalRules ?? [],
          answers,
          questionnaireFieldLookup
        )
      )
        continue;
      if (getFieldValidationState(field, answers).isInvalid) return true;
    }
    return false;
  }, [
    currentQuestion,
    currentQuestionVisibleFields,
    answers,
    questionnaire,
    questionnaireFieldLookup,
  ]);
  const isCurrentQuestionSaving = Boolean(currentGroupKey && savingGroupKeys.has(currentGroupKey));

  const validateCurrentQuestion = React.useCallback(() => {
    if (!currentQuestion) return true;
    let ok = true;
    const nextTouched = new Set(touchedFields);

    for (const field of currentQuestionVisibleFields) {
      if (
        !isFieldEnabledByRules(
          field.fieldKey,
          questionnaire?.conditionalRules ?? [],
          answers,
          questionnaireFieldLookup
        )
      )
        continue;
      if (isFieldValidationRequired(field)) {
        if (getFieldValidationState(field, answers).isInvalid) {
          ok = false;
          nextTouched.add(getAnswerKey(field));
        }
      }
    }

    if (!ok) {
      setTouchedFields(nextTouched);
    }
    return ok;
  }, [
    currentQuestion,
    currentQuestionVisibleFields,
    answers,
    touchedFields,
    setTouchedFields,
    questionnaire,
    questionnaireFieldLookup,
  ]);

  const handleSave = React.useCallback(async () => {
    if (!currentQuestion || !validateCurrentQuestion()) return;
    // Capture position at click time; after save we skip auto-advance if user navigated away
    const clickedStep = activeStepRef.current;
    const clickedQuestionIndex = activeQuestionIndexRef.current;
    setSavingGroupKeys((prev) => new Set(prev).add(currentQuestion.groupKey));
    try {
      const ok = await onPersistAnswers(currentQuestion);
      if (!ok) return;
      // Server-resolved defaults (displayHints.profileDefault, e.g. the managed-services
      // contract recommendation) may have become computable from the values just saved.
      // While any answerable default-carrying field is still empty, refetch the
      // questionnaire so those fields prefill immediately instead of on the next page
      // load. Fire-and-forget: the reload merge preserves local edits, and the gate
      // closes itself once every default-carrying field holds a value.
      const hasEmptyDefaultField = (questionnaire?.sections ?? []).some((s) =>
        s.groups.some((g) =>
          g.fields.some((f) => {
            // Only 'estimate'-sourced defaults depend on data that changes after load
            // (saved answers / generated estimates); calculator defaults are static.
            if (f.displayHints?.profileDefault?.profile !== 'estimate') return false;
            if (f.displayHints.readOnly) return false;
            const v = getAnswerValue(answers, f);
            return v === null || v === undefined || v === '';
          })
        )
      );
      if (hasEmptyDefaultField && onReloadQuestionnaire) {
        void onReloadQuestionnaire();
      }
      // User manually navigated during the save - don't override their choice
      if (
        activeStepRef.current !== clickedStep ||
        activeQuestionIndexRef.current !== clickedQuestionIndex
      ) {
        return;
      }
      if (!isLastQuestion) {
        setActiveQuestionIndex((q) => q + 1);
      } else if (!isLastStep) {
        setActiveStep((s) => s + 1);
        setActiveQuestionIndex(0);
      }
    } finally {
      setSavingGroupKeys((prev) => {
        const next = new Set(prev);
        next.delete(currentQuestion.groupKey);
        return next;
      });
    }
  }, [
    currentQuestion,
    validateCurrentQuestion,
    onPersistAnswers,
    isLastQuestion,
    isLastStep,
    questionnaire,
    answers,
    onReloadQuestionnaire,
  ]);

  // Navigate to any question in any section; persist when the current question is dirty (and valid)
  // OR when an ad-hoc scope brief edit is staged. The brief (AdhocScopeControls) stages into
  // pendingScopeMeta on blur and never marks an answer field dirty, so without hasPendingAdhocScope
  // a captured question's edits would only save on an explicit Save - unlike standard questions.
  const handleQuestionClick = React.useCallback(
    (sectionIndex: number, questionIndex: number) => {
      if (sectionIndex === activeStep && questionIndex === activeQuestionIndex) return;
      if ((currentQuestionHasDirtyFields || hasPendingAdhocScope) && currentQuestion) {
        // Only a dirty STANDARD field can be invalid; a staged-only brief edit must still flush and
        // never be blocked by an unrelated standard-field validation state.
        if (currentQuestionHasDirtyFields && isCurrentQuestionInvalid) {
          validateCurrentQuestion();
        } else {
          const groupKey = currentQuestion.groupKey;
          const questionToSave = currentQuestion;
          setSavingGroupKeys((prev) => new Set(prev).add(groupKey));
          void onPersistAnswers(questionToSave).finally(() => {
            setSavingGroupKeys((prev) => {
              const next = new Set(prev);
              next.delete(groupKey);
              return next;
            });
          });
        }
      }
      setActiveStep(sectionIndex);
      setActiveQuestionIndex(questionIndex);
    },
    [
      activeStep,
      activeQuestionIndex,
      currentQuestion,
      currentQuestionHasDirtyFields,
      hasPendingAdhocScope,
      isCurrentQuestionInvalid,
      validateCurrentQuestion,
      onPersistAnswers,
    ]
  );

  // Drag a question from one ad-hoc section to another in the nav: re-tags its businessProcess (or
  // ungroups it when dropped on the catch-all "Ad-hoc questions"). Routes through the SAME optimistic
  // commit the Prioritization pane uses, so the sidebar re-groups INSTANTLY (no PATCH+reload round
  // trip) and the merge still records the GROUP_ASSIGNMENT flywheel correction server-side.
  const handleQuestionMove = React.useCallback(
    (fromSectionIndex: number, fromQuestionIndex: number, toSectionIndex: number) => {
      if (!commitAdhocScopeFields || fromSectionIndex === toSectionIndex) return;
      const sections = questionnaire?.sections;
      const srcSection = sections?.[fromSectionIndex];
      const dstSection = sections?.[toSectionIndex];
      if (!srcSection || !dstSection) return;
      if (!isAdhocSection(srcSection.sectionKey) || !isAdhocSection(dstSection.sectionKey)) return;
      const srcGroup = srcSection.groups[fromQuestionIndex];
      const srcField = srcGroup?.fields.find((f) => f.isAdhocPrimary) ?? srcGroup?.fields[0];
      if (!srcField) return;
      // Catch-all section ungroups (''); a group section uses its members' businessProcess (falling
      // back to its label, which splitAdhocSectionByGroup sets to the group name).
      const targetBp =
        dstSection.sectionKey === ADHOC_SECTION_KEY
          ? ''
          : (dstSection.groups
              .map((g) =>
                g.fields.find((f) => f.isAdhocPrimary)?.scopeMeta?.businessProcess?.trim()
              )
              .find((bp): bp is string => !!bp) ?? dstSection.label);
      const currentBp = srcField.scopeMeta?.businessProcess?.trim() ?? '';
      if (targetBp === currentBp) return;
      commitAdhocScopeFields(srcField.fieldKey, { businessProcess: targetBp });
    },
    [commitAdhocScopeFields, questionnaire]
  );

  React.useEffect(() => {
    setActiveStep(0);
    setActiveQuestionIndex(0);
  }, [deal?.uuid]);

  // Static-SOW category footer handlers. Declared HERE, above the early returns
  // below - hooks after a conditional return change the hook count between the
  // loading and loaded renders (Rules of Hooks violation). The hook-free
  // `wizardFooterActions` JSX derived from these stays next to the render.
  const wizardSectionCount = wizardSteps.length;
  const handleFooterPrevious = React.useCallback(() => {
    setActiveStep((s) => Math.max(0, s - 1));
    setActiveQuestionIndex(0);
  }, []);
  const handleFooterNext = React.useCallback(() => {
    setActiveStep((s) => Math.min(Math.max(wizardSectionCount - 1, 0), s + 1));
    setActiveQuestionIndex(0);
  }, [wizardSectionCount]);

  if (dealError) {
    return <PageErrorState message={dealError} onRetry={onRetryLoadDeal} />;
  }

  if (!deal && !loadingDeal) {
    return null;
  }

  if (deal && !loadingDeal && salesModuleRole === 'viewer') {
    return (
      <p className="sales-create-shell__error">
        You don&apos;t have permission to edit this deal. Viewers can only view deals.
      </p>
    );
  }

  if (
    deal &&
    !loadingDeal &&
    salesModuleRole !== 'admin' &&
    currentUserUuid &&
    deal.createdByUserUuid !== currentUserUuid
  ) {
    return (
      <p className="sales-create-shell__error">
        You don&apos;t have access to this deal. Only the deal owner or a sales admin can edit it.
      </p>
    );
  }

  const inferInFlight = isEnrichmentRunning;
  const showEnrichButton =
    (currentSection?.sectionKey === 'project-overview' &&
      currentQuestion?.groupKey === 'company') ||
    (currentSection?.sectionKey === 'company-overview' &&
      currentQuestion?.groupKey === 'company_overview');
  const isAdditionalInfoGroup = currentQuestion?.groupKey === ADDITIONAL_INFO_GROUP.key;
  const hasQuestionnaireSections = Boolean(questionnaire && questionnaire.sections.length > 0);
  const surfaceLoading =
    (!deal && loadingDeal) || (loadingQuestionnaire && !hasQuestionnaireSections);

  // Static-SOW category footer: Previous/Next step through ALL sections. SOW
  // generation deliberately does NOT live here (it moved to the SOW tab, gated on
  // the contract terms). Rendered as a custom Wizard `actions` node; every other
  // category keeps `actions={null}` (no footer). Handlers are hooks and therefore
  // live above the early returns (see there).
  const wizardFooterActions =
    sectionStepperFooter && !compactMode ? (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={handleFooterPrevious}
          disabled={activeStep === 0}
          aria-label="Previous section"
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleFooterNext}
          disabled={activeStep >= wizardSectionCount - 1}
          aria-label="Next section"
        >
          Next
        </Button>
      </>
    ) : null;

  return (
    <ContractRecommendationHintContext.Provider value={liveContractRecommendation}>
    <div className="sales-create-deal">
      {saveError ? <p className="sales-create-shell__error">{saveError}</p> : null}
      {questionnaireError ? (
        <PageErrorState message={questionnaireError} onRetry={onRetryLoadQuestionnaire} />
      ) : null}

      {!loadingQuestionnaire && !questionnaireError && !hasQuestionnaireSections ? (
        <div className="sales-create-shell__status">
          <p>Questionnaire template is not available.</p>
        </div>
      ) : null}

      {loadingQuestionnaire || hasQuestionnaireSections ? (
        <div className="sales-create-deal__wizard-area">
          <Wizard
            steps={wizardSteps}
            loading={surfaceLoading}
            activeStep={activeStep}
            onQuestionClick={handleQuestionClick}
            onQuestionMove={handleQuestionMove}
            // While a capture is applying, force the ad-hoc pane open (robust, direct open control)
            // so its skeleton rows are visible immediately - independent of the activeStep chain.
            forceOpenStepIds={
              capturingAdhocCount > 0
                ? (questionnaire?.sections
                    .filter((s) => isAdhocSection(s.sectionKey))
                    .map((s) => s.sectionKey) ?? [])
                : []
            }
            stepNavigation="completed"
            sidebarWidth={sidebarWidth}
            onSidebarResize={handleSidebarResize}
            sidebarHeader={sidebarHeader}
            sidebarFooter={
              questionnaire?.structureMode === 'additive' && dealUuid && onReloadQuestionnaire ? (
                <AddSectionsControl
                  dealUuid={dealUuid}
                  onReloadQuestionnaire={onReloadQuestionnaire}
                />
              ) : undefined
            }
            contentTitle={
              compactMode
                ? undefined
                : surfaceLoading
                  ? undefined
                  : currentQuestion?.label || steps[activeStep]?.title
            }
            contentDescription={
              compactMode
                ? undefined
                : surfaceLoading
                  ? undefined
                  : (currentQuestion?.description ?? undefined)
            }
            contentDescriptionActions={
              // The Enrich action moved to the edit header's ⋮ menu; only the
              // in-flight spinner remains here as section-level feedback.
              showEnrichButton && inferInFlight ? (
                <span
                  className="sales-create-deal__header-archon"
                  role="status"
                  aria-label="Enriching company profile"
                >
                  <AiBrandIcon className="deal-page__ai-icon deal-page__ai-icon--sm deal-page__ai-icon--spinning" />
                </span>
              ) : undefined
            }
            contentHeaderActions={
              compactMode ? undefined : (
                <>
                  {/* Additive deals: remove the current section here; the ADD affordance lives
                    at the bottom of the steps sidebar (sidebarFooter) as a dashed button. */}
                  {questionnaire?.structureMode === 'additive' &&
                  dealUuid &&
                  onReloadQuestionnaire &&
                  !isAdhocSection(currentSection?.sectionKey) &&
                  currentQuestion?.groupUuid ? (
                    <RemoveSectionControl
                      dealUuid={dealUuid}
                      groupUuid={currentQuestion.groupUuid}
                      groupLabel={currentQuestion.label}
                      onAfterGroupRemoved={async () => {
                        setActiveStep(0);
                        setActiveQuestionIndex(0);
                        await onReloadQuestionnaire();
                      }}
                      onAfterFieldsChanged={async () => {
                        await onReloadQuestionnaire();
                      }}
                    />
                  ) : null}
                  {/* Additional Information: add a plain questionnaire field to this deal's template,
                    creating the group on first add (renders in this pane; not a scope/estimate item). */}
                  {isAdditionalInfoGroup && dealUuid && onReloadQuestionnaire && currentSection ? (
                    <AddTemplateQuestionControl
                      dealUuid={dealUuid}
                      sectionKey={currentSection.sectionKey}
                      groupKey={ADDITIONAL_INFO_GROUP.key}
                      groupLabel={ADDITIONAL_INFO_GROUP.label}
                      canEditTemplate={questionnaire?.permissions?.canEditTemplate ?? false}
                      permissionsKnown={questionnaire?.permissions != null}
                      onReloadQuestionnaire={onReloadQuestionnaire}
                    />
                  ) : null}
                  {/* Ad-hoc scope item: remove this captured question (and its answer) from the
                    deal. Destructive, type-to-confirm; lives here next to Save instead of inline
                    in the "What was discussed" block. */}
                  {isAdhocSection(currentSection?.sectionKey) &&
                  dealUuid &&
                  onReloadQuestionnaire &&
                  adhocPrimaryField ? (
                    <RemoveAdhocFieldControl
                      appearance="button"
                      dealUuid={dealUuid}
                      fieldKey={adhocPrimaryField.fieldKey}
                      label={adhocPrimaryField.label}
                      onReloadQuestionnaire={onReloadQuestionnaire}
                    />
                  ) : null}
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={
                      loadingQuestionnaire ||
                      isCurrentQuestionSaving ||
                      isCurrentQuestionInvalid ||
                      // Activate for a plain answer change (savable fields) OR an unsaved ad-hoc scope
                      // edit on this section (the toggle/Method/drivers/details, which aren't answers).
                      (!currentQuestionHasSavableFields &&
                        !(isAdhocSection(currentSection?.sectionKey) && hasPendingAdhocScope))
                    }
                    onClick={() => void handleSave()}
                  >
                    {isCurrentQuestionSaving ? (
                      <span className="wizard__btn-spinner" aria-hidden="true" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </>
              )
            }
            actions={wizardFooterActions}
            compactMode={compactMode}
          >
            {compactMode && providedSelectedSectionKeys && displayQuestionnaire?.sections ? (
              // Compact mode: render all selected sections as individual section cards
              <div className="deal-view-page__sections" ref={adhocRootRef}>
                {displayQuestionnaire.sections
                  // The pinned System Architecture section always shows (it is
                  // not in the section picker); user-picked sections filter.
                  .filter(
                    (s) =>
                      s.sectionKey === BPR_ARCHITECTURE_SECTION_KEY ||
                      providedSelectedSectionKeys.includes(s.sectionKey)
                  )
                  .map((section) => {
                    // BPR deals: the System Architecture card sits in section
                    // order (after Company, before the dynamic sections).
                    if (section.sectionKey === BPR_ARCHITECTURE_SECTION_KEY) {
                      return dealUuid ? (
                        <BprDealArchitecture
                          key={section.sectionKey}
                          dealUuid={dealUuid}
                          canGenerate={salesModuleRole !== 'viewer'}
                        />
                      ) : null;
                    }
                    const isAdhoc = isAdhocSection(section.sectionKey);
                    const canEditSections =
                      questionnaire?.structureMode === 'additive' &&
                      !!dealUuid &&
                      !!onReloadQuestionnaire;
                    return (
                      <div
                        key={section.sectionKey}
                        className="deal-view-page__section"
                        data-section-key={section.sectionKey}
                      >
                        <div className="deal-view-page__section-header">
                          <h2 className="deal-view-page__section-title">{section.label}</h2>
                          {canEditSections ? (
                            <div className="deal-view-page__section-actions">
                              {/* Additive deals: remove a section (group) here. Ad-hoc gets an
                                add-question affordance instead. */}
                              {isAdhoc
                                ? dealUuid &&
                                  onReloadQuestionnaire && (
                                    <AddAdhocQuestionControl
                                      dealUuid={dealUuid}
                                      onReloadQuestionnaire={onReloadQuestionnaire}
                                      onQuestionAdded={setPendingSelectFieldKey}
                                    />
                                  )
                                : section.groups
                                    .filter((g) => g.groupUuid)
                                    .map((group) => (
                                      <RemoveSectionControl
                                        key={group.groupKey}
                                        dealUuid={dealUuid}
                                        groupUuid={group.groupUuid as string}
                                        groupLabel={group.label}
                                        onAfterGroupRemoved={async () => {
                                          setActiveStep(0);
                                          setActiveQuestionIndex(0);
                                          await onReloadQuestionnaire();
                                        }}
                                        onAfterFieldsChanged={async () => {
                                          await onReloadQuestionnaire();
                                        }}
                                      />
                                    ))}
                            </div>
                          ) : null}
                        </div>
                        {/* BPR deals: the section opens with its current-state process flow. */}
                        {dealUuid ? (
                          <BprSectionDiagram
                            dealUuid={dealUuid}
                            sectionKey={section.sectionKey}
                            state="current"
                          />
                        ) : null}
                        <div className="sales-create-deal__fields">
                          {section.groups.length === 0 && isAdhoc ? (
                            <p className="sales-create-shell__status">
                              No ad-hoc questions yet - use + to add one, or capture them from a
                              transcript.
                            </p>
                          ) : (
                            // A group whose every field is hidden by the answers (e.g. Usage
                            // Metering with no usage revenue) is not drawn as an empty panel. Any
                            // product; never the tech-stack editor or an ad-hoc section.
                            section.groups
                              .filter(
                                (g) =>
                                  g.groupLayoutHint === 'tech-stack' ||
                                  isAdhoc ||
                                  !isGroupHiddenByRules(
                                    g.fields.map((f) => f.fieldKey),
                                    questionnaire?.conditionalRules ?? [],
                                    answers,
                                    questionnaireFieldLookup
                                  )
                              )
                              .map((group) => (
                              <div
                                key={group.groupKey}
                                className="deal-view-page__group"
                                data-group-key={group.groupKey}
                              >
                                {group.label ? (
                                  <h3 className="deal-view-page__group-label">{group.label}</h3>
                                ) : null}
                                <QuestionGroup
                                  question={group}
                                  answers={answers}
                                  touchedFields={touchedFields}
                                  fieldLookup={questionnaireFieldLookup}
                                  conditionalRules={questionnaire?.conditionalRules ?? []}
                                  onAnswerChange={handleAnswerChangeWithGeo}
                                  dealUuid={dealUuid}
                                  onReloadQuestionnaire={onReloadQuestionnaire}
                                  autocompleteValues={autocompleteValues}
                                  onPlaceSelect={onPlaceSelect}
                                  onEntitySelect={onCustomerSelect}
                                  placesLoading={placesLoading}
                                  isAdhocSection={isAdhocSection(section?.sectionKey)}
                                  loadingCount={capturingAdhocCount}
                                  onFocusAdhocField={onFocusAdhocField}
                                  canEditTemplate={
                                    questionnaire?.permissions?.canEditTemplate ?? false
                                  }
                                  permissionsKnown={questionnaire?.permissions != null}
                                  emptyHint={
                                    group.groupKey === ADDITIONAL_INFO_GROUP.key
                                      ? 'No additional questions yet. Use "Add question" above to add one specific to this deal.'
                                      : undefined
                                  }
                                />
                              </div>
                            ))
                          )}
                        </div>
                        {/* BPR deals: the section closes with its future-state process flow. */}
                        {dealUuid ? (
                          <BprSectionDiagram
                            dealUuid={dealUuid}
                            sectionKey={section.sectionKey}
                            state="future"
                          />
                        ) : null}
                      </div>
                    );
                  })}
              </div>
            ) : currentSection?.sectionKey === BPR_ARCHITECTURE_SECTION_KEY && dealUuid ? (
              // BPR deals: the pinned System Architecture section (after
              // Company, before the dynamic business-process sections). Its
              // one nav sub-item lands here and renders the current/future
              // architecture pane instead of a question form; the pane header
              // already titles the step, so the block's own heading is hidden.
              <BprDealArchitecture
                dealUuid={dealUuid}
                canGenerate={salesModuleRole !== 'viewer'}
                hideTitle
              />
            ) : currentSection ? (
              // Normal mode: render current section's current question
              <>
                {/* BPR deals: the section OPENS with its current-state process
                    flow - in this one-question-at-a-time view it renders above
                    the section's FIRST question only (the wizard's equivalent
                    of "after the section heading, before the first question"). */}
                {dealUuid && activeQuestionIndex === 0 ? (
                  <BprSectionDiagram
                    dealUuid={dealUuid}
                    sectionKey={currentSection.sectionKey}
                    state="current"
                  />
                ) : null}
                <div className="sales-create-deal__fields">
                  {isAdhocSection(currentSection.sectionKey) &&
                  capturingAdhocCount > 0 &&
                  !currentQuestion ? (
                    // Empty ad-hoc section mid-capture (auto-navigated here): show skeletons while the
                    // first captured questions are written + loaded.
                    <AdhocCaptureSkeleton count={capturingAdhocCount} />
                  ) : currentSection.groups.length === 0 ? (
                    <p className="sales-create-shell__status">
                      {isAdhocSection(currentSection.sectionKey)
                        ? 'No ad-hoc questions yet - use + to add one, or capture them from a transcript.'
                        : 'No fields configured for this section.'}
                    </p>
                  ) : currentQuestion ? (
                    <QuestionGroup
                      key={currentQuestion.groupKey}
                      question={currentQuestion}
                      answers={answers}
                      touchedFields={touchedFields}
                      fieldLookup={questionnaireFieldLookup}
                      conditionalRules={questionnaire?.conditionalRules ?? []}
                      onAnswerChange={handleAnswerChangeWithGeo}
                      dealUuid={dealUuid}
                      onReloadQuestionnaire={onReloadQuestionnaire}
                      autocompleteValues={autocompleteValues}
                      onPlaceSelect={onPlaceSelect}
                      onEntitySelect={onCustomerSelect}
                      placesLoading={placesLoading}
                      isAdhocSection={isAdhocSection(currentSection?.sectionKey)}
                      loadingCount={capturingAdhocCount}
                      onFocusAdhocField={onFocusAdhocField}
                      canEditTemplate={questionnaire?.permissions?.canEditTemplate ?? false}
                      permissionsKnown={questionnaire?.permissions != null}
                      // Normal single-question view: the primary's remove lives in the card header.
                      adhocRemoveInHeader
                      emptyHint={
                        currentQuestion.groupKey === ADDITIONAL_INFO_GROUP.key
                          ? 'No additional questions yet. Use "Add question" above to add one specific to this deal.'
                          : undefined
                      }
                    />
                  ) : null}
                </div>
                {/* BPR deals: the section CLOSES with its future-state process
                    flow - shown below the section's LAST question. Renders
                    nothing until the diagram batch exists or for non-BPR deals. */}
                {dealUuid && isLastQuestion ? (
                  <BprSectionDiagram
                    dealUuid={dealUuid}
                    sectionKey={currentSection.sectionKey}
                    state="future"
                  />
                ) : null}
              </>
            ) : null}
          </Wizard>
        </div>
      ) : null}
    </div>
    </ContractRecommendationHintContext.Provider>
  );
}

/**
 * DealPage - Sales module home page
 *
 * Placeholder experience for the Sales module.
 * This page is the entry point when the active module is 'sales'.
 *
 * Future work:
 * - Pipeline management
 * - Opportunity tracking
 * - CRM intelligence
 */

// ─── New-deal side panel ──────────────────────────────────────────────────────

/**
 * Rendered as a child of MainContentShell so it can consume
 * MainContentSidePanelContext (the context is provided by that shell, so
 * DealPageContent - which *renders* MainContentShell - cannot consume it).
 */
function NewDealSidePanel({
  deal,
  onClose,
  onOpen,
}: {
  deal: SalesDealSummary | null;
  onClose: () => void;
  onOpen: (uuid: string) => void;
}) {
  const sidePanelCtx = React.useContext(MainContentSidePanelContext);

  React.useEffect(() => {
    if (!sidePanelCtx) return;
    if (!deal) {
      sidePanelCtx.setPanel(null);
      return;
    }
    const statusPres = getDealStatusPresentation(deal.status);
    const createdDate = deal.createdAt
      ? new Date(deal.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;
    sidePanelCtx.setPanel(
      <ContextDrawer inline open onClose={onClose} title={deal.title}>
        <div className="sales-deal-preview-pane">
          <div className="sales-deal-preview-pane__meta">
            <Pill variant={statusPres.variant}>{statusPres.label}</Pill>
            {createdDate ? (
              <span className="sales-deal-preview-pane__date">Created {createdDate}</span>
            ) : null}
          </div>
          <div className="sales-deal-preview-pane__actions">
            <Button type="button" variant="primary" size="sm" onClick={() => onOpen(deal.uuid)}>
              Open deal
            </Button>
          </div>
        </div>
      </ContextDrawer>
    );
    return () => sidePanelCtx.setPanel(null);
  }, [sidePanelCtx, deal, onClose, onOpen]);

  return null;
}

/**
 * Single source of truth for the deal main-canvas tabs. `stage` (absent = always shown) gates a
 * tab via the workspace feature-min-stage: alpha-marked tabs show only where the min stage is alpha
 * (local/dev) and are hidden in uat/prod (min stage 'beta'). Drives the subnav, the docs-panel
 * cards, and chat open-doc navigation off one list, so a staged feature is fully hidden - not just
 * dropped from the tab bar. See isStageAvailable (@protoss/shared).
 */
const DEAL_TAB_SPECS: ReadonlyArray<{ value: string; label: string; stage?: 'alpha' | 'beta' }> = [
  // The questionnaire as a read-only Tiptap document (H1 section / H2 group / H3 question).
  { value: 'scope', label: 'Scope', stage: 'alpha' },
  { value: 'questionnaire', label: 'Questionnaire' },
  // Only surfaced when the deal's template has a tech-stack group (see hasSystemsLandscapeTab).
  { value: 'systems-landscape', label: 'Systems Landscape' },
  { value: 'prioritization', label: 'Prioritization' },
  { value: 'estimate', label: 'Estimate' },
  // Only surfaced when the deal's template has a contract section (see hasContractTab).
  { value: 'contract', label: 'Contract' },
  { value: 'resource-plan', label: 'Resource Plan', stage: 'alpha' },
  { value: 'sow', label: 'SOW' },
  { value: 'docs', label: 'Docs' },
];

// Re-exported for existing importers; the definition moved to dealWorkspaceContext.ts
// so leaf components (RegenerateMenuButton) can read it without a circular import.
export { STATIC_SOW_WIZARD_CATEGORY_KEYS };

/**
 * Section key that a deal template marks its contract-terms section with. A template
 * carrying this section gets a dedicated Contract tab (next to Estimate) and the section
 * is filtered OUT of the Questionnaire tab - the answers stay ordinary questionnaire
 * answers (same save flow, SOW bindings, readiness gate), only where they render moves.
 * Template-driven on purpose (like the Systems Landscape tab), not category-hardcoded.
 */
export const CONTRACT_SECTION_KEY = 'contract';

// Normalize a deal's product key, tolerant of bare ('netsuite') or composite
// ('oracle:netsuite') keys. Returns null when the deal has no product.
/**
 * Whether the deal page shows the BPR diagram layer (System Architecture + per-section
 * process flows). The API computes `diagramsEnabled` from the deal's category option; when it
 * is absent (older API, or a failed lookup) fall back to the original key check.
 */
function dealHasDiagramLayer(deal: SalesDealSummary | null | undefined): boolean {
  if (!deal) return false;
  return deal.diagramsEnabled ?? deal.dealCategoryKey === 'bpr';
}

/**
 * State reducer for the deal page's deal. `diagramsEnabled` is computed only by the single-deal
 * GET; the PATCH responses (status, title) return a summary without it, so carry the last known
 * value across those partial replacements of the SAME deal - otherwise a diagram-layer CI deal
 * would drop back to the 'bpr'-only fallback the moment discovery flips its status.
 */
function keepDiagramsEnabled(
  prev: SalesDealSummary | null,
  next: React.SetStateAction<SalesDealSummary | null>
): SalesDealSummary | null {
  const resolved = typeof next === 'function' ? next(prev) : next;
  if (
    resolved &&
    prev &&
    resolved.uuid === prev.uuid &&
    resolved.diagramsEnabled === undefined &&
    prev.diagramsEnabled !== undefined
  ) {
    return { ...resolved, diagramsEnabled: prev.diagramsEnabled };
  }
  return resolved;
}

function normalizeDealProductKey(deal: SalesDealSummary | null): string | null {
  const v = deal?.vendorProduct?.vendorKey;
  const p = deal?.vendorProduct?.productKey;
  if (!p) return null;
  return p.includes(':') ? p : v ? `${v}:${p}` : p;
}

/**
 * Human message for a failed SOW export. EXPORT_TEMPLATE_NOT_CONFIGURED (422) means the
 * deal's category has no published SOW export template - a workspace-admin fix, not a user
 * one (docs/plans/29). exportSowDocx throws the body's `error` code verbatim; exportSowDrive
 * wraps the whole body text, so match by substring.
 */
function sowExportErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : '';
  if (msg.includes('EXPORT_TEMPLATE_NOT_CONFIGURED')) {
    return "SOW export is not configured for this deal's category yet - ask your workspace admin to add a SOW export template for it.";
  }
  return msg || 'SOW export failed. Please try again.';
}

function DealWorkspaceContent() {
  type CreateSubmissionPhase = 'idle' | 'checking' | 'creating';

  const location = useLocation();
  const navigate = useNavigate();
  const { dealUuid } = useParams<{ dealUuid?: string }>();
  useAuth();
  const { appUser } = useAuth();
  const { workspace, isLoadingWorkspace } = useWorkspace();
  // Which DEAL_TAB_SPECS tabs are visible for this workspace's feature-min-stage. alpha-marked
  // tabs (Resource Plan) drop out in uat/prod (min stage 'beta'). One set feeds the subnav, the
  // docs-panel cards, and chat open-doc navigation so a staged feature is fully hidden. Defined
  // up here so every consumer (incl. handleChatAction below) can reference it without a TDZ.
  // Resource Plan and Scope also have per-workspace overrides (features.resource_plan /
  // features.scope_tab), resolved server-side onto workspace.resourcePlanEnabled /
  // workspace.scopeTabEnabled; the stage gate is the fallback for an older API.
  const visibleTabValues = React.useMemo(
    () =>
      new Set(
        DEAL_TAB_SPECS.filter((t) =>
          t.value === 'resource-plan'
            ? (workspace?.resourcePlanEnabled ??
              isStageAvailable(t.stage, workspace?.featureMinStage))
            : t.value === 'scope'
              ? (workspace?.scopeTabEnabled ??
                isStageAvailable(t.stage, workspace?.featureMinStage))
              : isStageAvailable(t.stage, workspace?.featureMinStage)
        ).map((t) => t.value)
      ),
    [workspace?.featureMinStage, workspace?.resourcePlanEnabled, workspace?.scopeTabEnabled]
  );
  // True for non-tab views (documents/upload) and visible tabs; false only for a stage-gated tab
  // that is currently hidden - lets the docs-panel + chat navigation reuse the same gate as the subnav.
  const isDocViewAllowed = React.useCallback(
    (kind: string) => !DEAL_TAB_SPECS.some((t) => t.value === kind) || visibleTabValues.has(kind),
    [visibleTabValues]
  );
  const chatSession = useModuleChatSession('sales');
  const { config: appConfig } = usePublicAppConfig();
  const aiAssistantName = appConfig.aiAssistantName ?? 'AI';
  const vendorLogoUrl = workspace?.documentLogoAssetUuid
    ? `/api/workspace-assets/${workspace.documentLogoAssetUuid}/content`
    : null;
  const vendorLogoSrc = useProtectedImageUrl(vendorLogoUrl);
  const {
    rightPaneMode,
    rightPaneVisible,
    setRightPaneMode,
    setRightPaneVisible,
    setRightPaneWidth,
  } = useLayout();

  // No context pane on the deal workspace. The hook also guarantees the right
  // rail/pane state driven dynamically below is reset on unmount, so leaving a
  // deal for any other page never strands the pane (previously only reset on the
  // /so/deal/new create route).
  useLayoutConfig({ showContextPane: false });

  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const { items: rightRailCatalog } = useSalesRightRail();
  const isCreateRoute = location.pathname.startsWith('/so/deal');
  const isCreateStartRoute = location.pathname === '/so/deal/new';
  // Edit mode = the `/edit` child route or the create flow; the index route is
  // read-only (DealMain reads the path to switch). Estimate edit controls (role picker,
  // pricing basis, allocation %, discount, regenerate) only render in edit mode.
  const isEditMode = location.pathname.endsWith('/edit') || isCreateStartRoute;

  const [companyName, setCompanyName] = React.useState('');
  const [companySuggestions, setCompanySuggestions] = React.useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = React.useState(false);
  const [createSubmissionPhase, setCreateSubmissionPhase] =
    React.useState<CreateSubmissionPhase>('idle');
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [, setCreateStatus] = React.useState<string | null>(null);
  const [, setExistingDraft] = React.useState<SalesDealSummary | null>(null);
  const [companyDeals, setCompanyDeals] = React.useState<SalesDealSummary[] | null>(null);
  const [existingDealPrompt, setExistingDealPrompt] =
    React.useState<ExistingDealChoicePrompt | null>(null);
  const [salesModuleRole, setSalesModuleRole] = React.useState<string | null>(null);
  const [selectedExistingDeal, setSelectedExistingDeal] = React.useState<SalesDealSummary | null>(
    null
  );
  const [showCreateNew, setShowCreateNew] = React.useState(false);

  // Workspace products - loaded once on create route mount for product selector
  const [workspaceProducts, setWorkspaceProducts] = React.useState<WorkspaceProductSummary[]>([]);
  // Single flag covers both product + category load so they appear together on first render
  const [isInitializingWizard, setIsInitializingWizard] = React.useState(isCreateStartRoute);
  const [selectedWorkspaceProductId, setSelectedWorkspaceProductId] = React.useState<number | null>(
    null
  );
  const [categoryOptions, setCategoryOptions] = React.useState<DealCategoryOption[]>([]);
  const [loadingCategoryOptions, setLoadingCategoryOptions] = React.useState(false);
  // Tracks which product ID was loaded on mount so product-change effect skips the initial auto-select
  const lastFetchedProductIdRef = React.useRef<number | null>(null);
  const [selectedCategoryKey, setSelectedCategoryKey] = React.useState<string | null>(null);
  // Chip under the pointer/focus: its description is shown instead of the selected one.
  const [hoveredCategoryKey, setHoveredCategoryKey] = React.useState<string | null>(null);
  const [attributeOptionsByKey, setAttributeOptionsByKey] = React.useState<
    Record<string, { valueKey: string; label: string }[]>
  >({});

  // Column-pref-driven deal preview state
  const [dealPreviewColumnKeys, setDealPreviewColumnKeys] = React.useState<string[]>(
    DEFAULT_DEAL_PREVIEW_COLUMN_KEYS
  );
  const [dealPreviewAttributeDefs, setDealPreviewAttributeDefs] = React.useState<
    DealPreviewAttributeDef[]
  >([]);
  const [dealPreviewTemplateDefs, setDealPreviewTemplateDefs] = React.useState<
    DealPreviewTemplateDef[]
  >([]);

  // attributesByKey for the preview: attributeKey → (valueKey → display label)
  const dealPreviewAttributesByKey = React.useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    for (const def of dealPreviewAttributeDefs) {
      map.set(def.attributeKey, new Map(def.options.map((o) => [o.valueKey, o.label])));
    }
    return map;
  }, [dealPreviewAttributeDefs]);

  // Mount fetch: products (with category/attribute piped immediately after) + metadata in parallel.
  // The server requires a workspaceProductId for category/attribute options, so we chain those
  // directly off the products promise - no extra React render cycle in between.
  React.useEffect(() => {
    if (!isCreateStartRoute) return;
    let cancelled = false;
    setIsInitializingWizard(true);

    // Metadata requests fire immediately in parallel (no product ID needed)
    void Promise.all([
      apiFetch<{ data: { visibleColumns?: string[] } }>(
        `/api/workspace/ui-preference/${DEAL_PREVIEW_UI_PREF_SURFACE}`
      ).catch(() => ({ data: {} as { visibleColumns?: string[] } })),
      apiFetch<{ data: DealPreviewAttributeDef[] }>('/api/sales/deals/attribute-defs').catch(
        () => ({ data: [] })
      ),
      apiFetch<{ data: DealPreviewTemplateDef[] }>('/api/sales/deals/template-field-defs').catch(
        () => ({ data: [] })
      ),
    ]).then(([{ data: prefsData }, { data: attrDefs }, { data: tmplDefs }]) => {
      if (cancelled) return;
      const cols = prefsData?.visibleColumns;
      if (Array.isArray(cols) && cols.length > 0 && cols.every((k) => typeof k === 'string')) {
        setDealPreviewColumnKeys(cols as string[]);
      }
      if (Array.isArray(attrDefs)) setDealPreviewAttributeDefs(attrDefs);
      if (Array.isArray(tmplDefs)) setDealPreviewTemplateDefs(tmplDefs);
    });

    // Products, then immediately pipe category + attribute options with the real product ID
    void salesApi
      .getWorkspaceProducts()
      .catch(() => ({ products: [] as WorkspaceProductSummary[] }))
      .then(async ({ products }) => {
        if (cancelled) return;
        setWorkspaceProducts(products);
        const primary = products.find((p) => p.isPrimary) ?? products[0] ?? null;
        const primaryId = primary?.id ?? null;
        lastFetchedProductIdRef.current = primaryId;
        setSelectedWorkspaceProductId(primaryId);
        // Fetch category + attribute options immediately using the real product ID
        const [{ options }, { byKey }] = await Promise.all([
          salesApi.getCategoryOptions(primaryId).catch(() => ({ options: [] })),
          salesApi.getAttributeOptions(primaryId).catch(() => ({ byKey: {} })),
        ]);
        if (cancelled) return;
        setCategoryOptions(options);
        setSelectedCategoryKey(options[0]?.valueKey ?? null);
        setAttributeOptionsByKey(byKey);
        setIsInitializingWizard(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isCreateStartRoute]);

  // When the user explicitly selects a different product, re-fetch category + attribute options.
  // Skips the initial auto-select (primary product was already fetched in the mount effect above).
  React.useEffect(() => {
    if (!isCreateStartRoute) return;
    if (selectedWorkspaceProductId == null) return;
    // Skip re-fetch when the product hasn't actually changed since the last fetch
    if (selectedWorkspaceProductId === lastFetchedProductIdRef.current) return;
    lastFetchedProductIdRef.current = selectedWorkspaceProductId;
    let cancelled = false;
    setSelectedCategoryKey(null);
    setCategoryOptions([]);
    setAttributeOptionsByKey({});
    setLoadingCategoryOptions(true);
    const params = new URLSearchParams({
      workspaceProductId: String(selectedWorkspaceProductId),
    });
    void Promise.all([
      apiFetch<{ options: DealCategoryOption[] }>(
        `/api/sales/deals/category-options?${params.toString()}`
      ).catch(() => ({ options: [] })),
      apiFetch<{ byKey: Record<string, { valueKey: string; label: string }[]> }>(
        `/api/sales/deals/attribute-options?${params.toString()}`
      ).catch(() => ({ byKey: {} })),
    ]).then(([{ options }, { byKey }]) => {
      if (cancelled) return;
      setCategoryOptions(options);
      setSelectedCategoryKey(options[0]?.valueKey ?? null);
      setAttributeOptionsByKey(byKey);
      setLoadingCategoryOptions(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isCreateStartRoute, selectedWorkspaceProductId]);

  // Deal documents state. These artifacts render in the MAIN canvas (via the
  // subnav tabs), not the right rail - so the AI copilot (right pane) and the
  // open document are visible at the same time. 'documents' is the index view
  // (AI docs + uploaded transcripts); 'upload' is a single uploaded transcript.
  const [viewingDoc, setViewingDoc] = React.useState<ViewingDocState | null>(() => {
    return (location.state as any)?.viewingDoc || null;
  });
  const [secondaryViewingDoc, setSecondaryViewingDoc] = React.useState<ViewingDocState | null>(
    null
  );
  // Cross-pane focus for the Questionnaire <-> Prioritization split view (the fieldKey both panes
  // scroll to + highlight). Lives on the shared provider because the two panes are sibling subtrees.
  const [focusedAdhocFieldKey, setFocusedAdhocFieldKey] = React.useState<string | null>(null);
  const [isExportingResourcePlan, setIsExportingResourcePlan] = React.useState(false);
  const [isExportingSow, setIsExportingSow] = React.useState(false);
  const [isExportingEstimate, setIsExportingEstimate] = React.useState(false);
  const [isExportingPrioritization, setIsExportingPrioritization] = React.useState(false);
  // Export to Jira (Phase A: Step 1) - the ticket plan preview drawer + xlsx download.
  const [isLoadingJiraPlan, setIsLoadingJiraPlan] = React.useState(false);
  const [isExportingJira, setIsExportingJira] = React.useState(false);
  const [jiraDrawerOpen, setJiraDrawerOpen] = React.useState(false);
  const [jiraPlan, setJiraPlan] = React.useState<JiraTicketPlan | null>(null);
  // Sync to Jira (Phase C) - target picker + push results.
  const [jiraConnections, setJiraConnections] = React.useState<
    Array<{ uuid: string; displayName: string }>
  >([]);
  const [jiraCredentialUuid, setJiraCredentialUuid] = React.useState('');
  const [jiraProjects, setJiraProjects] = React.useState<Array<{ id: string; name: string }>>([]);
  const [jiraProjectKey, setJiraProjectKey] = React.useState('');
  const [jiraLoadingProjects, setJiraLoadingProjects] = React.useState(false);
  const [isSyncingJira, setIsSyncingJira] = React.useState(false);
  const [jiraSyncSummary, setJiraSyncSummary] = React.useState<JiraSyncSummary | null>(null);
  const [estimateConfig, setEstimateConfig] = React.useState<{
    hasDriveIntegration: boolean;
    hasResourcePlanDriveIntegration: boolean;
    hasSowDriveIntegration: boolean;
  } | null>(null);

  // Fetch estimate configuration (Drive integration status)
  React.useEffect(() => {
    if (!dealUuid || isCreateStartRoute) return;
    let cancelled = false;
    void salesApi.getEstimateConfig().then((config) => {
      if (cancelled) return;
      setEstimateConfig(config);
    });
    return () => {
      cancelled = true;
    };
  }, [dealUuid, isCreateStartRoute]);

  // Clear cross-pane focus when the Prioritization pane is not open in either canvas, so a stale
  // highlight doesn't reappear when the user re-enters the Questionnaire/Prioritization split view.
  React.useEffect(() => {
    const prioOpen =
      viewingDoc?.kind === 'prioritization' || secondaryViewingDoc?.kind === 'prioritization';
    if (!prioOpen) setFocusedAdhocFieldKey(null);
  }, [viewingDoc?.kind, secondaryViewingDoc?.kind]);

  // Upload meeting script state. Multi-file: the user may upload several call recordings
  // to create-and-analyze in one go. scriptFiles[0] creates the deal; the rest attach.
  const [showUploadView, setShowUploadView] = React.useState(false);
  const [scriptFiles, setScriptFiles] = React.useState<File[]>([]);
  // Transcripts picked from the connected workspace Google Drive (create flow). Kept parallel
  // to scriptFiles; both feed the same create + analyze pipeline (local via multipart, Drive via
  // the from-drive routes). The combined count is capped at TRANSCRIPT_MAX_FILES.
  const [scriptDriveFiles, setScriptDriveFiles] = React.useState<PickedFile[]>([]);
  // Per-file result lines after an upload attempt (error = could not be read, warning = very long).
  const [createFileNotes, setCreateFileNotes] = React.useState<Record<string, FileNote>>({});
  // Set when the deal exists but some file needs a look first; "Continue to deal" then navigates.
  const [createReview, setCreateReview] = React.useState<{
    path: string;
    state: Record<string, unknown>;
  } | null>(null);
  const [isDraggingScript, setIsDraggingScript] = React.useState(false);
  const [submittingScript, setSubmittingScript] = React.useState(false);
  const [scriptFileContent, setScriptFileContent] = React.useState<string | null>(null);
  // Ask/Agent/Auto mode for the create-from-script flow - same picker and semantics as
  // the edit-page transcript dialog (transcriptUploadMode); threaded through navigation
  // state to the auto-trigger so create-flow analysis honors review vs direct-apply.
  const [scriptAnalyzeMode, setScriptAnalyzeMode] = React.useState<ChatMode>('agent');
  // Optional "Additional notes" about the whole deal (create panel). Handed to the analysis run
  // through the navigation state (never as a transcript); the deal page saves it on the deal at the
  // moment that run is sent (the auto-trigger effect), so backing out before the run stores nothing.
  const [scriptAdditionalNotes, setScriptAdditionalNotes] = React.useState('');

  // Upload transcript to existing deal state (edit-page flow). Multi-file: several
  // meeting transcripts can be analyzed together into one set of suggested answers.
  const [showTranscriptUploadPanel, setShowTranscriptUploadPanel] = React.useState(false);
  const [transcriptUploadFiles, setTranscriptUploadFiles] = React.useState<File[]>([]);
  // Drive-picked transcripts for the existing-deal upload dialog (parallel to the local files).
  const [transcriptUploadDriveFiles, setTranscriptUploadDriveFiles] = React.useState<PickedFile[]>(
    []
  );
  // The dialog's own Ask/Agent/Auto mode - deliberately independent of the chat
  // composer's picker; 'agent' (default) saves directly, 'ask'/'auto' run the analysis in
  // review mode (see TRANSCRIPT_ANALYZE_MODE_OPTIONS).
  const [transcriptUploadMode, setTranscriptUploadMode] = React.useState<ChatMode>('agent');
  // Optional "Additional notes" about the whole deal (Upload documents dialog). Handed to the
  // analysis run and saved on the deal only when that run is actually sent (proceedTranscriptAnalysis),
  // so cancelling a duplicate confirm or review hold stores nothing. The trimmed text travels inside
  // those holds so it survives the steps that come after the dialog state is cleared.
  const [transcriptAdditionalNotes, setTranscriptAdditionalNotes] = React.useState('');
  // Upload finalized estimate (CI bridge, docs/plans/01): a single estimate file uploaded from
  // the Estimate tab becomes the deal's own estimate (server converts + saves), replacing any
  // previously imported one. Only offered on continuous_improvement deals.
  const [showEstimateUploadPanel, setShowEstimateUploadPanel] = React.useState(false);
  const [estimateUploadFile, setEstimateUploadFile] = React.useState<File | null>(null);
  const [uploadingEstimateFile, setUploadingEstimateFile] = React.useState(false);
  const [estimateUploadError, setEstimateUploadError] = React.useState<string | null>(null);
  // Upload scope list (docs/plans/27): a partner's scope file (problem / proposed solution per row,
  // NO hours) becomes this deal's scope items, then the normal estimate run prices them. A separate
  // door from "Upload finalized estimate" on purpose - that one takes the client's own hours.
  // Two steps in one dialog: parse (no writes) -> confirm-or-correct the mapping -> import.
  const [showScopeUploadPanel, setShowScopeUploadPanel] = React.useState(false);
  const [scopeUploadFile, setScopeUploadFile] = React.useState<File | null>(null);
  const [scopeParse, setScopeParse] = React.useState<ScopeFileParseResponse | null>(null);
  const [scopeMapping, setScopeMapping] = React.useState<Record<string, number>>({});
  const [scopeUploadBusy, setScopeUploadBusy] = React.useState(false);
  const [scopeUploadError, setScopeUploadError] = React.useState<string | null>(null);
  // Create-deal flow (docs/plans/27): a scope spreadsheet picked on the "Upload documents" card,
  // kept apart from scriptFiles because it takes a different path - parse, create, import, estimate,
  // instead of transcript analysis. Category must be CI or BPR (the estimator engines).
  const [createScopeFile, setCreateScopeFile] = React.useState<File | null>(null);
  const [createScopeBusy, setCreateScopeBusy] = React.useState(false);
  // Enrich-company confirm dialog (opened from the edit header's ⋮ menu).
  const [showEnrichConfirmPanel, setShowEnrichConfirmPanel] = React.useState(false);
  const [enrichMode, setEnrichMode] = React.useState<ChatMode>('ask');
  // Stage-gate the Agent/Auto mode options (alpha = local/dev only) for the transcript
  // and enrich pickers against the workspace min stage. See isStageAvailable.
  const transcriptModeOptions = React.useMemo(
    () =>
      TRANSCRIPT_ANALYZE_MODE_OPTIONS.filter((o) =>
        isStageAvailable(o.stage, workspace?.featureMinStage)
      ),
    [workspace?.featureMinStage]
  );
  const enrichModeOptions = React.useMemo(
    () => CHAT_MODE_OPTIONS.filter((o) => isStageAvailable(o.stage, workspace?.featureMinStage)),
    [workspace?.featureMinStage]
  );
  const [isDraggingTranscript, setIsDraggingTranscript] = React.useState(false);
  const [uploadingTranscript, setUploadingTranscript] = React.useState(false);
  const [transcriptUploadError, setTranscriptUploadError] = React.useState<string | null>(null);
  // Confirm-or-override state: when an uploaded file is already attached (dedup hit), we hold the
  // upload results and ask the user to re-analyze the existing copy or add a fresh copy. Each result
  // carries its source file so "add a fresh copy" can re-upload with force.
  const [transcriptDupConfirm, setTranscriptDupConfirm] = React.useState<{
    results: Array<{
      docUuid: string;
      reused: boolean;
      title: string;
      source: { kind: 'local'; file: File } | { kind: 'drive'; file: PickedFile };
    }>;
    docLabel: string;
    additionalNotes?: string;
  } | null>(null);
  const [transcriptDupBusy, setTranscriptDupBusy] = React.useState(false);
  // Per-file result lines for the existing-deal upload dialog, and the held upload while the user
  // reads them (some file could not be read, or is very long). "Continue" then runs the analysis.
  const [transcriptFileNotes, setTranscriptFileNotes] = React.useState<Record<string, FileNote>>(
    {}
  );
  const [transcriptReview, setTranscriptReview] = React.useState<{
    results: NonNullable<typeof transcriptDupConfirm>['results'];
    docLabel: string;
    additionalNotes?: string;
  } | null>(null);
  // True while the Google Drive picker is on screen. The picker mounts on document.body, so the
  // transcript upload dialog must NOT auto-dismiss on the resulting outside interaction (that closed
  // the dialog and dropped the user's picked file before they could see it).
  const [transcriptDrivePickerOpen, setTranscriptDrivePickerOpen] = React.useState(false);
  // Connected workspace Drive credential for the transcript-import buttons. Resolved once on
  // workspace load (shared via WorkspaceContext), so it's already known when an upload panel opens.
  const driveIntegration = useGoogleDriveIntegration();

  React.useEffect(() => {
    // Preview the first uploaded file only (the others still upload + analyze).
    const scriptFile = scriptFiles[0];
    if (!scriptFile) {
      setScriptFileContent(null);
      return;
    }
    // The inline <pre> preview only makes sense for text; reading a .docx/.pdf as
    // text would render binary garbage, so skip the preview for those formats.
    const isText =
      scriptFile.type === 'text/plain' || scriptFile.name.toLowerCase().endsWith('.txt');
    if (!isText) {
      setScriptFileContent(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setScriptFileContent(typeof e.target?.result === 'string' ? e.target.result : null);
    };
    reader.readAsText(scriptFile);
  }, [scriptFiles]);

  // useReducer (not useState) only so a partial replacement keeps `diagramsEnabled`; see
  // keepDiagramsEnabled. Its dispatch is as stable as a state setter.
  const [deal, setDeal] = React.useReducer(keepDiagramsEnabled, null);
  const [loadingDeal, setLoadingDeal] = React.useState(false);
  const [dealError, setDealError] = React.useState<string | null>(null);
  const [enrichmentTriggered, setEnrichmentTriggered] = React.useState(false);
  const [enrichmentPollKey, setEnrichmentPollKey] = React.useState(0);

  // BPR deals: the knowledge graph's per-area priority rollup (decreasing
  // order) drives the Prioritization tab's group ordering + tier badges.
  // Null = not a BPR deal, or no graph built yet (alphabetical fallback).
  const [bprAreaPriorities, setBprAreaPriorities] = React.useState<BprGraphAreaPriority[] | null>(
    null
  );
  const dealCategoryKey = deal?.dealCategoryKey;
  // Diagram-layer deals: 'bpr' plus any category whose option turns `diagrams` on (e.g. CI,
  // relabelled "Existing NetSuite"). The Prioritization tiers above stay BPR-only.
  const dealDiagramsOn = dealHasDiagramLayer(deal);
  React.useEffect(() => {
    if (!dealUuid || dealCategoryKey !== 'bpr') {
      setBprAreaPriorities(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const engagement = await bprApi.getEngagementByDeal(dealUuid);
        if (!engagement || cancelled) return;
        const result = await bprApi.getGraphAreaPriorities(engagement.uuid);
        if (!cancelled) setBprAreaPriorities(result?.areas ?? null);
      } catch {
        if (!cancelled) setBprAreaPriorities(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealUuid, dealCategoryKey]);

  // BPR deals: the shared diagram state. The deal page generates the visual
  // layer AUTOMATICALLY once discovery has captured the questionnaire (deal
  // status flips to 'ready_for_generation') and nothing is generated yet - the
  // workflow lives in the deal page, so the diagrams should appear without
  // hunting for a button (the effect sits below the capture state it waits
  // on). One attempt per deal per mount; a failure surfaces on the System
  // Architecture block, whose manual Generate stays as the retry.
  //
  // The store's first load probes GET /bpr/engagements/by-deal, and only
  // diagram-layer deals ever have an engagement (that GET also creates one
  // lazily for a diagram-layer deal that predates it) - every other category
  // answers 404 (pure console noise). Pass the uuid only for diagram-layer
  // deals; the hook no-ops on ''. Both consumer effects below are gated the
  // same way and tolerate the resulting 'idle' state, and the BPR components
  // (BprDealArchitecture / BprSectionDiagram) call useDealDiagrams themselves,
  // so they load independently of this probe.
  const dealDiagrams = useDealDiagrams(dealDiagramsOn ? (dealUuid ?? '') : '');
  const [questionnaire, setQuestionnaire] = React.useState<SalesDealQuestionnaireSummary | null>(
    null
  );
  const [loadingQuestionnaire, setLoadingQuestionnaire] = React.useState(false);
  const [questionnaireError, setQuestionnaireError] = React.useState<string | null>(null);
  // Unsaved ad-hoc scope edits (status / work type / brief), keyed by fieldKey. A ref mirrors it so
  // handlePersistAnswers can flush on Save without re-creating the callback on every keystroke.
  const [pendingScopeMeta, setPendingScopeMeta] = React.useState<Record<string, ScopeMeta>>({});
  const pendingScopeMetaRef = React.useRef(pendingScopeMeta);
  pendingScopeMetaRef.current = pendingScopeMeta;
  // questionnaireRef gives the callback the latest questionnaire without re-creating it; the baseline
  // ref holds each field's ORIGINAL (last-saved) scope meta, captured the first time it is edited, so
  // a revert back to it clears the pending flag (the baseline is reset after Save, below).
  const questionnaireRef = React.useRef(questionnaire);
  questionnaireRef.current = questionnaire;
  const scopeMetaBaselineRef = React.useRef<Record<string, ScopeMeta | undefined>>({});
  // Reconcile pending ad-hoc scope edits against the current questionnaire whenever it reloads
  // (refetch / discovery apply / post-save response). An ad-hoc field can be removed or replaced
  // server-side (e.g. a discovery re-run), which would orphan a pending edit keyed by its fieldKey;
  // a flush of that orphan PATCHes a now-missing field -> 404 -> the whole Save aborts and the
  // orphan never clears, poisoning every subsequent Save until a full page reload. Pruning here -
  // the single point all reload paths funnel through - drops any pending key (and its baseline) no
  // longer present as an ad-hoc field. A field being actively edited stays present (its optimistic
  // update keeps it in the questionnaire), so live edits are never dropped.
  React.useEffect(() => {
    if (!questionnaire) return;
    const adhocFieldKeys = new Set<string>();
    for (const section of questionnaire.sections) {
      if (!isAdhocSection(section.sectionKey)) continue;
      for (const group of section.groups)
        for (const field of group.fields) adhocFieldKeys.add(field.fieldKey);
    }
    for (const key of Object.keys(scopeMetaBaselineRef.current)) {
      if (!adhocFieldKeys.has(key)) delete scopeMetaBaselineRef.current[key];
    }
    setPendingScopeMeta((prev) => {
      const orphans = Object.keys(prev).filter((key) => !adhocFieldKeys.has(key));
      if (orphans.length === 0) return prev;
      const next = { ...prev };
      for (const key of orphans) delete next[key];
      return next;
    });
  }, [questionnaire]);
  // Record an ad-hoc scope edit: reflect the full meta in the loaded questionnaire optimistically
  // (strikethrough + brief update immediately) and arm the Save button - but only while the edit
  // DIFFERS from the original; reverting to the original clears it. Persisted on Save (flushed below).
  const handleAdhocScopeChange = React.useCallback((fieldKey: string, meta: ScopeMeta) => {
    // Current in-memory meta for this field - carries every key the staged brief edit does NOT own.
    let current: ScopeMeta | undefined;
    for (const section of questionnaireRef.current?.sections ?? [])
      for (const group of section.groups)
        for (const field of group.fields)
          if (field.fieldKey === fieldKey) current = field.scopeMeta;
    // The staged edit owns ONLY the brief namespace (workType + the SCOPE_BRIEF text/tag keys), but
    // the Save flush is a FULL replace - so carry over every OTHER scope_meta key from `current` or
    // the replace would wipe it: the businessProcess grouping (the question would ungroup / vanish
    // from its functional area), the immediately-merged scopeStatus + priorities, the AI statusReason,
    // merged subProblems, and the forward-compat extra bag. Strip the brief keys from `current` first
    // so a CLEARED brief field (absent from `meta`) is dropped, not resurrected from the old value.
    const preserved: ScopeMeta = { ...current };
    delete preserved.workType;
    for (const d of SCOPE_BRIEF) delete preserved[d.key];
    const effective: ScopeMeta = { ...preserved, ...meta };
    // Capture the field's original meta on first edit, BEFORE the optimistic overwrite below.
    if (!(fieldKey in scopeMetaBaselineRef.current)) {
      scopeMetaBaselineRef.current[fieldKey] = current;
    }
    setQuestionnaire((prev) =>
      prev
        ? {
            ...prev,
            sections: prev.sections.map((section) => ({
              ...section,
              groups: section.groups.map((group) => ({
                ...group,
                fields: group.fields.map((field) =>
                  field.fieldKey === fieldKey ? { ...field, scopeMeta: effective } : field
                ),
              })),
            })),
          }
        : prev
    );
    const baselineSig = scopeMetaSignature(scopeMetaBaselineRef.current[fieldKey]);
    setPendingScopeMeta((prev) => {
      const next = { ...prev };
      if (scopeMetaSignature(effective) === baselineSig) {
        delete next[fieldKey]; // reverted to the original - no longer a pending change
      } else {
        next[fieldKey] = effective;
      }
      return next;
    });
  }, []);

  // Immediately persist + live-sync the always-saved scope fields (scopeStatus + priorities). Unlike
  // handleAdhocScopeChange (staged until Save), this shallow-merges the patch into scope_meta right
  // away and updates the in-memory questionnaire so the card AND the Prioritization pane reflect it.
  const handleCommitAdhocScopeFields = React.useCallback(
    (
      fieldKey: string,
      patch: Partial<
        Pick<
          ScopeMeta,
          'scopeStatus' | 'customerPriority' | 'recommendedPriority' | 'businessProcess'
        >
      >
    ) => {
      setQuestionnaire((prev) => {
        if (!prev) return prev;
        // A businessProcess change is a functional-area MOVE: restructure the ad-hoc sections
        // (mirrors the server split) so the questionnaire sidebar reflects it immediately - the same
        // shared state the Prioritization pane re-clusters from. Other scope keys (status / priority)
        // patch the field's scopeMeta in place.
        const { businessProcess, ...rest } = patch;
        let next =
          businessProcess !== undefined
            ? moveAdhocFieldToArea(prev, fieldKey, businessProcess)
            : prev;
        if (Object.keys(rest).length > 0) {
          next = {
            ...next,
            sections: next.sections.map((section) => ({
              ...section,
              groups: section.groups.map((group) => ({
                ...group,
                fields: group.fields.map((field) =>
                  field.fieldKey === fieldKey
                    ? { ...field, scopeMeta: { ...field.scopeMeta, ...rest } }
                    : field
                ),
              })),
            })),
          };
        }
        return next;
      });
      // Keep the staged baseline + pending snapshot's immediate keys fresh, so a later Save replace
      // neither clobbers them (pending must carry them) nor falsely re-arms after a brief revert.
      if (fieldKey in scopeMetaBaselineRef.current) {
        scopeMetaBaselineRef.current[fieldKey] = {
          ...scopeMetaBaselineRef.current[fieldKey],
          ...patch,
        };
      }
      setPendingScopeMeta((prev) =>
        fieldKey in prev ? { ...prev, [fieldKey]: { ...prev[fieldKey], ...patch } } : prev
      );
      if (dealUuid) void salesApi.mergeAdhocScopeMeta(dealUuid, fieldKey, patch);
    },
    [dealUuid]
  );
  // Bump these to re-run the deal / questionnaire load effects (the "Retry" action
  // on the load-failure error states). Both effects reset loading + error on entry.
  const [dealReloadKey, setDealReloadKey] = React.useState(0);
  const [questionnaireReloadKey, setQuestionnaireReloadKey] = React.useState(0);
  const retryLoadDeal = React.useCallback(() => setDealReloadKey((k) => k + 1), []);
  const retryLoadQuestionnaire = React.useCallback(
    () => setQuestionnaireReloadKey((k) => k + 1),
    []
  );

  // ── Deal attributes (type / category / status dropdowns above the split view) ──
  type DealAttributeOption = {
    id: number;
    valueKey: string;
    label: string;
    color: string | null;
    displayOrder: number;
  };
  type DealAttributeEntry = {
    id: number;
    attributeKey: string;
    label: string;
    displayOrder: number;
    isSystem: boolean;
    currentValue: string | null;
    options: DealAttributeOption[];
  };
  const [dealAttributes, setDealAttributes] = React.useState<DealAttributeEntry[]>([]);
  const [loadingDealAttributes, setLoadingDealAttributes] = React.useState(false);
  const [answers, setAnswers] = React.useState<QuestionnaireAnswerMap>({});
  const [persistedAnswers, setPersistedAnswers] = React.useState<QuestionnaireAnswerMap>({});
  const answersRef = React.useRef<QuestionnaireAnswerMap>({});
  const completedEnrichmentQuestionnaireRefreshRef = React.useRef<string | null>(null);
  const [touchedFields, setTouchedFields] = React.useState<Set<string>>(new Set());
  // Fresh mirror for callbacks that must not depend on the state (the live
  // contract-recommendation recompute reads which targets the user typed into
  // without re-creating handleAnswerChange on every keystroke).
  const touchedFieldsRef = React.useRef(touchedFields);
  touchedFieldsRef.current = touchedFields;
  const [pendingSaveCount, setPendingSaveCount] = React.useState(0);
  const savingAnswers = pendingSaveCount > 0;
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // Number of AI-captured questions being applied + reloaded (0 = idle); drives the ad-hoc-section
  // skeleton so "Questions captured" shows immediate writing/loading feedback.
  const [capturingAdhocCount, setCapturingAdhocCount] = React.useState(0);

  // BPR deals: generate the visual layer AUTOMATICALLY (see useDealDiagrams
  // above). The knowledge graph builds as soon as the deal is ready, so it
  // overlaps discovery; the diagram batch waits until the deal's discovery run
  // and its capture have SETTLED (any outcome, not only success), so one full
  // run carries every captured section in a single parallel fan-out. Firing
  // the batch earlier minted it before discovery's sections existed and forced
  // a second, serial top-up run for them (COS-129: ~128 s on UAT). Any active
  // chat run on the deal's session defers the batch.
  const bprAutoGenDealRef = React.useRef<string | null>(null);
  const bprGraphPrebuildDealRef = React.useRef<string | null>(null);
  const chatRunActive = Boolean(chatSession.state.activeRunId);
  React.useEffect(() => {
    if (!dealUuid || !dealDiagramsOn) return;
    if (salesModuleRole === 'viewer' || salesModuleRole === null) return;
    if (deal?.status !== 'ready_for_generation' && deal?.status !== 'generated') return;
    if (dealDiagrams.status !== 'ready' || !dealDiagrams.engagementUuid) return;
    if (dealDiagrams.diagrams.length > 0 || dealDiagrams.generating) return;
    if (bprGraphPrebuildDealRef.current !== dealUuid) {
      bprGraphPrebuildDealRef.current = dealUuid;
      prebuildDealGraph(dealUuid);
    }
    if (chatRunActive || capturingAdhocCount > 0) return;
    if (bprAutoGenDealRef.current === dealUuid) return;
    bprAutoGenDealRef.current = dealUuid;
    void generateDealDiagrams(dealUuid);
  }, [
    dealUuid,
    dealDiagramsOn,
    deal?.status,
    salesModuleRole,
    chatRunActive,
    capturingAdhocCount,
    dealDiagrams,
  ]);

  // BPR deals: TOP UP missing section process flows - when the diagram batch
  // exists but a business-process section has no workflow in it (its
  // questions were captured after the batch was minted, or the section was
  // added later), generate just those sections' pairs so every section
  // carries its flow alongside the architecture without a manual Regenerate.
  // Waits for an in-flight capture to settle so one call batches all new
  // sections; each key is attempted once per deal per mount (a failed top-up
  // retries on the next visit instead of looping).
  const bprEnsureAttemptedKeysRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!dealUuid || !dealDiagramsOn) return;
    if (salesModuleRole === 'viewer' || salesModuleRole === null) return;
    if (capturingAdhocCount > 0) return;
    if (dealDiagrams.status !== 'ready' || !dealDiagrams.engagementUuid) return;
    if (dealDiagrams.generating || dealDiagrams.diagrams.length === 0) return;
    if (!questionnaire) return;
    // Mirrors the server's section taxonomy (listDealSections): every
    // questionnaire section except the company profile and an EMPTY ad-hoc
    // catch-all should carry a process flow. The server re-validates, so a
    // drifted key is skipped there, never generated blindly. A section that
    // just FAILED validation is skipped too (its error is already shown;
    // Regenerate or the next visit retries it).
    const sectionKeys = questionnaire.sections
      .filter(
        (s) =>
          s.sectionKey !== 'company-overview' &&
          !(s.sectionKey === ADHOC_SECTION_KEY && s.groups.every((g) => g.fields.length === 0))
      )
      .map((s) => s.sectionKey);
    const missing = sectionsMissingFlow(
      sectionKeys,
      dealDiagrams.diagrams,
      dealDiagrams.failedSectionKeys
    ).filter((key) => !bprEnsureAttemptedKeysRef.current.has(`${dealUuid}:${key}`));
    if (missing.length === 0) return;
    for (const key of missing) bprEnsureAttemptedKeysRef.current.add(`${dealUuid}:${key}`);
    ensureSectionDiagrams(dealUuid, missing);
  }, [dealUuid, dealDiagramsOn, salesModuleRole, capturingAdhocCount, dealDiagrams, questionnaire]);
  // ── Scope tab (doc editor) state ──────────────────────────────────────────
  // The edit-mode Scope doc draft (Tiptap JSON; null = no unsaved edits), the save-in-flight
  // flag + error, and the view-mode answered-only filter (parity with the questionnaire header).
  const [scopeDraftDoc, setScopeDraftDoc] = React.useState<Record<string, unknown> | null>(null);
  const [scopeSavingDoc, setScopeSavingDoc] = React.useState(false);
  const [scopeSaveError, setScopeSaveError] = React.useState<string | null>(null);
  // Transient "Saved" hint after a successful autosave (Notion-style; no Save button). Shared by the
  // scope-doc harvest save AND the property-control live save so both flash the same indicator.
  const [scopeJustSaved, setScopeJustSaved] = React.useState(false);
  const scopeSavedHintTimerRef = React.useRef<number | null>(null);
  const flashScopeSaved = React.useCallback(() => {
    setScopeJustSaved(true);
    if (scopeSavedHintTimerRef.current !== null) {
      window.clearTimeout(scopeSavedHintTimerRef.current);
    }
    scopeSavedHintTimerRef.current = window.setTimeout(() => {
      scopeSavedHintTimerRef.current = null;
      setScopeJustSaved(false);
    }, 2000);
  }, []);
  const [scopeAnsweredOnly, setScopeAnsweredOnly] = React.useState(true);
  // Scope doc collapse-all: the live editor handle (from DealScopePane) + the header button's
  // toggle state. A fresh editor instance (view<->edit switch, deal change) resets the plugin's
  // collapse state to all-expanded, so the label resets with it. Accepted caveat: the label can
  // desync after per-heading chevron use (it is an action pair, not a state mirror).
  const scopeEditorRef = React.useRef<Editor | null>(null);
  const [scopeAllCollapsed, setScopeAllCollapsed] = React.useState(false);
  const handleScopeEditorReady = React.useCallback((ed: Editor | null) => {
    scopeEditorRef.current = ed;
    setScopeAllCollapsed(false);
  }, []);
  // Scope-doc removal (a heading's hover x): resolve the target from the loaded questionnaire,
  // confirm in a dialog, then delete through the model API (never an in-doc edit) and reload.
  const [scopeRemoveTarget, setScopeRemoveTarget] = React.useState<
    | { kind: 'item'; label: string; fieldKeys: [string] }
    | { kind: 'area'; label: string; fieldKeys: string[]; questionCount: number }
    | null
  >(null);
  const [scopeRemoveBusy, setScopeRemoveBusy] = React.useState(false);
  const handleScopeRemoveRequest = React.useCallback(
    (dataKey: string) => {
      const anchor = parseScopeDocKey(dataKey);
      if (!anchor || !questionnaire) return;
      if (anchor.type === 'adhoc') {
        for (const section of questionnaire.sections) {
          for (const group of section.groups) {
            const field = group.fields.find((f) => f.fieldKey === anchor.key);
            if (field) {
              setScopeRemoveTarget({
                kind: 'item',
                label: field.label || anchor.key,
                fieldKeys: [anchor.key],
              });
              return;
            }
          }
        }
        return;
      }
      if (anchor.type === 'section') {
        const section = questionnaire.sections.find((s) => s.sectionKey === anchor.key);
        if (!section) return;
        // Delete EVERY field in the area (primaries + their supplementary group members).
        const fieldKeys = section.groups.flatMap((g) => g.fields.map((f) => f.fieldKey));
        const questionCount = section.groups
          .flatMap((g) => g.fields)
          .filter((f) => f.isAdhocPrimary).length;
        setScopeRemoveTarget({ kind: 'area', label: section.label, fieldKeys, questionCount });
      }
    },
    [questionnaire]
  );
  // The model AS SEEN when the Scope edit session began (set on first keystroke, cleared with
  // the draft) - the harvest baseline, so concurrent changes to untouched fields never revert.
  const scopeDraftBaselineRef = React.useRef<{
    questionnaire: SalesDealQuestionnaireSummary;
    answers: QuestionnaireAnswerMap;
  } | null>(null);
  const {
    estimateResult,
    setEstimateResult,
    loadingEstimate,
    setLoadingEstimate,
    estimateError,
    setEstimateError,
    savedEstimate,
    setSavedEstimate,
    savingEstimate,
    setSavingEstimate,
    resourcePlan,
    setResourcePlan,
    loadingResourcePlan,
    setLoadingResourcePlan,
    generatingResourcePlan,
    setGeneratingResourcePlan,
    resourcePlanError,
    setResourcePlanError,
    sow,
    setSow,
    loadingSow,
    setLoadingSow,
    generatingSow,
    setGeneratingSow,
    savingSow,
    setSavingSow,
    sowError,
    setSowError,
    sowReadinessReport,
    setSowReadinessReport,
    setSowEditing,
    sowDraftContent,
    setSowDraftContent,
    reset: resetGenerationState,
  } = useDealGeneration();

  // ── Read-only version history (estimate + SOW) ────────────────────────────
  // Versions accumulate per deal; the dropdown lets the user view/export a past
  // one without leaving the current (working) version. Selecting a past version
  // loads it read-only; selecting Current clears the override.
  const [estimateVersions, setEstimateVersions] = React.useState<EstimateVersionSummary[]>([]);
  const [sowVersions, setSowVersions] = React.useState<SowVersionSummary[]>([]);
  // Bumped when the SOW is regenerated so the SOW pane REMOUNTS with a fresh edit render context
  // (otherwise the editor keeps the stale pre-regen assembly until a page refresh).
  const [sowRefreshKey, setSowRefreshKey] = React.useState(0);
  const [selectedEstimateVersionUuid, setSelectedEstimateVersionUuid] = React.useState<
    string | null
  >(null);
  const [selectedSowVersionUuid, setSelectedSowVersionUuid] = React.useState<string | null>(null);
  const [viewedEstimate, setViewedEstimate] = React.useState<SavedDealEstimate | null>(null);
  const [viewedSow, setViewedSow] = React.useState<DealSow | null>(null);

  // Refetch the estimate version list whenever the current estimate changes, and
  // reset the selection back to Current (the freshly-saved row is now latest).
  React.useEffect(() => {
    if (!dealUuid || !savedEstimate) {
      setEstimateVersions([]);
      return;
    }
    setSelectedEstimateVersionUuid(null);
    setViewedEstimate(null);
    let cancelled = false;
    void salesApi
      .listEstimateVersions(dealUuid)
      .then(({ versions }) => {
        if (!cancelled) setEstimateVersions(versions);
      })
      .catch(() => {
        if (!cancelled) setEstimateVersions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dealUuid, savedEstimate?.uuid, savedEstimate?.version]);

  React.useEffect(() => {
    if (!dealUuid || !sow) {
      setSowVersions([]);
      return;
    }
    setSelectedSowVersionUuid(null);
    setViewedSow(null);
    let cancelled = false;
    void salesApi
      .listSowVersions(dealUuid)
      .then(({ versions }) => {
        if (!cancelled) setSowVersions(versions);
      })
      .catch(() => {
        if (!cancelled) setSowVersions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dealUuid, sow?.uuid, sow?.version]);

  const handleSelectEstimateVersion = React.useCallback(
    (uuid: string) => {
      if (!dealUuid) return;
      if (uuid === savedEstimate?.uuid) {
        setSelectedEstimateVersionUuid(null);
        setViewedEstimate(null);
        return;
      }
      setSelectedEstimateVersionUuid(uuid);
      void salesApi
        .getEstimateVersion(dealUuid, uuid)
        .then(({ savedEstimate: v }) => setViewedEstimate(v))
        .catch(() => setViewedEstimate(null));
    },
    [dealUuid, savedEstimate?.uuid]
  );

  const handleSelectSowVersion = React.useCallback(
    (uuid: string) => {
      if (!dealUuid) return;
      if (uuid === sow?.uuid) {
        setSelectedSowVersionUuid(null);
        setViewedSow(null);
        return;
      }
      setSelectedSowVersionUuid(uuid);
      void salesApi
        .getSowVersion(dealUuid, uuid)
        .then(({ sow: v }) => setViewedSow(v))
        .catch(() => setViewedSow(null));
    },
    [dealUuid, sow?.uuid]
  );

  // A non-current version is being viewed -> render read-only + export that one.
  const viewingPastEstimate =
    selectedEstimateVersionUuid != null && selectedEstimateVersionUuid !== savedEstimate?.uuid;
  const viewingPastSow = selectedSowVersionUuid != null && selectedSowVersionUuid !== sow?.uuid;
  const effectiveSavedEstimate = viewingPastEstimate ? viewedEstimate : savedEstimate;
  const effectiveEstimateResult =
    viewingPastEstimate && viewedEstimate ? viewedEstimate.estimateData : estimateResult;
  const effectiveSow = viewingPastSow ? viewedSow : sow;

  const {
    documents: dealDocuments,
    isLoading: loadingDocuments,
    activityByDoc: dealDocActivity,
    userEvents: dealDocUserEvents,
    refetch: refetchDocuments,
  } = useDealDocuments(dealUuid ?? '');
  // Meeting context (Docs tab top card): auto-refreshed server-side after each doc apply; the hook
  // polls while a generation is in flight.
  const meetingContext = useMeetingContext(dealUuid ?? '');

  const estimatePaneRef = React.useRef<HTMLDivElement>(null);
  const resourcePlanPaneRef = React.useRef<HTMLDivElement>(null);
  const [updatingRolePricingBasisId, setUpdatingRolePricingBasisId] = React.useState<string | null>(
    null
  );
  const [updatingRoleAllocationId, setUpdatingRoleAllocationId] = React.useState<string | null>(
    null
  );
  // Workspace role catalog for the resource-plan role multi-select + a saving flag.
  const [availableRoles, setAvailableRoles] = React.useState<ResourcePlanAvailableRole[]>([]);
  const [updatingRoles, setUpdatingRoles] = React.useState(false);
  const sowPaneRef = React.useRef<HTMLDivElement>(null);
  const transcriptAutoTriggeredRef = React.useRef<string | null>(null);
  const transcriptDocUuidRef = React.useRef<string | null>(null);
  const [rateCardProfiles, setRateCardProfiles] = React.useState<
    Array<{ id: number; version: string; isDefault: boolean; scope: string }>
  >([]);

  // Token used to cancel in-flight handleReloadQuestionnaire calls when a save
  // provides fresher data. Each reload invocation sets a unique symbol; after
  // a successful save we clear the token so any concurrent stale reload is
  // discarded instead of overwriting the just-saved questionnaire state.
  const pendingReloadTokenRef = React.useRef<symbol | null>(null);

  // Refs so polling closures can always read the latest values without being
  // captured in stale closure state.
  const loadingEstimateRef = React.useRef(loadingEstimate);
  const loadingResourcePlanRef = React.useRef(loadingResourcePlan);
  const loadingSowRef = React.useRef(loadingSow);
  const activeRunIdRef = React.useRef(chatSession.state.activeRunId);
  React.useEffect(() => {
    loadingEstimateRef.current = loadingEstimate;
  }, [loadingEstimate]);
  React.useEffect(() => {
    loadingResourcePlanRef.current = loadingResourcePlan;
  }, [loadingResourcePlan]);
  React.useEffect(() => {
    loadingSowRef.current = loadingSow;
  }, [loadingSow]);
  React.useEffect(() => {
    activeRunIdRef.current = chatSession.state.activeRunId;
  }, [chatSession.state.activeRunId]);

  // ── Generation artifact polling fallback ──────────────────────────────────
  // Cloud Run sometimes closes the HTTP/WS connection before the skill pipeline
  // finishes. The backend still writes all artifacts to the DB, but the
  // completion events are lost. As a safety net we poll the DB (via the REST
  // endpoints) starting 30 s after generation begins. This covers all three
  // artifacts (estimate / resource plan / SOW) rather than only SOW, and fires
  // unconditionally - not only on WS reconnect.
  React.useEffect(() => {
    if (!savingEstimate || !dealUuid) return;

    let cancelled = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const pollOnce = async () => {
      if (cancelled) return;

      const stillLoadingEstimate = loadingEstimateRef.current;
      const stillLoadingPlan = loadingResourcePlanRef.current;
      const stillLoadingSow = loadingSowRef.current;

      // All already resolved by the WS - nothing left to do.
      if (!stillLoadingEstimate && !stillLoadingPlan && !stillLoadingSow) {
        if (pollInterval) clearInterval(pollInterval);
        return;
      }

      try {
        const [estimateRes, planRes, sowRes] = await Promise.all([
          stillLoadingEstimate ? salesApi.getSavedEstimate(dealUuid) : null,
          stillLoadingPlan ? salesApi.getResourcePlan(dealUuid) : null,
          stillLoadingSow ? salesApi.getSow(dealUuid) : null,
        ]);

        if (cancelled) return;

        const isNewEstimate =
          estimateRes?.savedEstimate &&
          estimateRes.savedEstimate.agentRunUuid === activeRunIdRef.current;
        const isNewPlan = planRes?.plan && planRes.plan.agentRunUuid === activeRunIdRef.current;
        const isNewSow =
          sowRes?.sow?.status === 'generated' && sowRes.sow.agentRunUuid === activeRunIdRef.current;

        if (stillLoadingEstimate && isNewEstimate && estimateRes?.savedEstimate) {
          setSavedEstimate(estimateRes.savedEstimate);
          setEstimateResult(estimateRes.savedEstimate.estimateData);
          setLoadingEstimate(false);
        }
        if (stillLoadingPlan && isNewPlan && planRes?.plan) {
          setResourcePlan(planRes.plan);
          setLoadingResourcePlan(false);
        }
        // Only treat the SOW as resolved when fully generated AND new - the DB row
        // is created with status='generating' before the LLM finishes, so a truthy
        // sow alone is not sufficient (would fire reconcileStalledRun while the
        // LLM call is still in progress).
        if (stillLoadingSow && isNewSow && sowRes?.sow) {
          setSow(sowRes.sow);
          setSowEditing(false);
          setSowDraftContent(null);
          setLoadingSow(false);
        }

        // If every expected artifact is now available, end the generation state.
        const allResolved =
          (!stillLoadingEstimate || !!isNewEstimate) &&
          (!stillLoadingPlan || !!isNewPlan) &&
          (!stillLoadingSow || !!isNewSow);

        if (allResolved) {
          if (pollInterval) clearInterval(pollInterval);
          setSavingEstimate(false);
          setGeneratingResourcePlan(false);
          setGeneratingSow(false);
          const runId = activeRunIdRef.current;
          if (runId) chatSession.reconcileStalledRun(runId);
        }
      } catch {
        // Ignore transient errors - retry on next tick.
      }
    };

    // Give the WebSocket path 30 s before the first poll so it can deliver
    // results directly without the polling adding unnecessary API calls.
    const initialDelay = setTimeout(() => {
      if (cancelled) return;
      void pollOnce();
      pollInterval = setInterval(() => void pollOnce(), 15_000);
    }, 30_000);

    // Absolute cap: stop after 10 minutes.
    const maxTimer = setTimeout(() => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
    }, 10 * 60_000);

    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
      if (pollInterval) clearInterval(pollInterval);
      clearTimeout(maxTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingEstimate, dealUuid]);

  const { setBaseline: setCompanyNameBaseline, updateCurrent: updateCompanyNameCurrent } =
    useDirtyState<{ companyName: string }>({ trimStrings: true });
  const {
    isDirty: questionnaireDirty,
    setBaseline: setQuestionnaireBaseline,
    updateCurrent: updateQuestionnaireCurrent,
  } = useDirtyState<Array<{ fieldKey: string; value: unknown }>>();

  const dirtyKey = dealUuid ? `deal-${dealUuid}` : 'deal-new';
  // The company name field on the create-start page is ephemeral pre-submission input;
  // nothing has been persisted yet so there is no data to lose on navigation.
  const hasUnsavedChanges = isCreateStartRoute ? false : !savingAnswers && questionnaireDirty;
  useUnsavedChanges(hasUnsavedChanges, dirtyKey, 'Create deal');
  const { confirmIfDirty } = useDirtyStateContext();

  const submittingCreate = createSubmissionPhase !== 'idle';

  // True when the search found deals created by someone else,
  // and the current user is not a sales module admin.
  const hasOtherOwnerDeal =
    salesModuleRole !== 'admin' &&
    (companyDeals?.some((d) => d.createdByUserUuid !== appUser?.uuid) ?? false);

  const resetDealSurfaceState = React.useCallback(() => {
    completedEnrichmentQuestionnaireRefreshRef.current = null;
    answersRef.current = {};
    setDeal(null);
    setDealError(null);
    setQuestionnaire(null);
    setQuestionnaireError(null);
    setAnswers({});
    setPersistedAnswers({});
    setTouchedFields(new Set());
    setSaveError(null);
    setScopeDraftDoc(null);
    scopeDraftBaselineRef.current = null;
    setScopeSaveError(null);
    resetGenerationState();
  }, [resetGenerationState]);

  React.useEffect(() => {
    if (!isCreateStartRoute) return;
    setCompanyName('');
    setCreateError(null);
    setCreateStatus(null);
    setExistingDraft(null);
    setCompanyDeals(null);
    setShowUploadView(false);
    setScriptFiles([]);
    setCreateReview(null);
    setScriptAdditionalNotes('');
    setCreateFileNotes({});
    setViewingDoc(null);
    setCompanyNameBaseline({ companyName: '' });
  }, [isCreateStartRoute, setCompanyNameBaseline]);

  React.useEffect(() => {
    if (!isCreateStartRoute) return;
    updateCompanyNameCurrent({ companyName });
  }, [companyName, isCreateStartRoute, updateCompanyNameCurrent]);

  React.useEffect(() => {
    if (!isCreateStartRoute) return;
    setCreateStatus(null);
    setExistingDraft(null);
    setCompanyDeals(null);
  }, [companyName, isCreateStartRoute]);

  React.useLayoutEffect(() => {
    if (isCreateStartRoute) {
      resetDealSurfaceState();
      return;
    }

    if (!dealUuid) {
      resetDealSurfaceState();
      return;
    }

    resetDealSurfaceState();
  }, [dealUuid, isCreateStartRoute, resetDealSurfaceState]);

  // Keep the shared sales chat session aware of the current deal so that any
  // message typed manually in the chat carries dealUuid - not just the Enrich
  // button which always passes it explicitly per-run.
  React.useEffect(() => {
    chatSession.updateContext({
      workspaceUuid: workspace?.uuid,
      dealUuid: dealUuid ?? undefined,
      pageScope: dealUuid ? 'deal' : undefined,
    });
  }, [dealUuid, workspace?.uuid, chatSession.updateContext]);

  const dealPageFieldLookup = React.useMemo(
    () => buildQuestionnaireFieldLookup(questionnaire),
    [questionnaire]
  );

  // Real dirty state for the Scope-doc editor: harvest the current draft against the edit-session
  // baseline and ask whether it produces ANY model change (the same field/brief/order diff Save
  // runs). `scopeDraftDoc` alone only means "the editor was touched" - it stays non-null after an
  // edit is reverted (type a space, delete it), so it cannot gate the Save button. `discarded`
  // keeps Save enabled when the doc holds unsaveable text so clicking it surfaces the reason.
  const scopeDocDirty = React.useMemo(() => {
    if (!scopeDraftDoc || !questionnaire) return false;
    const { hasChanges, discarded } = harvestScopeDoc({
      doc: scopeDraftDoc,
      questionnaire,
      answers: answersRef.current,
      fieldLookup: dealPageFieldLookup,
      baseline: scopeDraftBaselineRef.current ?? undefined,
    });
    return hasChanges || discarded.length > 0;
  }, [scopeDraftDoc, questionnaire, dealPageFieldLookup]);
  // The Scope-doc draft arms the same router blocker + beforeunload the questionnaire uses,
  // but only when it is genuinely dirty (disarmed while the save is in flight).
  useUnsavedChanges(scopeDocDirty && !scopeSavingDoc, 'deal-scope-doc', 'Scope document');

  const questionnaireBaseline = React.useMemo(
    () => buildQuestionnaireDirtySnapshot(questionnaire, persistedAnswers, true),
    [persistedAnswers, questionnaire]
  );
  const questionnaireCurrent = React.useMemo(
    () => buildQuestionnaireDirtySnapshot(questionnaire, answers, true),
    [answers, questionnaire]
  );
  // Two baselines, applying defaults to both sides or neither:
  //  - `changedFieldKeys` (defaults applied on both sides, like the nav guard) marks a
  //    field dirty only when the user changed it away from its default. Drives the sidebar
  //    `*` markers and estimate-staleness - a pristine deal with untouched defaults shows
  //    nothing.
  //  - `savableFieldKeys` (defaults omitted from the baseline) treats a displayed-but-unsaved
  //    default as a change so the per-group Save button activates and the user can persist it.
  const questionnaireBaselineValueMap = React.useMemo(
    () => buildQuestionnaireDirtyValueMap(questionnaire, persistedAnswers, true),
    [persistedAnswers, questionnaire]
  );
  const questionnaireSavableBaselineValueMap = React.useMemo(
    () => buildQuestionnaireDirtyValueMap(questionnaire, persistedAnswers, false),
    [persistedAnswers, questionnaire]
  );
  const questionnaireCurrentValueMap = React.useMemo(
    () => buildQuestionnaireDirtyValueMap(questionnaire, answers, true),
    [answers, questionnaire]
  );
  const changedFieldKeys = React.useMemo(
    () => diffDirtyValueMaps(questionnaireBaselineValueMap, questionnaireCurrentValueMap),
    [questionnaireBaselineValueMap, questionnaireCurrentValueMap]
  );
  const savableFieldKeys = React.useMemo(
    () => diffDirtyValueMaps(questionnaireSavableBaselineValueMap, questionnaireCurrentValueMap),
    [questionnaireSavableBaselineValueMap, questionnaireCurrentValueMap]
  );
  const questionnaireCompletion = React.useMemo(
    () => getQuestionnaireCompletionState(questionnaire, persistedAnswers),
    [questionnaire, persistedAnswers]
  );
  const hasQuestionnaireSections = Boolean(questionnaire && questionnaire.sections.length > 0);
  const canGenerateEstimate =
    !isCreateStartRoute &&
    Boolean(dealUuid) &&
    !loadingQuestionnaire &&
    hasQuestionnaireSections &&
    questionnaireCompletion.isComplete;
  const estimateIsStale = Boolean(estimateResult) && changedFieldKeys.size > 0;

  React.useEffect(() => {
    if (isCreateStartRoute) return;
    setQuestionnaireBaseline(questionnaireBaseline);
  }, [isCreateStartRoute, questionnaireBaseline, setQuestionnaireBaseline]);

  React.useEffect(() => {
    if (isCreateStartRoute) return;
    updateQuestionnaireCurrent(questionnaireCurrent);
  }, [isCreateStartRoute, questionnaireCurrent, updateQuestionnaireCurrent]);

  // Transition deal status based on questionnaire completion.
  // draft ↔ ready_for_generation when canGenerateEstimate changes.
  React.useEffect(() => {
    if (!dealUuid || !deal) return;
    const currentStatus = deal.status;
    if (canGenerateEstimate && currentStatus === 'draft') {
      setDeal((prev) => (prev ? { ...prev, status: 'ready_for_generation' } : prev));
      void salesApi
        .updateDealStatus(dealUuid, 'ready_for_generation')
        .then(({ deal: updated }) => {
          setDeal(updated);
        })
        .catch(() => {
          // Non-fatal: pill reverts on next getDeal poll.
        });
    } else if (!canGenerateEstimate && currentStatus === 'ready_for_generation') {
      setDeal((prev) => (prev ? { ...prev, status: 'draft' } : prev));
      void salesApi
        .updateDealStatus(dealUuid, 'draft')
        .then(({ deal: updated }) => {
          setDeal(updated);
        })
        .catch(() => {
          // Non-fatal.
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canGenerateEstimate, dealUuid]);

  React.useEffect(() => {
    if (isCreateStartRoute) {
      setRightPaneVisible(false);
      setRightPaneMode(null);
    }
  }, [dealUuid, isCreateStartRoute, setRightPaneMode, setRightPaneVisible]);

  React.useEffect(() => {
    if (!isCreateStartRoute) return;

    let cancelled = false;
    setLoadingSuggestions(true);
    setCreateError(null);

    void (async () => {
      try {
        const { companies } = await salesApi.getCompanySuggestions();
        if (cancelled) return;
        setCompanySuggestions(companies);
      } catch {
        // Silently ignore - suggestions are non-critical and may fail when unauthenticated
      } finally {
        if (!cancelled) {
          setLoadingSuggestions(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isCreateStartRoute]);

  React.useEffect(() => {
    setEnrichmentTriggered(false);
    setEnrichmentPollKey(0);
  }, [dealUuid]);

  React.useEffect(() => {
    if (!dealUuid) return;

    const isEnrichmentTriggered = enrichmentPollKey > 0;

    let cancelled = false;
    let intervalId: number | null = null;
    let hasLoadedDeal = false;
    // Safety valve: stop polling after 2 minutes regardless of status, so a
    // permanently-failing enrichment never leaves the spinner up forever.
    const ENRICHMENT_POLL_TIMEOUT_MS = 2 * 60 * 1000;
    const pollStartedAt = Date.now();

    const fetchDeal = async () => {
      if (!hasLoadedDeal) {
        setLoadingDeal(true);
      }
      setDealError(null);
      try {
        const { deal: nextDeal, moduleRole } = await salesApi.getDeal(dealUuid);
        if (cancelled) return;
        setDeal(nextDeal);
        setSalesModuleRole(moduleRole);
        if (!hasLoadedDeal) {
          hasLoadedDeal = true;
          setLoadingDeal(false);
        }
        const timedOut =
          nextDeal.enrichmentStatus !== 'completed' &&
          Date.now() - pollStartedAt > ENRICHMENT_POLL_TIMEOUT_MS;
        const shouldStopPolling =
          nextDeal.enrichmentStatus === 'completed' ||
          (nextDeal.enrichmentStatus === 'idle' && !isEnrichmentTriggered) ||
          timedOut;
        if (shouldStopPolling && intervalId !== null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        if (timedOut) {
          // spinner is now driven by chatSession.isRunActive; nothing more to set
        }
      } catch (error) {
        if (!cancelled) {
          setDealError(error instanceof Error ? error.message : 'Failed to load deal status');
        }
      } finally {
        if (!cancelled && !hasLoadedDeal) {
          setLoadingDeal(false);
        }
      }
    };

    void fetchDeal();
    intervalId = window.setInterval(() => {
      void fetchDeal();
    }, 2000);

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [dealUuid, enrichmentPollKey, dealReloadKey]);

  React.useEffect(() => {
    if (!dealUuid) return;

    let cancelled = false;
    setLoadingQuestionnaire(true);
    setQuestionnaireError(null);

    void (async () => {
      try {
        const { questionnaire: nextQuestionnaire } = await salesApi.getDealQuestionnaire(dealUuid);
        if (cancelled) return;
        const nextAnswers = buildInitialAnswers(nextQuestionnaire);
        setQuestionnaire(nextQuestionnaire);
        setPersistedAnswers(buildPersistedAnswers(nextQuestionnaire));
        answersRef.current = nextAnswers;
        setAnswers(nextAnswers);
        setTouchedFields(new Set());
      } catch (error) {
        if (cancelled) return;
        console.error('[DealPage] Failed to load questionnaire:', error);
        setQuestionnaireError(
          error instanceof Error ? error.message : 'Failed to load questionnaire'
        );
      } finally {
        if (!cancelled) {
          setLoadingQuestionnaire(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dealUuid, questionnaireReloadKey]);

  React.useEffect(() => {
    if (!dealUuid) return;

    let cancelled = false;
    setLoadingDealAttributes(true);

    void Promise.all([
      salesApi
        .getRateCardProfiles(dealUuid)
        .then((res) => {
          if (!cancelled) setRateCardProfiles(res.profiles);
        })
        .catch(() => {
          /* ignore */
        }),
      (async () => {
        try {
          const { data } = await salesApi.getDealAttributes(dealUuid);
          if (cancelled) return;
          setDealAttributes(data);
        } catch (error) {
          console.error('[DealPage] Failed to load deal attributes:', error);
          if (!cancelled) setDealAttributes([]);
        } finally {
          if (!cancelled) setLoadingDealAttributes(false);
        }
      })(),
    ]);

    return () => {
      cancelled = true;
    };
  }, [dealUuid]);

  React.useEffect(() => {
    if (!dealUuid || deal?.enrichmentStatus !== 'completed') return;
    if (completedEnrichmentQuestionnaireRefreshRef.current === dealUuid) return;

    let cancelled = false;
    completedEnrichmentQuestionnaireRefreshRef.current = dealUuid;

    void (async () => {
      try {
        const { questionnaire: nextQuestionnaire } = await salesApi.getDealQuestionnaire(dealUuid);
        if (cancelled) return;
        const nextAnswers = buildInitialAnswers(nextQuestionnaire);
        const latestAnswers = answersRef.current;
        const mergedAnswers = mergeSavedAnswers(
          nextQuestionnaire,
          latestAnswers,
          nextAnswers,
          persistedAnswers
        );
        const nextChangedFieldKeys = getChangedAnswerKeys(mergedAnswers, nextAnswers);

        setQuestionnaire(nextQuestionnaire);
        setPersistedAnswers(buildPersistedAnswers(nextQuestionnaire));
        answersRef.current = mergedAnswers;
        setAnswers(mergedAnswers);
        setTouchedFields(
          (currentTouchedFields) =>
            new Set(
              [...currentTouchedFields].filter((fieldKey) => nextChangedFieldKeys.has(fieldKey))
            )
        );
      } catch (error) {
        if (cancelled) return;
        completedEnrichmentQuestionnaireRefreshRef.current = null;
        setQuestionnaireError(
          error instanceof Error ? error.message : 'Failed to refresh questionnaire'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [deal?.enrichmentStatus, dealUuid, persistedAnswers]);

  // Confirmed from the enrich dialog - runs with the mode picked there.
  const handleTriggerEnrichment = React.useCallback(() => {
    if (!dealUuid) return;
    const companyLabel = deal?.company.name ? `"${deal.company.name}"` : 'this company';
    setShowEnrichConfirmPanel(false);
    setEnrichmentTriggered(true);
    setRightPaneVisible(true);
    setRightPaneMode('chat');
    setRightPaneWidth(Math.floor(window.innerWidth * 0.4));
    chatSession.sendRun({
      prompt: `enrich company information for ${companyLabel}`,
      agentKey: 'sales_company_enrichment',
      moduleKey: 'sales',
      origin: { page: 'Deal', label: 'Enrich company' },
      mode: enrichMode,
      context: {
        workspaceUuid: workspace?.uuid,
        dealUuid,
        pageScope: 'deal',
      },
    });
  }, [
    deal?.company.name,
    dealUuid,
    enrichMode,
    workspace?.uuid,
    setRightPaneMode,
    setRightPaneVisible,
    setRightPaneWidth,
    chatSession.sendRun,
  ]);

  const handleReloadQuestionnaire = React.useCallback(async () => {
    if (!dealUuid) return;
    // Stamp this invocation so we can detect if it has been superseded by a
    // subsequent reload request or by a save that already provided fresher data.
    const token = Symbol();
    pendingReloadTokenRef.current = token;
    try {
      const { questionnaire: nextQuestionnaire } = await salesApi.getDealQuestionnaire(dealUuid);
      // Discard if a save completed (or a newer reload started) while we were
      // fetching - the save response already carries fresh questionnaire state.
      if (pendingReloadTokenRef.current !== token) return;
      const nextAnswers = buildInitialAnswers(nextQuestionnaire);
      const latestAnswers = answersRef.current;
      // Use the current questionnaire state (with defaults applied) as the save-snapshot so
      // that fields whose only value is a synthetic default (e.g. pill_radio 'no') are not
      // treated as user-edited - which would silently discard AI-written values from the server.
      const currentAsSnapshot = buildInitialAnswers(questionnaire);
      const mergedAnswers = mergeSavedAnswers(
        nextQuestionnaire,
        latestAnswers,
        nextAnswers,
        currentAsSnapshot
      );
      setQuestionnaire(nextQuestionnaire);
      setPersistedAnswers(buildPersistedAnswers(nextQuestionnaire));
      answersRef.current = mergedAnswers;
      setAnswers(mergedAnswers);
    } catch {
      // silent - provenance change doesn't warrant a full error state
    }
  }, [dealUuid, questionnaire]);

  // Snapshot the edit-session baseline on the first keystroke (see scopeDraftBaselineRef).
  const handleScopeDraftChange = React.useCallback(
    (doc: Record<string, unknown> | null) => {
      if (doc && !scopeDraftBaselineRef.current && questionnaire) {
        scopeDraftBaselineRef.current = { questionnaire, answers: { ...answersRef.current } };
      }
      if (!doc) scopeDraftBaselineRef.current = null;
      setScopeDraftDoc(doc);
    },
    [questionnaire]
  );

  // Save the Scope-doc draft: harvest the edited doc against the edit-session baseline, send
  // the ONE transactional questionnaire-document request, then reload the questionnaire so
  // every surface (question view, Prioritization, the rebuilt doc) reflects the save. Refuses
  // (keeps the draft + shows why) when the doc holds text the harvest cannot save - never
  // silently drop a user's edit.
  const handleSaveScopeDoc = React.useCallback(async () => {
    if (!dealUuid || !scopeDraftDoc || !questionnaire) return;
    const { payload, hasChanges, discarded } = harvestScopeDoc({
      doc: scopeDraftDoc,
      questionnaire,
      answers: answersRef.current,
      fieldLookup: dealPageFieldLookup,
      baseline: scopeDraftBaselineRef.current ?? undefined,
    });
    if (discarded.length > 0) {
      setScopeSaveError(
        `Some text cannot be saved from this view - move it under a question, an ad-hoc scope item, or a heading of your own, or remove it: "${discarded[0]}"`
      );
      return;
    }
    if (!hasChanges) {
      setScopeDraftDoc(null);
      scopeDraftBaselineRef.current = null;
      setScopeSaveError(null);
      return;
    }
    setScopeSavingDoc(true);
    setScopeSaveError(null);
    try {
      await salesApi.applyScopeDocument(dealUuid, payload);
      await handleReloadQuestionnaire();
      setScopeDraftDoc(null);
      scopeDraftBaselineRef.current = null;
      flashScopeSaved(); // transient "Saved" hint (autosave has no explicit Save button)
    } catch (error) {
      setScopeSaveError(error instanceof Error ? error.message : 'Save failed - try again');
    } finally {
      setScopeSavingDoc(false);
    }
  }, [
    dealUuid,
    scopeDraftDoc,
    questionnaire,
    dealPageFieldLookup,
    handleReloadQuestionnaire,
    flashScopeSaved,
  ]);

  // Confirmed Scope-doc removal (item / whole functional area): flush pending edits, delete every
  // targeted field through the model API, reload, and reset the edit session so the rebuilt doc
  // (minus the removed section) renders.
  const handleScopeRemoveConfirm = React.useCallback(async () => {
    if (!dealUuid || !scopeRemoveTarget) return;
    setScopeRemoveBusy(true);
    try {
      // Flush pending doc edits first - the draft resets below, so unsaved typing must land.
      if (scopeDocDirty && !scopeSavingDoc) await handleSaveScopeDoc();
      for (const key of scopeRemoveTarget.fieldKeys) {
        await salesApi.deleteAdhocField(dealUuid, key);
      }
      await handleReloadQuestionnaire();
      setScopeDraftDoc(null);
      scopeDraftBaselineRef.current = null;
      setScopeRemoveTarget(null);
    } catch (error) {
      setScopeSaveError(error instanceof Error ? error.message : 'Delete failed - try again');
    } finally {
      setScopeRemoveBusy(false);
    }
  }, [
    dealUuid,
    scopeRemoveTarget,
    scopeDocDirty,
    scopeSavingDoc,
    handleSaveScopeDoc,
    handleReloadQuestionnaire,
  ]);

  // Autosave the Scope doc (Notion-style - no Save button): when the harvested draft is genuinely
  // dirty and no save is in flight, debounce a save. Runs regardless of the active tab so a draft
  // still commits after navigating away; the debounce coalesces bursts of edits. `handleSaveScopeDoc`
  // itself no-ops on a clean/undirty draft and surfaces a `scopeSaveError` for unsaveable content.
  React.useEffect(() => {
    if (!scopeDocDirty || scopeSavingDoc) return;
    const timer = window.setTimeout(() => {
      void handleSaveScopeDoc();
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [scopeDocDirty, scopeSavingDoc, handleSaveScopeDoc]);
  // Clear the "Saved" hint timer on unmount.
  React.useEffect(
    () => () => {
      if (scopeSavedHintTimerRef.current !== null) {
        window.clearTimeout(scopeSavedHintTimerRef.current);
      }
    },
    []
  );

  /**
   * Upload a transcript file to an existing deal, then trigger transcript analysis
   * via the AI chat WebSocket. The dialog's mode picker decides how results land:
   * ask/auto run in review mode (no DB writes - a before/after comparison card in
   * the chat pane lets the user selectively fill fields), agent applies the
   * extracted values directly and reloads the questionnaire when the run completes.
   */
  // Kick off the transcript-analysis chat run for a set of (already-uploaded) doc uuids. Shared by
  // the normal upload path and the duplicate confirm-or-override branches so the run is framed
  // identically (RE-analyze when reusing existing docs, Analyze otherwise).
  const proceedTranscriptAnalysis = React.useCallback(
    (
      docUuids: string[],
      opts: { reanalyze: boolean; docLabel: string; additionalNotes?: string }
    ) => {
      if (!dealUuid || docUuids.length === 0) return;
      transcriptDocUuidRef.current = docUuids[0] ?? null;
      setShowTranscriptUploadPanel(false);
      setRightPaneVisible(true);
      setRightPaneMode('chat');
      setRightPaneWidth(Math.floor(window.innerWidth * 0.4));
      const companyLabel = deal?.company.name ? `"${deal.company.name}"` : 'this deal';
      const directApply = transcriptUploadMode === 'agent';
      // Save the notes on the deal exactly when the run is sent (once per run). A save failure never
      // blocks the analysis: the notes still ride along in the run, and the user gets a heads-up.
      if (opts.additionalNotes) {
        void salesApi
          .saveDealAdditionalNotes(dealUuid, opts.additionalNotes)
          .catch(() => setSaveError(NOTES_NOT_SAVED_MESSAGE));
      }
      chatSession.sendRun({
        prompt: `${opts.reanalyze ? 'Re-analyze' : 'Analyze'} ${opts.docLabel} for ${companyLabel}`,
        agentKey: 'deal_transcript_analysis',
        moduleKey: 'sales',
        origin: { page: 'Deal', label: 'Upload document' },
        mode: transcriptUploadMode,
        context: {
          workspaceUuid: workspace?.uuid,
          dealUuid,
          transcriptDocUuids: docUuids,
          transcriptDocUuid: docUuids[0],
          transcriptReviewMode: !directApply,
          pageScope: 'deal',
          // The user's optional notes about the deal: prompt context for this run only.
          ...(opts.additionalNotes ? { additionalNotes: opts.additionalNotes } : {}),
        },
        // Review mode refreshes via the Fill / Capture actions; direct apply saved
        // server-side, so reload here - the questionnaire (filled answers + captured scope
        // questions) and, like the capture handler, the docs activity + meeting context.
        ...(directApply
          ? {
              onRunEvent: (event) => {
                if (event.type === 'run_completed') {
                  void handleReloadQuestionnaire();
                  refetchDocuments();
                  meetingContext.refetch();
                }
              },
            }
          : {}),
      });
    },
    [
      dealUuid,
      deal?.company.name,
      transcriptUploadMode,
      workspace?.uuid,
      chatSession.sendRun,
      handleReloadQuestionnaire,
      refetchDocuments,
      meetingContext.refetch,
      setSaveError,
      setRightPaneMode,
      setRightPaneVisible,
      setRightPaneWidth,
    ]
  );

  // After a successful upload set: clear the dialog and either ask about duplicates or run the
  // analysis. Shared by the direct path and the "Continue" step after a per-file review.
  const finishTranscriptUpload = React.useCallback(
    (
      results: NonNullable<typeof transcriptDupConfirm>['results'],
      docLabel: string,
      additionalNotes?: string
    ) => {
      setTranscriptUploadFiles([]);
      setTranscriptUploadDriveFiles([]);
      setTranscriptFileNotes({});
      setTranscriptReview(null);
      setTranscriptAdditionalNotes('');
      setTranscriptUploadError(null);
      refetchDocuments();
      if (results.some((r) => r.reused)) {
        // Already attached: don't auto-run. Surface a confirm-or-override choice.
        setTranscriptDupConfirm({ results, docLabel, additionalNotes });
        return;
      }
      proceedTranscriptAnalysis(
        results.map((r) => r.docUuid),
        { reanalyze: false, docLabel, additionalNotes }
      );
    },
    [proceedTranscriptAnalysis, refetchDocuments]
  );

  const handleUploadTranscript = React.useCallback(async () => {
    const totalFiles = transcriptUploadFiles.length + transcriptUploadDriveFiles.length;
    if (totalFiles === 0 || !dealUuid) return;
    const driveUuid = driveIntegration.integrationUuid;
    const additionalNotes = transcriptAdditionalNotes.trim();
    setUploadingTranscript(true);
    setTranscriptUploadError(null);
    setTranscriptFileNotes({});
    try {
      // Upload each file (single-file endpoint, looped) in selection order; the returned doc uuids
      // feed the analysis as a set the skill chunks + merges together. Local files upload via
      // multipart; Drive picks via the from-drive route (server downloads them). Each result keeps
      // its source file so a "fresh copy" override can re-upload it with force. A file that fails
      // is reported on that file and the others still upload.
      const results: NonNullable<typeof transcriptDupConfirm>['results'] = [];
      const notes: Record<string, FileNote> = {};
      const noteError = (key: string, fileName: string, err: unknown) => {
        notes[key] = { kind: 'error', text: formatFileIssue(fileName, uploadFailureReason(err)) };
      };
      for (const file of transcriptUploadFiles) {
        try {
          const { docUuid, reused, duplicateOf, warning } = await salesApi.uploadDealTranscript(
            dealUuid,
            file
          );
          results.push({
            docUuid,
            reused: !!reused,
            title: duplicateOf?.title ?? file.name,
            source: { kind: 'local', file },
          });
          if (warning) notes[localFileKey(file)] = { kind: 'warning', text: warning };
        } catch (err) {
          noteError(localFileKey(file), file.name, err);
        }
      }
      for (const file of transcriptUploadDriveFiles) {
        if (!driveUuid) break;
        try {
          const { docUuid, reused, duplicateOf, warning } =
            await salesApi.uploadDealTranscriptFromDrive(dealUuid, driveUuid, file);
          results.push({
            docUuid,
            reused: !!reused,
            title: duplicateOf?.title ?? file.name,
            source: { kind: 'drive', file },
          });
          if (warning) notes[driveFileKey(file)] = { kind: 'warning', text: warning };
        } catch (err) {
          noteError(driveFileKey(file), file.name, err);
        }
      }
      const errorCount = Object.values(notes).filter((n) => n.kind === 'error').length;
      if (results.length === 0) {
        setTranscriptFileNotes(notes);
        setTranscriptUploadError(
          'None of these files could be read. Remove them or fix them, then try again.'
        );
        return;
      }
      const firstName = results[0]?.title ?? 'document';
      const docLabel = results.length === 1 ? firstName : `${results.length} documents`;
      // The notes are NOT saved here: they are saved when the analysis run is sent
      // (proceedTranscriptAnalysis), after any duplicate confirm or review hold below.
      if (Object.keys(notes).length > 0) {
        // Something needs a look first (a file failed or one is very long): hold here.
        setTranscriptFileNotes(notes);
        setTranscriptReview({ results, docLabel, additionalNotes });
        setTranscriptUploadError(
          errorCount > 0
            ? `${errorCount} file${errorCount === 1 ? '' : 's'} could not be read. The others were uploaded.`
            : null
        );
        refetchDocuments();
        return;
      }
      finishTranscriptUpload(results, docLabel, additionalNotes);
    } catch (err) {
      setTranscriptUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingTranscript(false);
    }
  }, [
    transcriptUploadFiles,
    transcriptUploadDriveFiles,
    transcriptAdditionalNotes,
    driveIntegration.integrationUuid,
    dealUuid,
    finishTranscriptUpload,
    refetchDocuments,
  ]);

  // Confirm-or-override: re-analyze the existing copies (no new docs created).
  const handleTranscriptDupReanalyze = React.useCallback(() => {
    const confirm = transcriptDupConfirm;
    if (!confirm) return;
    setTranscriptDupConfirm(null);
    proceedTranscriptAnalysis(
      confirm.results.map((r) => r.docUuid),
      { reanalyze: true, docLabel: confirm.docLabel, additionalNotes: confirm.additionalNotes }
    );
  }, [transcriptDupConfirm, proceedTranscriptAnalysis]);

  // Confirm-or-override: re-upload the duplicates with force (fresh copies), then analyze.
  const handleTranscriptDupFreshCopy = React.useCallback(async () => {
    const confirm = transcriptDupConfirm;
    if (!confirm || !dealUuid) return;
    const driveUuid = driveIntegration.integrationUuid;
    setTranscriptDupBusy(true);
    try {
      const docUuids: string[] = [];
      for (const r of confirm.results) {
        if (!r.reused) {
          docUuids.push(r.docUuid);
          continue;
        }
        if (r.source.kind === 'local') {
          const { docUuid } = await salesApi.uploadDealTranscript(dealUuid, r.source.file, true);
          docUuids.push(docUuid);
        } else if (driveUuid) {
          const { docUuid } = await salesApi.uploadDealTranscriptFromDrive(
            dealUuid,
            driveUuid,
            r.source.file,
            true
          );
          docUuids.push(docUuid);
        }
      }
      setTranscriptDupConfirm(null);
      refetchDocuments();
      proceedTranscriptAnalysis(docUuids, {
        reanalyze: false,
        docLabel: confirm.docLabel,
        additionalNotes: confirm.additionalNotes,
      });
    } catch (err) {
      setTranscriptUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setTranscriptDupBusy(false);
    }
  }, [
    transcriptDupConfirm,
    dealUuid,
    driveIntegration.integrationUuid,
    proceedTranscriptAnalysis,
    refetchDocuments,
  ]);

  // Auto-trigger transcript analysis when navigating from create flow with a transcript doc.
  React.useEffect(() => {
    const state = location.state as {
      transcriptDocUuids?: string[];
      transcriptDocUuid?: string;
      docFileName?: string;
      companyName?: string;
      mode?: ChatMode;
      additionalNotes?: string;
    } | null;
    // Multi-file: prefer the array, fall back to the singular for back-compat.
    const transcriptDocUuids = state?.transcriptDocUuids?.length
      ? state.transcriptDocUuids
      : state?.transcriptDocUuid
        ? [state.transcriptDocUuid]
        : [];
    if (!dealUuid || isCreateStartRoute || transcriptDocUuids.length === 0) return;
    if (transcriptAutoTriggeredRef.current === dealUuid) return;
    transcriptAutoTriggeredRef.current = dealUuid;
    transcriptDocUuidRef.current = transcriptDocUuids[0] ?? null;

    // Clear location state so back-navigation doesn't re-trigger
    navigate(location.pathname, {
      replace: true,
      state: {
        ...state,
        transcriptDocUuids: undefined,
        transcriptDocUuid: undefined,
        docFileName: undefined,
        companyName: undefined,
        mode: undefined,
        additionalNotes: undefined,
      },
    });

    setRightPaneVisible(true);
    setRightPaneMode('chat');
    setRightPaneWidth(Math.floor(window.innerWidth * 0.4));

    const docLabel = state?.docFileName ?? 'document';
    const companyLabel = state?.companyName ?? 'this company';
    const prompt = `Analyze ${docLabel} for ${companyLabel}`;
    // Same mode semantics as the edit-page upload dialog (handleUploadTranscript):
    // agent (the default) saves the results directly and reloads the questionnaire;
    // ask/auto run in review mode (before/after cards, no DB writes until Fill / Capture).
    const mode = state?.mode ?? 'agent';
    const directApply = mode === 'agent';

    // The notes typed on the create panel are saved now, when the run is sent (this effect fires
    // once per deal), so backing out of the create review step stores nothing. A save failure never
    // blocks the analysis: the notes still ride along in the run, and the user gets a heads-up.
    if (state?.additionalNotes) {
      void salesApi
        .saveDealAdditionalNotes(dealUuid, state.additionalNotes)
        .catch(() => setSaveError(NOTES_NOT_SAVED_MESSAGE));
    }

    chatSession.sendRun({
      prompt,
      agentKey: 'deal_transcript_analysis',
      moduleKey: 'sales',
      origin: { page: 'Deal', label: 'Upload document' },
      mode,
      context: {
        workspaceUuid: workspace?.uuid,
        dealUuid,
        transcriptDocUuids,
        transcriptDocUuid: transcriptDocUuids[0],
        transcriptReviewMode: !directApply,
        pageScope: 'deal',
        // The user's optional notes about the deal, carried from the create panel.
        ...(state?.additionalNotes ? { additionalNotes: state.additionalNotes } : {}),
      },
      // Review mode refreshes via the Fill / Capture actions; direct apply saved server-side,
      // so reload here (matches handleUploadTranscript).
      ...(directApply
        ? {
            onRunEvent: (event) => {
              if (event.type === 'run_completed') {
                void handleReloadQuestionnaire();
                refetchDocuments();
                meetingContext.refetch();
              }
            },
          }
        : {}),
    });
  }, [
    dealUuid,
    isCreateStartRoute,
    location.state,
    location.pathname,
    navigate,
    workspace?.uuid,
    setRightPaneMode,
    setRightPaneVisible,
    setRightPaneWidth,
    chatSession.sendRun,
    handleReloadQuestionnaire,
    refetchDocuments,
    meetingContext.refetch,
    setSaveError,
  ]);

  // When an enrichment-triggered chat run completes, clear the enrichment trigger flag.
  // The user applies enrichment values locally via the Fill button - no DB reload needed
  // because enrichment no longer auto-saves to the database.
  const enrichmentRunActiveRef = React.useRef(false);
  React.useEffect(() => {
    if (enrichmentTriggered && chatSession.isRunActive) {
      enrichmentRunActiveRef.current = true;
    } else if (enrichmentRunActiveRef.current && !chatSession.isRunActive) {
      enrichmentRunActiveRef.current = false;
      setEnrichmentTriggered(false);
    }
  }, [chatSession.isRunActive, enrichmentTriggered]);

  // Reload the questionnaire whenever any chat run completes so that AI-written values
  // (e.g. transcript analysis) are reflected without requiring a manual page refresh.
  // The auto-trigger path already calls handleReloadQuestionnaire via onRunEvent, but
  // manually initiated runs (typed in the chat pane) have no onRunEvent hook.
  // When the completed run was an estimate REGENERATION (flag set by
  // handleRegenerateEstimate), the plain reload is replaced by the estimate-derived
  // re-fill: estimate-sourced questionnaire defaults (the managed-services contract
  // prefills) are re-derived from the new estimate even where old values were saved.
  // Ref indirection because the refill is defined below handlePersistAnswers.
  const chatRunWasActiveRef = React.useRef(false);
  const pendingEstimateRefillRef = React.useRef(false);
  const refillEstimateDerivedAnswersRef = React.useRef<(() => Promise<void>) | undefined>(
    undefined
  );
  React.useEffect(() => {
    if (chatSession.isRunActive) {
      chatRunWasActiveRef.current = true;
    } else if (chatRunWasActiveRef.current) {
      chatRunWasActiveRef.current = false;
      if (pendingEstimateRefillRef.current) {
        pendingEstimateRefillRef.current = false;
        const refill = refillEstimateDerivedAnswersRef.current;
        if (refill) {
          void refill();
        } else {
          void handleReloadQuestionnaire();
        }
      } else {
        void handleReloadQuestionnaire();
      }
    }
  }, [chatSession.isRunActive, handleReloadQuestionnaire]);

  const handleAnswerChange = React.useCallback(
    (fieldKey: string, value: unknown) => {
      setAnswers((current) => {
        const withNewValue = { ...current, [fieldKey]: value };
        const withTransitions = applyTransitionRules(
          questionnaire,
          fieldKey,
          withNewValue,
          dealPageFieldLookup
        );
        // Live contract recommendation: a change to a source field (client
        // capacity, counts, hours, cost) recalculates the recommended contract
        // terms in-form; runs before the calc rules so contract_amount derives
        // from the fresh recommendation in the same pass.
        const withRecommendation = applyContractRecommendationRules(
          questionnaire,
          fieldKey,
          withTransitions,
          (key) => touchedFieldsRef.current.has(key),
          dealPageFieldLookup
        );
        const withCalc = applyCalculationRules(
          questionnaire,
          withRecommendation,
          dealPageFieldLookup
        );
        const next = sanitizeQuestionnaireAnswers(questionnaire, withCalc, dealPageFieldLookup);
        answersRef.current = next;
        return next;
      });
      setTouchedFields((prev) => {
        const next = new Set(prev);
        next.add(fieldKey);
        return next;
      });
      setSaveError(null);
    },
    [questionnaire, dealPageFieldLookup]
  );

  /**
   * Refetch the questionnaire (e.g. after the chat wizard adds a new template field)
   * and merge it with the user's current unsaved answers, so the new field appears
   * without clobbering in-progress edits. Same merge strategy as the post-enrichment
   * refresh above.
   */
  const refetchQuestionnaire = React.useCallback(async () => {
    if (!dealUuid) return;
    try {
      const { questionnaire: nextQuestionnaire } = await salesApi.getDealQuestionnaire(dealUuid);
      const nextAnswers = buildInitialAnswers(nextQuestionnaire);
      const mergedAnswers = mergeSavedAnswers(
        nextQuestionnaire,
        answersRef.current,
        nextAnswers,
        persistedAnswers
      );
      const nextChangedFieldKeys = getChangedAnswerKeys(mergedAnswers, nextAnswers);
      setQuestionnaire(nextQuestionnaire);
      setPersistedAnswers(buildPersistedAnswers(nextQuestionnaire));
      answersRef.current = mergedAnswers;
      setAnswers(mergedAnswers);
      setTouchedFields(
        (current) => new Set([...current].filter((fieldKey) => nextChangedFieldKeys.has(fieldKey)))
      );
    } catch (error) {
      setQuestionnaireError(
        error instanceof Error ? error.message : 'Failed to refresh questionnaire'
      );
    }
  }, [dealUuid, persistedAnswers]);

  /**
   * Handle action button clicks from AiStructuredMessage cards (Accept / Cancel).
   * On accept: merge enrichment questionnaireValues OR transcript changed fields
   * into wizard form without saving.
   */
  // Ref to handlePersistAnswers (defined below). The Fill handlers below apply values
  // to local form state and must commit them immediately - the deal page has no autosave,
  // so without this an accepted Fill is lost on reload/navigation.
  const handlePersistAnswersRef = React.useRef<
    | ((
        question?: SalesDealTemplateGroupSummary,
        extraAnswers?: Array<{ templateFieldId?: number | null; fieldKey: string; value: unknown }>
      ) => Promise<boolean>)
    | undefined
  >(undefined);

  const handleChatAction = React.useCallback<AiMessageActionHandler>(
    (event) => {
      if (event.actionType === 'open') {
        // Generation-complete message links - open each document directly in the
        // main canvas. `open_documents` is kept for older rehydrated messages.
        const docByActionId: Record<string, ViewingDocState> = {
          open_estimate: { kind: 'estimate' },
          open_resource_plan: { kind: 'resource-plan' },
          open_sow: { kind: 'sow' },
          open_documents: { kind: 'documents' },
        };
        const target = docByActionId[event.actionId];
        // Gate the same way the subnav is: a link to a stage-hidden view (e.g. open_resource_plan
        // in uat/prod) no-ops rather than opening a tab the workspace can't see.
        if (target && isDocViewAllowed(target.kind)) {
          setViewingDoc(target);
          return;
        }
      }
      // Chat wizard added a new template field → pull it into the open questionnaire.
      if (event.actionType === 'open' && event.actionId === 'view_new_field') {
        void refetchQuestionnaire();
        return;
      }
      // Transcript discovery → capture one-time questions, then refresh the questionnaire
      // so the "AI-captured questions" section appears. The action is accept-typed
      // (its card settles to "Questions captured"), so match on actionId alone.
      if (event.actionId === 'capture_questions') {
        const capturePayload = event.payload as {
          discoveredQuestions?: Array<{
            label: string;
            fieldKey?: string;
            uiControl: string;
            options?: Array<{ label: string; value: string }>;
            suggestedValue?: string | null;
            scopeMeta?: ScopeMeta;
            // Re-run comparison: 'update' overwrites an existing template/ad-hoc answer.
            target?: 'new' | 'update';
            existingFieldKey?: string;
            existingKind?: 'template' | 'adhoc';
            templateFieldId?: number;
          }>;
          selectedFieldKeys?: string[];
        } | null;
        const all = capturePayload?.discoveredQuestions;
        if (!all?.length || !dealUuid) return;
        // The capture card is selectable: apply only the items the user kept checked. For a 'new'
        // item checked = add; for an 'update' item checked = replace the existing answer, unchecked
        // = keep. When no selection metadata is present (older payloads), apply all.
        const selected = capturePayload?.selectedFieldKeys
          ? new Set(capturePayload.selectedFieldKeys)
          : null;
        const chosen = selected ? all.filter((q) => q.fieldKey && selected.has(q.fieldKey)) : all;
        if (chosen.length === 0) return;
        // Items the user reviewed but un-checked feed the discovery flywheel as 'reject' signal.
        const rejected = selected
          ? all
              .filter((q) => q.fieldKey && !selected.has(q.fieldKey))
              .map((q) => ({
                label: q.label,
                scopeMeta: q.scopeMeta ?? null,
                strategicScope: q.scopeMeta?.businessProcess ?? null,
              }))
          : [];
        // One transactional endpoint: inserts new ad-hoc fields + overwrites changed existing
        // answers/scope; value serialization lives server-side. Show the ad-hoc skeleton for the
        // whole write + reload so the user gets immediate feedback (it takes a beat to display).
        setCapturingAdhocCount(chosen.length);
        void salesApi
          .applyDiscoveredQuestions(dealUuid, chosen, rejected)
          .then(() => {
            // The apply also kicks off a server-side meeting-context refresh + writes the per-doc
            // activity log - refetch both so the Docs tab picks up "generating" + fresh badges.
            meetingContext.refetch();
            refetchDocuments();
            return refetchQuestionnaire();
          })
          .catch(() => setSaveError('Failed to apply questions.'))
          .finally(() => setCapturingAdhocCount(0));
        return;
      }
      if (event.actionType !== 'accept') return;
      const p = event.payload as
        | {
            enrichmentOutput?: { questionnaireValues?: Record<string, unknown> };
            transcriptChangedFields?: Array<{ fieldKey: string; after: unknown }>;
            selectedFieldKeys?: string[];
          }
        | null
        | undefined;

      // ── Transcript review fill ──────────────────────────────────────────────
      const transcriptChangedFields = p?.transcriptChangedFields;
      if (transcriptChangedFields?.length) {
        const selectedKeys = p?.selectedFieldKeys ? new Set(p.selectedFieldKeys) : null;
        const fieldsToApply = selectedKeys
          ? transcriptChangedFields.filter((f) => selectedKeys.has(f.fieldKey))
          : transcriptChangedFields;

        if (fieldsToApply.length === 0) return;

        // Compute the merged answers synchronously so answersRef.current is up to date
        // BEFORE we persist (handlePersistAnswers reads answersRef.current as its snapshot).
        // Also collect every applied field as an explicit save entry: on an additive deal the
        // answered section may not be added yet, so the field isn't in the visible questionnaire
        // and buildQuestionnaireSavePayload would drop it - passing it as extraAnswers makes the
        // server persist it AND auto-include its group, surfacing the section after the reload.
        const next = { ...answersRef.current };
        const extraAnswers: Array<{ fieldKey: string; value: unknown }> = [];
        for (const { fieldKey, after } of fieldsToApply) {
          // after values from the WS payload are raw strings (as stored in the DB).
          // Parse them the same way parseStoredResponseValue does server-side so that
          // multi_select arrays arrive as arrays rather than JSON strings.
          let parsedValue: unknown = after;
          if (typeof after === 'string') {
            try {
              parsedValue = JSON.parse(after);
            } catch {
              parsedValue = after;
            }
          }
          if (parsedValue !== null && parsedValue !== undefined && parsedValue !== '') {
            setAnswerValueByFieldKey(next, fieldKey, parsedValue, dealPageFieldLookup);
            extraAnswers.push({ fieldKey, value: parsedValue });
          }
        }
        const withCalc = applyCalculationRules(questionnaire, next, dealPageFieldLookup);
        const sanitized = sanitizeQuestionnaireAnswers(
          questionnaire,
          withCalc,
          dealPageFieldLookup
        );
        answersRef.current = sanitized;
        setAnswers(sanitized);
        setTouchedFields((prev) => {
          const nextTouched = new Set(prev);
          for (const { fieldKey } of fieldsToApply) nextTouched.add(fieldKey);
          return nextTouched;
        });
        setSaveError(null);
        // Commit immediately: review-mode skips DB writes, so Fill is the persistence point.
        // Pass the captured fields explicitly so out-of-structure (not-yet-added) sections persist.
        void handlePersistAnswersRef.current?.(undefined, extraAnswers);
        return;
      }

      // ── Company enrichment fill ─────────────────────────────────────────────
      const questionnaireValues = p?.enrichmentOutput?.questionnaireValues;
      if (!questionnaireValues || Object.keys(questionnaireValues).length === 0) {
        return;
      }

      // Compute synchronously then persist - same rationale as the transcript fill above.
      const nextEnrich = { ...answersRef.current };
      for (const [fieldKey, value] of Object.entries(questionnaireValues)) {
        if (value !== null && value !== undefined && value !== '') {
          setAnswerValueByFieldKey(nextEnrich, fieldKey, value, dealPageFieldLookup);
        }
      }
      const withCalcEnrich = applyCalculationRules(questionnaire, nextEnrich, dealPageFieldLookup);
      const sanitizedEnrich = sanitizeQuestionnaireAnswers(
        questionnaire,
        withCalcEnrich,
        dealPageFieldLookup
      );
      answersRef.current = sanitizedEnrich;
      setAnswers(sanitizedEnrich);
      setTouchedFields((prev) => {
        const next = new Set(prev);
        for (const fieldKey of Object.keys(questionnaireValues)) {
          next.add(fieldKey);
        }
        return next;
      });
      setSaveError(null);
      // Commit immediately so enriched company/questionnaire values survive reload.
      void handlePersistAnswersRef.current?.();
    },
    [
      questionnaire,
      dealPageFieldLookup,
      refetchQuestionnaire,
      refetchDocuments,
      meetingContext.refetch,
      dealUuid,
      isDocViewAllowed,
    ]
  );

  // Always-current ref so the AiChatActionContext can serve as a stable fallback,
  // bypassing any stale closure in the rightRailItems memo.
  const handleChatActionRef = React.useRef<AiMessageActionHandler | undefined>(undefined);
  handleChatActionRef.current = handleChatAction;

  // Clickable chat references ([N] sources): open the grounded reference-estimate corpus doc
  // in the dialog. referenceId carries the corpus document uuid (set by the generation skill);
  // items (when present) are the matched line(s) so the dialog highlights them, not the flat sheet.
  const [chatReference, setChatReference] = React.useState<EstimateGroundingRef | null>(null);
  const chatReferenceHandlerRef = React.useRef<((ref: RagReference) => void) | undefined>(
    undefined
  );
  chatReferenceHandlerRef.current = (ref) =>
    setChatReference({
      documentUuid: ref.referenceId,
      ...(ref.title ? { title: ref.title } : {}),
      ...(ref.items && ref.items.length > 0 ? { items: ref.items } : {}),
    });

  const handlePersistAnswers = React.useCallback(
    async (
      question?: SalesDealTemplateGroupSummary,
      // Answers for fields that may NOT be in the currently-visible questionnaire (e.g. a
      // transcript Fill on an additive deal whose answered sections aren't added yet). They
      // are appended to the built payload so the server persists them AND auto-includes their
      // groups (upsertDealQuestionnaireAnswers → includeGroupsForAnsweredFieldKeys) - otherwise
      // buildQuestionnaireSavePayload, which only walks the visible structure, would drop them.
      extraAnswers?: Array<{ templateFieldId?: number | null; fieldKey: string; value: unknown }>
    ): Promise<boolean> => {
      if (!dealUuid) return false;

      setPendingSaveCount((c) => c + 1);
      setSaveError(null);

      try {
        // Flush any pending ad-hoc scope edits FIRST, so the questionnaire returned by the answer
        // save already reflects them (otherwise its reload would revert the optimistic scope meta).
        // Successfully-flushed keys are remembered for the BPR hook below: a scope-brief edit
        // (problem / proposed solution / boundaries) is a user-defined change to its section, so it
        // must queue that section's future-state refresh even when no answer VALUE changed.
        const scopeFlushedFieldKeys = new Set<string>();
        const pendingScope = pendingScopeMetaRef.current;
        const scopeEntries = Object.entries(pendingScope);
        if (scopeEntries.length > 0) {
          // Flush per-key with allSettled so one bad field can't abort the whole Save (answers
          // included). A 404 means the ad-hoc field no longer exists (removed/replaced server-side
          // since the edit) - drop that orphaned key and self-heal silently. Any other failure is a
          // real error: keep the key pending and re-throw so the catch below surfaces it.
          const results = await Promise.allSettled(
            scopeEntries.map(([fk, meta]) => salesApi.updateAdhocScopeMeta(dealUuid, fk, meta))
          );
          const resolvedKeys: string[] = [];
          let genuineError: unknown;
          results.forEach((result, i) => {
            const fk = scopeEntries[i]![0];
            if (result.status === 'fulfilled') {
              resolvedKeys.push(fk);
              scopeFlushedFieldKeys.add(fk);
            } else if (result.reason instanceof ApiError && result.reason.statusCode === 404) {
              resolvedKeys.push(fk); // orphaned field - treat as resolved (drop it)
            } else if (genuineError === undefined) {
              genuineError = result.reason;
            }
          });
          // Clear succeeded + 404-pruned keys; keep any genuinely-failed key pending.
          setPendingScopeMeta((prev) => {
            const next = { ...prev };
            for (const fk of resolvedKeys) delete next[fk];
            return next;
          });
          for (const fk of resolvedKeys) delete scopeMetaBaselineRef.current[fk];
          if (genuineError !== undefined) throw genuineError;
        }

        const saveSnapshot = answersRef.current;
        const builtPayload = question
          ? buildQuestionSavePayload(
              question,
              saveSnapshot,
              dealPageFieldLookup,
              questionnaire?.conditionalRules ?? []
            )
          : buildQuestionnaireSavePayload(questionnaire, saveSnapshot);
        // Append extra answers (deduped, last-wins) so out-of-structure fields are saved too.
        const payload = extraAnswers?.length
          ? Array.from(
              new Map(
                [...builtPayload, ...extraAnswers].map((e) => [
                  String(e.templateFieldId ?? e.fieldKey),
                  e,
                ])
              ).values()
            )
          : builtPayload;
        const { questionnaire: updatedRawQuestionnaire } = await salesApi.saveDealQuestionnaire(
          dealUuid,
          payload
        );
        // Invalidate any in-flight questionnaire reload: the save response already
        // carries fresh server state so a concurrent reload would only overwrite it
        // with stale data (the DB snapshot taken before this save completed).
        pendingReloadTokenRef.current = null;
        // nextAnswers reflects pure server state - used for setPersistedAnswers so
        // the dirty comparison remains accurate (unsaved fields from other groups
        // stay dirty rather than being falsely promoted to "persisted").
        const nextAnswers = buildInitialAnswers(updatedRawQuestionnaire);
        // For partial saves, build a patched copy for mergeSavedAnswers so fields
        // that were NOT sent to the server retain their current local value.
        // Without this patch, mergeSavedAnswers would see currentValue ===
        // snapshotValue for those fields and overwrite local state with the DB
        // value (null), visually resetting unsaved fields in other groups.
        let nextAnswersForMerge = nextAnswers;
        if (question) {
          const savedFieldKeys = new Set(
            payload.map((e) => String(e.templateFieldId ?? e.fieldKey))
          );
          nextAnswersForMerge = { ...nextAnswers };
          for (const key of Object.keys(answersRef.current)) {
            if (!savedFieldKeys.has(key)) {
              nextAnswersForMerge[key] = answersRef.current[key] ?? null;
            }
          }
        }
        const latestAnswers = answersRef.current;
        const mergedAnswers = mergeSavedAnswers(
          updatedRawQuestionnaire,
          latestAnswers,
          nextAnswersForMerge,
          saveSnapshot
        );
        const nextChangedFieldKeys = getChangedAnswerKeys(mergedAnswers, nextAnswers);

        // Diagram-layer deals: a saved answer is a user-defined change to its
        // section, so queue that section's FUTURE-STATE process flow for
        // regeneration. Fire-and-forget by design - the save already succeeded,
        // and the diagram store no-ops for sections without a workflow diagram
        // (e.g. Company) and for deals without generated diagrams.
        if (dealDiagramsOn) {
          const persistedBefore = new Map<string, unknown>();
          for (const section of questionnaire?.sections ?? []) {
            for (const group of section.groups) {
              for (const field of group.fields) {
                persistedBefore.set(field.fieldKey, field.valueIsDefault ? null : field.value);
              }
            }
          }
          const changedSectionKeys = new Set<string>();
          for (const section of updatedRawQuestionnaire.sections) {
            for (const group of section.groups) {
              for (const field of group.fields) {
                // A flushed scope-brief edit counts as a section change even
                // when the answer value itself is untouched.
                if (scopeFlushedFieldKeys.has(field.fieldKey)) {
                  changedSectionKeys.add(section.sectionKey);
                  break;
                }
                const after = field.valueIsDefault ? null : (field.value ?? null);
                const before = persistedBefore.get(field.fieldKey) ?? null;
                if (JSON.stringify(before) !== JSON.stringify(after)) {
                  changedSectionKeys.add(section.sectionKey);
                  break;
                }
              }
            }
          }
          if (changedSectionKeys.size > 0) {
            refreshDealFutureState(dealUuid, [...changedSectionKeys]);
          }
        }

        // Preserve per-user `permissions` if a mutation response ever omits it - the save response
        // overwrites the same state that drives the "All future deals" gate, and dropping
        // permissions would disable template authoring until the next GET reload.
        setQuestionnaire((prev) => ({
          ...updatedRawQuestionnaire,
          permissions: updatedRawQuestionnaire.permissions ?? prev?.permissions,
        }));
        setPersistedAnswers(buildPersistedAnswers(updatedRawQuestionnaire));
        answersRef.current = mergedAnswers;
        setAnswers(mergedAnswers);
        setTouchedFields(
          (currentTouchedFields) =>
            new Set(
              [...currentTouchedFields].filter((fieldKey) => nextChangedFieldKeys.has(fieldKey))
            )
        );
        return true;
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : 'Failed to save questionnaire');
        return false;
      } finally {
        setPendingSaveCount((c) => c - 1);
      }
    },
    [dealUuid, questionnaire, dealPageFieldLookup, dealDiagramsOn]
  );
  // Keep the ref current so the chat-action Fill handlers (defined above) can persist.
  handlePersistAnswersRef.current = handlePersistAnswers;

  // Regenerate-estimate re-fill: estimate-sourced questionnaire defaults (the
  // managed-services contract prefills - hours, cost, ticket/initiative counts,
  // capacity, recommended terms/rate) must track the NEW estimate even when their
  // previous values were saved, and a saved answer normally beats a default. So
  // after an estimate regeneration completes (see the chat-run completion effect),
  // persist NULL for every estimate-sourced field in one save - the save response
  // then resolves fresh defaults from the new estimate and delivers them in the
  // same round trip. Falls back to a plain reload when the template has no such
  // fields or the save fails.
  const refillEstimateDerivedAnswers = React.useCallback(async () => {
    const extras: Array<{ templateFieldId?: number | null; fieldKey: string; value: unknown }> =
      [];
    for (const section of questionnaire?.sections ?? []) {
      for (const group of section.groups) {
        for (const field of group.fields) {
          if (field.displayHints?.profileDefault?.profile === 'estimate') {
            extras.push({
              templateFieldId: field.templateFieldId,
              fieldKey: field.fieldKey,
              value: null,
            });
          }
        }
      }
    }
    if (extras.length === 0) {
      await handleReloadQuestionnaire();
      return;
    }
    const ok = await handlePersistAnswers(undefined, extras);
    if (!ok) await handleReloadQuestionnaire();
  }, [questionnaire, handlePersistAnswers, handleReloadQuestionnaire]);
  refillEstimateDerivedAnswersRef.current = refillEstimateDerivedAnswers;

  const handleUpdateDealTitle = React.useCallback(
    async (newTitle: string) => {
      if (!dealUuid) return;
      // Optimistic update
      setDeal((prev) => (prev ? { ...prev, title: newTitle } : prev));
      try {
        const { deal: updated } = await salesApi.updateDealTitle(dealUuid, newTitle);
        setDeal(updated);
      } catch {
        // Revert on failure - re-fetch to get server state
        const { deal: current } = await salesApi.getDeal(dealUuid);
        setDeal(current);
      }
    },
    [dealUuid]
  );

  const handleUpdateDealAttribute = React.useCallback(
    async (attributeKey: string, valueKey: string | null) => {
      if (!dealUuid) return;
      // Optimistic update
      setDealAttributes((prev) =>
        prev.map((a) => (a.attributeKey === attributeKey ? { ...a, currentValue: valueKey } : a))
      );
      try {
        await salesApi.updateDealAttribute(dealUuid, attributeKey, valueKey);
      } catch {
        // Revert - re-fetch authoritative state
        try {
          const { data } = await salesApi.getDealAttributes(dealUuid);
          setDealAttributes(data);
        } catch {
          // ignore secondary failure
        }
      }
    },
    [dealUuid]
  );

  const handleOpenAttributesPane = React.useCallback(() => {
    setRightPaneVisible(true);
    setRightPaneMode('deal-attributes');
  }, [setRightPaneVisible, setRightPaneMode]);

  // Live-save for the Scope tab's Notion property panel (Company Overview / Primary Contact). Each
  // control change updates the shared in-memory answers immediately (so the Questionnaire tab
  // reflects it) and debounces a full questionnaire persist - the deal page has no autosave, so this
  // is what makes the property controls "save immediately". Debounced so a burst of edits coalesces.
  const scopePropertySaveTimerRef = React.useRef<number | null>(null);
  const handleScopePropertyAnswerChange = React.useCallback(
    (fieldKey: string, value: unknown) => {
      handleAnswerChange(fieldKey, value);
      if (scopePropertySaveTimerRef.current !== null) {
        window.clearTimeout(scopePropertySaveTimerRef.current);
      }
      scopePropertySaveTimerRef.current = window.setTimeout(() => {
        scopePropertySaveTimerRef.current = null;
        void handlePersistAnswers().then((ok) => {
          if (ok) flashScopeSaved();
        });
      }, 700);
    },
    [handleAnswerChange, handlePersistAnswers, flashScopeSaved]
  );
  React.useEffect(
    () => () => {
      if (scopePropertySaveTimerRef.current !== null) {
        window.clearTimeout(scopePropertySaveTimerRef.current);
      }
    },
    []
  );

  // Refs so async callbacks always read the latest pane state without
  // stale-closure issues.
  const rightPaneVisibleRef = React.useRef(rightPaneVisible);
  const rightPaneModeRef = React.useRef(rightPaneMode);
  React.useEffect(() => {
    rightPaneVisibleRef.current = rightPaneVisible;
  }, [rightPaneVisible]);
  React.useEffect(() => {
    rightPaneModeRef.current = rightPaneMode;
  }, [rightPaneMode]);

  React.useEffect(() => {
    if (!rightPaneVisible || rightPaneMode !== 'deal-sow') {
      setSowEditing(false);
      setSowDraftContent(null);
    }
  }, [rightPaneMode, rightPaneVisible]);

  // ── SOW autosave (deal edit route) ────────────────────────────────────────
  // The SOW editor on the edit route autosaves the whole tiptap doc - there is
  // no manual Save/Cancel. Mirrors the estimate AI-summary hint (Saving… → Saved).
  // The editor keeps its draft as the source of truth, so a background save never
  // resets the cursor (the draft stays non-null, matching the editor's own JSON).
  const [sowSaveStatus, setSowSaveStatus] = React.useState<'idle' | 'saving' | 'saved'>('idle');
  const sowSaveTimerRef = React.useRef<number | null>(null);
  const sowHintTimerRef = React.useRef<number | null>(null);
  const sowSavingRef = React.useRef(false);
  const sowSavedSnapshotRef = React.useRef<string | null>(null);
  const sowDraftRef = React.useRef(sowDraftContent);
  sowDraftRef.current = sowDraftContent;
  const sowRowRef = React.useRef(sow);
  sowRowRef.current = sow;

  const autoSaveSow = React.useCallback(async () => {
    const draft = sowDraftRef.current;
    const row = sowRowRef.current;
    if (!dealUuid || !row || !draft || sowSavingRef.current) return;
    // Strip the view-only `sowDataTable` overlay nodes (injected into the editor from
    // the contract) before persisting, so stored tiptapContent stays prose-only. The
    // snapshot guard is seeded from the prose-only saved sow.tiptapContent, so comparing
    // on `cleaned` keeps it consistent (no phantom-save on open); the tables re-inject
    // idempotently on the next edit session.
    const cleaned = stripSowDataTableNodes(draft) ?? draft;
    const snapshot = JSON.stringify(cleaned);
    if (snapshot === sowSavedSnapshotRef.current) return; // nothing new since last save
    sowSavingRef.current = true;
    setSavingSow(true);
    setSowSaveStatus('saving');
    setSowError(null);
    try {
      const { sow: nextSow } = await salesApi.updateSow(dealUuid, row.uuid, {
        tiptapContent: cleaned,
      });
      sowSavedSnapshotRef.current = snapshot;
      setSow(nextSow);
      setSowSaveStatus('saved');
      if (sowHintTimerRef.current != null) window.clearTimeout(sowHintTimerRef.current);
      sowHintTimerRef.current = window.setTimeout(() => setSowSaveStatus('idle'), 1500);
    } catch (error) {
      setSowError(error instanceof Error ? error.message : 'Failed to save SOW');
      setSowSaveStatus('idle');
    } finally {
      sowSavingRef.current = false;
      setSavingSow(false);
    }
  }, [dealUuid, setSavingSow, setSow, setSowError]);

  // Seed the saved-snapshot guard from the loaded SOW when entering edit mode (and
  // reset it when switching deals/SOWs). Without a baseline, the first non-null draft
  // always autosaves regardless of content; seeding it means an unchanged draft is a
  // no-op, so opening the editor never phantom-saves.
  React.useEffect(() => {
    sowSavedSnapshotRef.current =
      isEditMode && sow?.tiptapContent ? JSON.stringify(sow.tiptapContent) : null;
  }, [dealUuid, isEditMode, sow?.uuid]);

  // Debounced autosave: re-armed on every edit while on the edit route.
  React.useEffect(() => {
    if (!isEditMode || viewingPastSow || !sowDraftContent) return;
    if (sowSaveTimerRef.current != null) window.clearTimeout(sowSaveTimerRef.current);
    sowSaveTimerRef.current = window.setTimeout(() => void autoSaveSow(), 1000);
    return () => {
      if (sowSaveTimerRef.current != null) {
        window.clearTimeout(sowSaveTimerRef.current);
        sowSaveTimerRef.current = null;
      }
    };
  }, [sowDraftContent, isEditMode, viewingPastSow, autoSaveSow]);

  // Leaving the edit route: flush a still-pending edit so the last keystrokes are
  // not dropped, then drop the draft so the next session starts from the saved sow.
  React.useEffect(() => {
    if (!isEditMode) return;
    return () => {
      if (sowSaveTimerRef.current != null) {
        window.clearTimeout(sowSaveTimerRef.current);
        sowSaveTimerRef.current = null;
      }
      void autoSaveSow();
      setSowDraftContent(null);
    };
  }, [isEditMode, autoSaveSow, setSowDraftContent]);

  // Always view/edit the latest persisted SOW. `generateSow` writes a new row and
  // the export reads the newest one, so a stale cached `sow` would show old content
  // and orphan edits. Refetch when the SOW view opens - but never mid-edit, so an
  // in-progress draft isn't clobbered.
  React.useEffect(() => {
    if (viewingDoc?.kind !== 'sow' || !dealUuid || isEditMode) return;
    void salesApi
      .getSow(dealUuid)
      .then(({ sow: latest }) => {
        if (latest) setSow(latest);
      })
      .catch(() => {});
  }, [viewingDoc?.kind, dealUuid, isEditMode]);

  const handleSectionOverride = React.useCallback(
    async (key: string, overrideHours: number | null): Promise<void> => {
      if (!dealUuid) return;
      try {
        const { savedEstimate: updated } = await salesApi.patchEstimateOverride(
          dealUuid,
          key,
          overrideHours
        );
        setSavedEstimate(updated);
        setEstimateResult(updated.estimateData);

        // Also refetch the resource plan so the Resource Plan right pane updates its totals inline.
        try {
          const res = await salesApi.getResourcePlan(dealUuid);
          setResourcePlan(res.plan);
        } catch {
          // Ignore if there is no active resource plan
        }
      } catch {
        // Non-blocking - override still shows optimistically
      }
    },
    [dealUuid]
  );

  const handleSectionOverrideReason = React.useCallback(
    async (key: string, reason: string): Promise<void> => {
      if (!dealUuid) return;
      setSavedEstimate((current) => {
        if (!current) return current;
        const existing = current.estimateData.overrides?.[key];
        if (!existing) return current;
        return {
          ...current,
          estimateData: {
            ...current.estimateData,
            overrides: {
              ...current.estimateData.overrides,
              [key]: { ...existing, overrideReason: reason },
            },
          },
        };
      });
      try {
        const currentOverride = savedEstimate?.estimateData.overrides?.[key];
        if (!currentOverride) return;
        const { savedEstimate: updated } = await salesApi.patchEstimateOverride(
          dealUuid,
          key,
          currentOverride.overrideHours,
          reason
        );
        setSavedEstimate(updated);
        setEstimateResult(updated.estimateData);
      } catch {
        // Non-blocking
      }
    },
    [dealUuid, savedEstimate]
  );

  const handleSubsectionSummaryEdit = React.useCallback(
    async (
      key: string,
      patch: { customerSummary?: string | null; aiSummary?: string | null }
    ): Promise<void> => {
      if (!dealUuid) return;
      try {
        const { savedEstimate: updated } = await salesApi.patchEstimateSummary(
          dealUuid,
          key,
          patch
        );
        setSavedEstimate(updated);
        setEstimateResult(updated.estimateData);
      } catch {
        // Non-blocking - the edit still shows optimistically in the textarea.
      }
    },
    [dealUuid]
  );

  const handleSubsectionSummarySuggest = React.useCallback(
    async (key: string, kind: 'customer' | 'ai', text: string): Promise<string | null> => {
      if (!dealUuid) return null;
      try {
        const res = await salesApi.suggestEstimateSummary(dealUuid, key, kind, text);
        return res.text ?? null;
      } catch {
        return null;
      }
    },
    [dealUuid]
  );

  const handleDiscountOverride = React.useCallback(
    async (discountPercentageOverride: number | null): Promise<void> => {
      if (!dealUuid) return;
      try {
        const { savedEstimate: updated } = await salesApi.patchEstimateDiscountOverride(
          dealUuid,
          discountPercentageOverride
        );
        setSavedEstimate(updated);
        setEstimateResult(updated.estimateData);

        try {
          const res = await salesApi.getResourcePlan(dealUuid);
          setResourcePlan(res.plan);
        } catch {
          // Ignore if no active resource plan
        }
      } catch {
        // Non-blocking
      }
    },
    [dealUuid]
  );

  const handleRegenerateResourcePlan = React.useCallback(
    async (rateCardProfileId?: number) => {
      if (!dealUuid) return;
      setGeneratingResourcePlan(true);
      try {
        const selectedRateCardProfile =
          rateCardProfiles.find((profile) => profile.id === rateCardProfileId) ??
          rateCardProfiles.find((profile) => profile.isDefault) ??
          rateCardProfiles[0];
        const { plan } = await salesApi.generateResourcePlan(dealUuid, selectedRateCardProfile?.id);
        setResourcePlan(plan);
      } catch (err) {
        setResourcePlanError(
          err instanceof Error ? err.message : 'Failed to regenerate resource plan'
        );
      } finally {
        setGeneratingResourcePlan(false);
      }
    },
    [dealUuid, rateCardProfiles, setGeneratingResourcePlan, setResourcePlan, setResourcePlanError]
  );

  const handleUpdateResourcePlanPricingBasis = React.useCallback(
    async (roleId: string, pricingBasis: ResourcePlanPricingBasis, offshorePercent?: number) => {
      if (!dealUuid || !resourcePlan) return;

      const previousPlan = resourcePlan;
      // Optimistically mirror the server's persistence rules: hybrid carries a
      // resolved offshore %, onshore/offshore carry none (so a switch back can't
      // leave a stale split).
      // Seed a freshly-hybrid role from the workspace's configured default
      // offshore percentage (estimateResult.offshorePercentage), falling back to
      // an existing stored value, then 0 when unconfigured.
      const selection: RolePricingSelection =
        pricingBasis === 'hybrid'
          ? {
              basis: 'hybrid',
              offshorePercent:
                offshorePercent ??
                resourcePlan.rolePricingBasisByRoleId[roleId]?.offshorePercent ??
                estimateResult?.offshorePercentage ??
                0,
            }
          : { basis: pricingBasis };

      setResourcePlanError(null);
      setUpdatingRolePricingBasisId(roleId);
      setResourcePlan({
        ...resourcePlan,
        rolePricingBasisByRoleId: {
          ...resourcePlan.rolePricingBasisByRoleId,
          [roleId]: selection,
        },
      });

      try {
        const { plan } = await salesApi.updateResourcePlanPricingBasis(
          dealUuid,
          roleId,
          pricingBasis,
          selection.offshorePercent
        );
        setResourcePlan(plan ?? previousPlan);
      } catch (err) {
        setResourcePlan(previousPlan);
        setResourcePlanError(
          err instanceof Error ? err.message : 'Failed to update resource plan pricing basis'
        );
      } finally {
        setUpdatingRolePricingBasisId(null);
      }
    },
    [
      dealUuid,
      resourcePlan,
      estimateResult?.offshorePercentage,
      setResourcePlan,
      setResourcePlanError,
    ]
  );

  const handleUpdateResourcePlanRoleAllocation = React.useCallback(
    async (roleId: string, allocationPercent: number | null) => {
      if (!dealUuid || !resourcePlan) return;

      const previousPlan = resourcePlan;
      // Optimistically mirror the server's persistence: hours = round(base × %),
      // stored as `allocationOverride` alongside the role's existing pricing basis
      // (null clears it). `base` is the frozen grand total.
      const base = resourcePlan.resourcingSnapshot.totals.total_hours;
      const entry = resourcePlan.resourcingSnapshot.staffing_plan.find((e) => e.role_id === roleId);
      const existing: RolePricingSelection = resourcePlan.rolePricingBasisByRoleId[roleId] ?? {
        basis: 'onshore',
      };
      let nextSelection: RolePricingSelection;
      if (allocationPercent == null) {
        const { allocationOverride: _dropped, ...rest } = existing;
        nextSelection = rest;
      } else {
        const fraction = Math.min(100, Math.max(0, allocationPercent)) / 100;
        nextSelection = {
          ...existing,
          allocationOverride: {
            percent: fraction,
            hours: Math.round(base * fraction),
            originalHours: entry?.hours ?? 0,
            overriddenAt: new Date().toISOString(),
          },
        };
      }

      setResourcePlanError(null);
      setUpdatingRoleAllocationId(roleId);
      setResourcePlan({
        ...resourcePlan,
        rolePricingBasisByRoleId: {
          ...resourcePlan.rolePricingBasisByRoleId,
          [roleId]: nextSelection,
        },
      });

      try {
        const { plan } = await salesApi.updateResourcePlanRoleAllocation(
          dealUuid,
          roleId,
          allocationPercent
        );
        setResourcePlan(plan ?? previousPlan);
      } catch (err) {
        setResourcePlan(previousPlan);
        setResourcePlanError(
          err instanceof Error ? err.message : 'Failed to update role allocation'
        );
      } finally {
        setUpdatingRoleAllocationId(null);
      }
    },
    [dealUuid, resourcePlan, setResourcePlan, setResourcePlanError]
  );

  // Set the full desired role roster (the multi-select). We update the table
  // optimistically (added roles are 0h/0%, removed roles drop - both exactly
  // representable client-side, so no flicker) for instant feedback, then swap in the
  // server's recomputed plan. A seq guard makes the latest toggle win so rapid
  // add/removes can't be clobbered by an out-of-order response.
  const rolesUpdateSeqRef = React.useRef(0);
  const handleSetResourcePlanRoles = React.useCallback(
    async (roleIds: string[]) => {
      if (!dealUuid || !resourcePlan) return;
      const previousPlan = resourcePlan;

      const snap = resourcePlan.resourcingSnapshot;
      const desired = new Set(roleIds);
      const existingIds = new Set(snap.staffing_plan.map((e) => e.role_id));
      const kept = snap.staffing_plan.filter((e) => desired.has(e.role_id));
      const added: StaffingPlanEntry[] = roleIds
        .filter((id) => !existingIds.has(id))
        .map((id) => {
          const meta = availableRoles.find((r) => r.role_id === id);
          return {
            role_id: id,
            role_name: meta?.role_name ?? id,
            level: (meta?.level ?? 'mid') as ResourcePlanRoleLevel,
            category: meta?.category ?? 'uncategorized',
            hours: 0,
            cost: { onshore: 0, offshore: 0, hybrid: 0 },
            allocation: 'limited',
            allocation_fraction: 0,
            weekly_hours_by_phase: {},
          };
        });
      const staffingPlan = [...kept, ...added];
      const optimisticPlan = {
        ...resourcePlan,
        resourcingSnapshot: {
          ...snap,
          staffing_plan: staffingPlan,
          totals: {
            ...snap.totals,
            total_hours: kept.reduce((s, e) => s + e.hours, 0),
            total_cost: {
              onshore: kept.reduce((s, e) => s + e.cost.onshore, 0),
              offshore: kept.reduce((s, e) => s + e.cost.offshore, 0),
              hybrid: kept.reduce((s, e) => s + e.cost.hybrid, 0),
            },
          },
        },
      };

      setResourcePlanError(null);
      setUpdatingRoles(true);
      setResourcePlan(optimisticPlan);
      const seq = ++rolesUpdateSeqRef.current;
      try {
        const { plan } = await salesApi.setResourcePlanRoles(dealUuid, roleIds);
        if (seq === rolesUpdateSeqRef.current) setResourcePlan(plan ?? optimisticPlan);
      } catch (err) {
        if (seq === rolesUpdateSeqRef.current) {
          setResourcePlan(previousPlan);
          setResourcePlanError(err instanceof Error ? err.message : 'Failed to update roles');
        }
      } finally {
        if (seq === rolesUpdateSeqRef.current) setUpdatingRoles(false);
      }
    },
    [dealUuid, resourcePlan, availableRoles, setResourcePlan, setResourcePlanError]
  );

  // Load the workspace role catalog for the role picker (once per deal; the catalog
  // is product/category-scoped and stable for a deal).
  React.useEffect(() => {
    if (!dealUuid) {
      setAvailableRoles([]);
      return;
    }
    let cancelled = false;
    salesApi
      .listResourcePlanAvailableRoles(dealUuid)
      .then((res) => {
        if (!cancelled) setAvailableRoles(res.roles);
      })
      .catch(() => {
        if (!cancelled) setAvailableRoles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dealUuid]);

  // ── Per-artifact generation runs ────────────────────────────────────────────
  // The deal pipeline is split into two independently-runnable AI actions so each
  // artifact gets its OWN chat message + narration scoped to its steps:
  //   - runEstimateGeneration -> agent `regenerate_estimate` (scope hours, estimate,
  //     line-item notes, resource plan). Does NOT touch the SOW.
  //   - runSowGeneration      -> agent `regenerate_sow` (SOW only, reads the saved
  //     estimate + resource plan).
  // "Generate all" composes them as a sequence (estimate run, then SOW run on success)
  // so it produces two chat messages, not one combined run.

  // Staffing rates (COS-TBD mcp-missing-staffing, Harman 2026-10-20): before generating or
  // exporting priced output, ask the server whether the deal's roles all have rates. When
  // they do not, the output would be 0 hours / $0, so show the blocking pop-up instead.
  // Fails open on a read error - the API refuses those routes itself (409) regardless.
  const [staffingBlock, setStaffingBlock] = React.useState<StaffingCoverage | null>(null);
  const checkStaffingRates = React.useCallback(async (): Promise<boolean> => {
    if (!dealUuid) return true;
    try {
      const coverage = await salesApi.getStaffingCoverage(dealUuid);
      if (coverage?.blocking === true && Array.isArray(coverage.missingRoles)) {
        setStaffingBlock(coverage);
        return false;
      }
    } catch {
      // Fail open.
    }
    return true;
  }, [dealUuid]);

  const runEstimateGeneration = React.useCallback(
    (opts: {
      keepOverrides: boolean;
      reinferHours: boolean;
      label: string;
      // Fired once the estimate run completes successfully (used by "Generate all" to
      // chain the SOW run). Not called on error.
      onCompletedSuccess?: () => void;
    }) => {
      if (!dealUuid || !canGenerateEstimate || savingEstimate || generatingResourcePlan) {
        return;
      }
      const { keepOverrides, reinferHours, label, onCompletedSuccess } = opts;

      // Open the AI chat pane so the user can see generation progress.
      setRightPaneVisible(true);
      setRightPaneMode('chat');
      setRightPaneWidth(Math.floor(window.innerWidth * 0.4));

      // Mark active immediately so the todo drawer renders before any async work.
      setSavingEstimate(true);
      // Clear existing results so panes show skeletons while generating.
      setEstimateResult(null);
      setResourcePlan(null);
      setResourcePlanError(null);
      setEstimateError(null);
      setLoadingEstimate(true);
      setLoadingResourcePlan(true);

      const dealLabel = deal?.title ?? deal?.company?.name ?? 'this deal';
      chatSession.sendRun({
        prompt: `regenerate cost estimate and resource plan for ${dealLabel}`,
        agentKey: 'regenerate_estimate',
        moduleKey: 'sales',
        origin: { page: 'Deal', label },
        context: {
          workspaceUuid: workspace?.uuid,
          dealUuid,
          pageScope: 'deal',
          // keepOverrides folds the prior estimate's manual hour overrides into the fresh
          // estimate; reinferHours re-infers all scope-item hours from scratch.
          keepOverrides,
          reinferHours,
        },
        beforeSend: async () => {
          // Persist before the skill runs so frontend-applied defaults are written to DB
          // before the readiness check.
          const saved = await handlePersistAnswers();
          if (!saved) {
            setSavingEstimate(false);
            setLoadingEstimate(false);
            setLoadingResourcePlan(false);
            setEstimateError(
              'Save the latest questionnaire changes before generating the estimate.'
            );
            return false;
          }
          if (!(await checkStaffingRates())) {
            setSavingEstimate(false);
            setLoadingEstimate(false);
            setLoadingResourcePlan(false);
            return false;
          }
          return true;
        },
        onRunEvent: (event) => {
          if (event.type === 'run_completed') {
            setSavingEstimate(false);
            setGeneratingResourcePlan(false);
            setLoadingEstimate(false);
            setLoadingResourcePlan(false);
            // BPR / CI: Generate marks a recommended solution per scope item (COS-92) - reload so
            // the briefs show its "Auto-selected" card.
            if (solutionCandidatesEnabledForCategory(deal?.dealCategoryKey)) {
              void handleReloadQuestionnaire();
            }
            void Promise.all([
              salesApi.getSavedEstimate(dealUuid),
              salesApi.getResourcePlan(dealUuid),
            ]).then(([estimateRes, planRes]) => {
              const se = estimateRes.savedEstimate;
              const plan = planRes.plan;
              if (se) {
                setSavedEstimate(se);
                setEstimateResult(se.estimateData);
              }
              if (plan) setResourcePlan(plan);
              // Refresh deal to reflect the 'generated' status transition.
              void salesApi
                .getDeal(dealUuid)
                .then(({ deal: refreshedDeal }) => setDeal(refreshedDeal))
                .catch(() => {});
            });
            onCompletedSuccess?.();
          } else if (event.type === 'run_error') {
            setSavingEstimate(false);
            setGeneratingResourcePlan(false);
            setLoadingEstimate(false);
            setLoadingResourcePlan(false);
            const msg = event.message ?? 'Generation failed';
            setEstimateError(msg);
            setResourcePlanError(msg);
          }
        },
      });
    },
    [
      checkStaffingRates,
      deal,
      dealUuid,
      canGenerateEstimate,
      savingEstimate,
      generatingResourcePlan,
      handlePersistAnswers,
      setRightPaneMode,
      setRightPaneVisible,
      setRightPaneWidth,
      chatSession.sendRun,
      workspace?.uuid,
    ]
  );

  const runSowGeneration = React.useCallback(
    (opts: { keepSowOverrides: boolean; label: string }) => {
      if (!dealUuid) return;
      // SOW generation is supported per product (SOW_SUPPORTED_PRODUCT_KEYS, pinned to the
      // API's SOW config registry). Tolerant of bare ('netsuite') or composite
      // ('oracle:netsuite') product keys.
      const sowProductKey = normalizeDealProductKey(deal);
      const isSowSupported = isSowSupportedProductKey(sowProductKey);
      if (!isSowSupported) return;
      const { keepSowOverrides, label } = opts;

      setRightPaneVisible(true);
      setRightPaneMode('chat');
      setRightPaneWidth(Math.floor(window.innerWidth * 0.4));

      setGeneratingSow(true);
      setSow(null);
      setSowEditing(false);
      setSowDraftContent(null);
      setSowError(null);
      setSowReadinessReport(null);
      setLoadingSow(true);

      const dealLabel = deal?.title ?? deal?.company?.name ?? 'this deal';
      chatSession.sendRun({
        prompt: `regenerate statement of work for ${dealLabel}`,
        agentKey: 'regenerate_sow',
        moduleKey: 'sales',
        origin: { page: 'Deal', label },
        context: {
          workspaceUuid: workspace?.uuid,
          dealUuid,
          pageScope: 'deal',
          // The regenerate_sow agent reads `keepOverrides` as "keep my SOW edits": it carries
          // the prior SOW's structured overrides (cells / token values / removed sections)
          // onto the regenerated document. Prose edits cannot be carried.
          keepOverrides: keepSowOverrides,
        },
        beforeSend: async () => {
          // Persist answers first so any questionnaire-derived SOW tokens reflect the
          // latest saved values (the SOW reads the saved estimate, but tokens read fields).
          const saved = await handlePersistAnswers();
          if (!saved) {
            setGeneratingSow(false);
            setLoadingSow(false);
            setSowError('Save the latest questionnaire changes before generating the SOW.');
            return false;
          }
          if (!(await checkStaffingRates())) {
            setGeneratingSow(false);
            setLoadingSow(false);
            return false;
          }
          return true;
        },
        onRunEvent: (event) => {
          if (event.type === 'run_completed') {
            setGeneratingSow(false);
            setLoadingSow(false);
            void salesApi.getSow(dealUuid).then((sowRes) => {
              const newSow = sowRes?.sow;
              if (newSow) {
                setSow(newSow);
                setSowEditing(false);
                setSowDraftContent(null);
                // The regenerated SOW changed the document: drop the cached render context and
                // remount the SOW pane (key bump) so the styled view + editor re-fetch a fresh
                // assembly instead of showing the stale pre-regen content until a page refresh.
                clearSowRenderContextCache(dealUuid);
                setSowRefreshKey((k) => k + 1);
              }
              void salesApi
                .getDeal(dealUuid)
                .then(({ deal: refreshedDeal }) => setDeal(refreshedDeal))
                .catch(() => {});
            });
          } else if (event.type === 'run_error') {
            setGeneratingSow(false);
            setLoadingSow(false);
            setSowError(event.message ?? 'SOW generation failed');
          }
        },
      });
    },
    [
      checkStaffingRates,
      deal,
      dealUuid,
      handlePersistAnswers,
      setRightPaneMode,
      setRightPaneVisible,
      setRightPaneWidth,
      chatSession.sendRun,
      workspace?.uuid,
    ]
  );

  // Preview SOW (static-SOW categories, e.g. managed_services): persist the latest
  // answers, generate the SOW directly ONLY when the deal has none yet (a static
  // document's contract bindings re-resolve saved answers on every render, so an
  // existing SOW already previews fresh values - regenerating instead would clobber
  // manual prose edits), then open the SOW tab. Generation errors (message +
  // readiness blocker list) render on the SOW pane, so navigation still happens;
  // a failed answer-save keeps the user on the questionnaire where saveError shows.
  const [previewingSow, setPreviewingSow] = React.useState(false);
  const handleWizardPreviewSow = React.useCallback(async () => {
    if (!dealUuid || previewingSow) return;
    setPreviewingSow(true);
    setSowError(null);
    setSowReadinessReport(null);
    try {
      const saved = await handlePersistAnswers();
      if (!saved) {
        setSowError('Save the latest questionnaire changes before generating the SOW.');
        return;
      }
      if (!sow) {
        setLoadingSow(true);
        const { sow: newSow } = await salesApi.generateSow(dealUuid);
        setSow(newSow);
        setSowEditing(false);
        setSowDraftContent(null);
        // A fresh SOW changed the document: drop the cached render context and remount
        // the SOW pane so the styled view fetches the new assembly (same contract as
        // runSowGeneration's post-success updates).
        clearSowRenderContextCache(dealUuid);
        setSowRefreshKey((k) => k + 1);
        void salesApi
          .getDeal(dealUuid)
          .then(({ deal: refreshedDeal }) => setDeal(refreshedDeal))
          .catch(() => {});
      }
      setViewingDoc({ kind: 'sow' });
    } catch (err) {
      if (err instanceof SowReadinessError) {
        setSowReadinessReport(err.readinessReport);
      }
      setSowError(err instanceof Error ? err.message : 'SOW generation failed');
      setViewingDoc({ kind: 'sow' });
    } finally {
      setPreviewingSow(false);
      setLoadingSow(false);
    }
  }, [
    dealUuid,
    previewingSow,
    sow,
    handlePersistAnswers,
    setSowError,
    setSowReadinessReport,
    setLoadingSow,
    setSow,
    setSowEditing,
    setSowDraftContent,
    setViewingDoc,
  ]);

  // "Generate all": ONE combined run through the sales_deal_generation agent (estimate ->
  // resource plan -> SOW in a single skill run), so it produces ONE chat message whose steps
  // and narration interleave chronologically - instead of chaining two separate runs (two
  // messages). The single skill handles a SOW-unsupported category gracefully (it just skips
  // the SOW step), so the caller no longer branches on isSowSupported for dispatch.
  const runFullGeneration = React.useCallback(
    (opts: {
      keepOverrides: boolean;
      reinferHours: boolean;
      keepSowOverrides: boolean;
      label: string;
    }) => {
      if (!dealUuid || !canGenerateEstimate || savingEstimate || generatingResourcePlan) {
        return;
      }
      const { keepOverrides, reinferHours, keepSowOverrides, label } = opts;

      const sowProductKey = normalizeDealProductKey(deal);
      const isSowSupported = isSowSupportedProductKey(sowProductKey);

      // Open the AI chat pane so the user can see generation progress.
      setRightPaneVisible(true);
      setRightPaneMode('chat');
      setRightPaneWidth(Math.floor(window.innerWidth * 0.4));

      // Estimate + resource plan panes -> skeleton while generating.
      setSavingEstimate(true);
      setEstimateResult(null);
      setResourcePlan(null);
      setResourcePlanError(null);
      setEstimateError(null);
      setLoadingEstimate(true);
      setLoadingResourcePlan(true);

      // SOW pane -> skeleton too (the same run rebuilds it), when the category supports one.
      if (isSowSupported) {
        setGeneratingSow(true);
        setSow(null);
        setSowEditing(false);
        setSowDraftContent(null);
        setSowError(null);
        setSowReadinessReport(null);
        setLoadingSow(true);
      }

      const dealLabel = deal?.title ?? deal?.company?.name ?? 'this deal';
      chatSession.sendRun({
        prompt: `generate cost estimate, resource plan, and statement of work for ${dealLabel}`,
        agentKey: 'sales_deal_generation',
        moduleKey: 'sales',
        origin: { page: 'Deal', label },
        context: {
          workspaceUuid: workspace?.uuid,
          dealUuid,
          pageScope: 'deal',
          keepOverrides,
          reinferHours,
          keepSowOverrides,
        },
        beforeSend: async () => {
          const saved = await handlePersistAnswers();
          if (!saved) {
            setSavingEstimate(false);
            setGeneratingResourcePlan(false);
            setLoadingEstimate(false);
            setLoadingResourcePlan(false);
            setGeneratingSow(false);
            setLoadingSow(false);
            setEstimateError(
              'Save the latest questionnaire changes before generating the estimate.'
            );
            return false;
          }
          if (!(await checkStaffingRates())) {
            setSavingEstimate(false);
            setGeneratingResourcePlan(false);
            setLoadingEstimate(false);
            setLoadingResourcePlan(false);
            setGeneratingSow(false);
            setLoadingSow(false);
            return false;
          }
          return true;
        },
        onRunEvent: (event) => {
          if (event.type === 'run_completed') {
            setSavingEstimate(false);
            setGeneratingResourcePlan(false);
            setLoadingEstimate(false);
            setLoadingResourcePlan(false);
            // BPR / CI: Generate marks a recommended solution per scope item (COS-92) - reload so
            // the briefs show its "Auto-selected" card.
            if (solutionCandidatesEnabledForCategory(deal?.dealCategoryKey)) {
              void handleReloadQuestionnaire();
            }
            setGeneratingSow(false);
            setLoadingSow(false);
            void Promise.all([
              salesApi.getSavedEstimate(dealUuid),
              salesApi.getResourcePlan(dealUuid),
            ]).then(([estimateRes, planRes]) => {
              const se = estimateRes.savedEstimate;
              const plan = planRes.plan;
              if (se) {
                setSavedEstimate(se);
                setEstimateResult(se.estimateData);
              }
              if (plan) setResourcePlan(plan);
            });
            if (isSowSupported) {
              void salesApi.getSow(dealUuid).then((sowRes) => {
                const newSow = sowRes?.sow;
                if (newSow) {
                  setSow(newSow);
                  setSowEditing(false);
                  setSowDraftContent(null);
                  // The regenerated SOW changed the document: drop the cached render context
                  // and remount the SOW pane so it re-fetches a fresh assembly.
                  clearSowRenderContextCache(dealUuid);
                  setSowRefreshKey((k) => k + 1);
                }
              });
            }
            // Refresh deal to reflect the 'generated' status transition.
            void salesApi
              .getDeal(dealUuid)
              .then(({ deal: refreshedDeal }) => setDeal(refreshedDeal))
              .catch(() => {});
          } else if (event.type === 'run_error') {
            setSavingEstimate(false);
            setGeneratingResourcePlan(false);
            setLoadingEstimate(false);
            setLoadingResourcePlan(false);
            setGeneratingSow(false);
            setLoadingSow(false);
            const msg = event.message ?? 'Generation failed';
            setEstimateError(msg);
            setResourcePlanError(msg);
            if (isSowSupported) setSowError(msg);
          }
        },
      });
    },
    [
      checkStaffingRates,
      deal,
      dealUuid,
      canGenerateEstimate,
      savingEstimate,
      generatingResourcePlan,
      handlePersistAnswers,
      setRightPaneMode,
      setRightPaneVisible,
      setRightPaneWidth,
      chatSession.sendRun,
      workspace?.uuid,
    ]
  );

  // "Generate all": one combined run. The keep/reinfer decisions are set by the
  // regenerate-confirm dialog and read here at call time (refs, so this handler need not
  // re-create on every toggle).
  const handleGenerateAll = React.useCallback(() => {
    runFullGeneration({
      keepOverrides: keepOverridesOnRegenerateRef.current,
      reinferHours: reinferHoursOnRegenerateRef.current,
      keepSowOverrides: keepSowOverridesOnRegenerateRef.current,
      label: estimateResult ? 'Regenerate' : 'Generate',
    });
  }, [estimateResult, runFullGeneration]);

  // Estimate-tab "Regenerate": estimate + resource plan only (no SOW).
  // Auto-trigger the estimate when navigating from the create flow with an imported scope list
  // (docs/plans/27). Mirrors the transcript effect below: read the handoff state, clear it so a
  // back-navigation cannot re-fire, then start the SAME run the Regenerate button starts. The rows
  // were written with no hours, so this is what prices them.
  const scopeImportRunRef = React.useRef(false);
  React.useEffect(() => {
    const state = location.state as {
      scopeImportedCount?: number;
      scopeSourceName?: string;
    } | null;
    const importedCount = state?.scopeImportedCount ?? 0;
    if (!dealUuid || isCreateStartRoute || importedCount <= 0) return;
    if (scopeImportRunRef.current) return;
    if (!canGenerateEstimate) return;
    scopeImportRunRef.current = true;

    navigate(location.pathname, {
      replace: true,
      state: { ...state, scopeImportedCount: undefined, scopeSourceName: undefined },
    });

    runEstimateGeneration({
      keepOverrides: false,
      reinferHours: false,
      label: `Scope import (${importedCount} item${importedCount === 1 ? '' : 's'})`,
      // One upload, three artifacts: the SOW run starts only after the estimate + resource plan
      // land, because it reads them. Export stays a separate, deliberate click.
      onCompletedSuccess: () =>
        runSowGeneration({ keepSowOverrides: false, label: 'Scope import (SOW)' }),
    });
  }, [
    dealUuid,
    isCreateStartRoute,
    location.pathname,
    location.state,
    navigate,
    canGenerateEstimate,
    runEstimateGeneration,
    runSowGeneration,
  ]);

  const handleRegenerateEstimate = React.useCallback(() => {
    // When this run completes, re-derive the estimate-sourced questionnaire
    // defaults (the managed-services contract prefills) from the new estimate -
    // see refillEstimateDerivedAnswers. No-ops for templates without such fields.
    pendingEstimateRefillRef.current = true;
    runEstimateGeneration({
      keepOverrides: keepOverridesOnRegenerateRef.current,
      reinferHours: reinferHoursOnRegenerateRef.current,
      label: 'Regenerate estimate',
    });
  }, [runEstimateGeneration]);

  // SOW-tab "Regenerate": SOW only (reads the saved estimate + resource plan).
  const handleRegenerateSow = React.useCallback(() => {
    runSowGeneration({
      keepSowOverrides: keepSowOverridesOnRegenerateRef.current,
      label: 'Regenerate SOW',
    });
  }, [runSowGeneration]);

  const reloadDealAssets = React.useCallback(async () => {
    if (!dealUuid || isCreateStartRoute) return;

    setLoadingResourcePlan(true);
    setLoadingSow(true);

    try {
      const [estimateRes, planRes, sowRes] = await Promise.allSettled([
        salesApi.getSavedEstimate(dealUuid),
        salesApi.getResourcePlan(dealUuid),
        salesApi.getSow(dealUuid),
      ]);

      if (estimateRes.status === 'fulfilled') {
        const { savedEstimate: s } = estimateRes.value;
        setSavedEstimate(s);
        if (s?.estimateData) {
          setEstimateResult(s.estimateData);
        }
      }

      if (planRes.status === 'fulfilled') {
        setResourcePlan(planRes.value.plan);
      } else {
        setResourcePlanError(
          planRes.reason instanceof Error ? planRes.reason.message : 'Failed to load resource plan'
        );
      }
      setLoadingResourcePlan(false);

      if (sowRes.status === 'fulfilled') {
        setSow(sowRes.value.sow);
      } else {
        setSowError(sowRes.reason instanceof Error ? sowRes.reason.message : 'Failed to load SOW');
      }
      setLoadingSow(false);
    } catch {
      // allSettled doesn't throw, so this is a safety net
      setSowError('Failed to load SOW');
      setLoadingSow(false);
    }
  }, [dealUuid, isCreateStartRoute]);

  React.useEffect(() => {
    void reloadDealAssets();
  }, [reloadDealAssets]);

  // Upload a finalized estimate file (CI bridge): the server parses + converts it into this
  // deal's estimate and saves the estimate + resource plan. On success the estimate (and the
  // questionnaire, which now carries the imported scope items) reload, and the existing
  // Generate SOW path is ready to run.
  const handleUploadEstimateFile = React.useCallback(async () => {
    if (!estimateUploadFile || !dealUuid) return;
    setUploadingEstimateFile(true);
    setEstimateUploadError(null);
    try {
      await salesApi.uploadDealEstimate(dealUuid, estimateUploadFile);
      setShowEstimateUploadPanel(false);
      setEstimateUploadFile(null);
      void reloadDealAssets();
      void handleReloadQuestionnaire();
    } catch (err) {
      setEstimateUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingEstimateFile(false);
    }
  }, [estimateUploadFile, dealUuid, reloadDealAssets, handleReloadQuestionnaire]);

  const resetScopeUpload = React.useCallback(() => {
    setScopeUploadFile(null);
    setScopeParse(null);
    setScopeMapping({});
    setScopeUploadError(null);
  }, []);

  // Step 1: parse only. Nothing is written, so a wrong file costs the user one click to replace.
  const handleScopeFileSelected = React.useCallback(
    async (file: File, sheetIndex?: number) => {
      if (!dealUuid) return;
      setScopeUploadFile(file);
      setScopeParse(null);
      setScopeUploadError(null);
      setScopeUploadBusy(true);
      try {
        const parsed = await salesApi.parseDealScopeFile(dealUuid, file, sheetIndex);
        setScopeParse(parsed);
        setScopeMapping(parsed.mapping);
      } catch (err) {
        setScopeUploadError(err instanceof Error ? err.message : 'Could not read that file');
        setScopeUploadFile(null);
      } finally {
        setScopeUploadBusy(false);
      }
    },
    [dealUuid]
  );

  // Step 2: import the confirmed mapping, then run the SAME estimate generation the Regenerate
  // button runs - the imported rows have no hours, so its scope-hours inference prices them (and
  // the chat pane shows the progress, with no bespoke plumbing here).
  const handleScopeImportConfirm = React.useCallback(async () => {
    if (!dealUuid || !scopeUploadFile || !scopeParse) return;
    setScopeUploadBusy(true);
    setScopeUploadError(null);
    try {
      const imported = await salesApi.importDealScopeFile(dealUuid, scopeUploadFile, {
        sheetIndex: scopeParse.sheetIndex,
        headerRow: scopeParse.headerRow,
        mapping: scopeMapping,
      });
      setShowScopeUploadPanel(false);
      resetScopeUpload();
      void handleReloadQuestionnaire();
      runEstimateGeneration({
        keepOverrides: false,
        reinferHours: false,
        label: `Scope import (${imported.importedItemCount} items)`,
        onCompletedSuccess: () =>
          runSowGeneration({ keepSowOverrides: false, label: 'Scope import (SOW)' }),
      });
    } catch (err) {
      setScopeUploadError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setScopeUploadBusy(false);
    }
  }, [
    dealUuid,
    scopeUploadFile,
    scopeParse,
    scopeMapping,
    resetScopeUpload,
    handleReloadQuestionnaire,
    runEstimateGeneration,
  ]);

  /** Targets the dialog offers a dropdown for, in SCOPE_IMPORT_TARGETS order. */
  const scopeMappingRows = React.useMemo(
    () =>
      SCOPE_IMPORT_TARGETS.map((target) => ({
        target,
        label: SCOPE_IMPORT_TARGET_LABELS[target],
        columnIndex: scopeMapping[target],
      })),
    [scopeMapping]
  );

  /** At least one scope-text column is required - the server enforces the same rule. */
  const scopeMappingUsable =
    scopeMapping['problem'] != null ||
    scopeMapping['proposedSolution'] != null ||
    scopeMapping['outcome'] != null;

  const handleContinue = async () => {
    const trimmedCompanyName = companyName.trim();
    if (!trimmedCompanyName || submittingCreate) return;

    setCreateSubmissionPhase('checking');
    setCreateError(null);
    setCreateStatus(null);
    // Reset any previous upload/AI session when starting a new search
    setShowUploadView(false);
    setScriptFiles([]);
    setCreateReview(null);
    setScriptAdditionalNotes('');
    setCreateFileNotes({});
    try {
      const { deals, moduleRole } = await salesApi.getCompanyDeals(
        trimmedCompanyName,
        selectedWorkspaceProductId
        // No categoryKey filter: show all deals for this company+product so the
        // user sees the full picture regardless of which category they selected.
      );
      setCompanyDeals(deals);
      setSalesModuleRole(moduleRole);
      setShowCreateNew(false);
      setSelectedExistingDeal(null);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Failed to create deal');
    } finally {
      setCreateSubmissionPhase('idle');
    }
  };

  // Existing deals for this company+product (already loaded into companyDeals)
  // that also match the selected category - any status. When non-empty, we ask
  // the user to choose continue-vs-new instead of silently reusing/creating.
  const getMatchingExistingDeals = (): SalesDealSummary[] => {
    const target = selectedCategoryKey ?? null;
    return (companyDeals ?? []).filter((d) => (d.dealCategoryKey ?? null) === target);
  };

  // Always force-creates: the wizard has already surfaced any existing deals and
  // the user explicitly chose "new draft" (or there were none to choose from).
  const createNewDealDraft = async () => {
    const trimmedCompanyName = companyName.trim();
    if (!trimmedCompanyName || submittingCreate) return;

    setCreateSubmissionPhase('creating');
    setCreateError(null);
    try {
      const { deal: createdDeal } = await salesApi.startDeal(
        trimmedCompanyName,
        selectedWorkspaceProductId,
        selectedCategoryKey,
        true
      );
      setExistingDealPrompt(null);
      setCompanyDeals(null);
      setExistingDraft(null);
      setCreateStatus(null);
      setCompanyName('');
      navigate(`/so/deal/${createdDeal.uuid}/edit`);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Failed to create deal');
    } finally {
      setCreateSubmissionPhase('idle');
    }
  };

  const handleCreateNew = async () => {
    const trimmedCompanyName = companyName.trim();
    if (!trimmedCompanyName || submittingCreate) return;

    const matches = getMatchingExistingDeals();
    if (matches.length > 0) {
      setExistingDealPrompt({ deals: matches, intent: 'manual' });
      return;
    }
    await createNewDealDraft();
  };

  // After some files were uploaded: navigate straight to the deal when every file went through,
  // otherwise stop here so the user sees which file failed (or is very long) first. The deal
  // already exists at this point, so the review step only offers "Continue to deal".
  const finishScriptUpload = async (
    dealUuid: string,
    docUuids: string[],
    docFileName: string,
    notes: Record<string, FileNote>
  ) => {
    setExistingDealPrompt(null);
    // The optional "Additional notes" travel in the navigation state; the deal page saves them when
    // the analysis run is sent, so a user who stops at the review step below stores nothing.
    const additionalNotes = scriptAdditionalNotes.trim();
    const target = {
      path: `/so/deal/${dealUuid}/edit`,
      state: {
        transcriptDocUuids: docUuids,
        transcriptDocUuid: docUuids[0],
        docFileName,
        companyName: companyName.trim(),
        mode: scriptAnalyzeMode,
        ...(additionalNotes ? { additionalNotes } : {}),
      },
    };
    const noteList = Object.values(notes);
    if (noteList.length === 0) {
      setScriptAdditionalNotes('');
      navigate(target.path, { state: target.state });
      return;
    }
    const errors = noteList.filter((n) => n.kind === 'error').length;
    setCreateFileNotes(notes);
    setCreateReview(target);
    setCreateError(
      errors > 0
        ? `${errors} file${errors === 1 ? '' : 's'} could not be read. The others were uploaded.`
        : null
    );
  };

  // Creates a brand-new deal from the selected transcript(s). start-from-script
  // already force-creates server-side, so this never reuses.
  const createDealFromScriptNew = async () => {
    const trimmedCompanyName = companyName.trim();
    const totalFiles = scriptFiles.length + scriptDriveFiles.length;
    if (!trimmedCompanyName || totalFiles === 0 || submittingScript) return;
    const driveUuid = driveIntegration.integrationUuid;

    setSubmittingScript(true);
    setCreateError(null);
    setCreateFileNotes({});
    const notes: Record<string, FileNote> = {};
    const noteError = (key: string, fileName: string, err: unknown) => {
      notes[key] = { kind: 'error', text: formatFileIssue(fileName, uploadFailureReason(err)) };
    };
    try {
      // The first readable file creates the deal (start-from-script[-from-drive]); the rest attach
      // to it. Local files go first when present; Drive picks attach via the from-drive route.
      // A file the server cannot read is reported on that file and the next one becomes the seed,
      // so one bad file never blocks the deal or the other files.
      let createdDeal: SalesDealSummary | null = null;
      let firstFileName = '';
      let uploaded = 0;
      const docUuids: string[] = [];
      const localQueue = [...scriptFiles];
      const driveQueue = [...scriptDriveFiles];

      while (!createdDeal && localQueue.length > 0) {
        const file = localQueue.shift()!;
        try {
          const res = await salesApi.startDealFromScript(
            trimmedCompanyName,
            file,
            selectedWorkspaceProductId,
            selectedCategoryKey
          );
          createdDeal = res.deal;
          firstFileName = file.name;
          uploaded += 1;
          if (res.docUuid) docUuids.push(res.docUuid);
          if (res.warning) notes[localFileKey(file)] = { kind: 'warning', text: res.warning };
        } catch (err) {
          if (!isFileLevelUploadError(err)) throw err;
          noteError(localFileKey(file), file.name, err);
        }
      }
      while (!createdDeal && driveQueue.length > 0) {
        if (!driveUuid) throw new Error('Google Drive is not connected.');
        const file = driveQueue.shift()!;
        try {
          const res = await salesApi.startDealFromScriptFromDrive(
            trimmedCompanyName,
            driveUuid,
            file,
            selectedWorkspaceProductId,
            selectedCategoryKey
          );
          createdDeal = res.deal;
          firstFileName = file.name;
          uploaded += 1;
          if (res.docUuid) docUuids.push(res.docUuid);
          if (res.warning) notes[driveFileKey(file)] = { kind: 'warning', text: res.warning };
        } catch (err) {
          if (!isFileLevelUploadError(err)) throw err;
          noteError(driveFileKey(file), file.name, err);
        }
      }
      if (!createdDeal) {
        setExistingDealPrompt(null);
        setCreateFileNotes(notes);
        setCreateError(
          'None of these files could be read. Remove them or fix them, then try again.'
        );
        return;
      }

      for (const file of localQueue) {
        try {
          const res = await salesApi.uploadDealTranscript(createdDeal.uuid, file);
          docUuids.push(res.docUuid);
          uploaded += 1;
          if (res.warning) notes[localFileKey(file)] = { kind: 'warning', text: res.warning };
        } catch (err) {
          noteError(localFileKey(file), file.name, err);
        }
      }
      for (const file of driveQueue) {
        if (!driveUuid) break;
        try {
          const res = await salesApi.uploadDealTranscriptFromDrive(
            createdDeal.uuid,
            driveUuid,
            file
          );
          docUuids.push(res.docUuid);
          uploaded += 1;
          if (res.warning) notes[driveFileKey(file)] = { kind: 'warning', text: res.warning };
        } catch (err) {
          noteError(driveFileKey(file), file.name, err);
        }
      }

      await finishScriptUpload(
        createdDeal.uuid,
        docUuids,
        uploaded === 1 ? firstFileName : `${uploaded} transcripts`,
        notes
      );
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Failed to create deal');
    } finally {
      setSubmittingScript(false);
    }
  };

  /**
   * A scope list can only be imported where the engine that prices it exists: the workspace has the
   * feature, and the chosen category is one of the two ad-hoc-scope + AI-estimation ones.
   */
  const scopeUploadAllowedForCreate =
    !!workspace?.scopeUploadEnabled &&
    (selectedCategoryKey === 'continuous_improvement' || selectedCategoryKey === 'bpr');

  /**
   * Route files picked on the create page: a spreadsheet is a SCOPE LIST (parse -> create -> import
   * -> estimate), anything else is a transcript (the existing analyze path). Never silently
   * misfile a sheet as a transcript - if the category cannot take one, say why.
   */
  const routeCreateFiles = (picked: FileList | File[]): void => {
    const files = Array.from(picked);
    const sheet = files.find((f) => isScopeFileUpload(f.type, f.name));
    if (sheet) {
      if (!scopeUploadAllowedForCreate) {
        setCreateError(
          `"${sheet.name}" looks like a scope list. Those import on Continuous Improvement or ` +
            'Business Process Review deals - pick that deal type first.'
        );
        return;
      }
      setCreateScopeFile(sheet);
      setScriptFiles([]);
      setCreateError(
        files.length > 1
          ? 'A scope list imports one file at a time - the other files were ignored.'
          : null
      );
      return;
    }
    setScriptFiles((prev) =>
      addTranscriptFiles(prev, files, TRANSCRIPT_MAX_FILES - scriptDriveFiles.length)
    );
  };

  /**
   * Create-deal + scope-list path (docs/plans/27). Order matters: PARSE first (workspace-scoped, no
   * writes) so an unreadable or ambiguous sheet never leaves a half-made deal behind; only then
   * create the deal, import the rows, and hand off to the deal page which fires the estimate.
   *
   * Confident detection imports with zero extra clicks. When detection is NOT confident we stop and
   * say so rather than silently importing a guessed mapping - the deal-page dialog is where a
   * mapping can be corrected column by column.
   */
  const handleCreateFromScopeFile = async () => {
    const trimmedCompanyName = companyName.trim();
    if (!trimmedCompanyName || !createScopeFile || createScopeBusy) return;

    setCreateScopeBusy(true);
    setCreateError(null);
    try {
      const parsed = await salesApi.parseScopeFileForWorkspace(createScopeFile);

      if (parsed.confidence !== 'high') {
        const mapped = Object.keys(parsed.mapping).length;
        setCreateError(
          `We read ${parsed.itemCount} row${parsed.itemCount === 1 ? '' : 's'} from ` +
            `"${createScopeFile.name}" but could only recognise ${mapped} column` +
            `${mapped === 1 ? '' : 's'} with confidence. Nothing was created. Please check column headers.`
        );
        return;
      }

      // No business process anywhere means the estimate would come out as ONE flat "Ad-hoc scope"
      // section instead of one section per process - visibly wrong, so stop rather than import it
      // silently. Workbooks often hold a raw working sheet next to the curated one, so name the
      // alternatives when there are any.
      if (parsed.itemCount > 0 && parsed.itemsWithoutBusinessProcess === parsed.itemCount) {
        const alternatives = parsed.sheetOptions
          .filter((option) => option.sheetIndex !== parsed.sheetIndex && option.withBusinessProcess > 0)
          .map((option) => `"${option.sheetName}" (${option.withBusinessProcess} of ${option.itemCount})`);
        setCreateError(
          `Sheet "${parsed.sheetName}" has ${parsed.itemCount} rows but no business process on any ` +
            'of them, so the estimate would be one flat section instead of one per business ' +
            `process. Nothing was created.${
              alternatives.length > 0
                ? ` Try sheet ${alternatives.join(' or ')} instead.`
                : ' Add a Business Process column.'
            }`
        );
        return;
      }

      const { deal: createdDeal } = await salesApi.startDeal(
        trimmedCompanyName,
        selectedWorkspaceProductId,
        selectedCategoryKey,
        true
      );

      const imported = await salesApi.importDealScopeFile(createdDeal.uuid, createScopeFile, {
        sheetIndex: parsed.sheetIndex,
        headerRow: parsed.headerRow,
        mapping: parsed.mapping,
      });

      setExistingDealPrompt(null);
      setCompanyDeals(null);
      setExistingDraft(null);
      setCreateStatus(null);
      setCompanyName('');
      setCreateScopeFile(null);
      // Same handoff the transcript path uses: the deal page reads this on mount and starts the
      // estimate run, so progress renders in its chat pane rather than here.
      navigate(`/so/deal/${createdDeal.uuid}/edit`, {
        state: {
          scopeImportedCount: imported.importedItemCount,
          scopeSourceName: createScopeFile.name,
          companyName: trimmedCompanyName,
        },
      });
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Could not import that scope list');
    } finally {
      setCreateScopeBusy(false);
    }
  };

  const handleCreateFromScript = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedCompanyName = companyName.trim();
    if (
      !trimmedCompanyName ||
      scriptFiles.length + scriptDriveFiles.length === 0 ||
      submittingScript
    )
      return;

    const matches = getMatchingExistingDeals();
    if (matches.length > 0) {
      setExistingDealPrompt({ deals: matches, intent: 'transcript' });
      return;
    }
    await createDealFromScriptNew();
  };

  // Dialog "Continue this deal": manual -> open it; transcript -> attach the
  // already-selected files to the chosen deal, then run analysis there.
  const continueWithExistingDeal = async (deal: SalesDealSummary) => {
    const prompt = existingDealPrompt;
    if (!prompt) return;

    if (prompt.intent === 'manual') {
      setExistingDealPrompt(null);
      navigate(`/so/deal/${deal.uuid}/edit`);
      return;
    }

    const totalFiles = scriptFiles.length + scriptDriveFiles.length;
    if (totalFiles === 0 || submittingScript) return;
    const driveUuid = driveIntegration.integrationUuid;
    setSubmittingScript(true);
    setCreateError(null);
    setCreateFileNotes({});
    const notes: Record<string, FileNote> = {};
    try {
      // Each file is uploaded on its own: one that cannot be read is reported on that file and the
      // rest still go through.
      const docUuids: string[] = [];
      let firstUploadedName: string | null = null;
      for (const file of scriptFiles) {
        try {
          const res = await salesApi.uploadDealTranscript(deal.uuid, file);
          docUuids.push(res.docUuid);
          firstUploadedName ??= file.name;
          if (res.warning) notes[localFileKey(file)] = { kind: 'warning', text: res.warning };
        } catch (err) {
          notes[localFileKey(file)] = {
            kind: 'error',
            text: formatFileIssue(file.name, uploadFailureReason(err)),
          };
        }
      }
      for (const file of scriptDriveFiles) {
        if (!driveUuid) break;
        try {
          const res = await salesApi.uploadDealTranscriptFromDrive(deal.uuid, driveUuid, file);
          docUuids.push(res.docUuid);
          firstUploadedName ??= file.name;
          if (res.warning) notes[driveFileKey(file)] = { kind: 'warning', text: res.warning };
        } catch (err) {
          notes[driveFileKey(file)] = {
            kind: 'error',
            text: formatFileIssue(file.name, uploadFailureReason(err)),
          };
        }
      }
      if (docUuids.length === 0) {
        setExistingDealPrompt(null);
        setCreateFileNotes(notes);
        setCreateError(
          'None of these files could be read. Remove them or fix them, then try again.'
        );
        return;
      }
      await finishScriptUpload(
        deal.uuid,
        docUuids,
        docUuids.length === 1
          ? (firstUploadedName ?? 'transcript')
          : `${docUuids.length} transcripts`,
        notes
      );
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Failed to add document');
    } finally {
      setSubmittingScript(false);
    }
  };

  const handleExistingDealCreateNew = () => {
    if (existingDealPrompt?.intent === 'transcript') void createDealFromScriptNew();
    else void createNewDealDraft();
  };

  // Reset selected deal whenever the deals list changes (new company search)
  React.useEffect(() => {
    setSelectedExistingDeal(null);
  }, [companyDeals]);

  const handleClosePreviewDeal = React.useCallback(() => {
    // no-op: right rail disabled for wizard; kept for NewDealSidePanel prop signature
  }, []);

  const handleOpenPreviewDeal = React.useCallback(
    (uuid: string) => {
      navigate(`/so/deal/${uuid}`, { state: { viewingDoc } });
    },
    [navigate, viewingDoc]
  );

  const handleNavRouteItemClick = React.useCallback(
    (_routeSegment: string, targetRoute: string): boolean => {
      if (targetRoute !== '/so/deal/new' || !isCreateStartRoute) {
        return false;
      }

      // Already on the create page - reset without a dirty-state guard:
      // nothing has been persisted yet, so there is no data to lose.
      setCompanyName('');
      setCreateError(null);
      setCreateStatus(null);
      setExistingDraft(null);
      setCompanyDeals(null);
      setShowUploadView(false);
      setScriptFiles([]);
      setCreateReview(null);
      setScriptAdditionalNotes('');
      setCreateFileNotes({});
      setViewingDoc(null);

      return true;
    },
    [isCreateStartRoute]
  );

  const leftNav = (
    <AppNav
      selected={isCreateStartRoute ? 'deal/new' : dealUuid ? 'deals' : null}
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
      onRouteItemClick={handleNavRouteItemClick}
    />
  );

  const isGenerating = savingEstimate || generatingResourcePlan || generatingSow;
  // Single source of truth: true when ANY AI run is in flight (chat WS or local generation state).
  // Used to disable all AI-action buttons and show the unified "AI is running" tooltip.
  const isAnyAiRunning = chatSession.isRunActive || isGenerating;

  const isRcpWorkspace = workspace?.slug === 'rapid-cloud-partners';

  // Hide the Prioritization tab for greenfield NetSuite/Rillet deals and RCP BPR/CI deals. Declared before
  // railAndDocs so buildDocPanel (inside that memo) can reference it, and so documentTabItems
  // below can drop the tab chip - both surface points use the same gate.
  const hidePrioritizationTab = React.useMemo(() => {
    // RCP CI / BPR / scope-list deals never prioritise (everything listed is in scope).
    if (
      isRcpWorkspace &&
      (deal?.scopeSourced === true ||
        deal?.dealCategoryKey === 'continuous_improvement' ||
        deal?.dealCategoryKey === 'bpr')
    ) {
      return true;
    }
    if (deal?.dealCategoryKey !== 'greenfield') return false;
    return isQuestionnaireGreenfieldProductKey(normalizeDealProductKey(deal));
  }, [
    isRcpWorkspace,
    deal?.scopeSourced,
    deal?.dealCategoryKey,
    deal?.vendorProduct?.vendorKey,
    deal?.vendorProduct?.productKey,
  ]);

  // For RCP workspace (rapid-cloud-partners), Continuous Improvement and BPR deals hide BOTH the
  // Questionnaire and Prioritization tabs. Scope for these deals comes from an uploaded scope list
  // or from discovery, and every row is in scope by definition - so the two tabs render an empty
  // questionnaire and an unprioritised list. Scope-list deals in ANY category hide them too (the file
  // IS the scope). Other workspaces (e.g. cosow) and other categories - greenfield, managed services,
  // dev stories - are untouched: there the questionnaire is still the primary surface.
  const hideQuestionnaireTabs =
    isRcpWorkspace &&
    (deal?.dealCategoryKey === 'continuous_improvement' ||
      deal?.dealCategoryKey === 'bpr' ||
      deal?.scopeSourced === true);

  // The Systems Landscape tab is seeded per deal template: it exists only when the questionnaire has
  // a tech-stack group (the same `groupLayoutHint === 'tech-stack'` that drives the inline editor).
  // Templates without that group (e.g. NetSuite) get no tab and no diagram. The tab pairs the Current
  // Technology Stack editor with the live landscape diagram side by side.
  const techStackGroup = React.useMemo(
    () =>
      questionnaire?.sections
        .flatMap((section) => section.groups)
        .find((group) => group.groupLayoutHint === 'tech-stack') ?? null,
    [questionnaire]
  );
  // Maxio deals have no tech-stack group: their diagram is derived from the Maxio answers (GL, CRM,
  // payments, tax, usage, current billing system), read-only, the same picture the Maxio SOW embeds.
  const isMaxioDeal = isMaxioProductKey(normalizeDealProductKey(deal));
  const hasSystemsLandscapeTab = techStackGroup != null || isMaxioDeal;
  // The Contract tab exists only when the deal's template carries a contract section
  // (e.g. managed_services). Its fields render there instead of in the Questionnaire.
  const hasContractTab = React.useMemo(
    () => (questionnaire?.sections ?? []).some((s) => s.sectionKey === CONTRACT_SECTION_KEY),
    [questionnaire]
  );
  // The landscape canvas is a pure function of the resolved tech-stack systems. Depending the
  // doc-panel memo on this compact signature (not the whole `answers`) keeps the right rail + doc
  // surfaces referentially stable on every OTHER questionnaire keystroke - so typing stays smooth -
  // while still repainting the live diagram the moment tech-stack data changes (a user edit in a
  // side-by-side split, or an AI fill).
  const techStackAnswersSignature = React.useMemo(
    () => JSON.stringify(resolveTechStackSystemsFromAnswers(answers, dealPageFieldLookup)),
    [answers, dealPageFieldLookup]
  );
  // The Scope doc projects the WHOLE answer map, so it must repaint on every answer change
  // (a user edit in a side-by-side split, or an AI fill). Scoped to when a scope panel is
  // actually open - `null` otherwise - so questionnaire keystrokes on every other tab keep the
  // rail + doc surfaces referentially stable (the reason `answers` itself is not a memo dep).
  const scopeAnswersDep =
    viewingDoc?.kind === 'scope' || secondaryViewingDoc?.kind === 'scope' ? answers : null;
  // The Contract tab renders an editable questionnaire section, so it must repaint on every
  // answer/edit-state change while open - and stay referentially stable (null) on every other
  // tab, keeping questionnaire keystrokes cheap (the reason these aren't direct memo deps).
  const contractPanelState =
    viewingDoc?.kind === 'contract' || secondaryViewingDoc?.kind === 'contract'
      ? { answers, persistedAnswers, touchedFields, changedFieldKeys, savableFieldKeys, saveError }
      : null;

  // Builds the right-rail items (chat + attributes) AND the active document
  // panel that now renders in the MAIN canvas. Both are derived from the same
  // deal state, so computing them together keeps a single dependency set.
  const railAndDocs = React.useMemo((): {
    items: RightRailItem[] | undefined;
    documentPanel: { surface: React.ReactNode; headerActions: React.ReactNode } | null;
    secondaryDocumentPanel: {
      surface: React.ReactNode;
      headerActions: React.ReactNode | null;
    } | null;
  } => {
    if (isCreateStartRoute || !dealUuid) {
      return { items: undefined, documentPanel: null, secondaryDocumentPanel: null };
    }
    type DealPanelProps = {
      content: React.ReactNode;
      headerAction?: React.ReactNode;
      /** Optional override for the pane header's left-side title (defaults to label) */
      headerTitle?: React.ReactNode;
      noPadding?: boolean;
      label?: string;
      icon?: React.ReactNode;
      /** Marks the page's own module chat so AppShell doesn't append a duplicate. */
      isModuleChat?: boolean;
    };

    const buildDocPanel = (kind: string | undefined, uuid?: string): DealPanelProps => {
      // Sub-view: Estimate
      if (kind === 'estimate') {
        return {
          label: 'Estimate',
          headerAction: (
            <div className="sales-estimate-pane__header-actions">
              {(isEditMode || salesModuleRole !== 'viewer') && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(isEditMode ? `/so/deal/${dealUuid}` : `/so/deal/${dealUuid}/edit`)
                  }
                >
                  {isEditMode ? 'View' : 'Edit'}
                </Button>
              )}
              {isEditMode ? <RegenerateMenuButton currentArtifact="estimate" /> : null}

              {savedEstimate && !(workspace?.slug === 'rapid-cloud-partners' && deal?.dealCategoryKey === 'managed_services') ? (
                estimateConfig?.hasDriveIntegration ? (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        disabled={isExportingEstimate}
                      >
                        {isExportingEstimate ? 'Exporting…' : 'Export'}
                        <ChevronDownIcon
                          width={IconSize.sm}
                          height={IconSize.sm}
                          aria-hidden="true"
                        />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content className="dropdown-content" sideOffset={4} align="end">
                        <DropdownMenu.Item
                          className="dropdown-item"
                          onSelect={async () => {
                            if (!dealUuid) return;
                            // Priced output: stop with the rates pop-up when roles have no rate.
                            if (!(await checkStaffingRates())) return;
                            setIsExportingEstimate(true);
                            const start = Date.now();
                            void salesApi
                              .exportEstimateXlsx(
                                dealUuid,
                                selectedEstimateVersionUuid ?? undefined
                              )
                              .finally(() => {
                                const elapsed = Date.now() - start;
                                const remaining = Math.max(0, 600 - elapsed);
                                setTimeout(() => setIsExportingEstimate(false), remaining);
                              });
                          }}
                        >
                          Export to Excel (.xlsx)
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          className="dropdown-item"
                          onSelect={async () => {
                            if (!dealUuid) return;
                            // Priced output: stop with the rates pop-up when roles have no rate.
                            if (!(await checkStaffingRates())) return;
                            setIsExportingEstimate(true);
                            const start = Date.now();
                            salesApi
                              .exportEstimateDrive(
                                dealUuid,
                                selectedEstimateVersionUuid ?? undefined
                              )
                              .then(({ url }) => {
                                window.open(url, '_blank', 'noopener,noreferrer');
                              })
                              .finally(() => {
                                const elapsed = Date.now() - start;
                                const remaining = Math.max(0, 600 - elapsed);
                                setTimeout(() => setIsExportingEstimate(false), remaining);
                              });
                          }}
                        >
                          Export to Google Drive
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={isExportingEstimate}
                    onClick={async () => {
                      if (!dealUuid) return;
                      // Priced output: stop with the rates pop-up when roles have no rate.
                      if (!(await checkStaffingRates())) return;
                      setIsExportingEstimate(true);
                      const start = Date.now();
                      void salesApi
                        .exportEstimateXlsx(dealUuid, selectedEstimateVersionUuid ?? undefined)
                        .finally(() => {
                          const elapsed = Date.now() - start;
                          const remaining = Math.max(0, 600 - elapsed);
                          setTimeout(() => setIsExportingEstimate(false), remaining);
                        });
                    }}
                  >
                    {isExportingEstimate ? 'Exporting…' : 'Export'}
                  </Button>
                )
              ) : null}
            </div>
          ),
          content: (
            <div ref={estimatePaneRef}>
              <DealEstimatePane
                result={effectiveEstimateResult}
                loading={loadingEstimate || loadingQuestionnaire || loadingDeal}
                error={estimateError}
                isReady={canGenerateEstimate}
                isStale={estimateIsStale && !viewingPastEstimate}
                missingRequiredCount={questionnaireCompletion.missingRequiredCount}
                savedEstimate={effectiveSavedEstimate}
                resourcePlan={resourcePlan}
                onSectionOverride={
                  isEditMode && !viewingPastEstimate ? handleSectionOverride : undefined
                }
                onSectionOverrideReason={
                  isEditMode && !viewingPastEstimate ? handleSectionOverrideReason : undefined
                }
                onSubsectionSummaryEdit={
                  isEditMode && !viewingPastEstimate ? handleSubsectionSummaryEdit : undefined
                }
                onSubsectionSummarySuggest={
                  isEditMode && !viewingPastEstimate ? handleSubsectionSummarySuggest : undefined
                }
                onDiscountOverride={
                  isEditMode && !viewingPastEstimate ? handleDiscountOverride : undefined
                }
                onUpdateRolePricingBasis={
                  isEditMode && !viewingPastEstimate
                    ? handleUpdateResourcePlanPricingBasis
                    : undefined
                }
                updatingRolePricingBasisId={updatingRolePricingBasisId}
                onUpdateRoleAllocation={
                  isEditMode && !viewingPastEstimate
                    ? handleUpdateResourcePlanRoleAllocation
                    : undefined
                }
                updatingRoleAllocationId={updatingRoleAllocationId}
                availableRoles={isEditMode && !viewingPastEstimate ? availableRoles : undefined}
                onRoleSelectionChange={
                  isEditMode && !viewingPastEstimate ? handleSetResourcePlanRoles : undefined
                }
                updatingRoles={updatingRoles}
                availableVersions={rateCardProfiles}
                onRegenerate={
                  isEditMode && !viewingPastEstimate ? handleRegenerateResourcePlan : undefined
                }
                regenerating={generatingResourcePlan}
                versions={estimateVersions}
                selectedVersionUuid={selectedEstimateVersionUuid}
                onSelectVersion={handleSelectEstimateVersion}
                viewingPastVersion={viewingPastEstimate}
              />
            </div>
          ),
        };
      }

      // Sub-view: Resource Plan. Defense-in-depth: if this stage-gated view is hidden for the
      // workspace (uat/prod), don't render it even if viewingDoc were somehow set to it.
      if (kind === 'resource-plan' && isDocViewAllowed('resource-plan')) {
        return {
          label: 'Resource Plan',
          headerAction: (
            <div className="sales-estimate-pane__header-actions">
              {(isEditMode || salesModuleRole !== 'viewer') && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(isEditMode ? `/so/deal/${dealUuid}` : `/so/deal/${dealUuid}/edit`)
                  }
                >
                  {isEditMode ? 'View' : 'Edit'}
                </Button>
              )}
              {isEditMode ? <RegenerateMenuButton currentArtifact="resource-plan" /> : null}
              {resourcePlan?.status === 'generated' ? (
                estimateConfig?.hasResourcePlanDriveIntegration ? (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        disabled={loadingResourcePlan || isExportingResourcePlan}
                      >
                        {isExportingResourcePlan ? 'Exporting…' : 'Export'}
                        <ChevronDownIcon
                          width={IconSize.sm}
                          height={IconSize.sm}
                          aria-hidden="true"
                        />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content className="dropdown-content" sideOffset={4} align="end">
                        <DropdownMenu.Item
                          className="dropdown-item"
                          onSelect={() => {
                            if (!dealUuid) return;
                            setIsExportingResourcePlan(true);
                            const start = Date.now();
                            void salesApi
                              .exportResourcePlanDocx(dealUuid, resourcePlan)
                              .finally(() => {
                                const elapsed = Date.now() - start;
                                const remaining = Math.max(0, 600 - elapsed);
                                setTimeout(() => setIsExportingResourcePlan(false), remaining);
                              });
                          }}
                        >
                          Export to Word (.docx)
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          className="dropdown-item"
                          onSelect={() => {
                            if (!dealUuid) return;
                            setIsExportingResourcePlan(true);
                            const start = Date.now();
                            salesApi
                              .exportResourcePlanDrive(dealUuid, resourcePlan)
                              .then(({ url }) => {
                                window.open(url, '_blank', 'noopener,noreferrer');
                              })
                              .finally(() => {
                                const elapsed = Date.now() - start;
                                const remaining = Math.max(0, 600 - elapsed);
                                setTimeout(() => setIsExportingResourcePlan(false), remaining);
                              });
                          }}
                        >
                          Export to Google Drive
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={loadingResourcePlan || isExportingResourcePlan}
                    onClick={() => {
                      if (!dealUuid) return;
                      setIsExportingResourcePlan(true);
                      const start = Date.now();
                      void salesApi.exportResourcePlanDocx(dealUuid, resourcePlan).finally(() => {
                        const elapsed = Date.now() - start;
                        const remaining = Math.max(0, 600 - elapsed);
                        setTimeout(() => setIsExportingResourcePlan(false), remaining);
                      });
                    }}
                  >
                    {isExportingResourcePlan ? 'Exporting…' : 'Export'}
                  </Button>
                )
              ) : null}
            </div>
          ),
          content: (
            <div ref={resourcePlanPaneRef}>
              <DealResourcePlanPane
                plan={resourcePlan}
                loading={loadingResourcePlan}
                error={resourcePlanError}
                discountPercentageOverride={estimateResult?.discountPercentageOverride ?? null}
                defaultDiscountPercentage={estimateResult?.discountPercentage ?? null}
                onRetry={reloadDealAssets}
                available={estimateResult?.documentAvailability?.resourcePlanAvailable}
              />
            </div>
          ),
        };
      }

      // Sub-view: SOW
      if (kind === 'sow') {
        return {
          label: 'SOW',
          headerAction: (
            <div className="sales-estimate-pane__header-actions">
              {(isEditMode || salesModuleRole !== 'viewer') && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(isEditMode ? `/so/deal/${dealUuid}` : `/so/deal/${dealUuid}/edit`)
                  }
                >
                  {isEditMode ? 'View' : 'Edit'}
                </Button>
              )}
              {isEditMode ? <RegenerateMenuButton currentArtifact="sow" /> : null}
              {/* Static-SOW categories: first-time SOW generation lives HERE (not on the
                  questionnaire wizard or the Contract tab). Deterministic direct generation;
                  the server readiness gate (required contract terms) surfaces its blocker
                  list on the pane when the contract is incomplete. */}
              {!sow &&
              salesModuleRole !== 'viewer' &&
              deal?.dealCategoryKey != null &&
              STATIC_SOW_WIZARD_CATEGORY_KEYS.has(deal.dealCategoryKey) ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={previewingSow || loadingSow || isAnyAiRunning}
                  onClick={() => void handleWizardPreviewSow()}
                >
                  {previewingSow ? (
                    <span className="wizard__btn-spinner" aria-hidden="true" />
                  ) : null}
                  Generate SOW
                </Button>
              ) : null}
              {sow ? (
                estimateConfig?.hasSowDriveIntegration ? (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        disabled={!sow || loadingSow || isExportingSow}
                      >
                        {isExportingSow ? 'Exporting…' : 'Export'}
                        <ChevronDownIcon
                          width={IconSize.sm}
                          height={IconSize.sm}
                          aria-hidden="true"
                        />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content className="dropdown-content" sideOffset={4} align="end">
                        <DropdownMenu.Item
                          className="dropdown-item"
                          onSelect={async () => {
                            if (!dealUuid) return;
                            // Priced output: stop with the rates pop-up when roles have no rate.
                            if (!(await checkStaffingRates())) return;
                            setIsExportingSow(true);
                            setSowError(null);
                            const start = Date.now();
                            void salesApi
                              .exportSowDocx(dealUuid, selectedSowVersionUuid ?? undefined)
                              .catch((err) => setSowError(sowExportErrorMessage(err)))
                              .finally(() => {
                                const elapsed = Date.now() - start;
                                const remaining = Math.max(0, 600 - elapsed);
                                setTimeout(() => setIsExportingSow(false), remaining);
                              });
                          }}
                        >
                          Export to Word (.docx)
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          className="dropdown-item"
                          onSelect={async () => {
                            if (!dealUuid) return;
                            // Priced output: stop with the rates pop-up when roles have no rate.
                            if (!(await checkStaffingRates())) return;
                            setIsExportingSow(true);
                            setSowError(null);
                            const start = Date.now();
                            salesApi
                              .exportSowDrive(dealUuid, selectedSowVersionUuid ?? undefined)
                              .then(({ url }) => {
                                window.open(url, '_blank', 'noopener,noreferrer');
                              })
                              .catch((err) => setSowError(sowExportErrorMessage(err)))
                              .finally(() => {
                                const elapsed = Date.now() - start;
                                const remaining = Math.max(0, 600 - elapsed);
                                setTimeout(() => setIsExportingSow(false), remaining);
                              });
                          }}
                        >
                          Export to Google Drive
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                ) : (
                  <Button
                    key="download"
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={!sow || loadingSow || isExportingSow}
                    onClick={async () => {
                      if (!dealUuid) return;
                      // Priced output: stop with the rates pop-up when roles have no rate.
                      if (!(await checkStaffingRates())) return;
                      setIsExportingSow(true);
                      setSowError(null);
                      const start = Date.now();
                      void salesApi
                        .exportSowDocx(dealUuid, selectedSowVersionUuid ?? undefined)
                        .catch((err) => setSowError(sowExportErrorMessage(err)))
                        .finally(() => {
                          const elapsed = Date.now() - start;
                          const remaining = Math.max(0, 600 - elapsed);
                          setTimeout(() => setIsExportingSow(false), remaining);
                        });
                    }}
                  >
                    {isExportingSow ? 'Exporting…' : 'Export'}
                  </Button>
                )
              ) : null}
            </div>
          ),
          content: (
            <div ref={sowPaneRef} className="deal-sow-pane-host">
              <DealSowPane
                key={sowRefreshKey}
                sow={effectiveSow}
                loading={loadingSow}
                error={sowError}
                readinessReport={sowReadinessReport}
                isEditing={isEditMode && !viewingPastSow}
                saveStatus={sowSaveStatus}
                draftContent={sowDraftContent}
                onDraftChange={setSowDraftContent}
                onRetry={reloadDealAssets}
                dealUuid={dealUuid}
                available={estimateResult?.documentAvailability?.sowAvailable}
                versions={sowVersions}
                selectedVersionUuid={selectedSowVersionUuid}
                onSelectVersion={handleSelectSowVersion}
                viewingPastVersion={viewingPastSow}
              />
            </div>
          ),
        };
      }

      // Sub-view: uploaded document
      if (kind === 'upload') {
        return {
          label: 'View document',
          noPadding: true,
          headerAction: (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setViewingDoc({ kind: 'docs' })}
            >
              Back
            </Button>
          ),
          content: <DealDocumentViewPanel dealUuid={dealUuid} docUuid={uuid ?? ''} />,
        };
      }

      // Scope tab: the questionnaire as a Notion-style Tiptap document (H1 section / H2 group or
      // scope item / H3 question), the primary surface for scoping data. View mode is the
      // read-only projection (answered-only filter, like the question view); edit mode is a real
      // editor - locked template headings, :: drag-reorder on scope items, harvest-on-save
      // through PUT questionnaire-document (see docs/plans/03). Defense-in-depth like Resource
      // Plan: never render it when the tab is hidden for the workspace.
      if (kind === 'scope' && isDocViewAllowed('scope')) {
        return {
          label: 'Scope',
          // The scope doc is a self-contained page (own padding + centered max-width), so it
          // owns its gutters - skip the pane wrapper padding like the other document tabs.
          noPadding: true,
          headerAction: (
            <div className="deal-view-page__header-actions">
              <Tooltip.Simple
                label={scopeAllCollapsed ? 'Expand all sections' : 'Collapse all sections'}
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={scopeAllCollapsed ? 'Expand all sections' : 'Collapse all sections'}
                  aria-pressed={scopeAllCollapsed}
                  onClick={() => {
                    const ed = scopeEditorRef.current;
                    if (!ed) return; // questionnaire still loading -> no-op
                    (scopeAllCollapsed ? expandAllHeadings : collapseAllHeadings)(ed);
                    setScopeAllCollapsed((v) => !v);
                  }}
                >
                  {scopeAllCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
                </Button>
              </Tooltip.Simple>
              {!isEditMode ? (
                <div className="deal-view-page__toggle" role="group" aria-label="Filter fields">
                  <button
                    type="button"
                    className={`deal-view-page__toggle-btn${scopeAnsweredOnly ? ' deal-view-page__toggle-btn--active' : ''}`}
                    aria-pressed={scopeAnsweredOnly}
                    onClick={() => setScopeAnsweredOnly(true)}
                  >
                    Answered
                  </button>
                  <button
                    type="button"
                    className={`deal-view-page__toggle-btn${!scopeAnsweredOnly ? ' deal-view-page__toggle-btn--active' : ''}`}
                    aria-pressed={!scopeAnsweredOnly}
                    onClick={() => setScopeAnsweredOnly(false)}
                  >
                    All
                  </button>
                </div>
              ) : null}
              {(isEditMode || salesModuleRole !== 'viewer') && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void (async () => {
                      // Autosave has no discard prompt: flush any pending scope-doc save before
                      // leaving edit mode so nothing is lost (a save already in flight completes on
                      // its own via the autosave effect).
                      if (isEditMode && scopeDocDirty && !scopeSavingDoc) {
                        await handleSaveScopeDoc();
                      }
                      navigate(isEditMode ? `/so/deal/${dealUuid}` : `/so/deal/${dealUuid}/edit`);
                    })()
                  }
                >
                  {isEditMode ? 'View' : 'Edit'}
                </Button>
              )}
              {/* Scope autosaves (no Save button); the "Saving…/Saved" status renders in the
                  header BEFORE the "Edit mode" badge (ctx.documentHeaderStatus). */}
            </div>
          ),
          content: (
            <DealScopePane
              questionnaire={questionnaire}
              answers={answersRef.current}
              fieldLookup={dealPageFieldLookup}
              estimate={savedEstimate?.estimateData ?? null}
              loading={loadingQuestionnaire}
              isEditMode={isEditMode && salesModuleRole !== 'viewer'}
              draftDoc={scopeDraftDoc}
              onDraftChange={handleScopeDraftChange}
              answeredOnly={scopeAnsweredOnly}
              onAnswerChange={
                salesModuleRole !== 'viewer' ? handleScopePropertyAnswerChange : undefined
              }
              commitAdhocScopeFields={
                salesModuleRole !== 'viewer' ? handleCommitAdhocScopeFields : undefined
              }
              onEditorReady={handleScopeEditorReady}
              onRemoveHeading={salesModuleRole !== 'viewer' ? handleScopeRemoveRequest : undefined}
            />
          ),
        };
      }

      // Systems Landscape tab: the Current Technology Stack editor + the live landscape diagram side
      // by side. Both bind to `answers`, so the diagram updates live as the user edits and re-renders
      // automatically when the AI fills the tech stack. Gated on the deal template actually having a
      // tech-stack group (matches the subnav gate); read-only in view mode (editor disabled).
      if (kind === 'systems-landscape' && hasSystemsLandscapeTab) {
        return {
          label: 'Systems Landscape',
          noPadding: true,
          content: (
            <div className="systems-landscape-doc">
              <CurrentTechnologyStackLandscape
                answers={answersRef.current}
                fieldLookup={dealPageFieldLookup}
                productKey={normalizeDealProductKey(deal)}
              />
            </div>
          ),
        };
      }

      // Prioritization tab: AI-inferred (during transcript extraction) and user-set priorities for
      // scope items. Priorities are inferred as part of the deal extraction step, so there is no
      // manual analyze trigger here - re-running discovery re-infers them.
      // Hidden (defense-in-depth) for greenfield NetSuite/Rillet deals, matching the subnav gate.
      if (kind === 'prioritization' && !hidePrioritizationTab) {
        return {
          label: 'Prioritization',
          headerAction: (
            <div className="sales-estimate-pane__header-actions">
              {/* Branded XLSX download of the matrix - same min-spinner flow as the estimate export. */}
              <Button
                type="button"
                size="sm"
                variant="primary"
                disabled={isExportingPrioritization}
                onClick={() => {
                  if (!dealUuid) return;
                  setIsExportingPrioritization(true);
                  const start = Date.now();
                  void salesApi.exportPrioritizationXlsx(dealUuid).finally(() => {
                    const elapsed = Date.now() - start;
                    const remaining = Math.max(0, 600 - elapsed);
                    setTimeout(() => setIsExportingPrioritization(false), remaining);
                  });
                }}
              >
                {isExportingPrioritization ? 'Exporting…' : 'Export'}
              </Button>
              {/* Export to Jira (Phase A): open a drawer with the ticket plan preview + xlsx download. */}
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isLoadingJiraPlan}
                onClick={() => {
                  if (!dealUuid) return;
                  setIsLoadingJiraPlan(true);
                  setJiraSyncSummary(null);
                  const start = Date.now();
                  void salesApi
                    .getJiraTicketPlan(dealUuid)
                    .then((plan) => {
                      setJiraPlan(plan);
                      setJiraDrawerOpen(true);
                    })
                    .catch((err) => {
                      console.error('[jira-export] plan load failed', err);
                    })
                    .finally(() => {
                      const elapsed = Date.now() - start;
                      setTimeout(() => setIsLoadingJiraPlan(false), Math.max(0, 600 - elapsed));
                    });
                  // Load connected Jira credentials + their projects for the sync target picker.
                  void salesApi
                    .listJiraConnections()
                    .then((conns) => {
                      setJiraConnections(conns);
                      const first = conns[0]?.uuid ?? '';
                      setJiraCredentialUuid(first);
                      if (first) {
                        setJiraLoadingProjects(true);
                        void salesApi
                          .listJiraProjects(first)
                          .then((ps) => {
                            setJiraProjects(ps);
                            setJiraProjectKey(ps[0]?.id ?? '');
                          })
                          .catch((err) => console.error('[jira-export] projects load failed', err))
                          .finally(() => setJiraLoadingProjects(false));
                      }
                    })
                    .catch((err) => console.error('[jira-export] connections load failed', err));
                }}
              >
                {isLoadingJiraPlan ? 'Loading…' : 'Export to Jira'}
              </Button>
              <ContextDrawer
                open={jiraDrawerOpen}
                onClose={() => setJiraDrawerOpen(false)}
                title="Export to Jira"
                subtitle={
                  jiraPlan
                    ? `${jiraPlan.totals.ticketCount} ticket(s), ${jiraPlan.totals.totalEstimateHours}h total`
                    : undefined
                }
                width={760}
                trapFocus
              >
                <div className="deal-jira-export">
                  <p className="deal-jira-export__intro">
                    One Jira ticket per costed scope item. Review the list, then download the Excel
                    to validate it before connecting Jira.
                  </p>
                  {jiraPlan && jiraPlan.warnings.length > 0 && (
                    <ul className="deal-jira-export__warnings">
                      {jiraPlan.warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  )}
                  {jiraPlan && jiraPlan.tickets.length > 0 ? (
                    <div className="deal-jira-export__table" role="table">
                      <div className="deal-jira-export__row deal-jira-export__row--head" role="row">
                        <span className="deal-jira-export__cell deal-jira-export__cell--num">
                          #
                        </span>
                        <span className="deal-jira-export__cell">Summary</span>
                        <span className="deal-jira-export__cell deal-jira-export__cell--type">
                          Type
                        </span>
                        <span className="deal-jira-export__cell">Business Process</span>
                        <span className="deal-jira-export__cell deal-jira-export__cell--prio">
                          Priority
                        </span>
                        <span className="deal-jira-export__cell deal-jira-export__cell--hours">
                          Est (h)
                        </span>
                      </div>
                      {jiraPlan.tickets.map((t, i) => (
                        <div className="deal-jira-export__row" role="row" key={t.sourceKey}>
                          <span className="deal-jira-export__cell deal-jira-export__cell--num">
                            {i + 1}
                          </span>
                          <span className="deal-jira-export__cell deal-jira-export__cell--summary">
                            {t.summary}
                          </span>
                          <span className="deal-jira-export__cell deal-jira-export__cell--type">
                            {t.issueType}
                          </span>
                          <span className="deal-jira-export__cell">{t.businessProcess || '-'}</span>
                          <span className="deal-jira-export__cell deal-jira-export__cell--prio">
                            {t.recommendedPriority ?? '-'}
                          </span>
                          <span className="deal-jira-export__cell deal-jira-export__cell--hours">
                            {t.estimateHours ?? '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="deal-jira-export__empty">No costed scope items to export yet.</p>
                  )}
                  <div className="deal-jira-export__footer">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={
                        isExportingJira || !jiraPlan || jiraPlan.tickets.length === 0 || !dealUuid
                      }
                      onClick={() => {
                        if (!dealUuid) return;
                        setIsExportingJira(true);
                        const start = Date.now();
                        void salesApi.exportJiraTicketsXlsx(dealUuid).finally(() => {
                          const elapsed = Date.now() - start;
                          setTimeout(() => setIsExportingJira(false), Math.max(0, 600 - elapsed));
                        });
                      }}
                    >
                      {isExportingJira ? 'Downloading…' : 'Download Excel'}
                    </Button>
                  </div>

                  {/* Step 2: push to Jira (deal = Epic; re-sync updates/closes - no duplicates). */}
                  <div className="deal-jira-export__sync">
                    {jiraConnections.length === 0 ? (
                      <p className="deal-jira-export__hint">
                        Connect Jira under Integrations to enable one-click sync.
                      </p>
                    ) : (
                      <>
                        <div className="deal-jira-export__sync-row">
                          {jiraConnections.length > 1 && (
                            <label className="deal-jira-export__field">
                              <span className="deal-jira-export__field-label">Jira</span>
                              <select
                                className="deal-jira-export__select"
                                value={jiraCredentialUuid}
                                onChange={(e) => {
                                  const uuid = e.target.value;
                                  setJiraCredentialUuid(uuid);
                                  setJiraProjects([]);
                                  setJiraProjectKey('');
                                  if (uuid) {
                                    setJiraLoadingProjects(true);
                                    void salesApi
                                      .listJiraProjects(uuid)
                                      .then((ps) => {
                                        setJiraProjects(ps);
                                        setJiraProjectKey(ps[0]?.id ?? '');
                                      })
                                      .catch((err) =>
                                        console.error('[jira-export] projects load failed', err)
                                      )
                                      .finally(() => setJiraLoadingProjects(false));
                                  }
                                }}
                              >
                                {jiraConnections.map((c) => (
                                  <option key={c.uuid} value={c.uuid}>
                                    {c.displayName}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                          <label className="deal-jira-export__field">
                            <span className="deal-jira-export__field-label">Project</span>
                            <select
                              className="deal-jira-export__select"
                              value={jiraProjectKey}
                              disabled={jiraLoadingProjects || jiraProjects.length === 0}
                              onChange={(e) => setJiraProjectKey(e.target.value)}
                            >
                              {jiraProjects.length === 0 && (
                                <option value="">
                                  {jiraLoadingProjects ? 'Loading…' : 'No projects'}
                                </option>
                              )}
                              {jiraProjects.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            disabled={
                              isSyncingJira ||
                              !dealUuid ||
                              !jiraCredentialUuid ||
                              !jiraProjectKey ||
                              !jiraPlan ||
                              jiraPlan.tickets.length === 0
                            }
                            onClick={() => {
                              if (!dealUuid || !jiraCredentialUuid || !jiraProjectKey) return;
                              setIsSyncingJira(true);
                              setJiraSyncSummary(null);
                              const start = Date.now();
                              void salesApi
                                .syncJiraTickets(dealUuid, {
                                  credentialUuid: jiraCredentialUuid,
                                  projectKey: jiraProjectKey,
                                  containerType: 'epic',
                                })
                                .then((summary) => setJiraSyncSummary(summary))
                                .catch((err) => {
                                  console.error('[jira-export] sync failed', err);
                                  setJiraSyncSummary({
                                    container: null,
                                    created: 0,
                                    updated: 0,
                                    reopened: 0,
                                    skipped: 0,
                                    removed: 0,
                                    errors: [err instanceof Error ? err.message : String(err)],
                                    links: [],
                                  });
                                })
                                .finally(() => {
                                  const elapsed = Date.now() - start;
                                  setTimeout(
                                    () => setIsSyncingJira(false),
                                    Math.max(0, 600 - elapsed)
                                  );
                                });
                            }}
                          >
                            {isSyncingJira
                              ? 'Syncing…'
                              : jiraSyncSummary
                                ? 'Re-sync to Jira'
                                : 'Sync to Jira'}
                          </Button>
                        </div>
                        <p className="deal-jira-export__hint">
                          Each scope item becomes a Task under one Epic for this deal. Re-syncing
                          updates changed tickets and closes removed ones - no duplicates.
                        </p>
                      </>
                    )}

                    {jiraSyncSummary && (
                      <div className="deal-jira-export__result">
                        <p className="deal-jira-export__result-counts">
                          {jiraSyncSummary.created} created, {jiraSyncSummary.updated} updated,{' '}
                          {jiraSyncSummary.reopened} reopened, {jiraSyncSummary.skipped} skipped,{' '}
                          {jiraSyncSummary.removed} removed
                        </p>
                        {jiraSyncSummary.container?.url && jiraSyncSummary.container.key && (
                          <p className="deal-jira-export__result-epic">
                            <a
                              href={jiraSyncSummary.container.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open Epic {jiraSyncSummary.container.key}
                            </a>
                          </p>
                        )}
                        {jiraSyncSummary.errors.length > 0 && (
                          <ul className="deal-jira-export__warnings">
                            {jiraSyncSummary.errors.map((e) => (
                              <li key={e}>{e}</li>
                            ))}
                          </ul>
                        )}
                        {jiraSyncSummary.links.length > 0 && (
                          <ul className="deal-jira-export__links">
                            {jiraSyncSummary.links.map((l) => (
                              <li key={`${l.sourceKey}-${l.issueKey}`}>
                                {l.url ? (
                                  <a href={l.url} target="_blank" rel="noreferrer">
                                    {l.issueKey}
                                  </a>
                                ) : (
                                  <span>{l.issueKey}</span>
                                )}{' '}
                                - {l.action}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </ContextDrawer>
              {(isEditMode || salesModuleRole !== 'viewer') && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(isEditMode ? `/so/deal/${dealUuid}` : `/so/deal/${dealUuid}/edit`)
                  }
                >
                  {isEditMode ? 'View' : 'Edit'}
                </Button>
              )}
              {isEditMode ? <RegenerateMenuButton currentArtifact="estimate" /> : null}
            </div>
          ),
          content: (
            <DealPrioritizationPane
              dealUuid={dealUuid ?? ''}
              questionnaire={questionnaire}
              loading={loadingQuestionnaire}
              estimate={savedEstimate?.estimateData ?? null}
              focusedAdhocFieldKey={focusedAdhocFieldKey}
              onFocusAdhocField={setFocusedAdhocFieldKey}
              onCommitScopeFields={handleCommitAdhocScopeFields}
              onReorderComplete={handleReloadQuestionnaire}
              canReorder={salesModuleRole !== 'viewer'}
              areaPriorities={bprAreaPriorities}
            />
          ),
        };
      }

      // Docs tab: uploaded documents only
      if (kind === 'docs') {
        return {
          headerAction:
            isEditMode || salesModuleRole !== 'viewer' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate(isEditMode ? `/so/deal/${dealUuid}` : `/so/deal/${dealUuid}/edit`)
                }
              >
                {isEditMode ? 'View' : 'Edit'}
              </Button>
            ) : null,
          content: (
            <div className="deal-docs-home">
              <MeetingContextPanel
                context={meetingContext.context}
                isLoading={meetingContext.isLoading}
                isGenerating={meetingContext.isGenerating}
                error={meetingContext.error}
                canRegenerate={salesModuleRole !== 'viewer'}
                onRegenerate={meetingContext.regenerate}
                onOpenDocument={(docUuid) => setViewingDoc({ kind: 'upload', docUuid })}
              />
              <DealDocumentsPanel
                documents={dealDocuments}
                isLoading={loadingDocuments}
                aiDocs={[]}
                uploadedOnly
                activityByDoc={dealDocActivity}
                userEvents={dealDocUserEvents}
                onViewDocument={(docUuid) => setViewingDoc({ kind: 'upload', docUuid })}
              />
            </div>
          ),
        };
      }

      // Contract tab: the template's contract section as its own form next to Estimate.
      // The fields stay ordinary questionnaire answers (same answer map, save flow, SOW
      // fields.* bindings and readiness gate) - only the surface moved out of the
      // Questionnaire tab. Editable for non-viewers on both the view and edit routes,
      // like the Estimate tab's overrides; viewers get the read-only projection.
      // Header actions: Save, Generate/Regenerate ESTIMATE (the contract prefills all
      // derive from it), and - for static-SOW categories - Generate SOW, clickable only
      // once every required contract field is filled (live answers; the click persists
      // them, and the server readiness report stays the authoritative gate).
      if (kind === 'contract' && hasContractTab && contractPanelState) {
        const canEditContract = salesModuleRole !== 'viewer';
        const isStaticSowCategory =
          deal?.dealCategoryKey != null &&
          STATIC_SOW_WIZARD_CATEGORY_KEYS.has(deal.dealCategoryKey);
        const contractComplete = getQuestionnaireCompletionState(
          questionnaire,
          contractPanelState.answers,
          'contract'
        ).isComplete;
        // Mirrors generateButtonDisabled || isAiRunActive (declared after this memo, so
        // rebuilt inline from the same pre-memo inputs).
        const contractGenerateDisabled =
          isGenerating ||
          isAnyAiRunning ||
          !canGenerateEstimate ||
          savingSow ||
          savingAnswers ||
          estimateResult?.documentAvailability?.estimateAvailable === false;
        return {
          label: 'Contract',
          headerAction: canEditContract ? (
            <div className="sales-estimate-pane__header-actions">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={savingAnswers || contractPanelState.savableFieldKeys.size === 0}
                onClick={() => void handlePersistAnswers()}
              >
                {savingAnswers ? <span className="wizard__btn-spinner" aria-hidden="true" /> : 'Save'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={contractGenerateDisabled}
                onClick={() => {
                  // A saved estimate gets the overrides-aware confirm dialog (scoped to
                  // the estimate); a fresh deal generates directly - same routing as
                  // RegenerateMenuButton's estimate item.
                  if (savedEstimate) {
                    setRegenerateScope('estimate');
                    setRegenerateKeepOverrides(true);
                    setRegenerateConfirmOpen(true);
                  } else {
                    handleRegenerateEstimate();
                  }
                }}
              >
                {isGenerating
                  ? 'Generating...'
                  : savedEstimate
                    ? 'Regenerate estimate'
                    : 'Generate estimate'}
              </Button>
              {isStaticSowCategory && !sow ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!contractComplete || previewingSow || loadingSow || isAnyAiRunning}
                  onClick={() => void handleWizardPreviewSow()}
                >
                  {previewingSow ? (
                    <span className="wizard__btn-spinner" aria-hidden="true" />
                  ) : null}
                  Generate SOW
                </Button>
              ) : null}
            </div>
          ) : null,
          content: (
            <div className="deal-contract-pane">
              {canEditContract ? (
                <SalesCreateDealSurface
                  deal={deal}
                  loadingDeal={loadingDeal}
                  dealError={dealError}
                  onRetryLoadDeal={retryLoadDeal}
                  questionnaire={questionnaire}
                  loadingQuestionnaire={loadingQuestionnaire}
                  questionnaireError={questionnaireError}
                  onRetryLoadQuestionnaire={retryLoadQuestionnaire}
                  answers={contractPanelState.answers}
                  persistedAnswers={contractPanelState.persistedAnswers}
                  touchedFields={contractPanelState.touchedFields}
                  setTouchedFields={setTouchedFields}
                  changedFieldKeys={contractPanelState.changedFieldKeys}
                  savableFieldKeys={contractPanelState.savableFieldKeys}
                  saveError={contractPanelState.saveError}
                  onAnswerChange={handleAnswerChange}
                  onPersistAnswers={handlePersistAnswers}
                  isEnrichmentRunning={false}
                  currentUserUuid={appUser?.uuid}
                  salesModuleRole={salesModuleRole}
                  dealUuid={dealUuid}
                  onReloadQuestionnaire={handleReloadQuestionnaire}
                  compactMode
                  selectedSectionKeys={[CONTRACT_SECTION_KEY]}
                />
              ) : (
                <DealViewSurface
                  loading={loadingQuestionnaire}
                  questionnaire={questionnaire}
                  answers={contractPanelState.answers}
                  fieldLookup={dealPageFieldLookup}
                  showAnsweredOnly={false}
                  compactMode
                  selectedSectionKeys={[CONTRACT_SECTION_KEY]}
                />
              )}
            </div>
          ),
        };
      }

      // No dedicated panel for this view. The AI-generated artifacts (estimate / resource plan / SOW)
      // each have their own tab and uploads live under the Docs tab, so the old two-list documents
      // index was redundant - show a short hint instead of resurrecting that fallback UI.
      return {
        content: (
          <div className="deal-doc-empty">
            <p className="deal-doc-empty__text">Open a document from the tabs above.</p>
          </div>
        ),
      };
    };

    const PANEL_BUILDERS: Record<string, () => DealPanelProps> = {
      'deal-documents': () =>
        buildDocPanel(
          viewingDoc?.kind,
          viewingDoc?.kind === 'upload' ? viewingDoc.docUuid : undefined
        ),
      chat: () => ({
        noPadding: true,
        // This IS the module copilot (with page-specific card handlers) - tells
        // the shell not to append the global module-chat item on this page.
        isModuleChat: true,
        label: aiAssistantName,
        icon: <AiBrandIcon className="deal-page__ai-icon deal-page__ai-icon--md" />,
        headerTitle: (
          <SessionTitleEditable
            sessions={chatSession.state.sessions}
            activeSessionUuid={chatSession.state.activeSessionUuid}
            onRename={chatSession.renameSession}
          />
        ),
        headerAction: (
          <div className="chat-header-action">
            <ChatConnectionIndicator
              connectionStatus={chatSession.state.connectionStatus}
              reconnectAttempt={chatSession.state.reconnectAttempt}
              onReconnect={chatSession.manualReconnect}
            />
            <SessionControls
              sessions={chatSession.state.sessions}
              sessionsStatus={chatSession.state.sessionsStatus}
              activeSessionUuid={chatSession.state.activeSessionUuid}
              sessionPool={chatSession.state.sessionPool}
              onLoad={chatSession.loadSessions}
              onSwitch={chatSession.switchSession}
              onCreate={chatSession.createNewSession}
            />
          </div>
        ),
        content: (
          <AiChatPane
            state={chatSession.state}
            isConnected={chatSession.isConnected}
            isRunActive={chatSession.isRunActive}
            sendRun={chatSession.sendRun}
            cancelRun={chatSession.cancelRun}
            confirmRun={chatSession.confirmRun}
            denyRun={chatSession.denyRun}
            respondPrompt={chatSession.respondPrompt}
            retryRun={chatSession.retryRun}
            updateContext={chatSession.updateContext}
            setDraftExplicitContext={chatSession.setDraftExplicitContext}
            confirmDraftExplicitContext={chatSession.confirmDraftExplicitContext}
            clearDraftExplicitContext={chatSession.clearDraftExplicitContext}
            removeExplicitContext={chatSession.removeExplicitContext}
            clearExplicitContext={chatSession.clearExplicitContext}
            loadOlderHistory={chatSession.loadOlderHistory}
            setComposerMode={chatSession.setComposerMode}
            projectUuid={workspace?.uuid ?? ''}
            pageScope="deal"
            onAction={handleChatAction}
            onActionSettled={chatSession.setMessageActionResult}
          />
        ),
      }),
    };

    // Deal attributes pane - always present when the deal has configured attributes,
    // independent of the DB-driven right-rail catalog.
    const attrPaneItem: RightRailItem | null =
      dealAttributes.length > 0 || loadingDealAttributes
        ? {
            id: 'deal-attributes',
            label: 'Attributes',
            icon: <ListBulletIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
            loading: loadingDealAttributes,
            content: (
              <div className="deal-attr-pane">
                {deal ? (
                  <div className="deal-attr-pane__meta">
                    {deal.source ? (
                      <Pill variant={deal.source === 'ai' ? 'accent' : 'ghost'}>
                        {deal.source === 'ai' ? 'AI' : 'Manual'}
                      </Pill>
                    ) : null}
                    <Pill variant={getDealStatusPresentation(deal.status).variant}>
                      {getDealStatusPresentation(deal.status).label}
                    </Pill>
                  </div>
                ) : null}
                {dealAttributes.map((attr) => (
                  <div key={attr.attributeKey} className="deal-attr-pane__row">
                    <span className="deal-attr-pane__label">{attr.label}</span>
                    <Select
                      value={attr.currentValue ?? ''}
                      onValueChange={(val) =>
                        void handleUpdateDealAttribute(attr.attributeKey, val || null)
                      }
                      placeholder="-"
                      size="comfortable"
                    >
                      {attr.options.map((opt) => (
                        <SelectItem key={opt.valueKey} value={opt.valueKey}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                ))}
              </div>
            ),
          }
        : null;

    const catalogItems = rightRailCatalog
      // 'deal-documents' moved to the main canvas (subnav tabs) - keep it out of the rail.
      .filter((item) => item.panelKey !== 'deal-documents')
      .filter((item) => item.isEnabled)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .flatMap((item) => {
        const builder = PANEL_BUILDERS[item.panelKey];
        if (!builder) return [];
        const props = builder();
        return [
          {
            id: item.panelKey,
            label: props.label ?? item.label,
            icon: props.icon ?? resolveProjectMenuIcon(item.iconKey),
            content: props.content,
            headerAction: props.headerAction,
            headerTitle: props.headerTitle,
            noPadding: props.noPadding,
            // Must survive the mapping: AppShell appends the global module-chat
            // item unless an item carries this flag (else: duplicated AI chat).
            isModuleChat: props.isModuleChat,
          } satisfies RightRailItem,
        ];
      });

    const all = [...catalogItems, ...(attrPaneItem ? [attrPaneItem] : [])];
    // Active document viewer + its toolbar for the main canvas. Reuses the
    // existing 'deal-documents' builder (estimate/RP/SOW/upload/documents index);
    // null when no document is open (the center pane shows the deal Overview).
    const effectiveDocKind =
      viewingDoc?.kind ?? (hideQuestionnaireTabs ? 'estimate' : undefined);
    const documentPanel = effectiveDocKind
      ? (() => {
          const p = buildDocPanel(
            effectiveDocKind,
            viewingDoc?.kind === 'upload' ? viewingDoc.docUuid : undefined
          );
          return p ? { surface: p.content, headerActions: p.headerAction ?? null } : null;
        })()
      : null;
    const secondaryDocumentPanel = secondaryViewingDoc
      ? (() => {
          const p = buildDocPanel(
            secondaryViewingDoc.kind,
            secondaryViewingDoc.kind === 'upload' ? secondaryViewingDoc.docUuid : undefined
          );
          return p ? { surface: p.content, headerActions: p.headerAction ?? null } : null;
        })()
      : null;
    return { items: all.length > 0 ? all : undefined, documentPanel, secondaryDocumentPanel };
  }, [
    canGenerateEstimate,
    dealAttributes,
    dealUuid,
    // The Prioritization pane reads these reactively - without them the memoized panel surface goes
    // stale and immediate scope/priority commits (which mutate the in-memory questionnaire) and
    // cross-pane focus never repaint the pane, even though the questionnaire card does.
    questionnaire,
    loadingQuestionnaire,
    isAnyAiRunning,
    isEditMode,
    salesModuleRole,
    focusedAdhocFieldKey,
    // The Estimate tab's "Upload finalized estimate" action gates on the deal's category.
    deal,
    estimateError,
    estimateIsStale,
    estimateResult,
    generatingResourcePlan,
    generatingSow,
    handleUpdateDealAttribute,
    loadingDealAttributes,
    handleSectionOverride,
    isCreateStartRoute,
    loadingEstimate,
    loadingResourcePlan,
    loadingSow,
    questionnaireCompletion.missingRequiredCount,
    resourcePlan,
    resourcePlanError,
    rightRailCatalog,
    savedEstimate,
    savingSow,
    savingAnswers,
    savingEstimate,
    sow,
    sowDraftContent,
    sowError,
    sowReadinessReport,
    sowSaveStatus,
    isExportingEstimate,
    setIsExportingEstimate,
    isLoadingJiraPlan,
    isExportingJira,
    jiraDrawerOpen,
    jiraPlan,
    jiraConnections,
    jiraCredentialUuid,
    jiraProjects,
    jiraProjectKey,
    jiraLoadingProjects,
    isSyncingJira,
    jiraSyncSummary,
    viewingDoc,
    setViewingDoc,
    secondaryViewingDoc,
    isExportingResourcePlan,
    setIsExportingResourcePlan,
    isExportingSow,
    setIsExportingSow,
    setRightPaneMode,
    setRightPaneVisible,
    chatSession,
    aiAssistantName,
    vendorLogoSrc,
    dealDocuments,
    loadingDocuments,
    dealDocActivity,
    dealDocUserEvents,
    meetingContext.context,
    meetingContext.isLoading,
    meetingContext.isGenerating,
    meetingContext.error,
    meetingContext.regenerate,
    isDocViewAllowed,
    hidePrioritizationTab,
    hideQuestionnaireTabs,
    // The Systems Landscape canvas repaints when its resolved-systems signature changes (not on every
    // keystroke); it reads the latest answers via answersRef, so only the signature + lookup are deps.
    hasSystemsLandscapeTab,
    techStackAnswersSignature,
    // The Scope doc repaints on any answer change while a scope panel is open (null when
    // closed, so other tabs stay keystroke-stable); it reads the latest answers via answersRef.
    scopeAnswersDep,
    // Scope-doc editor state: the draft (edit mode), save flight/error, autosave status, view filter.
    scopeDraftDoc,
    scopeDocDirty,
    scopeSavingDoc,
    scopeSaveError,
    scopeJustSaved,
    scopeAnsweredOnly,
    scopeAllCollapsed,
    handleScopeEditorReady,
    handleScopeRemoveRequest,
    handleSaveScopeDoc,
    handleScopeDraftChange,
    handleScopePropertyAnswerChange,
    confirmIfDirty,
    dealPageFieldLookup,
    // Prioritization group ordering follows the BPR knowledge graph's area rollup.
    bprAreaPriorities,
    // Contract tab: gated live edit state (null unless a contract panel is open) + its handlers.
    hasContractTab,
    contractPanelState,
    loadingDeal,
    dealError,
    retryLoadDeal,
    questionnaireError,
    retryLoadQuestionnaire,
    appUser?.uuid,
    handleAnswerChange,
    handlePersistAnswers,
    handleRegenerateEstimate,
    // SOW tab's static-category "Generate SOW" button.
    handleWizardPreviewSow,
    previewingSow,
  ]);

  const rightRailItems = railAndDocs.items;
  const documentPanel = railAndDocs.documentPanel;
  const secondaryDocumentPanel = railAndDocs.secondaryDocumentPanel;

  // Main-canvas document switcher. Tabs live in MainContentShell's subnav slot;
  // 'overview' is the deal form, the rest map onto viewingDoc. 'upload' and
  // 'documents' are transient views (reached from chat / the options menu) and
  // intentionally have no tab, so no tab is highlighted while they are open.
  const documentTabItems = React.useMemo<TabsItem[]>(
    () =>
      DEAL_TAB_SPECS.filter(
        (t) =>
          visibleTabValues.has(t.value) &&
          !(t.value === 'questionnaire' && hideQuestionnaireTabs) &&
          !(t.value === 'prioritization' && hidePrioritizationTab) &&
          !(t.value === 'systems-landscape' && !hasSystemsLandscapeTab) &&
          !(t.value === 'contract' && !hasContractTab)
      ).map((t) => ({
        value: t.value,
        label: t.stage ? (
          <span className="deal-page__tab-stage-label">
            {t.label}
            <StageBadge stage={t.stage} />
          </span>
        ) : (
          t.label
        ),
      })),
    [
      visibleTabValues,
      hideQuestionnaireTabs,
      hidePrioritizationTab,
      hasSystemsLandscapeTab,
      hasContractTab,
    ]
  );
  const activeDocValue: string = viewingDoc?.kind ?? (hideQuestionnaireTabs ? 'estimate' : 'questionnaire');
  // No dirty guard needed: switching tabs is an in-page view change that keeps
  // all parent-owned state (questionnaire answers, SOW draft), exactly like the
  // old right-rail switch did (which only guarded 'rr:' sources, never these).
  // A scope-list deal has no Questionnaire tab, but the canvas default (viewingDoc === null) IS the
  // questionnaire - so without this the deal would open on a view whose tab is gone. Land on the
  // Estimate instead, once, and only while nothing else is being viewed.
  const scopeSourcedLandingRef = React.useRef(false);
  React.useEffect(() => {
    if (!hideQuestionnaireTabs || scopeSourcedLandingRef.current) return;
    scopeSourcedLandingRef.current = true;
    setViewingDoc((current) => current ?? ({ kind: 'estimate' } as ViewingDocState));
  }, [hideQuestionnaireTabs, setViewingDoc]);

  const switchDocMode = React.useCallback(
    (value: string) => {
      setViewingDoc(value === 'questionnaire' ? null : ({ kind: value } as ViewingDocState));
    },
    [setViewingDoc]
  );
  const openDocumentsIndex = React.useCallback(
    () => setViewingDoc({ kind: 'documents' }),
    [setViewingDoc]
  );

  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = React.useState(false);
  // Which action the estimate/all confirm dialog dispatches on confirm: 'all' (Generate all =
  // estimate then SOW) or 'estimate' (Regenerate estimate only). Drives the dialog copy + which
  // handler runs + whether the SOW keep-edits row is shown.
  const [regenerateScope, setRegenerateScope] = React.useState<'all' | 'estimate'>('all');
  // Whether to carry the user's manual hour overrides onto the regenerated
  // estimate. Defaults to keeping them (the safer choice - no silent data loss).
  const [regenerateKeepOverrides, setRegenerateKeepOverrides] = React.useState(true);
  const [regenerateReinferHours, setRegenerateReinferHours] = React.useState(false);
  // Whether "Generate all" should carry the prior SOW's structured overrides onto the
  // regenerated SOW (the dialog's "Keep my SOW edits" row, shown only when scope==='all').
  const [regenerateKeepSowOverrides, setRegenerateKeepSowOverrides] = React.useState(true);
  // The keep/discard decisions read by the generate handlers at call time (refs so the
  // handlers need not depend on dialog state / be re-created on toggle).
  const keepOverridesOnRegenerateRef = React.useRef(false);
  const reinferHoursOnRegenerateRef = React.useRef(false);
  const keepSowOverridesOnRegenerateRef = React.useRef(false);

  // SOW-only regenerate confirm dialog (the "Regenerate SOW" button).
  const [sowRegenerateConfirmOpen, setSowRegenerateConfirmOpen] = React.useState(false);
  const [sowRegenerateKeepEdits, setSowRegenerateKeepEdits] = React.useState(true);

  // The current estimate's manual overrides, resolved to display labels, for the
  // regenerate-confirm dialog preview. Empty when there are no overrides.
  const regenerateOverridePreview = React.useMemo(() => {
    const overrides = estimateResult?.overrides;
    if (!overrides) return [];
    const sections = estimateResult?.estimate.sections ?? [];
    return Object.entries(overrides).map(([key, ov]) => {
      // key = "subsection:{sectionKey}:{subsectionKey}"
      const [, sectionKey, subsectionKey] = key.split(':');
      const section = sections.find((s) => s.sectionKey === sectionKey);
      const sub = section?.subsections.find((x) => x.subsectionKey === subsectionKey);
      return {
        key,
        label: sub?.label ?? subsectionKey ?? key,
        originalHours: ov.originalHours,
        overrideHours: ov.overrideHours,
      };
    });
  }, [estimateResult]);
  const hasRegenerateOverrides = regenerateOverridePreview.length > 0;

  // The current SOW's edits, summarized for the SOW regenerate-confirm dialog. Structured
  // overrides (table cells / token values / removed sections) CAN be carried onto the
  // regenerated SOW ("Keep my SOW edits"); manual prose edits cannot (kept: false).
  const sowRegenerateOverridePreview = React.useMemo(() => {
    if (!sow) return [] as Array<{ key: string; label: string; kept: boolean }>;
    const items: Array<{ key: string; label: string; kept: boolean }> = [];
    const dataCount = sow.dataOverrides ? Object.keys(sow.dataOverrides).length : 0;
    const tokenCount = sow.tokenOverrides ? Object.keys(sow.tokenOverrides).length : 0;
    const exclCount = sow.excludedSections ? sow.excludedSections.length : 0;
    if (dataCount > 0)
      items.push({
        key: 'data',
        label: `${dataCount} edited table${dataCount > 1 ? 's' : ''}`,
        kept: true,
      });
    if (tokenCount > 0)
      items.push({
        key: 'token',
        label: `${tokenCount} edited value${tokenCount > 1 ? 's' : ''}`,
        kept: true,
      });
    if (exclCount > 0)
      items.push({
        key: 'excluded',
        label: `${exclCount} removed section${exclCount > 1 ? 's' : ''}`,
        kept: true,
      });
    if (sow.userEditedAt) items.push({ key: 'prose', label: 'Manual text edits', kept: false });
    return items;
  }, [sow]);
  const hasSowRegenerateOverrides = sowRegenerateOverridePreview.length > 0;
  // Whether the deal's product supports SOW generation (SOW_SUPPORTED_PRODUCT_KEYS).
  const isSowSupportedForDeal = React.useMemo(
    () => isSowSupportedProductKey(normalizeDealProductKey(deal)),
    [deal]
  );

  // Strict category gating: nothing to generate when the deal's category has no estimate
  // configuration (the resource plan + SOW key off the estimate, so this gates the batch).
  const estimateNotConfigured = estimateResult?.documentAvailability?.estimateAvailable === false;
  // COS-100 (docs/plans/72): an AI-mode deal with no captured scope items can only generate a
  // silent 0 h estimate + SOW - the same predicate the API gate refuses on. Reads the live answer
  // map (Generate saves it first). Kept out of canGenerateEstimate, which also drives deal status.
  const noScopeCaptured = React.useMemo(
    () =>
      isDealPageBlockedForNoScope(
        questionnaire,
        deal?.dealCategoryKey,
        answers,
        estimateResult?.overrides ?? null
      ),
    [questionnaire, deal?.dealCategoryKey, answers, estimateResult?.overrides]
  );
  const generateButtonDisabled =
    isGenerating ||
    !canGenerateEstimate ||
    savingSow ||
    savingAnswers ||
    estimateNotConfigured ||
    noScopeCaptured;
  // Every reason Generate is disabled, shown on hover - one line per reason, mirroring the
  // conditions above (isAiRunActive is added by RegenerateMenuButton, which owns that read).
  const generateDisabledReasons = React.useMemo(() => {
    const reasons: GenerateDisabledReason[] = [];
    if (loadingQuestionnaire) reasons.push({ text: 'The questionnaire is still loading.' });
    else if (!hasQuestionnaireSections)
      reasons.push({ text: 'This deal has no questionnaire yet.' });
    else if (!questionnaireCompletion.isComplete) {
      const labels = questionnaireCompletion.missingRequiredLabels;
      const count = questionnaireCompletion.missingRequiredCount;
      reasons.push({
        text: `${count} required ${count === 1 ? 'question is' : 'questions are'} not answered yet:`,
        details: [
          ...labels.slice(0, 8),
          ...(labels.length > 8 ? [`and ${labels.length - 8} more`] : []),
        ],
      });
    }
    if (savingAnswers) reasons.push({ text: 'Your answers are still saving.' });
    if (savingSow) reasons.push({ text: 'The SOW is still saving.' });
    if (estimateNotConfigured)
      reasons.push({
        text: 'Estimates are not set up for this product and deal type yet. Ask a workspace admin.',
      });
    if (noScopeCaptured)
      reasons.push({
        text: `${NO_SCOPE_CAPTURED_MESSAGE} Upload the transcript or document (or click Capture questions if the Assistant shows that card), or mark a questionnaire item as Requested.`,
      });
    return reasons;
  }, [
    loadingQuestionnaire,
    hasQuestionnaireSections,
    questionnaireCompletion,
    savingAnswers,
    savingSow,
    estimateNotConfigured,
    noScopeCaptured,
  ]);

  // Leading header status shown BEFORE the "Edit mode" badge: the Scope tab's autosave indicator
  // (Scope autosaves, so there's no Save button - just this). Null on every other tab.
  const documentHeaderStatus: React.ReactNode =
    viewingDoc?.kind === 'scope' && isEditMode ? (
      <span className="deal-scope-doc__autosave" aria-live="polite">
        {scopeSaveError ? (
          <span className="deal-scope-doc__save-error">{scopeSaveError}</span>
        ) : scopeSavingDoc || scopeDocDirty || savingAnswers ? (
          'Saving…'
        ) : scopeJustSaved ? (
          'Saved'
        ) : null}
      </span>
    ) : null;

  // Shared state/handlers passed to the center-pane child route (DealMain, shared
  // by the view + edit routes) via Outlet context. Owned here so the right rail (chat/docs/
  // props) - which reads the same data and answer-editing state - never
  // remounts when toggling view ↔ edit.
  const ctx: DealWorkspaceOutletContext = {
    dealUuid,
    appUserUuid: appUser?.uuid,
    salesModuleRole,
    isWorkspaceAdmin,
    deal,
    loadingDeal,
    dealError,
    retryLoadDeal,
    questionnaire,
    loadingQuestionnaire,
    questionnaireError,
    retryLoadQuestionnaire,
    answers,
    persistedAnswers,
    fieldLookup: dealPageFieldLookup,
    touchedFields,
    setTouchedFields,
    changedFieldKeys,
    savableFieldKeys,
    saveError,
    handleAnswerChange,
    handlePersistAnswers,
    handleReloadQuestionnaire,
    dealAttributes,
    handleOpenAttributesPane,
    handleUpdateDealTitle,
    handleGenerateAll,
    handleRegenerateEstimate,
    handleRegenerateSow,
    handleWizardPreviewSow,
    previewingSow,
    openEnrichDialog: () => setShowEnrichConfirmPanel(true),
    isAiRunActive: chatSession.isRunActive,
    isEnrichmentRunning: enrichmentTriggered && chatSession.isRunActive,
    capturingAdhocCount,
    onAdhocScopeChange: handleAdhocScopeChange,
    commitAdhocScopeFields: handleCommitAdhocScopeFields,
    hasPendingAdhocScope: Object.keys(pendingScopeMeta).length > 0,
    focusedAdhocFieldKey,
    setFocusedAdhocFieldKey,
    setShowTranscriptUploadPanel,
    setViewingDoc,
    secondaryDocumentSurface: secondaryDocumentPanel?.surface ?? null,
    setSecondaryViewingDoc,
    // Main-canvas document tabs/viewer (estimate/RP/SOW now render in main).
    documentTabItems,
    activeDocValue,
    switchDocMode,
    documentSurface: documentPanel?.surface ?? null,
    documentHeaderActions: documentPanel?.headerActions ?? null,
    documentHeaderStatus,
    openDocumentsIndex,
    isGenerating,
    generateButtonDisabled,
    generateDisabledReasons,
    noScopeCaptured,
    hasSavedEstimate: !!savedEstimate,
    hasEstimateResult: !!estimateResult,
    hasSavedSow: !!sow,
    isSowSupportedForDeal,
    // "Generate all" confirm (estimate then SOW). Defaults to keeping both estimate + SOW edits.
    openRegenerateConfirm: () => {
      setRegenerateScope('all');
      setRegenerateKeepOverrides(true);
      setRegenerateKeepSowOverrides(true);
      setRegenerateConfirmOpen(true);
    },
    // Estimate-only "Regenerate" confirm (reuses the same dialog, scoped to the estimate).
    openEstimateRegenerateConfirm: () => {
      setRegenerateScope('estimate');
      setRegenerateKeepOverrides(true);
      setRegenerateConfirmOpen(true);
    },
    // SOW-only "Regenerate" confirm.
    openSowRegenerateConfirm: () => {
      setSowRegenerateKeepEdits(true);
      setSowRegenerateConfirmOpen(true);
    },
    confirmIfDirty,
    dirtyKey,
  };

  if (isLoadingWorkspace) {
    return null;
  }

  const main = isCreateRoute ? (
    isCreateStartRoute ? (
      <MainContentShell title="New deal">
        <NewDealSidePanel
          deal={null}
          onClose={handleClosePreviewDeal}
          onOpen={handleOpenPreviewDeal}
        />
        <div
          className="sales-create-shell__new-body"
          style={{
            gridTemplateColumns: `repeat(${companyDeals === null ? 1 : 2}, 1fr)`,
          }}
        >
          {/* ── Step cells (row 1 of the shared grid) ── */}
          <div className="sales-create-shell__step sales-create-shell__step--active">
            <span className="sales-create-shell__step-num">1</span>
            <span className="sales-create-shell__step-text">
              <span className="sales-create-shell__step-label">Find or create a deal</span>
              <span className="sales-create-shell__step-subtitle">
                Search for a company to open any of its deals, or start a new one
              </span>
            </span>
          </div>
          {companyDeals !== null ? (
            <div className="sales-create-shell__step sales-create-shell__step--active">
              <span className="sales-create-shell__step-num">2</span>
              <span className="sales-create-shell__step-label">
                {selectedExistingDeal ? 'Open deal' : showCreateNew ? 'New deal' : 'Choose action'}
              </span>
            </div>
          ) : null}

          {/* ── Column 1: Company search + Product + Category + Existing deals ── */}
          <div className="sales-create-shell__col">
            <div className="sales-create-shell__company-label-row">
              <label className="sales-create-shell__product-selector-label">Company</label>
            </div>
            <form
              className="sales-create-shell__search-row"
              onSubmit={(e) => {
                e.preventDefault();
                void handleContinue();
              }}
            >
              <AutocompleteField
                className="sales-create-shell__autocomplete sales-create-shell__autocomplete--new"
                value={companyName}
                onChange={(val) => {
                  setCompanyName(val);
                  if (showUploadView) {
                    setShowUploadView(false);
                    setScriptFiles([]);
                    setCreateError(null);
                    setCreateReview(null);
                    setScriptAdditionalNotes('');
                    setCreateFileNotes({});
                  }
                  setShowCreateNew(false);
                }}
                options={companySuggestions}
                loading={loadingSuggestions}
                placeholder="Search company name…"
                disabled={submittingCreate || submittingScript}
                error={createError ?? undefined}
                size="comfortable"
              />
              <Button
                type="submit"
                size="md"
                disabled={!companyName.trim() || submittingCreate || submittingScript}
                className="sales-create-shell__search-btn"
              >
                {submittingCreate && createSubmissionPhase === 'checking' ? (
                  <span className="sales-create-shell__cta-spinner" aria-hidden="true" />
                ) : null}
                <span>Search</span>
              </Button>
            </form>
            {isInitializingWizard ? (
              <>
                <div className="sales-create-shell__product-selector">
                  <label className="sales-create-shell__product-selector-label">Product</label>
                  <div className="sales-create-shell__product-options">
                    <VendorCardSkeleton />
                    <VendorCardSkeleton />
                  </div>
                </div>
                <div className="sales-create-shell__product-selector">
                  <label className="sales-create-shell__product-selector-label">Category</label>
                  <div className="sales-create-shell__category-options">
                    <span className="sales-create-shell__category-chip sales-create-shell__category-chip--skeleton">
                      <SkeletonBlock />
                    </span>
                    <span className="sales-create-shell__category-chip sales-create-shell__category-chip--skeleton">
                      <SkeletonBlock />
                    </span>
                  </div>
                  <p className="sales-create-shell__category-help" aria-hidden="true" />
                </div>
              </>
            ) : (
              <>
                {workspaceProducts.length > 0 ? (
                  <div className="sales-create-shell__product-selector">
                    <label className="sales-create-shell__product-selector-label">Product</label>
                    <div className="sales-create-shell__product-options">
                      {workspaceProducts.map((p) => (
                        <VendorCard
                          key={p.id}
                          logo={
                            <VendorLogo
                              vendorKey={p.vendorKey}
                              productKey={p.productKey}
                              productName={p.productName}
                            />
                          }
                          name={p.productName}
                          subtitle={p.vendorName}
                          isSelected={selectedWorkspaceProductId === p.id}
                          onClick={() => setSelectedWorkspaceProductId(p.id)}
                          disabled={submittingCreate || submittingScript}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
                {loadingCategoryOptions ? (
                  <div className="sales-create-shell__product-selector">
                    <label className="sales-create-shell__product-selector-label">Category</label>
                    <div className="sales-create-shell__category-options">
                      <span className="sales-create-shell__category-chip sales-create-shell__category-chip--skeleton">
                        <SkeletonBlock />
                      </span>
                      <span className="sales-create-shell__category-chip sales-create-shell__category-chip--skeleton">
                        <SkeletonBlock />
                      </span>
                    </div>
                    <p className="sales-create-shell__category-help" aria-hidden="true" />
                  </div>
                ) : categoryOptions.length > 0 ? (
                  <div className="sales-create-shell__product-selector">
                    <label className="sales-create-shell__product-selector-label">Category</label>
                    <div className="sales-create-shell__category-options">
                      {categoryOptions.map((o) => (
                        <button
                          key={o.valueKey}
                          type="button"
                          className={`sales-create-shell__category-chip${
                            selectedCategoryKey === o.valueKey
                              ? ' sales-create-shell__category-chip--selected'
                              : ''
                          }`}
                          disabled={submittingCreate || submittingScript}
                          onClick={() => setSelectedCategoryKey(o.valueKey)}
                          onMouseEnter={() => setHoveredCategoryKey(o.valueKey)}
                          onMouseLeave={() => setHoveredCategoryKey(null)}
                          onFocus={() => setHoveredCategoryKey(o.valueKey)}
                          onBlur={() => setHoveredCategoryKey(null)}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    {/* One plain line saying what the category is for. The line's height is
                        always reserved (CSS min-height) so the layout does not jump. */}
                    <p className="sales-create-shell__category-help" aria-live="polite">
                      {categoryOptions.find(
                        (o) => o.valueKey === (hoveredCategoryKey ?? selectedCategoryKey)
                      )?.description ?? ''}
                    </p>
                  </div>
                ) : null}
              </>
            )}
            {companyDeals !== null && companyDeals.length > 0 ? (
              <div className="sales-create-shell__action-list">
                <div className="sales-create-shell__deals-scroll">
                  <div className="sales-create-shell__list-divider" role="separator">
                    <span className="sales-create-shell__list-divider-label">Existing deals</span>
                  </div>
                  {companyDeals.map((d) => (
                    <ExistingDealRow
                      key={d.uuid}
                      deal={d}
                      attributeOptionsByKey={attributeOptionsByKey}
                      showOwner={salesModuleRole === 'admin'}
                      selected={selectedExistingDeal?.uuid === d.uuid}
                      locked={hasOtherOwnerDeal}
                      onToggle={() =>
                        setSelectedExistingDeal((prev) => (prev?.uuid === d.uuid ? null : d))
                      }
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* ── Column 2: Deal preview OR new-deal action cards OR file upload ── */}
          {companyDeals !== null ? (
            <div className="sales-create-shell__col">
              {selectedExistingDeal ? (
                (() => {
                  const d = selectedExistingDeal;
                  const tmplDefsByKey = new Map(
                    dealPreviewTemplateDefs.map((f) => [f.fieldKey, f.label])
                  );
                  const attrDefsByKey = new Map(
                    dealPreviewAttributeDefs.map((a) => [a.attributeKey, a.label])
                  );
                  const rows = dealPreviewColumnKeys
                    .filter((key) => key !== 'createdAt' && key !== 'updatedAt')
                    .map((key) => {
                      let label = DEAL_PREVIEW_COLUMN_LABELS[key] ?? key;
                      let value: React.ReactNode = '-';
                      if (key === 'title') {
                        value = d.title || d.company.name;
                      } else if (key === 'company') {
                        value = d.company.name;
                      } else if (key === 'product') {
                        value = d.vendorProduct?.productName ?? '-';
                      } else if (key === 'createdBy') {
                        value = d.createdByDisplayName ?? '-';
                      } else if (key === 'createdAt') {
                        value = d.createdAt
                          ? new Date(d.createdAt).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '-';
                      } else if (key === 'status') {
                        const sp = getDealStatusPresentation(d.status);
                        value = <Pill variant={sp.variant}>{sp.label}</Pill>;
                      } else if (key.startsWith('attr:')) {
                        const attrKey = key.slice(5);
                        label = attrDefsByKey.get(attrKey) ?? attrKey;
                        const rawVal = d.fieldValues?.[attrKey] ?? null;
                        value = rawVal
                          ? (dealPreviewAttributesByKey.get(attrKey)?.get(rawVal) ?? rawVal)
                          : '-';
                      } else if (key.startsWith('tmpl:')) {
                        const fieldKey = key.slice(5);
                        label = tmplDefsByKey.get(fieldKey) ?? fieldKey;
                        value = d.fieldValues?.[fieldKey] ?? '-';
                      }
                      return { key, label, value };
                    });
                  return (
                    <div className="sales-create-shell__deal-preview">
                      <div className="sales-create-shell__deal-preview-rows">
                        {rows.map(({ key, label, value }) => (
                          <div key={key} className="sales-create-shell__deal-preview-row">
                            <span className="sales-create-shell__deal-preview-label">{label}</span>
                            <span className="sales-create-shell__deal-preview-value">{value}</span>
                          </div>
                        ))}
                        {d.createdAt ? (
                          <div className="sales-create-shell__deal-preview-row">
                            <span className="sales-create-shell__deal-preview-label">Created</span>
                            <span className="sales-create-shell__deal-preview-value">
                              {new Date(d.createdAt).toLocaleString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        ) : null}
                        {d.updatedAt ? (
                          <div className="sales-create-shell__deal-preview-row">
                            <span className="sales-create-shell__deal-preview-label">Updated</span>
                            <span className="sales-create-shell__deal-preview-value">
                              {new Date(d.updatedAt).toLocaleString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        ) : null}
                      </div>
                      {d.dealCategoryKey || d.dealTypeKey || d.dealStatusKey ? (
                        <div className="sales-create-shell__deal-pills">
                          {d.dealCategoryKey ? (
                            <span className="sales-create-shell__category-chip sales-create-shell__category-chip--static">
                              {dealPreviewAttributesByKey
                                .get('deal_category')
                                ?.get(d.dealCategoryKey) ?? d.dealCategoryKey}
                            </span>
                          ) : null}
                          {d.dealTypeKey ? (
                            <span className="sales-create-shell__category-chip sales-create-shell__category-chip--static">
                              {dealPreviewAttributesByKey.get('deal_type')?.get(d.dealTypeKey) ??
                                d.dealTypeKey}
                            </span>
                          ) : null}
                          {d.dealStatusKey ? (
                            <span className="sales-create-shell__category-chip sales-create-shell__category-chip--static">
                              {dealPreviewAttributesByKey
                                .get('deal_status')
                                ?.get(d.dealStatusKey) ?? d.dealStatusKey}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="sales-create-shell__deal-preview-footer">
                        <div className="sales-create-shell__confirm-create-actions sales-create-shell__confirm-create-actions--row">
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(`/so/deal/${d.uuid}`)}
                          >
                            Open deal
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedExistingDeal(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : showCreateNew ? (
                showUploadView ? (
                  <form
                    className="sales-create-shell__upload-panel sales-create-shell__upload-panel--standalone"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (createReview) {
                        // The deal already exists and some file needs a look: just continue to it.
                        const target = createReview;
                        setCreateReview(null);
                        setScriptAdditionalNotes('');
                        setCreateFileNotes({});
                        navigate(target.path, { state: target.state });
                      } else if (createScopeFile) {
                        // A scope list takes the import path; transcripts keep the analyze path.
                        void handleCreateFromScopeFile();
                      } else {
                        void handleCreateFromScript(e);
                      }
                    }}
                  >
                    {scriptFiles.length > 0 ? (
                      <ul className="deal-page__transcript-upload-files">
                        {scriptFiles.map((file, i) => (
                          <li
                            key={`${file.name}-${file.size}-${i}`}
                            className="deal-page__transcript-upload-file"
                          >
                            <span className="deal-page__transcript-upload-file-name">
                              {file.name}
                            </span>
                            <span className="deal-page__transcript-upload-file-size">
                              {(file.size / 1024).toFixed(1)} KB
                            </span>
                            <button
                              type="button"
                              className="deal-page__transcript-upload-file-remove"
                              aria-label={`Remove ${file.name}`}
                              disabled={submittingScript || createReview !== null}
                              onClick={() =>
                                setScriptFiles((prev) => prev.filter((_, idx) => idx !== i))
                              }
                            >
                              <Cross1Icon
                                width={IconSize.xs}
                                height={IconSize.xs}
                                aria-hidden="true"
                              />
                            </button>
                            <FileNoteLine note={createFileNotes[localFileKey(file)]} />
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {scriptDriveFiles.length > 0 ? (
                      <ul className="deal-page__transcript-upload-files">
                        {scriptDriveFiles.map((file, i) => (
                          <li key={file.id} className="deal-page__transcript-upload-file">
                            <span className="deal-page__transcript-upload-file-name">
                              {file.name}
                            </span>
                            <span className="deal-page__transcript-upload-file-size">Drive</span>
                            <button
                              type="button"
                              className="deal-page__transcript-upload-file-remove"
                              aria-label={`Remove ${file.name}`}
                              disabled={submittingScript || createReview !== null}
                              onClick={() =>
                                setScriptDriveFiles((prev) => prev.filter((_, idx) => idx !== i))
                              }
                            >
                              <Cross1Icon
                                width={IconSize.xs}
                                height={IconSize.xs}
                                aria-hidden="true"
                              />
                            </button>
                            <FileNoteLine note={createFileNotes[driveFileKey(file)]} />
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {createScopeFile ? (
                      <ul className="deal-page__transcript-upload-files">
                        <li className="deal-page__transcript-upload-file">
                          <span className="deal-page__transcript-upload-file-name">
                            {createScopeFile.name}
                          </span>
                          <span className="deal-page__transcript-upload-file-size">
                            scope list
                          </span>
                          <button
                            type="button"
                            className="deal-page__transcript-upload-file-remove"
                            aria-label={`Remove ${createScopeFile.name}`}
                            disabled={createScopeBusy}
                            onClick={() => {
                              setCreateScopeFile(null);
                              setCreateError(null);
                            }}
                          >
                            <Cross1Icon
                              width={IconSize.xs}
                              height={IconSize.xs}
                              aria-hidden="true"
                            />
                          </button>
                        </li>
                      </ul>
                    ) : null}
                    {createScopeFile == null &&
                    scriptFiles.length + scriptDriveFiles.length < TRANSCRIPT_MAX_FILES ? (
                      <>
                        <div className="customer-doc-add-panel__dropzone">
                          <label
                            className={`customer-doc-add-panel__file-label${isDraggingScript ? ' customer-doc-add-panel__file-label--dragging' : ''}`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setIsDraggingScript(true);
                            }}
                            onDragLeave={() => setIsDraggingScript(false)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setIsDraggingScript(false);
                              if (e.dataTransfer.files.length > 0) {
                                routeCreateFiles(e.dataTransfer.files);
                              }
                            }}
                          >
                            <input
                              type="file"
                              accept={
                                scopeUploadAllowedForCreate
                                  ? `${DEAL_DOCUMENT_ACCEPT_ATTR},${SCOPE_FILE_EXTENSIONS.join(',')}`
                                  : DEAL_DOCUMENT_ACCEPT_ATTR
                              }
                              multiple
                              className="customer-doc-add-panel__file-input"
                              disabled={submittingScript || createReview !== null}
                              onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                  routeCreateFiles(e.target.files);
                                }
                                e.target.value = '';
                              }}
                            />
                            <>
                              <span>
                                {scriptFiles.length > 0
                                  ? 'Add more files'
                                  : scopeUploadAllowedForCreate
                                    ? 'Drop your scope spreadsheet here or click to select'
                                    : 'Drop files here or click to select'}
                              </span>
                              <span className="customer-doc-add-panel__file-hint">
                                {scopeUploadAllowedForCreate
                                  ? `Scope list: ${SCOPE_FILE_EXTENSIONS.join(', ')} - one file, estimated straight away. Other documents instead: ${DEAL_DOCUMENT_FORMATS_LABEL} (up to ${TRANSCRIPT_MAX_FILES})`
                                  : `${DEAL_DOCUMENT_FORMATS_LABEL} (up to ${TRANSCRIPT_MAX_FILES})`}
                              </span>
                            </>
                          </label>
                        </div>
                        <DriveTranscriptImport
                          integrationUuid={driveIntegration.integrationUuid}
                          disabled={submittingScript || createReview !== null}
                          remainingCapacity={
                            TRANSCRIPT_MAX_FILES - scriptFiles.length - scriptDriveFiles.length
                          }
                          onPicked={(files) =>
                            setScriptDriveFiles((prev) =>
                              [...prev, ...files].slice(
                                0,
                                TRANSCRIPT_MAX_FILES - scriptFiles.length
                              )
                            )
                          }
                          onError={setCreateError}
                        />
                        <DealDocumentPasteBox
                          disabled={submittingScript || createReview !== null}
                          existingNames={scriptFiles.map((f) => f.name)}
                          onAdd={(file) =>
                            setScriptFiles((prev) =>
                              addTranscriptFiles(
                                prev,
                                [file],
                                TRANSCRIPT_MAX_FILES - scriptDriveFiles.length
                              )
                            )
                          }
                        />
                      </>
                    ) : null}
                    {createError ? (
                      <span className="sales-create-shell__error">{createError}</span>
                    ) : null}
                    {createScopeFile == null ? (
                      <DealAdditionalNotesBox
                        value={scriptAdditionalNotes}
                        onChange={setScriptAdditionalNotes}
                        disabled={submittingScript || createReview !== null}
                      />
                    ) : null}
                    {createScopeFile == null && transcriptModeOptions.length > 1 ? (
                      <div className="deal-page__transcript-upload-mode">
                        <span className="deal-page__transcript-upload-mode-label">Mode</span>
                        <ComposerPicker
                          value={scriptAnalyzeMode}
                          options={transcriptModeOptions}
                          onChange={setScriptAnalyzeMode}
                          disabled={submittingScript}
                          ariaLabel="Analysis mode"
                        />
                      </div>
                    ) : null}
                    <div className="customer-doc-add-panel__actions">
                      <Button
                        type="button"
                        variant="ghost"
                        size="md"
                        onClick={() => {
                          setShowUploadView(false);
                          setScriptFiles([]);
                          setScriptDriveFiles([]);
                          setCreateScopeFile(null);
                          setCreateError(null);
                          setCreateFileNotes({});
                          setCreateReview(null);
                          setScriptAdditionalNotes('');
                        }}
                        disabled={submittingScript || createScopeBusy}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        size="md"
                        disabled={
                          (createReview == null &&
                            createScopeFile == null &&
                            scriptFiles.length + scriptDriveFiles.length === 0) ||
                          submittingScript ||
                          createScopeBusy
                        }
                      >
                        {createReview
                          ? 'Continue to deal'
                          : createScopeFile
                            ? createScopeBusy
                              ? 'Importing…'
                              : 'Import scope and estimate'
                            : submittingScript
                              ? 'Creating…'
                              : 'Upload'}
                      </Button>
                    </div>
                    {scriptFileContent ? (
                      <div className="sales-create-shell__script-preview">
                        <pre className="sales-create-shell__script-preview-text">
                          {scriptFileContent}
                        </pre>
                      </div>
                    ) : null}
                  </form>
                ) : (
                  <div className="sales-create-shell__action-cards">
                    <button
                      type="button"
                      className="sales-create-shell__action-card sales-create-shell__action-card--manual"
                      onClick={() => void handleCreateNew()}
                      disabled={submittingCreate}
                    >
                      <span className="sales-create-shell__action-card-icon" aria-hidden="true">
                        <Pencil1Icon width={18} height={18} />
                      </span>
                      <span className="sales-create-shell__action-card-copy">
                        <span className="sales-create-shell__action-card-title">
                          {createSubmissionPhase === 'creating' ? 'Creating…' : 'Start manually'}
                        </span>
                        <span className="sales-create-shell__action-card-desc">
                          Fill out the questionnaire step by step
                        </span>
                      </span>
                      {createSubmissionPhase === 'creating' ? (
                        <span className="sales-create-shell__cta-spinner" aria-hidden="true" />
                      ) : null}
                    </button>
                    <button
                      type="button"
                      className="sales-create-shell__action-card sales-create-shell__action-card--upload"
                      onClick={() => setShowUploadView(true)}
                      disabled={submittingCreate}
                    >
                      <span className="sales-create-shell__action-card-icon" aria-hidden="true">
                        <FileTextIcon width={18} height={18} />
                      </span>
                      <span className="sales-create-shell__action-card-copy">
                        <span className="sales-create-shell__action-card-title">
                          Upload documents
                        </span>
                        <span className="sales-create-shell__action-card-desc">
                          {scopeUploadAllowedForCreate
                            ? 'Upload a scope list spreadsheet (.xlsx, .csv) to scope and estimate straight away - or a transcript to discover the scope instead'
                            : 'Add a transcript, issue log, or status report (.md, .txt, .docx, .pdf)'}
                        </span>
                      </span>
                    </button>
                    <div className="sales-create-shell__action-cancel-row">
                      <Button
                        type="button"
                        variant="ghost"
                        size="md"
                        onClick={() => setShowCreateNew(false)}
                        disabled={submittingCreate}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )
              ) : (
                <>
                  {hasOtherOwnerDeal ? (
                    <div className="sales-create-shell__confirm-create">
                      <div className="sales-create-shell__confirm-create-header">
                        <span className="sales-create-shell__confirm-create-label">
                          Deal exists
                        </span>
                        <p className="sales-create-shell__confirm-create-msg">
                          A deal for this company already exists and was created by another team
                          member. Contact them or your workspace admin to get access.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="sales-create-shell__create-new-col">
                      <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={() => setShowCreateNew(true)}
                        disabled={submittingCreate || submittingScript}
                      >
                        Create new deal
                      </Button>
                      <p className="sales-create-shell__create-new-hint">
                        Select an existing deal from the list, or create a new one.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
        <ExistingDealChoiceDialog
          prompt={existingDealPrompt}
          attributeOptionsByKey={attributeOptionsByKey}
          showOwner={salesModuleRole === 'admin'}
          busy={submittingCreate || submittingScript}
          onContinue={(d) => void continueWithExistingDeal(d)}
          onCreateNew={handleExistingDealCreateNew}
          onCancel={() => setExistingDealPrompt(null)}
        />
      </MainContentShell>
    ) : (
      <Outlet context={ctx} />
    )
  ) : (
    <MainContentShell title={aiAssistantName}>
      {/* Sales-operation landing: hero composer → docked copilot chat (fresh
          session per interaction). The right rail is suppressed on this route,
          so the landing pane is the only mounted AiChatPane for the session. */}
      <SalesLandingChat
        chatSession={chatSession}
        aiAssistantName={aiAssistantName}
        onAction={handleChatAction}
        workspaceUuid={workspace?.uuid ?? ''}
      />
    </MainContentShell>
  );

  return (
    <AiChatActionProvider handlerRef={handleChatActionRef}>
      <AiChatReferenceProvider handlerRef={chatReferenceHandlerRef}>
        <ReferenceEstimateDialog
          groundingRef={chatReference}
          open={chatReference !== null}
          onOpenChange={(o) => {
            if (!o) setChatReference(null);
          }}
        />
        <AppShell
          leftNav={leftNav}
          main={main}
          rightRailItems={rightRailItems}
          suppressModuleChat={isCreateStartRoute || !dealUuid}
        />

        <StaffingRatesDialog coverage={staffingBlock} onClose={() => setStaffingBlock(null)} />

        <Dialog.Root open={regenerateConfirmOpen} onOpenChange={setRegenerateConfirmOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="pi-dialog-overlay" />
            <Dialog.Content className="pi-dialog-content">
              <div className="pi-modal__header">
                <Dialog.Title className="pi-modal__title">
                  {regenerateScope === 'all' ? 'Regenerate documents?' : 'Regenerate estimate?'}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button className="pi-modal__close" aria-label="Close">
                    ×
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="pi-modal__body-text">
                {regenerateScope === 'all'
                  ? 'This will replace the existing estimate, resource plan, and SOW.'
                  : 'This will replace the existing estimate and resource plan. The SOW is left unchanged.'}
              </Dialog.Description>
              {hasRegenerateOverrides && (
                <div className="regen-overrides">
                  <p className="regen-overrides__lead">
                    You&apos;ve manually adjusted hours on{' '}
                    {regenerateOverridePreview.length === 1
                      ? '1 item'
                      : `${regenerateOverridePreview.length} items`}
                    . Keep your edits, or let the new estimate replace them?
                  </p>
                  <ul className="regen-overrides__list">
                    {regenerateOverridePreview.slice(0, 4).map((o) => (
                      <li key={o.key} className="regen-overrides__item">
                        <span className="regen-overrides__label">{o.label}</span>
                        <span className="regen-overrides__hours">
                          {o.originalHours}h <span aria-hidden="true">→</span> {o.overrideHours}h
                        </span>
                      </li>
                    ))}
                  </ul>
                  {regenerateOverridePreview.length > 4 && (
                    <p className="regen-overrides__more">
                      +{regenerateOverridePreview.length - 4} more
                    </p>
                  )}
                  <label className="regen-overrides__toggle">
                    <input
                      type="checkbox"
                      checked={regenerateKeepOverrides}
                      onChange={(e) => setRegenerateKeepOverrides(e.target.checked)}
                    />
                    <span>Keep my manual overrides</span>
                  </label>
                </div>
              )}
              <label className="regen-overrides__toggle">
                <input
                  type="checkbox"
                  checked={regenerateReinferHours}
                  onChange={(e) => setRegenerateReinferHours(e.target.checked)}
                />
                <span>Re-estimate scope-item hours from scratch</span>
              </label>
              {/* Generate all also rebuilds the SOW: offer to carry its structured edits. */}
              {regenerateScope === 'all' && isSowSupportedForDeal && hasSowRegenerateOverrides && (
                <div className="regen-overrides">
                  <p className="regen-overrides__lead">
                    The SOW will be regenerated too. You&apos;ve edited{' '}
                    {sowRegenerateOverridePreview
                      .filter((o) => o.kept)
                      .map((o) => o.label)
                      .join(', ')}
                    . Keep those on the new SOW?
                  </p>
                  {sowRegenerateOverridePreview.some((o) => !o.kept) && (
                    <p className="regen-overrides__more">
                      Manual text edits to the SOW body can&apos;t be carried over.
                    </p>
                  )}
                  <label className="regen-overrides__toggle">
                    <input
                      type="checkbox"
                      checked={regenerateKeepSowOverrides}
                      onChange={(e) => setRegenerateKeepSowOverrides(e.target.checked)}
                    />
                    <span>Keep my SOW edits</span>
                  </label>
                </div>
              )}
              <div className="pi-modal__actions">
                <Dialog.Close asChild>
                  <Button type="button" variant="ghost" size="sm">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    keepOverridesOnRegenerateRef.current =
                      hasRegenerateOverrides && regenerateKeepOverrides;
                    reinferHoursOnRegenerateRef.current = regenerateReinferHours;
                    keepSowOverridesOnRegenerateRef.current =
                      regenerateScope === 'all' &&
                      hasSowRegenerateOverrides &&
                      regenerateKeepSowOverrides;
                    setRegenerateConfirmOpen(false);
                    if (regenerateScope === 'all') {
                      void handleGenerateAll();
                    } else {
                      void handleRegenerateEstimate();
                    }
                  }}
                >
                  Regenerate
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Scope-doc removal confirm (a heading's hover x): a scope item or a whole functional
            area. Deletes through the model API (estimate/SOW inputs shrink accordingly). */}
        <Dialog.Root
          open={scopeRemoveTarget != null}
          onOpenChange={(open) => {
            if (!open && !scopeRemoveBusy) setScopeRemoveTarget(null);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="pi-dialog-overlay" />
            <Dialog.Content className="pi-dialog-content">
              <div className="pi-modal__header">
                <Dialog.Title className="pi-modal__title">
                  {scopeRemoveTarget?.kind === 'area'
                    ? 'Delete this functional area?'
                    : 'Delete this question?'}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button className="pi-modal__close" aria-label="Close">
                    ×
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="pi-modal__body-text">
                {scopeRemoveTarget?.kind === 'area'
                  ? `"${scopeRemoveTarget.label}" and its ${scopeRemoveTarget.questionCount === 1 ? 'question' : `${scopeRemoveTarget.questionCount} questions`} will be removed from the deal - they leave the estimate and SOW on the next regenerate.`
                  : `"${scopeRemoveTarget?.label ?? ''}" will be removed from the deal - it leaves the estimate and SOW on the next regenerate.`}
              </Dialog.Description>
              <div className="pi-modal__actions">
                <Dialog.Close asChild>
                  <Button type="button" variant="ghost" size="sm" disabled={scopeRemoveBusy}>
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={scopeRemoveBusy}
                  onClick={() => void handleScopeRemoveConfirm()}
                >
                  {scopeRemoveBusy ? 'Deleting…' : 'Delete'}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* SOW-only regenerate confirm ("Regenerate SOW" button). Reads the saved estimate +
            resource plan; offers to carry structured SOW edits onto the new document. */}
        <Dialog.Root open={sowRegenerateConfirmOpen} onOpenChange={setSowRegenerateConfirmOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="pi-dialog-overlay" />
            <Dialog.Content className="pi-dialog-content">
              <div className="pi-modal__header">
                <Dialog.Title className="pi-modal__title">Regenerate SOW?</Dialog.Title>
                <Dialog.Close asChild>
                  <button className="pi-modal__close" aria-label="Close">
                    ×
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="pi-modal__body-text">
                This rewrites the statement of work from the saved estimate and resource plan. The
                estimate and resource plan are left unchanged.
              </Dialog.Description>
              {hasSowRegenerateOverrides && (
                <div className="regen-overrides">
                  <p className="regen-overrides__lead">
                    You&apos;ve edited{' '}
                    {sowRegenerateOverridePreview
                      .filter((o) => o.kept)
                      .map((o) => o.label)
                      .join(', ')}
                    . Keep your edits, or let the new SOW replace them?
                  </p>
                  {sowRegenerateOverridePreview.some((o) => !o.kept) && (
                    <p className="regen-overrides__more">
                      Manual text edits to the SOW body can&apos;t be carried over.
                    </p>
                  )}
                  {sowRegenerateOverridePreview.some((o) => o.kept) && (
                    <label className="regen-overrides__toggle">
                      <input
                        type="checkbox"
                        checked={sowRegenerateKeepEdits}
                        onChange={(e) => setSowRegenerateKeepEdits(e.target.checked)}
                      />
                      <span>Keep my SOW edits</span>
                    </label>
                  )}
                </div>
              )}
              <div className="pi-modal__actions">
                <Dialog.Close asChild>
                  <Button type="button" variant="ghost" size="sm">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    // Only carry structured overrides exist + the box is checked.
                    keepSowOverridesOnRegenerateRef.current =
                      hasSowRegenerateOverrides &&
                      sowRegenerateOverridePreview.some((o) => o.kept) &&
                      sowRegenerateKeepEdits;
                    setSowRegenerateConfirmOpen(false);
                    void handleRegenerateSow();
                  }}
                >
                  Regenerate
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Duplicate document confirm-or-override (already-attached file detected on upload) */}
        <Dialog.Root
          open={!!transcriptDupConfirm}
          onOpenChange={(open) => {
            if (!open && !transcriptDupBusy) setTranscriptDupConfirm(null);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="pi-dialog-overlay" />
            <Dialog.Content className="pi-dialog-content">
              <div className="pi-modal__header">
                <Dialog.Title className="pi-modal__title">Already attached</Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    className="pi-modal__close"
                    aria-label="Close"
                    disabled={transcriptDupBusy}
                  >
                    ×
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="pi-modal__body-text">
                {(() => {
                  const dups = transcriptDupConfirm?.results.filter((r) => r.reused) ?? [];
                  return dups.length === 1
                    ? `"${dups[0]?.title}" is already attached to this deal.`
                    : `${dups.length} of these files are already attached to this deal.`;
                })()}
              </Dialog.Description>
              <div className="pi-modal__actions">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={transcriptDupBusy}
                  onClick={() => setTranscriptDupConfirm(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={transcriptDupBusy}
                  onClick={() => void handleTranscriptDupFreshCopy()}
                >
                  {transcriptDupBusy ? 'Adding…' : 'Add a fresh copy'}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={transcriptDupBusy}
                  onClick={handleTranscriptDupReanalyze}
                >
                  Re-analyze the existing copy
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Transcript upload dialog (edit-page review mode) */}
        <Dialog.Root
          open={showTranscriptUploadPanel}
          // Drop modality while the Drive picker is open: a modal Radix dialog traps focus and sets
          // `pointer-events: none` on the body, which the picker's iframe (mounted on document.body)
          // inherits - that's what froze the picker. Non-modal restores interaction; the dialog still
          // stays open via the onOpenChange + onInteractOutside guards below.
          modal={!transcriptDrivePickerOpen}
          onOpenChange={(open) => {
            // Ignore close requests while the Drive picker is up (its iframe steals focus and would
            // otherwise dismiss the dialog, dropping the user's picked file).
            if (!open && transcriptDrivePickerOpen) return;
            if (!open) {
              setShowTranscriptUploadPanel(false);
              setTranscriptUploadFiles([]);
              setTranscriptUploadDriveFiles([]);
              setTranscriptUploadError(null);
              setTranscriptFileNotes({});
              setTranscriptReview(null);
              setTranscriptAdditionalNotes('');
            }
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="pi-dialog-overlay" />
            <Dialog.Content
              className="pi-dialog-content deal-page__transcript-upload-dialog"
              // While the Drive picker is open, its iframe/backdrop (mounted on document.body) read
              // as outside interactions; suppress the auto-dismiss so the dialog stays put.
              onInteractOutside={(e) => {
                if (transcriptDrivePickerOpen) e.preventDefault();
              }}
              onPointerDownOutside={(e) => {
                if (transcriptDrivePickerOpen) e.preventDefault();
              }}
              onFocusOutside={(e) => {
                if (transcriptDrivePickerOpen) e.preventDefault();
              }}
            >
              <div className="pi-modal__header">
                <Dialog.Title className="pi-modal__title">Upload documents</Dialog.Title>
                <Dialog.Close asChild>
                  <button className="pi-modal__close" aria-label="Close">
                    ×
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="pi-modal__body-text">
                {transcriptUploadMode === 'agent'
                  ? 'Upload one or more documents - a transcript, issue log, status report, or call notes. The AI reads them together, fills in the questionnaire and adds the scope questions it finds. Answers you already gave are kept, and you can edit or remove anything afterwards.'
                  : 'Upload one or more documents - a transcript, issue log, status report, or call notes. The AI reads them together, extracts answers to the questionnaire, and shows a before/after comparison - nothing is saved until you click Fill.'}
              </Dialog.Description>
              <form
                className="deal-page__transcript-upload-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (transcriptReview) {
                    finishTranscriptUpload(
                      transcriptReview.results,
                      transcriptReview.docLabel,
                      transcriptReview.additionalNotes
                    );
                  } else {
                    void handleUploadTranscript();
                  }
                }}
              >
                {transcriptUploadFiles.length > 0 ? (
                  <ul className="deal-page__transcript-upload-files">
                    {transcriptUploadFiles.map((file, i) => (
                      <li
                        key={`${file.name}-${file.size}-${i}`}
                        className="deal-page__transcript-upload-file"
                      >
                        <span className="deal-page__transcript-upload-file-name">{file.name}</span>
                        <span className="deal-page__transcript-upload-file-size">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                        <button
                          type="button"
                          className="deal-page__transcript-upload-file-remove"
                          aria-label={`Remove ${file.name}`}
                          disabled={uploadingTranscript || transcriptReview !== null}
                          onClick={() =>
                            setTranscriptUploadFiles((prev) => prev.filter((_, idx) => idx !== i))
                          }
                        >
                          <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
                        </button>
                        <FileNoteLine note={transcriptFileNotes[localFileKey(file)]} />
                      </li>
                    ))}
                  </ul>
                ) : null}
                {transcriptUploadDriveFiles.length > 0 ? (
                  <ul className="deal-page__transcript-upload-files">
                    {transcriptUploadDriveFiles.map((file, i) => (
                      <li key={file.id} className="deal-page__transcript-upload-file">
                        <span className="deal-page__transcript-upload-file-name">{file.name}</span>
                        <span className="deal-page__transcript-upload-file-size">Drive</span>
                        <button
                          type="button"
                          className="deal-page__transcript-upload-file-remove"
                          aria-label={`Remove ${file.name}`}
                          disabled={uploadingTranscript || transcriptReview !== null}
                          onClick={() =>
                            setTranscriptUploadDriveFiles((prev) =>
                              prev.filter((_, idx) => idx !== i)
                            )
                          }
                        >
                          <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
                        </button>
                        <FileNoteLine note={transcriptFileNotes[driveFileKey(file)]} />
                      </li>
                    ))}
                  </ul>
                ) : null}
                {transcriptUploadFiles.length + transcriptUploadDriveFiles.length <
                TRANSCRIPT_MAX_FILES ? (
                  <>
                    <div className="customer-doc-add-panel__dropzone">
                      <label
                        className={`customer-doc-add-panel__file-label${isDraggingTranscript ? ' customer-doc-add-panel__file-label--dragging' : ''}`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingTranscript(true);
                        }}
                        onDragLeave={() => setIsDraggingTranscript(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDraggingTranscript(false);
                          if (e.dataTransfer.files.length > 0) {
                            setTranscriptUploadFiles((prev) =>
                              addTranscriptFiles(
                                prev,
                                e.dataTransfer.files,
                                TRANSCRIPT_MAX_FILES - transcriptUploadDriveFiles.length
                              )
                            );
                          }
                        }}
                      >
                        <input
                          type="file"
                          accept={DEAL_DOCUMENT_ACCEPT_ATTR}
                          multiple
                          className="customer-doc-add-panel__file-input"
                          disabled={uploadingTranscript || transcriptReview !== null}
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              setTranscriptUploadFiles((prev) =>
                                addTranscriptFiles(
                                  prev,
                                  e.target.files!,
                                  TRANSCRIPT_MAX_FILES - transcriptUploadDriveFiles.length
                                )
                              );
                            }
                            e.target.value = '';
                          }}
                        />
                        <>
                          <span>
                            {transcriptUploadFiles.length === 0
                              ? 'Drop files here or click to select'
                              : 'Add more files'}
                          </span>
                          <span className="customer-doc-add-panel__file-hint">
                            {DEAL_DOCUMENT_FORMATS_LABEL} (up to {TRANSCRIPT_MAX_FILES})
                          </span>
                        </>
                      </label>
                    </div>
                    <DriveTranscriptImport
                      integrationUuid={driveIntegration.integrationUuid}
                      disabled={uploadingTranscript}
                      remainingCapacity={
                        TRANSCRIPT_MAX_FILES -
                        transcriptUploadFiles.length -
                        transcriptUploadDriveFiles.length
                      }
                      onPicked={(files) =>
                        setTranscriptUploadDriveFiles((prev) =>
                          [...prev, ...files].slice(
                            0,
                            TRANSCRIPT_MAX_FILES - transcriptUploadFiles.length
                          )
                        )
                      }
                      onError={setTranscriptUploadError}
                      onPickerOpenChange={setTranscriptDrivePickerOpen}
                    />
                    <DealDocumentPasteBox
                      disabled={uploadingTranscript || transcriptReview !== null}
                      existingNames={transcriptUploadFiles.map((f) => f.name)}
                      onAdd={(file) =>
                        setTranscriptUploadFiles((prev) =>
                          addTranscriptFiles(
                            prev,
                            [file],
                            TRANSCRIPT_MAX_FILES - transcriptUploadDriveFiles.length
                          )
                        )
                      }
                    />
                  </>
                ) : null}
                {transcriptUploadError ? (
                  <span className="sales-create-shell__error">{transcriptUploadError}</span>
                ) : null}
                <DealAdditionalNotesBox
                  value={transcriptAdditionalNotes}
                  onChange={setTranscriptAdditionalNotes}
                  disabled={uploadingTranscript || transcriptReview !== null}
                />
                {transcriptModeOptions.length > 1 ? (
                  <div className="deal-page__transcript-upload-mode">
                    <span className="deal-page__transcript-upload-mode-label">Mode</span>
                    <ComposerPicker
                      value={transcriptUploadMode}
                      options={transcriptModeOptions}
                      onChange={setTranscriptUploadMode}
                      disabled={uploadingTranscript}
                      ariaLabel="Analysis mode"
                    />
                  </div>
                ) : null}
                <div className="pi-modal__actions">
                  <Dialog.Close asChild>
                    <Button type="button" variant="ghost" size="sm" disabled={uploadingTranscript}>
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={
                      (transcriptReview === null &&
                        transcriptUploadFiles.length + transcriptUploadDriveFiles.length === 0) ||
                      uploadingTranscript
                    }
                  >
                    {uploadingTranscript ? 'Uploading…' : transcriptReview ? 'Continue' : 'Upload'}
                  </Button>
                </div>
              </form>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Upload finalized estimate dialog (CI bridge): single file, parsed server-side into
            the deal's own estimate; a re-upload replaces the previously imported line items. */}
        <Dialog.Root
          open={showEstimateUploadPanel}
          onOpenChange={(open) => {
            if (!open) {
              setShowEstimateUploadPanel(false);
              setEstimateUploadFile(null);
              setEstimateUploadError(null);
            }
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="pi-dialog-overlay" />
            <Dialog.Content className="pi-dialog-content deal-page__transcript-upload-dialog">
              <div className="pi-modal__header">
                <Dialog.Title className="pi-modal__title">Upload finalized estimate</Dialog.Title>
                <Dialog.Close asChild>
                  <button className="pi-modal__close" aria-label="Close">
                    ×
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="pi-modal__body-text">
                Upload your finished estimate (e.g. the improvement estimator spreadsheet). Its line
                items become this deal&apos;s estimate - replacing any previously uploaded ones - so
                you can generate the SOW without filling the questionnaire.
              </Dialog.Description>
              <form
                className="deal-page__transcript-upload-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleUploadEstimateFile();
                }}
              >
                {estimateUploadFile ? (
                  <ul className="deal-page__transcript-upload-files">
                    <li className="deal-page__transcript-upload-file">
                      <span className="deal-page__transcript-upload-file-name">
                        {estimateUploadFile.name}
                      </span>
                      <span className="deal-page__transcript-upload-file-size">
                        {(estimateUploadFile.size / 1024).toFixed(1)} KB
                      </span>
                      <button
                        type="button"
                        className="deal-page__transcript-upload-file-remove"
                        aria-label={`Remove ${estimateUploadFile.name}`}
                        disabled={uploadingEstimateFile}
                        onClick={() => setEstimateUploadFile(null)}
                      >
                        <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
                      </button>
                    </li>
                  </ul>
                ) : (
                  <div className="customer-doc-add-panel__dropzone">
                    <label className="customer-doc-add-panel__file-label">
                      <input
                        type="file"
                        accept={REFERENCE_ESTIMATE_EXTENSIONS.join(',')}
                        className="customer-doc-add-panel__file-input"
                        disabled={uploadingEstimateFile}
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          if (file) setEstimateUploadFile(file);
                          e.target.value = '';
                        }}
                      />
                      <span>Drop a file here or click to select</span>
                      <span className="customer-doc-add-panel__file-hint">
                        Spreadsheet (.xlsx, .csv, ...), Word (.docx), or PDF
                      </span>
                    </label>
                  </div>
                )}
                {estimateUploadError ? (
                  <span className="sales-create-shell__error">{estimateUploadError}</span>
                ) : null}
                <div className="pi-modal__actions">
                  <Dialog.Close asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={uploadingEstimateFile}
                    >
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={!estimateUploadFile || uploadingEstimateFile}
                  >
                    {uploadingEstimateFile ? 'Uploading…' : 'Upload'}
                  </Button>
                </div>
              </form>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Upload scope list dialog (docs/plans/27): the partner's own scope file (problem +
            proposed solution per row, no hours) becomes this deal's scope items, then the normal
            estimate run prices them. Two steps in one modal: the file is parsed for preview only,
            and nothing is written until the mapping is confirmed. */}
        <Dialog.Root
          open={showScopeUploadPanel}
          onOpenChange={(open) => {
            if (!open) {
              setShowScopeUploadPanel(false);
              resetScopeUpload();
            }
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="pi-dialog-overlay" />
            <Dialog.Content className="pi-dialog-content deal-page__scope-upload-dialog">
              <div className="pi-modal__header">
                <Dialog.Title className="pi-modal__title">Upload scope list</Dialog.Title>
                <Dialog.Close asChild>
                  <button className="pi-modal__close" aria-label="Close">
                    ×
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="pi-modal__body-text">
                Upload a spreadsheet where each row is one scope item - the problem and the proposed
                solution. We read the columns, you confirm them, and we estimate the hours. This is
                this deal&apos;s scope, not past-project history.
              </Dialog.Description>

              {!scopeParse ? (
                <div className="customer-doc-add-panel__dropzone">
                  <label className="customer-doc-add-panel__file-label">
                    <input
                      type="file"
                      accept={REFERENCE_ESTIMATE_EXTENSIONS.join(',')}
                      className="customer-doc-add-panel__file-input"
                      disabled={scopeUploadBusy}
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        if (file) void handleScopeFileSelected(file);
                        e.target.value = '';
                      }}
                    />
                    <span>
                      {scopeUploadBusy ? 'Reading the file…' : 'Drop a file here or click to select'}
                    </span>
                    <span className="customer-doc-add-panel__file-hint">
                      Spreadsheet (.xlsx, .csv, ...) - one row per scope item, no hours needed
                    </span>
                  </label>
                </div>
              ) : (
                <div className="deal-page__scope-upload-review">
                  <p className="deal-page__scope-upload-summary">
                    Found <strong>{scopeParse.itemCount}</strong> scope{' '}
                    {scopeParse.itemCount === 1 ? 'item' : 'items'} in{' '}
                    <strong>{scopeParse.sheetName || 'the sheet'}</strong>
                    {scopeParse.sheetCount > 1 ? ` (of ${scopeParse.sheetCount} sheets)` : ''}
                    {scopeUploadFile ? ` - ${scopeUploadFile.name}` : ''}.
                  </p>

                  {scopeParse.confidence === 'low' ? (
                    <p className="deal-page__scope-upload-note deal-page__scope-upload-note--warn">
                      Some columns were hard to recognise - check the mapping below before importing.
                    </p>
                  ) : null}
                  {scopeParse.ignoredHoursColumns.length > 0 ? (
                    <p className="deal-page__scope-upload-note">
                      Ignored hours columns: {scopeParse.ignoredHoursColumns.join(', ')} - we estimate
                      the hours ourselves.
                    </p>
                  ) : null}
                  {scopeParse.unmappedColumns && scopeParse.unmappedColumns.length > 0 ? (
                    <p className="deal-page__scope-upload-note">
                      Unmapped columns: {scopeParse.unmappedColumns.join(', ')} - preserved for exports.
                    </p>
                  ) : null}
                  {scopeParse.existingScopeItemCount > 0 ? (
                    <p className="deal-page__scope-upload-note deal-page__scope-upload-note--warn">
                      This deal already has {scopeParse.existingScopeItemCount} scope{' '}
                      {scopeParse.existingScopeItemCount === 1 ? 'item' : 'items'}. They are kept and
                      estimated alongside these. A previous scope-list upload is replaced.
                    </p>
                  ) : null}
                  {scopeParse.itemCount > 100 ? (
                    <p className="deal-page__scope-upload-note deal-page__scope-upload-note--warn">
                      That is a lot of items to estimate in one run - it will take a while.
                    </p>
                  ) : null}

                  {scopeParse.sheetOptions.length > 1 ? (
                    <div className="deal-page__scope-upload-mapping-row">
                      <span className="deal-page__scope-upload-mapping-label">Sheet</span>
                      <Select
                        size="sm"
                        value={String(scopeParse.sheetIndex)}
                        onValueChange={(value) => {
                          if (scopeUploadFile) void handleScopeFileSelected(scopeUploadFile, Number(value));
                        }}
                      >
                        {scopeParse.sheetOptions.map((option) => (
                          <SelectItem key={option.sheetIndex} value={String(option.sheetIndex)}>
                            {`${option.sheetName || `Sheet ${option.sheetIndex + 1}`} - ${option.itemCount} items, ${option.withBusinessProcess} with a business process`}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  ) : null}
                  {scopeParse.itemsWithoutBusinessProcess > 0 ? (
                    <p className="deal-page__scope-upload-note deal-page__scope-upload-note--warn">
                      {scopeParse.itemsWithoutBusinessProcess} of {scopeParse.itemCount} rows have no
                      business process. The estimate splits its sections by business process, so
                      those land in one flat group - map a Business Process column, or pick another
                      sheet, to get the split.
                    </p>
                  ) : null}
                  <div className="deal-page__scope-upload-mapping">
                    {scopeMappingRows.map((row) => (
                      <div className="deal-page__scope-upload-mapping-row" key={row.target}>
                        <span className="deal-page__scope-upload-mapping-label">{row.label}</span>
                        <Select
                          size="sm"
                          value={row.columnIndex == null ? SCOPE_COLUMN_NONE : String(row.columnIndex)}
                          onValueChange={(value) =>
                            setScopeMapping((prev) => {
                              const next = { ...prev };
                              if (value === SCOPE_COLUMN_NONE) {
                                delete next[row.target];
                                return next;
                              }
                              const columnIndex = Number(value);
                              // One column, one target: clear whoever held it before.
                              for (const [key, held] of Object.entries(next)) {
                                if (held === columnIndex) delete next[key];
                              }
                              next[row.target] = columnIndex;
                              return next;
                            })
                          }
                        >
                          <SelectItem value={SCOPE_COLUMN_NONE}>Not imported</SelectItem>
                          {scopeParse.headers.map((header, index) => (
                            <SelectItem key={`${index}-${header}`} value={String(index)}>
                              {header || `Column ${index + 1}`}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    ))}
                  </div>

                  {scopeParse.previewRows.length > 0 ? (
                    <div className="deal-page__scope-upload-preview">
                      <span className="deal-page__scope-upload-preview-title">
                        First {scopeParse.previewRows.length} row
                        {scopeParse.previewRows.length === 1 ? '' : 's'} as parsed
                      </span>
                      <ul className="deal-page__scope-upload-preview-list">
                        {scopeParse.previewRows.map((previewRow) => (
                          <li
                            className="deal-page__scope-upload-preview-item"
                            key={previewRow.sourceRow}
                          >
                            <span className="deal-page__scope-upload-preview-label">
                              {previewRow.label}
                            </span>
                            {previewRow.values['problem'] ? (
                              <span className="deal-page__scope-upload-preview-text">
                                {previewRow.values['problem']}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {!scopeMappingUsable ? (
                    <p className="deal-page__scope-upload-note deal-page__scope-upload-note--warn">
                      Map a problem or proposed-solution column - without one there is nothing to
                      estimate from.
                    </p>
                  ) : null}
                </div>
              )}

              {scopeUploadError ? (
                <span className="sales-create-shell__error">{scopeUploadError}</span>
              ) : null}

              <div className="pi-modal__actions">
                {scopeParse ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={scopeUploadBusy}
                    onClick={resetScopeUpload}
                  >
                    Choose another file
                  </Button>
                ) : null}
                <Dialog.Close asChild>
                  <Button type="button" variant="ghost" size="sm" disabled={scopeUploadBusy}>
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={!scopeParse || !scopeMappingUsable || scopeUploadBusy}
                  onClick={() => void handleScopeImportConfirm()}
                >
                  {scopeUploadBusy ? 'Working…' : 'Import and estimate'}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Enrich-company confirm dialog (opened from the edit header's ⋮ menu) */}
        <Dialog.Root
          open={showEnrichConfirmPanel}
          onOpenChange={(open) => {
            if (!open) setShowEnrichConfirmPanel(false);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="pi-dialog-overlay" />
            <Dialog.Content className="pi-dialog-content deal-page__transcript-upload-dialog">
              <div className="pi-modal__header">
                <Dialog.Title className="pi-modal__title">Enrich Company</Dialog.Title>
                <Dialog.Close asChild>
                  <button className="pi-modal__close" aria-label="Close">
                    ×
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="pi-modal__body-text">
                The AI will research{' '}
                {deal?.company.name ? `"${deal.company.name}"` : 'this company'} and fill matching
                questionnaire fields with what it finds.
              </Dialog.Description>
              {enrichModeOptions.length > 1 ? (
                <div className="deal-page__transcript-upload-mode">
                  <span className="deal-page__transcript-upload-mode-label">Mode</span>
                  <ComposerPicker
                    value={enrichMode}
                    options={enrichModeOptions}
                    onChange={setEnrichMode}
                    ariaLabel="Enrichment mode"
                  />
                </div>
              ) : null}
              <div className="pi-modal__actions">
                <Dialog.Close asChild>
                  <Button type="button" variant="ghost" size="sm">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button type="button" variant="primary" size="sm" onClick={handleTriggerEnrichment}>
                  Enrich
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </AiChatReferenceProvider>
    </AiChatActionProvider>
  );
}

/**
 * DealPage - exported page component
 *
 * Wrapped by ProtectedRoute in App.tsx.
 */
export function DealWorkspace() {
  return <DealWorkspaceContent />;
}
